// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AIExtractionResponse
 *
 * Response for POST /ai/extract.
 */
export const zAiExtractionResponse = z.object({
    documentId: z.string(),
    engine: z.string(),
    message: z.string(),
    status: z.string()
});

/**
 * AIProfileOverrideRequest
 *
 * Per-message AI profile override.
 */
export const zAiProfileOverrideRequest = z.object({
    credibility_threshold: z.string().nullish(),
    grounding_mode: z.string().nullish(),
    research_mode: z.string().nullish(),
    summary_depth: z.string().nullish(),
    tone: z.string().nullish(),
    transparency: z.string().nullish()
});

/**
 * AISuggestionRead
 *
 * AI-suggested citation.
 *
 * Maps to Nova AISuggestionDTO:
 * { id, title, authors, year, journal?, reason, confidenceScore }
 */
export const zAiSuggestionRead = z.object({
    authors: z.array(z.string()).optional(),
    confidenceScore: z.number().gte(0).lte(100).optional().default(0),
    id: z.uuid(),
    journal: z.string().nullish(),
    reason: z.string(),
    title: z.string(),
    year: z.int()
});

/**
 * AISummary
 */
export const zAiSummary = z.object({
    brief: z.string(),
    detailed: z.string().nullish(),
    generated_at: z.string().optional().default(''),
    key_findings: z.array(z.string()).optional().default([]),
    limitations: z.array(z.string()).nullish(),
    methodology: z.string().nullish()
});

/**
 * AccountDeletionResponse
 *
 * Response when account deletion is initiated.
 */
export const zAccountDeletionResponse = z.object({
    days_remaining: z.int(),
    deletion_scheduled_at: z.iso.datetime(),
    message: z.string()
});

/**
 * AccountDeletionStatusResponse
 *
 * Status of a pending account deletion.
 */
export const zAccountDeletionStatusResponse = z.object({
    can_cancel: z.boolean(),
    days_remaining: z.int(),
    deletion_requested_at: z.iso.datetime(),
    deletion_scheduled_at: z.iso.datetime(),
    status: z.string()
});

/**
 * ActiveWorkPipelineStage
 *
 * Pipeline-stage chip values for active-work rows.
 */
export const zActiveWorkPipelineStage = z.enum([
    'ready',
    'preflight',
    'parse',
    'sections',
    'artifacts',
    'embeddings',
    'enrichment',
    'failed',
    'metadata_only'
]);

/**
 * ActiveWorkItem
 *
 * One row in ActiveWork. Failed rows are surfaced via Critical Blockers
 * (5.3a) instead and do NOT appear here — the two surfaces partition the
 * workspace document set, never overlap.
 */
export const zActiveWorkItem = z.object({
    documentId: z.uuid(),
    lastInteractionAt: z.iso.datetime(),
    pipelineStage: zActiveWorkPipelineStage.optional().default('ready'),
    title: z.string()
});

/**
 * ActiveWorkSurface
 *
 * Documents the user is currently working through.
 */
export const zActiveWorkSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zActiveWorkItem).min(1),
    kind: z.literal('active_work')
});

/**
 * AddCollaboratorRequest
 *
 * Invite a specific user/email to access a resource.
 */
export const zAddCollaboratorRequest = z.object({
    display_name: z.string().max(255).nullish(),
    email: z.string().max(320),
    role: z.string().optional().default('viewer')
});

/**
 * AddCollaboratorsRequest
 *
 * Batch invite multiple people to a resource.
 */
export const zAddCollaboratorsRequest = z.object({
    collaborators: z.array(zAddCollaboratorRequest).min(1).max(50)
});

/**
 * AddDocumentsRequest
 *
 * Add documents to an extraction table.
 */
export const zAddDocumentsRequest = z.object({
    document_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * AddOnCheckoutResponse
 *
 * POST /billing/addons/checkout response.
 */
export const zAddOnCheckoutResponse = z.object({
    addon_id: z.string().nullish(),
    checkout_url: z.string().nullish()
});

/**
 * AddOnRecommendation
 *
 * Smart add-on suggestion based on usage patterns.
 */
export const zAddOnRecommendation = z.object({
    name: z.string(),
    price_display: z.string(),
    product_key: z.string(),
    projected_days_extension: z.int(),
    reason: z.string()
});

/**
 * AddOnStatus
 *
 * Lifecycle state of a purchased add-on.
 */
export const zAddOnStatus = z.enum([
    'active',
    'expired',
    'canceled'
]);

/**
 * AddOnType
 *
 * Type of billing add-on product.
 */
export const zAddOnType = z.enum(['one_time_boost', 'recurring']);

/**
 * AddOnCatalogEntrySchema
 *
 * Product definition for a single add-on in the catalog.
 */
export const zAddOnCatalogEntrySchema = z.object({
    addon_type: zAddOnType,
    bucket: z.string().nullish(),
    description: z.string(),
    min_plan: z.string(),
    name: z.string(),
    price_cents: z.int(),
    price_display: z.string(),
    product_key: z.string(),
    quantity_label: z.string()
});

/**
 * AddOnCatalogResponse
 *
 * GET /billing/addons/catalog response.
 */
export const zAddOnCatalogResponse = z.object({
    items: z.array(zAddOnCatalogEntrySchema)
});

/**
 * AddOnRead
 *
 * Serialized BillingAddOn for API responses.
 */
export const zAddOnRead = z.object({
    activated_at: z.iso.datetime(),
    addon_type: zAddOnType,
    bucket: z.string().nullish(),
    canceled_at: z.iso.datetime().nullish(),
    expires_at: z.iso.datetime().nullish(),
    id: z.uuid(),
    period_key: z.string().nullish(),
    price_cents: z.int(),
    product_key: z.string(),
    quantity: z.int(),
    status: zAddOnStatus,
    unit_label: z.string(),
    workspace_id: z.uuid()
});

/**
 * ActiveAddOnsResponse
 *
 * GET /billing/addons response.
 */
export const zActiveAddOnsResponse = z.object({
    addons: z.array(zAddOnRead),
    effects: z.record(z.string(), z.int()).optional()
});

/**
 * AdminApiKeyRead
 *
 * Active API key summary returned to Commander (no secret material).
 */
export const zAdminApiKeyRead = z.object({
    created_at: z.string(),
    description: z.string().nullish(),
    expires_at: z.string().nullish(),
    id: z.string(),
    is_active: z.boolean(),
    key_prefix: z.string(),
    last_used_at: z.string().nullish(),
    last_used_ip: z.string().nullish(),
    name: z.string(),
    scopes: z.array(z.string()),
    usage_count: z.int(),
    workspace_id: z.string()
});

/**
 * AdminPaginatedResponse
 */
export const zAdminPaginatedResponse = z.object({
    items: z.array(z.record(z.string(), z.unknown())).optional(),
    total: z.int().optional().default(0)
});

/**
 * AdminSessionRead
 *
 * Active session summary returned to Commander.
 */
export const zAdminSessionRead = z.object({
    browser: z.string().nullish(),
    browser_version: z.string().nullish(),
    device_name: z.string().nullish(),
    device_type: z.string().nullish(),
    id: z.string(),
    ip_address: z.string().nullish(),
    ip_city: z.string().nullish(),
    ip_country: z.string().nullish(),
    is_suspicious: z.boolean().optional().default(false),
    is_trusted_device: z.boolean().optional().default(false),
    last_activity_at: z.string(),
    login_at: z.string(),
    login_method: z.string().nullish(),
    os: z.string().nullish(),
    os_version: z.string().nullish(),
    status: z.string(),
    suspicious_reason: z.string().nullish()
});

/**
 * AdminUserDetail
 */
export const zAdminUserDetail = z.object({
    created_at: z.string(),
    email: z.string(),
    email_verified: z.boolean().optional().default(false),
    failed_login_attempts: z.int().optional().default(0),
    first_name: z.string().nullish(),
    id: z.string(),
    is_superuser: z.boolean().optional().default(false),
    last_login_at: z.string().nullish(),
    last_name: z.string().nullish(),
    mfa_enrolled: z.boolean().optional().default(false),
    onboarding_completed: z.boolean().optional().default(false),
    staff_role: z.string().nullish(),
    status: z.string(),
    workspace_count: z.int().optional().default(0),
    workspaces: z.array(z.record(z.string(), z.unknown())).optional()
});

/**
 * AdminWorkspaceDetail
 */
export const zAdminWorkspaceDetail = z.object({
    created_at: z.string(),
    description: z.string().nullish(),
    id: z.string(),
    member_count: z.int().optional().default(0),
    members: z.array(z.record(z.string(), z.unknown())).optional(),
    name: z.string(),
    owner_id: z.string().nullish(),
    plan: z.string(),
    timezone: z.string().optional().default('UTC'),
    workspace_type: z.string()
});

/**
 * AlertLevel
 */
export const zAlertLevel = z.enum([
    'ok',
    'warning',
    'critical'
]);

/**
 * AnalyticsHealthResponse
 *
 * Analytics service health check response.
 */
export const zAnalyticsHealthResponse = z.object({
    site_url: z.string(),
    status: z.string()
});

/**
 * ApdexEndpoint
 *
 * Per-endpoint Apdex score.
 */
export const zApdexEndpoint = z.object({
    endpoint: z.string(),
    rating: z.string(),
    score: z.number()
});

/**
 * ApdexStatus
 *
 * Global Apdex score and satisfaction distribution.
 */
export const zApdexStatus = z.object({
    frustrated: z.int(),
    rating: z.string(),
    satisfied: z.int(),
    satisfied_threshold_ms: z.int(),
    score: z.number().gte(0).lte(1),
    tolerating: z.int(),
    tolerating_threshold_ms: z.int()
});

/**
 * ApdexDashboard
 *
 * Apdex dashboard payload.
 */
export const zApdexDashboard = z.object({
    endpoints: z.array(zApdexEndpoint),
    global_apdex: zApdexStatus,
    timestamp: z.string()
});

/**
 * ApiKeyCreate
 *
 * Request body for creating a new API key.
 */
export const zApiKeyCreate = z.object({
    description: z.string().max(1000).nullish(),
    expiry_days: z.int().gte(1).lte(3650).nullish().default(365),
    name: z.string().min(1).max(200),
    scopes: z.array(z.string()).min(1)
});

/**
 * ApiKeyCreated
 *
 * Response after creating a key — includes the full key (shown ONCE).
 */
export const zApiKeyCreated = z.object({
    created_at: z.iso.datetime(),
    expires_at: z.iso.datetime().nullish(),
    id: z.uuid(),
    key: z.string(),
    key_prefix: z.string(),
    name: z.string(),
    scopes: z.array(z.string())
});

/**
 * ApiKeyRead
 *
 * Read-only representation — never exposes the full key.
 */
export const zApiKeyRead = z.object({
    created_at: z.iso.datetime(),
    description: z.string().nullish(),
    expires_at: z.iso.datetime().nullish(),
    id: z.uuid(),
    is_active: z.boolean(),
    key_prefix: z.string(),
    last_used_at: z.iso.datetime().nullish(),
    name: z.string(),
    scopes: z.array(z.string()),
    usage_count: z.int().optional().default(0)
});

/**
 * ApiKeyList
 *
 * Paginated list of API keys.
 */
export const zApiKeyList = z.object({
    hasMore: z.boolean().optional().default(false),
    keys: z.array(zApiKeyRead),
    nextCursor: z.string().nullish(),
    pageCount: z.int(),
    prevCursor: z.string().nullish()
});

/**
 * ApiKeyRevokeResult
 */
export const zApiKeyRevokeResult = z.object({
    key_id: z.string(),
    status: z.string()
});

/**
 * ArtifactAIRequest
 *
 * Request body for artifact AI streaming endpoint.
 */
export const zArtifactAiRequest = z.object({
    contextMode: z.enum(['explain', 'challenge'])
});

/**
 * ArtifactCounts
 *
 * Per-artifact-type counts from pipeline extraction.
 *
 * Note: section counts are excluded from the API response — section artifacts
 * power internal search quality (section_tag embeddings) but are too noisy
 * for user-facing display.
 */
export const zArtifactCounts = z.object({
    citations: z.int().optional().default(0),
    claims: z.int().optional().default(0),
    figures: z.int().optional().default(0),
    tables: z.int().optional().default(0)
});

/**
 * ArtifactFailureDetail
 *
 * Failure detail for a single artifact type (shown in ready_with_warnings UI).
 */
export const zArtifactFailureDetail = z.object({
    failureCode: z.string().nullish(),
    failureReason: z.string().nullish()
});

/**
 * ArtifactStatusEnum
 *
 * Per-artifact processing status returned by the pipeline.
 *
 * Mirrors ArtifactStatusField from app.models.documents.
 */
export const zArtifactStatusEnum = z.enum([
    'pending',
    'processing',
    'ready',
    'failed',
    'partial',
    'not_triggered',
    'enriched'
]);

/**
 * ArtifactTypeEnum
 *
 * Types of structured artifacts extracted from documents.
 */
export const zArtifactTypeEnum = z.enum([
    'section',
    'table',
    'figure',
    'citation',
    'claim',
    'equation',
    'cross_reference'
]);

/**
 * ArtifactRead
 *
 * A single extracted artifact (section, table, figure, citation, or claim).
 */
export const zArtifactRead = z.object({
    artifactStatus: z.string().nullish(),
    artifactType: zArtifactTypeEnum,
    bboxNorm: z.record(z.string(), z.unknown()).nullish(),
    caption: z.string().nullish(),
    captionOverride: z.string().nullish(),
    confidence: z.number().nullish(),
    confidenceVersion: z.string().nullish(),
    content: z.record(z.string(), z.unknown()),
    createdAt: z.iso.datetime(),
    id: z.uuid(),
    isHidden: z.boolean().optional().default(false),
    isPinned: z.boolean().optional().default(false),
    ocrText: z.string().nullish(),
    page: z.int().nullish(),
    position: z.int().nullish(),
    previewUrl: z.string().nullish(),
    processedPlanTier: z.string().nullish(),
    reviewState: z.string().nullish(),
    reviewThresholdVersion: z.string().nullish(),
    signals: z.record(z.string(), z.unknown()).optional().default({}),
    source: z.string().nullish()
});

/**
 * ArtifactListResponse
 *
 * Response from GET /documents/{id}/artifacts.
 */
export const zArtifactListResponse = z.object({
    artifacts: z.array(zArtifactRead),
    documentId: z.uuid(),
    globalVersionId: z.uuid().nullish(),
    hasMore: z.boolean().optional().default(false),
    nextCursor: z.string().nullish(),
    pageSize: z.int().optional().default(100),
    prevCursor: z.string().nullish(),
    total: z.int()
});

/**
 * AssetCreateRequest
 *
 * Request to create asset record after upload.
 *
 * Step 2 of upload flow (after file uploaded to storage).
 *
 * Validation:
 * - Uses centralized MIME type and SHA256 validation
 * - Duration required for videos, null for images
 * - Dimensions validated for reasonable screen sizes
 */
export const zAssetCreateRequest = z.object({
    asset_type: z.string(),
    checksum_sha256: z.string().length(64),
    duration_s: z.int().gte(1).lte(600).nullish(),
    entity_id: z.uuid().nullish(),
    file_size_bytes: z.int().gt(0).lte(524288000),
    height: z.int().gte(50).lte(4320),
    mime_type: z.string(),
    original_filename: z.string().max(255).nullish(),
    storage_key: z.string().max(512),
    width: z.int().gte(50).lte(7680)
});

/**
 * AssetDeleteRequest
 *
 * Optional body for delete endpoint.
 */
export const zAssetDeleteRequest = z.object({
    reason: z.string().max(500).nullish()
});

/**
 * AssetExportResponse
 *
 * GDPR data export response.
 */
export const zAssetExportResponse = z.object({
    asset: z.array(z.record(z.string(), z.unknown())),
    exported_at: z.iso.datetime(),
    exported_by: z.uuid(),
    total_assets: z.int(),
    user_id: z.uuid()
});

/**
 * AssetHistoryResponse
 *
 * Detailed deletion/restore history for admin.
 *
 * Used for audit trails and GDPR compliance.
 */
export const zAssetHistoryResponse = z.object({
    asset_id: z.uuid(),
    can_be_restored: z.boolean(),
    cleanup_retry_count: z.int(),
    cleanup_status: z.string(),
    deleted_by_id: z.uuid().nullish(),
    deletion_reason: z.string().nullish(),
    deletion_request_type: z.string().nullish(),
    is_deleted: z.boolean(),
    last_cleanup_attempt_at: z.iso.datetime().nullish(),
    last_restored_at: z.iso.datetime().nullish(),
    last_restored_by_id: z.uuid().nullish(),
    restore_count: z.int(),
    scheduled_deletion_at: z.iso.datetime().nullish(),
    soft_deleted_at: z.iso.datetime().nullish(),
    storage_deleted_at: z.iso.datetime().nullish()
});

/**
 * AssetResponse
 *
 * Asset response with signed URL.
 *
 * This is what API returns for all asset queries.
 */
export const zAssetResponse = z.object({
    aspect_ratio: z.string(),
    asset_type: z.string(),
    category: z.string(),
    checksum_sha256: z.string(),
    created_at: z.iso.datetime(),
    deleted_by_id: z.uuid().nullish(),
    duration_s: z.int().nullish(),
    entity_id: z.uuid(),
    entity_type: z.string(),
    file_size_bytes: z.int(),
    file_size_mb: z.number(),
    height: z.int(),
    id: z.uuid(),
    is_deleted: z.boolean(),
    is_image: z.boolean(),
    is_video: z.boolean(),
    last_restored_at: z.iso.datetime().nullish(),
    mime_type: z.string(),
    original_filename: z.string().nullish(),
    restore_count: z.int().optional().default(0),
    soft_deleted_at: z.iso.datetime().nullish(),
    updated_at: z.iso.datetime(),
    url: z.string().nullish(),
    width: z.int()
});

/**
 * AssetListResponse
 *
 * Response for list endpoints.
 */
export const zAssetListResponse = z.object({
    items: z.array(zAssetResponse),
    page: z.record(z.string(), z.unknown()).optional(),
    total: z.int()
});

/**
 * AssetRestoreRequest
 *
 * Optional body for restore endpoint.
 */
export const zAssetRestoreRequest = z.object({
    retention_days: z.int().gte(1).lte(90).optional().default(30)
});

/**
 * AssetStatsResponse
 *
 * Asset statistics for monitoring dashboard.
 */
export const zAssetStatsResponse = z.object({
    active_assets: z.int(),
    by_entity_type: z.record(z.string(), z.int()).optional(),
    deleted_assets: z.int(),
    failed_cleanup: z.int(),
    pending_cleanup: z.int(),
    total_assets: z.int(),
    total_images: z.int(),
    total_storage_bytes: z.int(),
    total_storage_gb: z.number(),
    total_videos: z.int()
});

/**
 * AssetUploadRequest
 *
 * Request for presigned upload URL.
 *
 * Step 1 of upload flow.
 *
 * Validation:
 * - Uses centralized MIME type validation
 * - File size limits from FileSizeLimits
 * - SHA256 checksum for integrity verification
 */
export const zAssetUploadRequest = z.object({
    asset_type: z.string(),
    checksum_sha256: z.string().length(64),
    entity_id: z.uuid().nullish(),
    file_size_bytes: z.int().gt(0).lte(524288000),
    mime_type: z.string()
});

/**
 * AttachPdfRequest
 *
 * Request body for POST /documents/{id}/attach-pdf.
 *
 * Attaches a PDF file to a metadata-only document. The server returns a
 * presigned PUT URL for direct-to-R2 upload, identical to the standard
 * upload flow. After uploading, the client calls confirm-upload (not a
 * separate endpoint — the standard confirm-upload path handles both).
 */
export const zAttachPdfRequest = z.object({
    content_hash: z.string().length(64).regex(/^[a-f0-9]{64}$/).nullish(),
    file_name: z.string().min(1).max(500),
    file_size: z.int().gt(0).lte(524288000),
    mime_type: z.string()
});

/**
 * AttentionItemReason
 *
 * Reason an attention item is surfaced.
 *
 * Slice 1 only emits ``READY_WITH_WARNINGS``. ``NEEDS_REVIEW`` is reserved
 * for a future slice that adds confidence-gated review detection.
 */
export const zAttentionItemReason = z.enum(['ready_with_warnings', 'needs_review']);

/**
 * AttentionItem
 *
 * One row in the Standard Attention Items surface.
 */
export const zAttentionItem = z.object({
    detail: z.string().nullish(),
    documentId: z.uuid(),
    lastEventAt: z.iso.datetime(),
    reason: zAttentionItemReason,
    title: z.string()
});

/**
 * AttentionItemsSurface
 *
 * Documents the user can ignore without losing the document.
 *
 * Slice 1 only surfaces READY_WITH_WARNINGS rows. NEEDS_REVIEW detection
 * lands in a future slice.
 */
export const zAttentionItemsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zAttentionItem).min(1),
    kind: z.literal('standard_attention_items')
});

/**
 * AuditAction
 *
 * Categorized audit actions for filtering and reporting.
 */
export const zAuditAction = z.enum([
    'auth.login',
    'auth.logout',
    'auth.login_failed',
    'auth.mfa_verify',
    'auth.mfa_recovery',
    'auth.password_reset',
    'auth.password_change',
    'auth.session_revoke',
    'authz.role_change',
    'authz.permission_grant',
    'authz.permission_revoke',
    'authz.superuser_grant',
    'authz.superuser_revoke',
    'workspace.create',
    'workspace.update',
    'workspace.delete',
    'workspace.member_add',
    'workspace.member_remove',
    'workspace.invite_send',
    'workspace.invite_accept',
    'org.create',
    'org.update',
    'org.delete',
    'org.sso_configure',
    'org.domain_verify',
    'data.create',
    'data.update',
    'data.delete',
    'data.export',
    'data.import',
    'data.share',
    'billing.subscribe',
    'billing.upgrade',
    'billing.downgrade',
    'billing.cancel',
    'billing.payment_failed',
    'system.config_change',
    'system.feature_flag',
    'system.webhook_config',
    'system.api_key_create',
    'system.api_key_revoke',
    'security.suspicious',
    'security.rate_limited',
    'security.account_lockout',
    'security.account_unlock',
    'document.uploaded',
    'document.scanned',
    'document.scan_passed',
    'document.scan_blocked',
    'document.viewed',
    'document.deleted',
    'document.restored',
    'extraction.started',
    'extraction.completed',
    'extraction.edited',
    'extraction.approved',
    'citation.suggested',
    'citation.accepted',
    'citation.edited',
    'citation.removed',
    'export.docx',
    'export.pdf',
    'quota.exceeded',
    'quota.updated',
    'member.invited',
    'member.removed',
    'member.role_changed',
    'workspace.ownership_transferred',
    'workspace.member_removed',
    'workspace.deleted',
    'workspace.restored',
    'workspace.invite_declined',
    'workspace.invite_revoked',
    'doc.metadata_updated',
    'doc.moved',
    'doc.purged',
    'doc.restored',
    'doc.exported',
    'note.created',
    'note.updated',
    'note.deleted',
    'note.restored',
    'note.session_started',
    'note.session_committed',
    'note.citation_inserted',
    'note.edge_created',
    'note.edge_deleted',
    'note.exported',
    'note.snapshot_created',
    'note.snapshot_restored',
    'graph.edge_created',
    'graph.edge_updated',
    'graph.edge_deleted',
    'graph.signal_created',
    'collection.created',
    'collection.updated',
    'collection.deleted',
    'collection.archived',
    'collection.unarchived',
    'collection.document_added',
    'collection.document_removed',
    'collection.reordered',
    'collection.ownership_transferred',
    'auth.login_succeeded',
    'auth.session_expired',
    'auth.api_key_created',
    'auth.api_key_revoked',
    'ai.chat_started',
    'ai.chat_completed',
    'ai.chat_failed',
    'ai.extraction_job_started',
    'ai.extraction_job_completed',
    'ai.extraction_job_failed',
    'ai.citation_suggestions_generated',
    'ai.agent_action',
    'ai.safety_policy_applied',
    'security.permission_policy_changed',
    'security.data_retention_changed',
    'security.audit_export_generated',
    'security.support_access_granted',
    'security.support_access_revoked',
    'staff.role_changed',
    'staff.user_viewed',
    'staff.user_impersonated',
    'staff.user_disabled',
    'staff.user_enabled',
    'staff.workspace_viewed',
    'staff.feature_flag_toggled',
    'staff.document_retried',
    'staff.document_bulk_retried',
    'staff.dlq_replayed',
    'staff.dlq_purged',
    'staff.sentinel_action_approved',
    'staff.sentinel_action_denied',
    'org.plan_changed',
    'ingestion.triggered',
    'ingestion.stage_started',
    'ingestion.stage_completed',
    'ingestion.stage_failed',
    'ingestion.completed',
    'ingestion.failed',
    'ingestion.dead_lettered',
    'ingestion.reprocess_requested',
    'ingestion.model_upgrade_applied',
    'ingestion.dead_letter_replayed',
    'review.human_override',
    'review.human_confirmed',
    'review.human_rejected',
    'audit.chain_verified',
    'audit.chain_broken',
    'sr.project.created',
    'sr.project.status_changed',
    'sr.protocol.finalized',
    'sr.protocol.amended',
    'sr.record.imported',
    'sr.dedup.decision',
    'sr.screening.decision',
    'sr.screening.blinded_access_denied',
    'sr.screening.conflict.created',
    'sr.screening.conflict.resolved',
    'sr.extraction.value.set',
    'sr.extraction.value.confirmed',
    'sr.rob.domain.answered',
    'sr.rob.domain.judgment',
    'sr.rob.assessment.completed'
]);

/**
 * AuditEventRead
 *
 * Single audit event for API responses.
 */
export const zAuditEventRead = z.object({
    action: z.string(),
    actorDisplay: z.string().nullish(),
    actorId: z.uuid().nullish(),
    actorType: z.string(),
    auditStatus: z.string().optional().default('success'),
    chainHash: z.string().nullish(),
    clientApp: z.string().nullish(),
    clientVersion: z.string().nullish(),
    createdAt: z.iso.datetime(),
    description: z.string().nullish(),
    entityId: z.string().nullish(),
    entityType: z.string().nullish(),
    eventMetadata: z.record(z.string(), z.unknown()).nullish(),
    id: z.uuid(),
    ipAddress: z.string().nullish(),
    modelInfo: z.record(z.string(), z.unknown()).nullish(),
    reason: z.string().nullish(),
    requestId: z.string().nullish(),
    schemaVersion: z.int().optional().default(1),
    sessionId: z.string().nullish(),
    severity: z.string().optional().default('info'),
    traceId: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    userAgent: z.string().nullish(),
    workspaceId: z.uuid().nullish()
});

/**
 * AuditListResponse
 *
 * Paginated list of audit events.
 */
export const zAuditListResponse = z.object({
    items: z.array(zAuditEventRead),
    nextCursor: z.string().nullish()
});

/**
 * AuditStatus
 *
 * Outcome of the audited action.
 */
export const zAuditStatus = z.enum([
    'success',
    'denied',
    'failed'
]);

/**
 * AuthSessionStatus
 *
 * Login / authentication session status.
 *
 * Renamed from SessionStatus to avoid OpenAPI enum collision with
 * the research-session SessionStatus defined in schemas/session.py.
 */
export const zAuthSessionStatus = z.enum([
    'active',
    'expired',
    'revoked',
    'suspicious'
]);

/**
 * AuthorDTO
 */
export const zAuthorDto = z.object({
    affiliation: z.string().nullish(),
    name: z.string(),
    orcid: z.string().nullish()
});

/**
 * BackfillTriggerResponse
 *
 * Response for backfill trigger operations.
 */
export const zBackfillTriggerResponse = z.object({
    job_id: z.string(),
    message: z.string(),
    status: z.string().optional().default('enqueued')
});

/**
 * BatchImportFailure
 */
export const zBatchImportFailure = z.object({
    error: z.string(),
    id: z.string()
});

/**
 * BatchImportRequestSchema
 */
export const zBatchImportRequestSchema = z.object({
    itemIds: z.array(z.uuid()).min(1).max(100)
});

/**
 * BatchImportResponseSchema
 */
export const zBatchImportResponseSchema = z.object({
    failed: z.array(zBatchImportFailure),
    imported: z.array(z.string()),
    skipped: z.array(z.string())
});

/**
 * BatchImportWithPdfRequestSchema
 *
 * Batch import saved items through the full document pipeline.
 */
export const zBatchImportWithPdfRequestSchema = z.object({
    itemIds: z.array(z.uuid()).min(1).max(50)
});

/**
 * BatchReprocessRequest
 *
 * Request for POST /documents/batch-reprocess.
 */
export const zBatchReprocessRequest = z.object({
    stages: z.array(z.string()).min(1).max(8)
});

/**
 * BatchReprocessResponse
 *
 * Response for POST /documents/batch-reprocess.
 */
export const zBatchReprocessResponse = z.object({
    documentIds: z.array(z.string()),
    enqueued: z.int(),
    stages: z.array(z.string())
});

/**
 * BillingBucket
 *
 * Independent cost buckets. Never cross-normalised (RFC-002 §4.2).
 */
export const zBillingBucket = z.enum([
    'ai',
    'discovery',
    'extraction',
    'storage'
]);

/**
 * AuditExportRow
 *
 * Single ledger event row in audit export (user_id hashed for privacy).
 */
export const zAuditExportRow = z.object({
    action_type: z.string(),
    bucket: zBillingBucket,
    correction_of: z.uuid().nullable(),
    cost_units: z.number().nullable(),
    created_at: z.iso.datetime(),
    id: z.uuid(),
    model: z.string().nullable(),
    period_key: z.string(),
    provider: z.string().nullable(),
    provider_cost_usd: z.number().nullish(),
    quantity: z.number(),
    unit: z.string(),
    user_hash: z.string(),
    workspace_id: z.uuid()
});

/**
 * AuditExportResponse
 *
 * GET /v1/billing/audit/export response.
 */
export const zAuditExportResponse = z.object({
    has_more: z.boolean(),
    next_cursor: z.string().nullish(),
    page_count: z.int(),
    period_key: z.string(),
    prev_cursor: z.string().nullish(),
    rows: z.array(zAuditExportRow),
    workspace_id: z.uuid()
});

/**
 * BillingMode
 *
 * Server-side billing mode. Controls whether payment is active.
 */
export const zBillingMode = z.enum(['beta', 'live']);

/**
 * BillingOverview
 */
export const zBillingOverview = z.object({
    current_month: z.string().optional().default(''),
    mtd_ai_spend_usd: z.number().optional().default(0),
    total_subscriptions: z.int().optional().default(0)
});

/**
 * BillingPortalResponse
 *
 * POST /v1/billing/portal response.
 */
export const zBillingPortalResponse = z.object({
    portal_url: z.string()
});

/**
 * Body_post_api_v1_systematic-reviews_review_id_import
 */
export const zBodyPostApiV1SystematicReviewsReviewIdImport = z.object({
    file: z.string()
});

/**
 * BoundingBoxSchema
 *
 * Bounding box as percentage of page dimensions (0–100).
 */
export const zBoundingBoxSchema = z.object({
    height: z.number().gte(0).lte(100),
    width: z.number().gte(0).lte(100),
    x: z.number().gte(0).lte(100),
    y: z.number().gte(0).lte(100)
});

/**
 * BulkDocumentIds
 *
 * Request body for bulk document operations.
 */
export const zBulkDocumentIds = z.object({
    ids: z.array(z.uuid()).min(1).max(500)
});

/**
 * BulkMoveRequest
 *
 * Request body for bulk move to collection.
 */
export const zBulkMoveRequest = z.object({
    collectionId: z.uuid().nullish(),
    ids: z.array(z.uuid()).min(1).max(500)
});

/**
 * BulkOperationResponse
 *
 * Response for bulk operations.
 */
export const zBulkOperationResponse = z.object({
    affected: z.int(),
    ids: z.array(z.uuid())
});

/**
 * BulkTagRequest
 *
 * Request body for bulk tag operations.
 */
export const zBulkTagRequest = z.object({
    addTags: z.array(z.string()).max(20).optional(),
    ids: z.array(z.uuid()).min(1).max(500),
    removeTags: z.array(z.string()).max(20).optional()
});

/**
 * CatalogMetadata
 *
 * Metadata for cache and versioning of the plan catalog.
 */
export const zCatalogMetadata = z.object({
    catalog_version: z.string(),
    default_currency: z.string().optional().default('usd'),
    default_interval: z.string().optional().default('annual')
});

/**
 * CellStatus
 *
 * Status of a single extraction cell.
 */
export const zCellStatus = z.enum([
    'pending',
    'extracting',
    'done',
    'failed',
    'not_found'
]);

/**
 * ChatCitationRead
 *
 * A citation in a chat message.
 */
export const zChatCitationRead = z.object({
    docId: z.uuid().nullish(),
    id: z.uuid(),
    page: z.int().nullish(),
    rankScore: z.number().nullish(),
    sourceId: z.string(),
    sourceType: z.string(),
    valid: z.boolean().optional().default(true)
});

/**
 * CheckoutSessionResponse
 *
 * POST /v1/billing/checkout-session response.
 */
export const zCheckoutSessionResponse = z.object({
    checkout_url: z.string(),
    session_id: z.string().nullish()
});

/**
 * CircuitBreakerResetResponse
 *
 * Response for circuit breaker reset action.
 */
export const zCircuitBreakerResetResponse = z.object({
    breaker: z.string(),
    status: z.string()
});

/**
 * CircuitBreakerStatusResponse
 *
 * Response for circuit breaker status listing.
 */
export const zCircuitBreakerStatusResponse = z.record(z.string(), z.unknown());

/**
 * CircuitInfo
 *
 * Single circuit breaker status.
 */
export const zCircuitInfo = z.object({
    failure_count: z.int(),
    failure_threshold: z.int(),
    name: z.string(),
    recovery_timeout: z.number(),
    state: z.string()
});

/**
 * CircuitBreakerProbeResponse
 *
 * Circuit breaker health probe response.
 */
export const zCircuitBreakerProbeResponse = z.object({
    circuits: z.array(zCircuitInfo),
    status: z.string(),
    timestamp: z.string()
});

/**
 * CircuitState
 */
export const zCircuitState = z.enum([
    'closed',
    'open',
    'half_open'
]);

/**
 * CircuitBreakerStatus
 *
 * Status of a single circuit breaker.
 */
export const zCircuitBreakerStatus = z.object({
    failure_count: z.int(),
    failure_threshold: z.int(),
    last_failure_time: z.string().nullish(),
    name: z.string(),
    recovery_timeout_seconds: z.number(),
    state: zCircuitState,
    total_calls: z.int(),
    total_failures: z.int(),
    total_short_circuits: z.int()
});

/**
 * CircuitBreakerDashboard
 *
 * Circuit breaker dashboard payload.
 */
export const zCircuitBreakerDashboard = z.object({
    all_healthy: z.boolean(),
    breakers: z.array(zCircuitBreakerStatus),
    timestamp: z.string()
});

/**
 * CitationDocumentRef
 *
 * Lightweight reference to a document that uses this citation.
 */
export const zCitationDocumentRef = z.object({
    id: z.uuid(),
    title: z.string()
});

/**
 * CitationEntryRead
 *
 * Single reference or citing paper in the citation graph view.
 */
export const zCitationEntryRead = z.object({
    artifactId: z.uuid().nullish(),
    authors: z.array(z.string()).optional(),
    contextConfidence: z.number().nullish(),
    contextType: z.string().nullish(),
    doi: z.string().nullish(),
    inLibrary: z.boolean().optional().default(false),
    journal: z.string().nullish(),
    title: z.string().nullish(),
    workspaceDocumentId: z.uuid().nullish(),
    year: z.int().nullish()
});

/**
 * CitationExportFormat
 *
 * Supported citation export formats.
 */
export const zCitationExportFormat = z.enum([
    'bibtex',
    'ris',
    'csl-json'
]);

/**
 * CitationExportRequestSchema
 *
 * Request to export citations for workspace items.
 */
export const zCitationExportRequestSchema = z.object({
    format: z.enum([
        'bibtex',
        'ris',
        'csl-json'
    ]).optional().default('bibtex'),
    itemIds: z.array(z.uuid()).min(1).max(200)
});

/**
 * CitationGraphRead
 *
 * Combined references and cited-by view for a single document.
 *
 * references: papers THIS document cites (from extraction pipeline).
 * cited_by: papers that cite THIS document (from CrossRef API).
 */
export const zCitationGraphRead = z.object({
    citedBy: z.array(zCitationEntryRead).optional(),
    citedByCached: z.boolean().optional().default(false),
    citedByCount: z.int().gte(0).optional().default(0),
    documentId: z.uuid(),
    doi: z.string().nullish(),
    references: z.array(zCitationEntryRead).optional(),
    referencesCount: z.int().gte(0).optional().default(0)
});

/**
 * CitationMetricsRead
 *
 * Aggregate citation metrics.
 *
 * Maps to Nova CitationMetricsDTO:
 * { total, usedInDocuments, aiSuggested, unlinked }
 */
export const zCitationMetricsRead = z.object({
    aiSuggested: z.int().optional().default(0),
    total: z.int().optional().default(0),
    unlinked: z.int().optional().default(0),
    usedInDocuments: z.int().optional().default(0)
});

/**
 * CitationStatus
 *
 * Citation usage status.
 */
export const zCitationStatus = z.enum([
    'used',
    'suggested',
    'incomplete'
]);

/**
 * CitationStrength
 *
 * How strongly this citation supports the argument.
 */
export const zCitationStrength = z.enum([
    'strong',
    'moderate',
    'weak'
]);

/**
 * CitationStyle
 */
export const zCitationStyle = z.enum([
    'apa7',
    'mla9',
    'chicago',
    'harvard',
    'ieee',
    'vancouver'
]);

/**
 * CitationType
 *
 * Citation source type.
 */
export const zCitationType = z.enum([
    'article',
    'book',
    'conference',
    'thesis',
    'web',
    'other'
]);

/**
 * CitationCreate
 *
 * Create a citation.
 *
 * Maps to Nova CreateCitationRequest from contracts/citations.ts:
 * { type, title, authors, year, journal?, volume?, issue?,
 * pages?, doi?, url?, abstract?, tags? }
 */
export const zCitationCreate = z.object({
    abstract: z.string().max(5000).nullish(),
    authors: z.array(z.string()).min(1),
    doi: z.string().max(200).nullish(),
    issue: z.string().max(50).nullish(),
    journal: z.string().max(500).nullish(),
    pages: z.string().max(50).nullish(),
    session_id: z.uuid().nullish(),
    tags: z.array(z.string()).optional(),
    title: z.string().min(1).max(1000),
    type: zCitationType.optional().default('article'),
    url: z.url().min(1).max(2083).nullish(),
    volume: z.string().max(50).nullish(),
    workspace_id: z.uuid().nullish(),
    year: z.int().gte(1800).lte(2100)
});

/**
 * CitationRead
 *
 * Full citation response.
 *
 * Maps to Nova CitationDTO from contracts/citations.ts:
 * { id, type, title, authors, year, journal?, volume?, issue?,
 * pages?, doi?, url?, abstract?, tags,
 * status, strength, confidenceScore, usedInDocuments[],
 * aiSuggested, extractedEvidence?, hasMissingMetadata,
 * missingFields?, duplicateOfId?, createdAt, updatedAt }
 */
export const zCitationRead = z.object({
    abstract: z.string().nullish(),
    aiSuggested: z.boolean().optional().default(false),
    authors: z.array(z.string()).optional(),
    confidenceScore: z.number().gte(0).lte(100).optional().default(0),
    createdAt: z.iso.datetime(),
    doi: z.string().nullish(),
    duplicateOfId: z.uuid().nullish(),
    extractedEvidence: z.string().nullish(),
    hasMissingMetadata: z.boolean().optional().default(false),
    id: z.uuid(),
    issue: z.string().nullish(),
    journal: z.string().nullish(),
    missingFields: z.array(z.string()).nullish(),
    pages: z.string().nullish(),
    status: zCitationStatus,
    strength: zCitationStrength,
    tags: z.array(z.string()).optional(),
    title: z.string(),
    type: zCitationType,
    updatedAt: z.iso.datetime(),
    url: z.string().nullish(),
    usedInDocuments: z.array(zCitationDocumentRef).optional(),
    volume: z.string().nullish(),
    year: z.int()
});

/**
 * CitationUpdate
 *
 * Update a citation.
 *
 * Maps to Nova UpdateCitationRequest:
 * { title?, authors?, year?, journal?, volume?, issue?,
 * pages?, doi?, url?, abstract?, tags?, status? }
 */
export const zCitationUpdate = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    doi: z.string().nullish(),
    issue: z.string().nullish(),
    journal: z.string().nullish(),
    pages: z.string().nullish(),
    status: zCitationStatus.nullish(),
    tags: z.array(z.string()).nullish(),
    title: z.string().min(1).max(1000).nullish(),
    url: z.url().min(1).max(2083).nullish(),
    volume: z.string().nullish(),
    year: z.int().gte(1800).lte(2100).nullish()
});

/**
 * ClaimRowRead
 *
 * Single claim row for the structured claims data table.
 *
 * Surfaces all structured fields from GPT-4o-mini extraction for
 * tabular display, filtering, and sorting in the frontend.
 */
export const zClaimRowRead = z.object({
    artifactId: z.uuid(),
    claimText: z.string().max(500),
    confidence: z.number().nullish(),
    confidenceQualifier: z.string().nullish(),
    createdAt: z.iso.datetime(),
    documentId: z.uuid(),
    documentTitle: z.string(),
    effectDirection: z.string().nullish(),
    effectSize: z.number().nullish(),
    evidenceType: z.string(),
    pValue: z.number().nullish(),
    page: z.int().nullish(),
    population: z.string().nullish(),
    sampleSize: z.int().nullish()
});

/**
 * CoCitationPairRead
 *
 * A pair of DOIs that appear together in multiple reference lists.
 */
export const zCoCitationPairRead = z.object({
    coOccurrenceCount: z.int().gte(1),
    doiA: z.string(),
    doiB: z.string(),
    inLibraryA: z.boolean().optional().default(false),
    inLibraryB: z.boolean().optional().default(false),
    titleA: z.string().nullish(),
    titleB: z.string().nullish(),
    workspaceDocumentIdA: z.uuid().nullish(),
    workspaceDocumentIdB: z.uuid().nullish()
});

/**
 * CoCitationListRead
 *
 * Co-citation pairs for a given DOI query.
 */
export const zCoCitationListRead = z.object({
    pairs: z.array(zCoCitationPairRead).optional(),
    queryDoi: z.string(),
    total: z.int().gte(0).optional().default(0)
});

/**
 * CollaboratorResponse
 *
 * A single collaborator on a shared resource.
 */
export const zCollaboratorResponse = z.object({
    acceptedAt: z.iso.datetime().nullish(),
    displayName: z.string().nullish(),
    email: z.string(),
    id: z.string(),
    invitedAt: z.iso.datetime(),
    invitedById: z.string().nullish(),
    role: z.string(),
    userId: z.string().nullish()
});

/**
 * CollaboratorRole
 *
 * Session collaborator roles.
 */
export const zCollaboratorRole = z.enum([
    'owner',
    'editor',
    'commenter',
    'viewer'
]);

/**
 * CollectionCreate
 *
 * Create a collection.
 *
 * Maps to Nova CreateCollectionDto:
 * { name, parent_id?, color?, description?, is_pinned? }
 *
 * workspace_id is NOT in the body — it is resolved from the
 * X-Workspace-ID request header by the route dependency.
 */
export const zCollectionCreate = z.object({
    color: z.string().optional().default('neutral'),
    description: z.string().max(2000).nullish(),
    is_pinned: z.boolean().optional().default(false),
    name: z.string().min(1).max(255),
    parent_id: z.uuid().nullish()
});

/**
 * CollectionItemReorder
 *
 * Reorder documents in a collection.
 */
export const zCollectionItemReorder = z.object({
    ordered_document_ids: z.array(z.uuid()).min(1).max(500)
});

/**
 * CollectionItemReorderBetween
 *
 * Move a single document between two neighbours (drag-and-drop).
 */
export const zCollectionItemReorderBetween = z.object({
    after_document_id: z.uuid().nullish(),
    before_document_id: z.uuid().nullish(),
    document_id: z.uuid()
});

/**
 * CollectionItemsAdd
 *
 * Add documents to a collection (idempotent).
 */
export const zCollectionItemsAdd = z.object({
    document_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * CollectionItemsListResponse
 *
 * Response for listing collection items (documents or notes).
 */
export const zCollectionItemsListResponse = z.object({
    data: z.array(z.unknown()),
    hasMore: z.boolean().optional().default(false),
    nextCursor: z.string().nullish(),
    prevCursor: z.string().nullish(),
    total: z.int().gte(0)
});

/**
 * CollectionItemsRemove
 *
 * Remove documents from a collection (idempotent).
 */
export const zCollectionItemsRemove = z.object({
    document_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * CollectionModifyResponse
 *
 * Response for add/remove operations on collection items.
 */
export const zCollectionModifyResponse = z.object({
    added: z.int().gte(0).nullish(),
    removed: z.int().gte(0).nullish()
});

/**
 * CollectionNotesAdd
 *
 * Move notes into a collection by setting collection_id.
 */
export const zCollectionNotesAdd = z.object({
    note_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * CollectionNotesRemove
 *
 * Remove notes from a collection by unsetting collection_id.
 */
export const zCollectionNotesRemove = z.object({
    note_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * CollectionType
 */
export const zCollectionType = z.enum([
    'personal',
    'shared',
    'smart'
]);

/**
 * CollectionRead
 *
 * Collection response for API.
 *
 * Maps to Nova Collection interface:
 * { id, name, parent_id?, workspace_id, collection_type, color, description?,
 * is_pinned, is_archived, sort_mode, created_by,
 * created_at, updated_at, document_count? }
 */
export const zCollectionRead = z.object({
    collectionType: zCollectionType.optional().default('personal'),
    color: z.string().optional().default('neutral'),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid().nullish(),
    description: z.string().nullish(),
    documentCount: z.int().optional().default(0),
    id: z.uuid(),
    isArchived: z.boolean().optional().default(false),
    isPinned: z.boolean().optional().default(false),
    name: z.string(),
    noteCount: z.int().optional().default(0),
    ownerId: z.uuid().nullish(),
    parentId: z.uuid().nullish(),
    smartRules: z.record(z.string(), z.unknown()).nullish(),
    sortMode: z.string().optional().default('manual'),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * CollectionUpdate
 *
 * Update a collection.
 *
 * Maps to Nova UpdateCollectionDto:
 * { name?, parent_id?, color?, description?, is_pinned?, is_archived?, sort_mode? }
 */
export const zCollectionUpdate = z.object({
    color: z.string().nullish(),
    description: z.string().max(2000).nullish(),
    is_archived: z.boolean().nullish(),
    is_pinned: z.boolean().nullish(),
    name: z.string().min(1).max(255).nullish(),
    parent_id: z.uuid().nullish(),
    sort_mode: z.string().nullish()
});

/**
 * ColumnDataType
 *
 * Expected data type of an extraction column's values.
 */
export const zColumnDataType = z.enum([
    'text',
    'number',
    'boolean',
    'list'
]);

/**
 * ColumnTemplateRead
 *
 * A pre-built column template.
 */
export const zColumnTemplateRead = z.object({
    dataType: zColumnDataType,
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    position: z.int(),
    prompt: z.string(),
    studyType: z.string()
});

/**
 * ColumnTemplateGroupRead
 *
 * Column templates grouped by study type.
 */
export const zColumnTemplateGroupRead = z.object({
    studyType: z.string(),
    templates: z.array(zColumnTemplateRead)
});

/**
 * CompareBoardRead
 *
 * Compare board response.
 */
export const zCompareBoardRead = z.object({
    createdAt: z.iso.datetime(),
    createdBy: z.uuid(),
    id: z.uuid(),
    items: z.array(z.record(z.string(), z.unknown())),
    layout: z.string(),
    title: z.string().nullable(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid(),
    zoomLocked: z.boolean()
});

/**
 * ConfidenceLevel
 *
 * Confidence level for insights.
 */
export const zConfidenceLevel = z.enum([
    'high',
    'medium',
    'low',
    'speculative'
]);

/**
 * ConnectedArtifactItem
 *
 * One row in the Connected Artifacts surface (Slice 6, spec §5.14).
 *
 * Polymorphic by ``target_kind`` — the target_id resolves to a
 * WorkspaceDocument (paper) or WorkspaceArtifact (figure / table).
 * Title is resolved server-side via a single batch query per kind so
 * the client never needs a second round-trip to render.
 */
export const zConnectedArtifactItem = z.object({
    edgeCount: z.int().gte(1),
    lastEdgeAt: z.iso.datetime(),
    targetKind: z.enum([
        'paper',
        'figure',
        'table'
    ]),
    targetNodeId: z.uuid(),
    title: z.string().max(512),
    totalWeight: z.number().gte(0)
});

/**
 * ContradictionPairRead
 *
 * Two claims from different documents that contradict each other.
 */
export const zContradictionPairRead = z.object({
    claimAId: z.uuid(),
    claimBId: z.uuid(),
    claimTextA: z.string(),
    claimTextB: z.string(),
    documentAId: z.uuid(),
    documentBId: z.uuid(),
    documentTitleA: z.string().nullish(),
    documentTitleB: z.string().nullish(),
    explanation: z.string().nullish(),
    id: z.uuid(),
    severity: z.string(),
    similarityScore: z.number().gte(0).lte(1)
});

/**
 * ContradictionListRead
 *
 * Contradiction pairs for a workspace, ordered by severity.
 */
export const zContradictionListRead = z.object({
    highCount: z.int().gte(0).optional().default(0),
    lowCount: z.int().gte(0).optional().default(0),
    mediumCount: z.int().gte(0).optional().default(0),
    pairs: z.array(zContradictionPairRead).optional(),
    total: z.int().gte(0).optional().default(0)
});

/**
 * ContradictionSummary
 *
 * Contradiction pair counts by severity for the dashboard.
 */
export const zContradictionSummary = z.object({
    highCount: z.int().optional().default(0),
    lowCount: z.int().optional().default(0),
    mediumCount: z.int().optional().default(0),
    total: z.int().optional().default(0)
});

/**
 * ConversationListItem
 *
 * Lightweight conversation for list views.
 */
export const zConversationListItem = z.object({
    createdAt: z.iso.datetime(),
    id: z.uuid(),
    lastMessagePreview: z.string().nullish(),
    scopeMode: z.string(),
    title: z.string().nullish(),
    updatedAt: z.iso.datetime()
});

/**
 * ConversationUpdateRequest
 *
 * Request body for PATCH /v1/chat/conversations/{id}.
 */
export const zConversationUpdateRequest = z.object({
    title: z.string().min(1).max(200)
});

/**
 * CreateAddOnCheckoutRequest
 *
 * POST /billing/addons/checkout request.
 */
export const zCreateAddOnCheckoutRequest = z.object({
    product_key: z.string()
});

/**
 * CreateCheckoutSessionRequest
 *
 * POST /v1/billing/checkout-session request.
 */
export const zCreateCheckoutSessionRequest = z.object({
    billing_interval: z.enum(['monthly', 'annual']).optional().default('monthly'),
    cancel_url: z.url().min(1).max(2083).nullish(),
    plan_id: z.enum([
        'starter',
        'pro',
        'team',
        'enterprise'
    ]),
    success_url: z.url().min(1).max(2083).nullish()
});

/**
 * CreateShareCommentRequest
 *
 * Create a comment on a shared resource. Auth required.
 */
export const zCreateShareCommentRequest = z.object({
    content: z.string().min(1).max(2000),
    page_number: z.int().gte(1).nullish(),
    parent_id: z.uuid().nullish()
});

/**
 * CriticalBlockerItem
 *
 * One row in the Critical Operational Blockers surface.
 */
export const zCriticalBlockerItem = z.object({
    attemptNumber: z.int().gte(1).nullish(),
    documentId: z.uuid(),
    failedAt: z.iso.datetime(),
    failureDetail: z.string().nullish(),
    reasonCode: z.string(),
    title: z.string()
});

/**
 * CriticalBlockersSurface
 *
 * Hard ingestion failures that block expected output. Pinned above
 * pipeline tempo so failures are never buried under ambient intelligence.
 */
export const zCriticalBlockersSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zCriticalBlockerItem).min(1),
    kind: z.literal('critical_operational_blockers')
});

/**
 * CriticalInsightType
 *
 * Insight types for the Critical Insight Shelf (spec §16.2).
 *
 * Slice 4 scope ships only ``contradicted_by_newer_evidence`` —
 * the other four types (retracted/corrected source, missing high-centrality
 * paper, single-source dependency, conclusion gap) depend on data sources
 * (Retraction Watch feed, citation-graph centrality, NLI claim detector)
 * that are not yet wired. Their literal values are declared up front so
 * the wire contract is stable when those detectors ship.
 */
export const zCriticalInsightType = z.enum([
    'contradicted_by_newer_evidence',
    'retracted_or_corrected_source',
    'missing_high_centrality_paper',
    'single_source_dependency',
    'conclusion_gap'
]);

/**
 * CriticalInsightItem
 *
 * One row in the Critical Insight Shelf surface.
 *
 * ``severity_rank`` mirrors spec §5.10 ordering (3=retraction,
 * 2=high-confidence contradiction, 1=single-source). Only types that
 * have backing data populate the wire surface; the schema admits the
 * full set so future detectors can plug in without a contract change.
 */
export const zCriticalInsightItem = z.object({
    confidence: z.number().gte(0).lte(1),
    description: z.string(),
    detectedAt: z.iso.datetime(),
    insightId: z.uuid(),
    severityRank: z.int().gte(1).lte(3),
    sourceDocumentIds: z.array(z.uuid()).max(8).optional(),
    title: z.string(),
    type: zCriticalInsightType
});

/**
 * CursorPage
 *
 * Cursor-based pagination metadata.
 */
export const zCursorPage = z.object({
    hasMore: z.boolean(),
    nextCursor: z.string().nullish(),
    prevCursor: z.string().nullish()
});

/**
 * ConversationListResponse
 *
 * Cursor-paginated list of conversations.
 */
export const zConversationListResponse = z.object({
    items: z.array(zConversationListItem),
    pagination: zCursorPage
});

/**
 * DORALevel
 */
export const zDoraLevel = z.enum([
    'elite',
    'high',
    'medium',
    'low'
]);

/**
 * DORAMetricDetail
 *
 * Individual DORA metric with performance classification.
 */
export const zDoraMetricDetail = z.object({
    description: z.string(),
    display_name: z.string(),
    name: z.string(),
    performance_level: zDoraLevel,
    thresholds: z.record(z.string(), z.string()),
    unit: z.string(),
    value: z.number()
});

/**
 * DORADashboard
 *
 * DORA metrics dashboard payload.
 */
export const zDoraDashboard = z.object({
    metrics: z.array(zDoraMetricDetail),
    overall_level: zDoraLevel,
    timestamp: z.string()
});

/**
 * DatabaseHealthDetailResponse
 *
 * Response for database health detail endpoint.
 */
export const zDatabaseHealthDetailResponse = z.object({
    checked_in: z.int().gte(0).nullish(),
    checked_out: z.int().gte(0).nullish(),
    latency_ms: z.number().gte(0),
    overflow: z.int().nullish(),
    pool_size: z.int().gte(0).nullish(),
    status: z.string(),
    timestamp: z.string()
});

/**
 * DatabaseStatsResponse
 *
 * Response for database statistics endpoint.
 */
export const zDatabaseStatsResponse = z.object({
    statistics: z.record(z.string(), z.unknown()),
    status: z.string(),
    timestamp: z.string()
});

/**
 * DauTrendPoint
 */
export const zDauTrendPoint = z.object({
    date: z.string(),
    dau: z.int(),
    sessions: z.int()
});

/**
 * DauTrendResponse
 */
export const zDauTrendResponse = z.object({
    period_days: z.int().optional().default(90),
    points: z.array(zDauTrendPoint).optional()
});

/**
 * DeadLetterBulkReplayRequest
 *
 * Bulk replay body — same required reason, optional filter.
 */
export const zDeadLetterBulkReplayRequest = z.object({
    jobFunction: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(50),
    reason: z.string().min(3).max(500)
});

/**
 * DeadLetterBulkReplayResponse
 *
 * Response for bulk dead letter replay.
 */
export const zDeadLetterBulkReplayResponse = z.object({
    failed: z.int().gte(0),
    replayed: z.int().gte(0),
    total_found: z.int().gte(0)
});

/**
 * DeadLetterBulkReplayResult
 *
 * Outcome of a bulk replay.
 */
export const zDeadLetterBulkReplayResult = z.object({
    failed: z.int(),
    replayed: z.int(),
    totalFound: z.int()
});

/**
 * DeadLetterDetail
 *
 * Full detail of a single dead letter job.
 */
export const zDeadLetterDetail = z.object({
    attempts: z.int(),
    error: z.string(),
    failed_at: z.string(),
    id: z.string(),
    job_function: z.string(),
    payload: z.record(z.string(), z.unknown()),
    replayed_at: z.string().nullish(),
    replayed_by: z.string().nullish(),
    workspace_id: z.string().nullish()
});

/**
 * DeadLetterDetailView
 *
 * Full inspection shape for a single dead-letter row.
 */
export const zDeadLetterDetailView = z.object({
    attempts: z.int(),
    error: z.string(),
    errorChain: z.string().nullish(),
    failedAt: z.iso.datetime(),
    id: z.uuid(),
    jobFunction: z.string(),
    payload: z.record(z.string(), z.unknown()),
    replayedAt: z.iso.datetime().nullish(),
    replayedBy: z.uuid().nullish(),
    workspaceId: z.uuid().nullish()
});

/**
 * DeadLetterListItem
 *
 * Compact shape for the paginated list view.
 */
export const zDeadLetterListItem = z.object({
    attempts: z.int(),
    errorPreview: z.string(),
    failedAt: z.iso.datetime(),
    id: z.uuid(),
    jobFunction: z.string(),
    replayedAt: z.iso.datetime().nullish(),
    workspaceId: z.uuid().nullish()
});

/**
 * DeadLetterReplayRequest
 *
 * Operator-supplied context for replay.
 *
 * ``reason`` is REQUIRED — it is written to the DEAD_LETTER_REPLAYED
 * audit event's ``reason`` column and to the structured log line, so
 * future auditors see *why* an operator chose to replay a specific
 * failed ingestion run. A free-text field (max 500 chars) is enough;
 * SOC 2 Type II audit procedures ask only that the justification be
 * captured and non-empty.
 */
export const zDeadLetterReplayRequest = z.object({
    reason: z.string().min(3).max(500)
});

/**
 * DeadLetterReplayResponse
 *
 * Response for single dead letter replay.
 */
export const zDeadLetterReplayResponse = z.object({
    id: z.string(),
    status: z.string()
});

/**
 * DeadLetterReplayResult
 *
 * Outcome of a single replay.
 */
export const zDeadLetterReplayResult = z.object({
    id: z.uuid(),
    replayedAt: z.iso.datetime().nullish(),
    status: z.string()
});

/**
 * DeadLetterSummary
 *
 * Summary of dead letter queue.
 */
export const zDeadLetterSummary = z.object({
    last_24h_count: z.int().gte(0).optional().default(0),
    top_functions: z.array(z.record(z.string(), z.unknown())).optional(),
    total_unreplayed: z.int().gte(0).optional().default(0)
});

/**
 * DependencyCheckStatus
 *
 * Status of a single dependency check.
 */
export const zDependencyCheckStatus = z.object({
    error: z.string().nullish(),
    latency_ms: z.number().gte(0).nullish(),
    status: z.string()
});

/**
 * DeviceTrustRequest
 *
 * Request to trust/untrust a device
 */
export const zDeviceTrustRequest = z.object({
    session_id: z.uuid(),
    trusted: z.boolean().optional().default(true)
});

/**
 * DirectionBucket
 *
 * Count of claims by effect direction (positive, negative, neutral, mixed).
 */
export const zDirectionBucket = z.object({
    count: z.int().gte(0),
    direction: z.string()
});

/**
 * DisableMFA
 *
 * Disable MFA for a user (admin recovery operation).
 *
 * **Security:** Admin-only, used for account recovery when user loses authenticator.
 * Requires the 6-digit OTP generated by the ``mfa-disable/initiate`` endpoint —
 * this proves the user controls their email and is cooperating with the admin.
 *
 * **Use Cases:**
 * - User lost phone/authenticator device
 * - User locked out due to MFA issues
 * - Device factory reset without backup
 */
export const zDisableMfa = z.object({
    reason: z.string().min(20).max(1000),
    verification_method: z.string().min(5).max(200),
    verification_otp: z.string().length(6).regex(/^\d{6}$/)
});

/**
 * DisableMFAInitiate
 *
 * Initiate the MFA-disable flow for a user (admin recovery operation).
 *
 * Calling this endpoint generates a time-limited OTP and sends it to the
 * user's registered email address.  The admin must obtain the OTP from the
 * user (out-of-band) and supply it in the follow-up ``disable-mfa`` call.
 * This proves the user still controls their email and is cooperating.
 */
export const zDisableMfaInitiate = z.object({
    reason: z.string().min(20).max(1000)
});

/**
 * DisableMFAResponse
 *
 * Response for MFA disable operation.
 */
export const zDisableMfaResponse = z.object({
    factors_found: z.int(),
    factors_removed: z.int(),
    message: z.string(),
    success: z.boolean(),
    user_id: z.string()
});

/**
 * DiscoveryFiltersSchema
 */
export const zDiscoveryFiltersSchema = z.object({
    yearMax: z.int().gte(1900).lte(2100).nullish(),
    yearMin: z.int().gte(1900).lte(2100).nullish()
});

/**
 * DiscoveryResultItem
 *
 * A single discovery search result for onboarding first-value.
 */
export const zDiscoveryResultItem = z.object({
    abstractSnippet: z.string().nullish(),
    authors: z.array(z.string()).optional(),
    citationCount: z.int().nullish(),
    doi: z.string().nullish(),
    journal: z.string().nullish(),
    title: z.string(),
    url: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * DiscoveryResultSchema
 */
export const zDiscoveryResultSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).optional(),
    citationCount: z.int().optional().default(0),
    doi: z.string().nullish(),
    externalId: z.string().nullish(),
    pdfAvailable: z.boolean().optional().default(false),
    resolutionConfidence: z.enum([
        'confirmed',
        'likely',
        'none'
    ]).optional().default('none'),
    resolutionSource: z.enum([
        'unpaywall',
        'arxiv',
        'semantic_scholar',
        'openalex',
        'publisher'
    ]).nullish(),
    resolutionStatus: z.enum([
        'resolved',
        'pending',
        'unavailable'
    ]).optional().default('resolved'),
    score: z.number().optional().default(0),
    source: z.string(),
    title: z.string(),
    url: z.string().nullish(),
    venue: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * DiscoverySearchRequestSchema
 */
export const zDiscoverySearchRequestSchema = z.object({
    filters: zDiscoveryFiltersSchema.nullish(),
    query: z.string().min(1).max(500),
    workspaceId: z.uuid()
});

/**
 * DiscoverySuggestionItem
 *
 * One row in the Discovery Suggestions Queue surface.
 *
 * ``reason_codes`` is a list of short coded labels extracted from
 * ``SuggestionQueue.reason_json`` (e.g. ``["objective_match", "trending"]``).
 * The frontend resolves these to user-facing chips via a static i18n map —
 * we ship codes, not display text, so the rendering layer is interchangeable.
 */
export const zDiscoverySuggestionItem = z.object({
    computedAt: z.iso.datetime(),
    documentId: z.uuid(),
    rankPosition: z.int().gte(0),
    reasonCodes: z.array(z.string()).optional(),
    relevanceScore: z.number().gte(0).lte(1),
    suggestionId: z.uuid(),
    title: z.string()
});

/**
 * DiscoverySuggestionsSurface
 *
 * Discovery suggestions queue (Slice 3, spec §5.7).
 *
 * Trigger: fingerprint ``status='ready'`` AND queue has ``>= 3`` pending
 * suggestions AND the workspace has been active in the last 7 days.
 *
 * Ranking is frozen at compute time by the nightly suggestion-queue
 * worker; the surface returns rows in stored ``rank_position`` order and
 * the home does not re-rank. ``computed_at`` is forwarded so the client
 * can render "Updated Xh ago".
 */
export const zDiscoverySuggestionsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zDiscoverySuggestionItem).min(3),
    kind: z.literal('discovery_suggestions').optional().default('discovery_suggestions')
});

/**
 * DocumentCollectionsAdd
 *
 * Cross-cutting: add a document to multiple collections.
 */
export const zDocumentCollectionsAdd = z.object({
    collection_ids: z.array(z.uuid()).min(1).max(50)
});

/**
 * DocumentCollectionsResponse
 *
 * Response for GET /documents/{id}/collections.
 */
export const zDocumentCollectionsResponse = z.object({
    collectionIds: z.array(z.string())
});

/**
 * DocumentCommentAuthor
 *
 * Lightweight user reference for comment author.
 */
export const zDocumentCommentAuthor = z.object({
    avatarUrl: z.string().nullish(),
    email: z.string().nullish(),
    id: z.uuid(),
    name: z.string()
});

/**
 * DocumentCommentCreate
 *
 * Request body for POST /documents/{id}/comments.
 */
export const zDocumentCommentCreate = z.object({
    content: z.string().min(1).max(10000)
});

/**
 * DocumentCommentRead
 *
 * A single document comment.
 */
export const zDocumentCommentRead = z.object({
    author: zDocumentCommentAuthor,
    content: z.string(),
    createdAt: z.iso.datetime(),
    editedAt: z.iso.datetime().nullish(),
    id: z.uuid()
});

/**
 * DocumentCommentListResponse
 *
 * Cursor-paginated list of document comments.
 */
export const zDocumentCommentListResponse = z.object({
    hasMore: z.boolean().optional().default(false),
    items: z.array(zDocumentCommentRead),
    nextCursor: z.string().nullish(),
    prevCursor: z.string().nullish(),
    total: z.int()
});

/**
 * DocumentCommentUpdate
 *
 * Request body for PATCH /documents/{id}/comments/{comment_id}.
 */
export const zDocumentCommentUpdate = z.object({
    content: z.string().min(1).max(10000)
});

/**
 * DocumentConfirmUploadRequest
 *
 * Request body for POST /documents/{id}/confirm-upload.
 *
 * Optional: client may know the page count from client-side PDF parsing.
 */
export const zDocumentConfirmUploadRequest = z.object({
    page_count: z.int().gte(1).lte(10000).nullish()
});

/**
 * DocumentDeleteResponse
 *
 * Response for DELETE /documents/{id}.
 */
export const zDocumentDeleteResponse = z.object({
    deleted: z.boolean().optional().default(true),
    id: z.uuid(),
    permanent: z.boolean()
});

/**
 * DocumentFormat
 *
 * Supported document formats.
 */
export const zDocumentFormat = z.enum([
    'pdf',
    'docx',
    'doc',
    'txt',
    'md',
    'tex',
    'epub',
    'html',
    'csv',
    'tsv',
    'xlsx',
    'xls',
    'pptx',
    'ppt',
    'rtf',
    'odt',
    'ods',
    'json',
    'xml'
]);

/**
 * DocumentCreate
 *
 * Create a document record (after file upload).
 *
 * Maps to Nova CreateDocumentRequest from contracts/documents.ts.
 * workspace_id is NOT in the body — resolved from X-Workspace-ID header.
 */
export const zDocumentCreate = z.object({
    authors: z.array(z.string()).optional(),
    collection_id: z.uuid().nullish(),
    file_name: z.string().min(1).max(500),
    file_size: z.int().gt(0),
    format: zDocumentFormat.optional().default('pdf'),
    is_key_source: z.boolean().optional().default(false),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    notes: z.string().nullish(),
    page_count: z.int().nullish(),
    source: z.string().max(500).optional().default(''),
    tags: z.array(z.string()).optional(),
    title: z.string().min(1).max(500)
});

/**
 * DocumentMeta
 */
export const zDocumentMeta = z.object({
    abstract: z.string().nullish(),
    arxiv_id: z.string().nullish(),
    authors: z.array(zAuthorDto).optional().default([]),
    conference: z.string().nullish(),
    doi: z.string().nullish(),
    file_size: z.int().nullish(),
    id: z.string(),
    journal: z.string().nullish(),
    language: z.string().nullish(),
    page_count: z.int().nullish(),
    publication_date: z.string().nullish(),
    title: z.string(),
    type: z.string().optional().default('paper')
});

/**
 * DocumentMetadata
 *
 * Structured metadata extracted by the M-step pipeline (DOI, arXiv, journal, etc.).
 */
export const zDocumentMetadata = z.object({
    abstract: z.string().nullish(),
    arxivId: z.string().nullish(),
    doi: z.string().nullish(),
    institutions: z.array(z.string()).optional(),
    journal: z.string().nullish(),
    publicationYear: z.int().nullish(),
    sourceMap: z.record(z.string(), z.string()).optional()
});

/**
 * DocumentOwner
 *
 * Lightweight user reference for document ownership.
 *
 * avatar_url is resolved at response time from the user's avatar_asset_id.
 */
export const zDocumentOwner = z.object({
    avatarUrl: z.string().nullish(),
    email: z.string().nullish(),
    id: z.uuid(),
    name: z.string()
});

/**
 * DocumentPinCreate
 *
 * Request body for POST /documents/{id}/pins.
 */
export const zDocumentPinCreate = z.object({
    artifactId: z.string().min(1).max(64),
    content: z.string().max(5000).optional().default(''),
    pageNumber: z.int().gte(1).lte(10000).nullish(),
    pinType: z.string().regex(/^(claim|figure|table|data|quote)$/),
    title: z.string().min(1).max(500)
});

/**
 * DocumentPinRead
 *
 * Response for pin endpoints.
 */
export const zDocumentPinRead = z.object({
    artifactId: z.string(),
    content: z.string(),
    createdAt: z.iso.datetime(),
    documentId: z.uuid(),
    id: z.uuid(),
    pageNumber: z.int().nullish(),
    pinType: z.string(),
    title: z.string()
});

/**
 * DocumentPipelineDetail
 */
export const zDocumentPipelineDetail = z.object({
    created_at: z.string(),
    document_id: z.string(),
    jobs: z.array(z.record(z.string(), z.unknown())).optional(),
    status: z.string(),
    updated_at: z.string(),
    workspace_id: z.string()
});

/**
 * DocumentRetryResponse
 */
export const zDocumentRetryResponse = z.object({
    document_id: z.string(),
    message: z.string(),
    status: z.string()
});

/**
 * DocumentSearchRequest
 *
 * Request body for within-document search.
 */
export const zDocumentSearchRequest = z.object({
    limit: z.int().gte(1).lte(100).optional().default(20),
    mode: z.enum([
        'hybrid',
        'lexical',
        'semantic'
    ]).optional().default('hybrid'),
    query: z.string().min(1).max(1000),
    types: z.array(z.string()).nullish()
});

/**
 * DocumentStatus
 *
 * Document processing status — mirrors WorkspaceDocumentStatus wire values.
 */
export const zDocumentStatus = z.enum([
    'uploading',
    'preflight_failed',
    'dedup_hit',
    'processing',
    'available',
    'enriching',
    'ready_core',
    'ready',
    'ready_with_warnings',
    'failed',
    'infected',
    'metadata_only'
]);

/**
 * DocumentUpdate
 *
 * Update a document's metadata.
 *
 * Maps to Nova UpdateDocumentRequest and PATCH /documents/:id.
 */
export const zDocumentUpdate = z.object({
    authors: z.array(z.string()).nullish(),
    collection_id: z.uuid().nullish(),
    is_key_source: z.boolean().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    notes: z.string().nullish(),
    tags: z.array(z.string()).nullish(),
    title: z.string().min(1).max(500).nullish()
});

/**
 * DocumentUploadUrlRequest
 *
 * Request body for POST /documents/upload-url.
 *
 * The client sends file metadata before uploading.  The server returns a
 * presigned PUT URL that the client uses to upload the file directly to
 * R2 storage (no file bytes pass through our API).
 * workspace_id is NOT in the body — resolved from X-Workspace-ID header.
 */
export const zDocumentUploadUrlRequest = z.object({
    authors: z.array(z.string()).optional(),
    collection_id: z.uuid().nullish(),
    content_hash: z.string().length(64).regex(/^[a-f0-9]{64}$/).nullish(),
    file_name: z.string().min(1).max(500),
    file_size: z.int().gt(0).lte(524288000),
    idempotency_key: z.string().min(16).max(64).regex(/^[a-zA-Z0-9_-]+$/).nullish(),
    is_key_source: z.boolean().optional().default(false),
    mime_type: z.string().min(1),
    notes: z.string().nullish(),
    processing_mode: z.string().regex(/^(full|fast)$/).optional().default('full'),
    tags: z.array(z.string()).optional(),
    title: z.string().min(1).max(500)
});

/**
 * DocumentUploadUrlResponse
 *
 * Response from POST /documents/upload-url.
 *
 * The client should:
 * 1. PUT the file bytes to upload_url with the headers from upload_headers.
 * 2. Call POST /documents/{document_id}/confirm-upload.
 */
export const zDocumentUploadUrlResponse = z.object({
    documentId: z.uuid(),
    expiresInSeconds: z.int().optional().default(300),
    instantDuplicate: z.boolean().optional().default(false),
    pageLimit: z.int(),
    sizeLimit: z.int(),
    storageKey: z.string().optional().default(''),
    uploadHeaders: z.record(z.string(), z.string()).optional(),
    uploadUrl: z.string().optional().default('')
});

/**
 * DocumentVersionCreate
 *
 * Request body for POST /documents/{id}/versions (new version upload).
 */
export const zDocumentVersionCreate = z.object({
    note: z.string().max(2000).nullish()
});

/**
 * DocumentVersionOwner
 *
 * Lightweight user reference for version uploader.
 */
export const zDocumentVersionOwner = z.object({
    avatarUrl: z.string().nullish(),
    id: z.uuid(),
    name: z.string()
});

/**
 * DocumentVersionRead
 *
 * A single document version snapshot.
 */
export const zDocumentVersionRead = z.object({
    fileSize: z.int().optional().default(0),
    id: z.uuid(),
    note: z.string().nullish(),
    timestamp: z.iso.datetime(),
    uploadedBy: zDocumentVersionOwner,
    versionNumber: z.int()
});

/**
 * DocumentVersionUploadUrlRequest
 *
 * Request body for POST /documents/{id}/versions/upload-url.
 *
 * Industry-standard version upload flow:
 * 1. Client sends file metadata + optional note.
 * 2. Server snapshots current Document as a version.
 * 3. Server returns a presigned PUT URL for the new version.
 * 4. Client PUTs file, then calls confirm-upload.
 */
export const zDocumentVersionUploadUrlRequest = z.object({
    file_name: z.string().min(1).max(500),
    file_size: z.int().gt(0).lte(524288000),
    mime_type: z.string().min(1),
    note: z.string().max(2000).nullish()
});

/**
 * DocumentVersionUploadUrlResponse
 *
 * Response from POST /documents/{id}/versions/upload-url.
 *
 * Includes the presigned URL and the snapshot version that was created.
 */
export const zDocumentVersionUploadUrlResponse = z.object({
    documentId: z.uuid(),
    expiresInSeconds: z.int().optional().default(300),
    storageKey: z.string(),
    uploadHeaders: z.record(z.string(), z.string()).optional(),
    uploadUrl: z.string(),
    versionNumber: z.int()
});

/**
 * DownloadURLResponse
 *
 * Response for GET /documents/download/{id}.
 */
export const zDownloadUrlResponse = z.object({
    fileName: z.string(),
    url: z.string()
});

/**
 * DuplicateAlertRead
 *
 * Duplicate citation alert.
 *
 * Maps to Nova DuplicateAlertDTO:
 * { id, citationA: {id, title}, citationB: {id, title}, similarity }
 */
export const zDuplicateAlertRead = z.object({
    citationA: zCitationDocumentRef,
    citationB: zCitationDocumentRef,
    id: z.uuid(),
    similarity: z.number().gte(0).lte(1).optional().default(0)
});

/**
 * EmbedTokenRequest
 *
 * Request body for POST /analytics/embed-token.
 */
export const zEmbedTokenRequest = z.object({
    dashboard_id: z.int().gte(1),
    extra_params: z.record(z.string(), z.unknown()).optional()
});

/**
 * EmbedTokenResponse
 *
 * Response for POST /analytics/embed-token.
 */
export const zEmbedTokenResponse = z.object({
    expires_at: z.int(),
    iframe_url: z.string()
});

/**
 * EncodeResponse
 *
 * Response from encode endpoint.
 */
export const zEncodeResponse = z.object({
    token: z.string()
});

/**
 * EndpointEntry
 *
 * Single API endpoint entry.
 */
export const zEndpointEntry = z.object({
    methods: z.array(z.string()),
    name: z.string().nullish(),
    path: z.string()
});

/**
 * EndpointListResponse
 *
 * Response for endpoint listing.
 */
export const zEndpointListResponse = z.object({
    endpoints_by_tag: z.record(z.string(), z.array(zEndpointEntry)),
    timestamp: z.string(),
    total_endpoints: z.int().gte(0)
});

/**
 * EnforcementMode
 *
 * Controls how limit breaches are handled.
 */
export const zEnforcementMode = z.enum([
    'off',
    'warn',
    'enforce'
]);

/**
 * EnrichmentSignal
 *
 * A single enrichment signal from objective relevance scoring.
 */
export const zEnrichmentSignal = z.object({
    label: z.string(),
    signal: z.string(),
    weight: z.number()
});

/**
 * DocumentEnrichment
 *
 * Workspace-local enrichment context computed by ContextEnrichmentService.
 */
export const zDocumentEnrichment = z.object({
    librarySimilarity: z.number().nullish(),
    nearDuplicates: z.array(z.string()).nullish(),
    notesLinkedCount: z.int().optional().default(0),
    objectiveExplanation: z.array(zEnrichmentSignal).nullish(),
    objectiveRelevance: z.number().nullish(),
    workspaceTags: z.array(z.string()).nullish()
});

/**
 * ErrorBudgetItem
 *
 * Error budget status for a single SLO.
 */
export const zErrorBudgetItem = z.object({
    alert_level: zAlertLevel,
    burn_rates: z.record(z.string(), z.number()),
    consumed_percent: z.number(),
    is_compliant: z.boolean(),
    remaining_percent: z.number(),
    slo_name: z.string(),
    target: z.number(),
    time_to_exhaustion_hours: z.number().nullish()
});

/**
 * ErrorBudgetDashboard
 *
 * Error budget dashboard payload.
 */
export const zErrorBudgetDashboard = z.object({
    budgets: z.array(zErrorBudgetItem),
    slos_at_risk: z.int(),
    timestamp: z.string()
});

/**
 * ErrorDocumentation
 *
 * Documentation for an API error type.
 */
export const zErrorDocumentation = z.object({
    common_causes: z.array(z.string()).optional(),
    description: z.string(),
    documentation_url: z.string().nullish(),
    related_errors: z.array(z.string()).optional(),
    resolution: z.array(z.string()),
    status: z.int(),
    title: z.string(),
    type: z.string()
});

/**
 * ErrorCatalog
 *
 * Complete catalog of API error types.
 */
export const zErrorCatalog = z.object({
    errors: z.array(zErrorDocumentation),
    total: z.int()
});

/**
 * EventLoopHealthResponse
 *
 * Event loop health metrics.
 */
export const zEventLoopHealthResponse = z.object({
    avg_lag_ms: z.number(),
    critical: z.int(),
    last_lag_ms: z.number(),
    max_lag_ms: z.number(),
    p99_lag_ms: z.number(),
    status: z.string(),
    total_checks: z.int(),
    warnings: z.int()
});

/**
 * DetailedHealthCheckResponse
 *
 * Detailed health check with event loop metrics (internal use).
 */
export const zDetailedHealthCheckResponse = z.object({
    database: z.boolean(),
    db_latency_ms: z.number().optional().default(0),
    event_loop: zEventLoopHealthResponse.nullish(),
    service: z.string(),
    status: z.string(),
    storage: z.boolean(),
    timestamp: z.iso.datetime(),
    version: z.string()
});

/**
 * EvidenceHighlightType
 *
 * Artifact kinds that surface as evidence highlights.
 */
export const zEvidenceHighlightType = z.enum(['table', 'figure']);

/**
 * EvidenceHighlightItem
 *
 * One row in the Evidence Highlights surface.
 */
export const zEvidenceHighlightItem = z.object({
    artifactId: z.uuid(),
    artifactType: zEvidenceHighlightType,
    caption: z.string().nullish(),
    confidence: z.number().gte(0).lte(1),
    documentId: z.uuid(),
    documentTitle: z.string(),
    extractedAt: z.iso.datetime(),
    page: z.int().gte(1).nullish(),
    previewStorageKey: z.string().nullish()
});

/**
 * EvidenceHighlightsSurface
 *
 * Tables and figures extracted in the last 72h, ranked by confidence
 * and recency, with table/figure interleaving for visual balance.
 */
export const zEvidenceHighlightsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zEvidenceHighlightItem).min(3),
    kind: z.literal('evidence_highlights').optional().default('evidence_highlights')
});

/**
 * EvidenceItemRead
 *
 * An evidence item from the run's evidence pack.
 */
export const zEvidenceItemRead = z.object({
    channel: z.string(),
    compressed: z.boolean().optional().default(false),
    docId: z.uuid().nullish(),
    includedTokens: z.int(),
    itemId: z.string(),
    itemType: z.string(),
    page: z.int().nullish(),
    pinLevel: z.int().nullish(),
    score: z.number().nullish()
});

/**
 * EvidenceTypeBucket
 *
 * Count of claims by evidence type (statistical, observational, etc.).
 */
export const zEvidenceTypeBucket = z.object({
    count: z.int().gte(0),
    evidenceType: z.string()
});

/**
 * ExportFormat
 *
 * Output format for exports.
 */
export const zExportFormat = z.enum([
    'zip',
    'json',
    'csv'
]);

/**
 * ExportStatus
 *
 * Export job lifecycle states.
 */
export const zExportStatus = z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
    'expired',
    'canceled'
]);

/**
 * ExportType
 *
 * Types of data exports.
 */
export const zExportType = z.enum([
    'gdpr_full',
    'workspace_data',
    'research_data',
    'writing_data',
    'audit_log'
]);

/**
 * DataExportRead
 *
 * Read-only representation of a data export job.
 */
export const zDataExportRead = z.object({
    completed_at: z.iso.datetime().nullish(),
    download_url: z.string().nullish(),
    expires_at: z.iso.datetime().nullish(),
    export_format: zExportFormat,
    export_type: zExportType,
    file_size_bytes: z.int().nullish(),
    id: z.uuid(),
    record_count: z.int().nullish(),
    requested_at: z.iso.datetime(),
    started_at: z.iso.datetime().nullish(),
    status: zExportStatus,
    user_id: z.uuid()
});

/**
 * DataExportList
 *
 * Paginated list of data exports.
 */
export const zDataExportList = z.object({
    exports: z.array(zDataExportRead),
    hasMore: z.boolean().optional().default(false),
    nextCursor: z.string().nullish(),
    pageCount: z.int(),
    prevCursor: z.string().nullish()
});

/**
 * DataExportRequest
 *
 * Request body for creating a data export.
 */
export const zDataExportRequest = z.object({
    export_format: zExportFormat.optional().default('zip'),
    export_type: zExportType.optional().default('gdpr_full')
});

/**
 * ExternalImportRequest
 *
 * Import a paper by DOI or URL.
 */
export const zExternalImportRequest = z.object({
    doi: z.string().max(256).nullish(),
    external_id: z.string().max(256).nullish(),
    source_provider: z.string().max(50).nullish(),
    url: z.url().min(1).max(2083).nullish()
});

/**
 * ExternalImportResponse
 *
 * Response from external document import.
 */
export const zExternalImportResponse = z.object({
    documentId: z.string(),
    mode: z.string(),
    pdfAvailable: z.boolean(),
    status: z.string(),
    title: z.string()
});

/**
 * ExtractionCellRead
 *
 * Cell response shape — the extracted value for a paper × column intersection.
 */
export const zExtractionCellRead = z.object({
    columnId: z.uuid(),
    confidence: z.number().nullable(),
    createdAt: z.iso.datetime(),
    documentId: z.uuid(),
    id: z.uuid(),
    sourceChunk: z.string().nullable(),
    status: zCellStatus,
    updatedAt: z.iso.datetime(),
    value: z.string().nullable()
});

/**
 * ExtractionColumnCreate
 *
 * Create an extraction column.
 */
export const zExtractionColumnCreate = z.object({
    data_type: zColumnDataType.optional().default('text'),
    name: z.string().min(1).max(200),
    position: z.int().gte(0).lte(100).optional().default(0),
    prompt: z.string().min(5).max(2000),
    template_key: z.string().nullish()
});

/**
 * ExtractionColumnRead
 *
 * Column response shape.
 */
export const zExtractionColumnRead = z.object({
    createdAt: z.iso.datetime(),
    dataType: zColumnDataType,
    id: z.uuid(),
    name: z.string(),
    position: z.int(),
    prompt: z.string(),
    tableId: z.uuid(),
    templateKey: z.string().nullable(),
    updatedAt: z.iso.datetime()
});

/**
 * ExtractionQueuedResponse
 *
 * Response for POST /documents/{id}/extract.
 */
export const zExtractionQueuedResponse = z.object({
    documentId: z.string(),
    status: z.string()
});

/**
 * ExtractionStatusRead
 *
 * Lightweight polling endpoint response for extraction progress.
 */
export const zExtractionStatusRead = z.object({
    artifactsFailed: z.int().gte(0),
    artifactsReady: z.int().gte(0),
    artifactsTotal: z.int().gte(0),
    status: z.string()
});

/**
 * ExtractionTableCreate
 *
 * Create an extraction table with optional initial columns and documents.
 */
export const zExtractionTableCreate = z.object({
    columns: z.array(zExtractionColumnCreate).max(20).optional(),
    description: z.string().max(2000).nullish(),
    document_ids: z.array(z.uuid()).max(100).optional(),
    session_id: z.uuid().nullish(),
    title: z.string().min(1).max(500)
});

/**
 * ExtractionTableStatus
 *
 * Lifecycle status of an extraction table.
 */
export const zExtractionTableStatus = z.enum([
    'draft',
    'extracting',
    'ready',
    'failed'
]);

/**
 * ExtractionTableDetailRead
 *
 * Full table detail with columns and cells.
 */
export const zExtractionTableDetailRead = z.object({
    cells: z.array(zExtractionCellRead),
    columns: z.array(zExtractionColumnRead),
    createdAt: z.iso.datetime(),
    description: z.string().nullable(),
    documentIds: z.array(z.uuid()),
    id: z.uuid(),
    ownerId: z.uuid().nullable(),
    sessionId: z.uuid().nullable(),
    status: zExtractionTableStatus,
    title: z.string(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * ExtractionTableSummary
 *
 * Lightweight table summary for list views.
 */
export const zExtractionTableSummary = z.object({
    cellsDone: z.int(),
    cellsTotal: z.int(),
    columnCount: z.int(),
    createdAt: z.iso.datetime(),
    description: z.string().nullable(),
    documentCount: z.int(),
    id: z.uuid(),
    ownerId: z.uuid().nullable(),
    status: zExtractionTableStatus,
    title: z.string(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * ExtractionTableUpdate
 *
 * Update table metadata.
 */
export const zExtractionTableUpdate = z.object({
    description: z.string().max(2000).nullish(),
    title: z.string().min(1).max(500).nullish()
});

/**
 * FastAPIInfo
 *
 * FastAPI version information.
 */
export const zFastApiInfo = z.object({
    version: z.string()
});

/**
 * FeatureConfig
 *
 * Feature configuration flags.
 */
export const zFeatureConfig = z.object({
    analytics: z.boolean().optional().default(true),
    frequency_capping: z.boolean().optional().default(true),
    offline_sync: z.boolean().optional().default(true)
});

/**
 * FeatureFlagsResponse
 *
 * Response schema for feature flags.
 *
 * Infrastructure flags are env-var based (observability tooling).
 * Product feature flags are managed via GrowthBook and served via /me/features.
 */
export const zFeatureFlagsResponse = z.object({
    feature_performance_profiling: z.boolean().optional().default(false),
    growthbook_flags: z.record(z.string(), z.unknown()).optional()
});

/**
 * AdminSettingsResponse
 *
 * Combined admin settings response.
 *
 * Aggregates feature flags and environment info.
 */
export const zAdminSettingsResponse = z.object({
    environment: z.string(),
    feature_flags: zFeatureFlagsResponse,
    timestamp: z.string()
});

/**
 * FeatureMatrixValue
 *
 * A single cell in the feature comparison matrix.
 */
export const zFeatureMatrixValue = z.object({
    type: z.enum([
        'boolean',
        'text',
        'limit',
        'unlimited'
    ]),
    value: z.union([
        z.string(),
        z.boolean()
    ]).nullish()
});

/**
 * FeatureMatrixFeature
 *
 * A single row in the feature comparison matrix.
 */
export const zFeatureMatrixFeature = z.object({
    badge: z.string().nullish(),
    key: z.string(),
    name: z.string(),
    values: z.record(z.string(), zFeatureMatrixValue)
});

/**
 * FeatureMatrixCategory
 *
 * A category group in the feature comparison matrix.
 */
export const zFeatureMatrixCategory = z.object({
    features: z.array(zFeatureMatrixFeature),
    name: z.string()
});

/**
 * FeedFrequency
 *
 * How often to generate feed items.
 */
export const zFeedFrequency = z.enum(['daily', 'weekly']);

/**
 * FeedItemStatus
 *
 * User interaction status.
 */
export const zFeedItemStatus = z.enum([
    'active',
    'saved',
    'dismissed'
]);

/**
 * FeedActionResponse
 *
 * Response after save/dismiss action.
 */
export const zFeedActionResponse = z.object({
    id: z.uuid(),
    status: zFeedItemStatus
});

/**
 * FeedItemType
 *
 * Type of feed recommendation.
 */
export const zFeedItemType = z.enum([
    'new_paper',
    'trending_cluster',
    'forward_citation'
]);

/**
 * FeedItemRead
 *
 * Feed item response shape.
 */
export const zFeedItemRead = z.object({
    createdAt: z.iso.datetime(),
    description: z.string().nullable(),
    expiresAt: z.iso.datetime(),
    externalId: z.string().nullable(),
    externalUrl: z.string().nullable(),
    id: z.uuid(),
    itemType: zFeedItemType,
    matchScore: z.number(),
    matchedDocumentIds: z.array(z.string()).nullable(),
    metadataJson: z.record(z.string(), z.unknown()).nullable(),
    sourceProvider: z.string().nullable(),
    status: zFeedItemStatus,
    title: z.string(),
    updatedAt: z.iso.datetime(),
    whyRecommended: z.string().nullable(),
    workspaceId: z.uuid()
});

/**
 * FeedListResponse
 *
 * Paginated feed list.
 */
export const zFeedListResponse = z.object({
    hasMore: z.boolean(),
    items: z.array(zFeedItemRead),
    nextCursor: z.string().nullish(),
    pageSize: z.int(),
    prevCursor: z.string().nullish()
});

/**
 * FeedPreferencesRead
 *
 * Feed preferences response shape.
 */
export const zFeedPreferencesRead = z.object({
    excludedTopics: z.array(z.string()).nullable(),
    frequency: zFeedFrequency,
    minMatchScore: z.number(),
    workspaceId: z.uuid()
});

/**
 * FeedPreferencesUpdate
 *
 * Update feed preferences.
 */
export const zFeedPreferencesUpdate = z.object({
    excluded_topics: z.array(z.string()).nullish(),
    frequency: zFeedFrequency.nullish(),
    min_match_score: z.number().gte(0).lte(1).nullish()
});

/**
 * FeedbackAcknowledge
 *
 * Simple acknowledgement for feedback actions.
 */
export const zFeedbackAcknowledge = z.object({
    action: z.string(),
    contextUpdated: z.boolean().optional().default(false),
    documentId: z.uuid(),
    message: z.string().optional().default('Feedback recorded'),
    success: z.boolean().optional().default(true)
});

/**
 * FeedbackActionSchema
 */
export const zFeedbackActionSchema = z.enum([
    'accept',
    'decline',
    'save',
    'undo'
]);

/**
 * FeedbackClientContext
 *
 * Context captured from the client at the time of submission.
 */
export const zFeedbackClientContext = z.object({
    aiModel: z.string().nullish(),
    documentId: z.string().nullish(),
    feature: z.string().nullish(),
    path: z.string().nullish(),
    precedingAction: z.string().nullish(),
    sessionId: z.string().nullish(),
    surface: z.string().nullish(),
    timeOnPage: z.number().nullish(),
    viewport: z.record(z.string(), z.int()).nullish()
});

/**
 * FeedbackCreate
 *
 * Schema for creating new product feedback.
 */
export const zFeedbackCreate = z.object({
    appVersion: z.string().max(50).nullish(),
    canFollowUp: z.boolean().optional().default(false),
    category: z.string(),
    clientContext: zFeedbackClientContext.nullish(),
    message: z.string().min(1).max(2000),
    rating: z.int().gte(1).lte(5).nullish(),
    screenshotData: z.string().max(5000000).nullish(),
    sentiment: z.string().optional().default('neutral'),
    source: z.string().optional().default('widget'),
    trigger: z.string().max(50).nullish()
});

/**
 * FeedbackRead
 *
 * Single feedback item for API responses.
 */
export const zFeedbackRead = z.object({
    appVersion: z.string().nullish(),
    assignedTo: z.string().nullish(),
    canFollowUp: z.boolean().optional().default(false),
    category: z.string(),
    clientContext: z.record(z.string(), z.unknown()).nullish(),
    createdAt: z.iso.datetime(),
    githubIssueNumber: z.int().nullish(),
    githubIssueUrl: z.string().nullish(),
    id: z.uuid(),
    internalNotes: z.string().nullish(),
    message: z.string(),
    priority: z.string(),
    rating: z.int().nullish(),
    resolvedAt: z.iso.datetime().nullish(),
    screenshotPath: z.string().nullish(),
    sentiment: z.string(),
    source: z.string(),
    status: z.string(),
    tags: z.array(z.string()).nullish(),
    trigger: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    userAgent: z.string().nullish(),
    userId: z.uuid().nullish(),
    workspaceId: z.uuid()
});

/**
 * FeedbackListResponse
 *
 * Paginated list of feedback items.
 */
export const zFeedbackListResponse = z.object({
    hasMore: z.boolean().optional().default(false),
    items: z.array(zFeedbackRead),
    nextCursor: z.string().nullish(),
    prevCursor: z.string().nullish(),
    total: z.int().optional().default(0)
});

/**
 * FeedbackStats
 *
 * Aggregate feedback statistics for dashboard.
 */
export const zFeedbackStats = z.object({
    avgRating: z.number().nullish(),
    byCategory: z.record(z.string(), z.int()).optional(),
    byPriority: z.record(z.string(), z.int()).optional(),
    byStatus: z.record(z.string(), z.int()).optional(),
    npsScore: z.number().nullish(),
    openBugs: z.int().optional().default(0),
    resolvedLast7d: z.int().optional().default(0),
    submittedLast7d: z.int().optional().default(0),
    topSurfaces: z.array(z.record(z.string(), z.unknown())).optional(),
    total: z.int().optional().default(0)
});

/**
 * FeedbackSurface
 */
export const zFeedbackSurface = z.enum([
    'search',
    'suggestions',
    'trending'
]);

/**
 * FeedbackUpdate
 *
 * Schema for updating/triaging feedback (admin only).
 */
export const zFeedbackUpdate = z.object({
    assignedTo: z.string().nullish(),
    internalNotes: z.string().nullish(),
    priority: z.string().nullish(),
    status: z.string().nullish(),
    tags: z.array(z.string()).nullish()
});

/**
 * FingerprintStatusSurface
 *
 * Workspace fingerprint readiness + composition health (Slice 3).
 *
 * Per spec §5.6, this surface is the intelligence engine's self-disclosure
 * and is only included when something is worth saying:
 *
 * * ``status`` is one of ``pending`` / ``backfilling`` / ``failed`` (engine
 * not yet usable, or recovering); OR
 * * the workspace is in ``stage0`` — fingerprint exists but the library is
 * below the threshold where ranking is useful.
 *
 * Healthy intelligence does not advertise itself: when ``status='ready'``
 * AND the workspace has reached ``stage1``, this surface is omitted.
 *
 * ``last_error`` carries a short, sanitized hint when ``status='failed'``
 * (no stack traces, no PII, capped to 200 chars). ``embedded_doc_count``
 * is the number of documents whose embedding vector has been generated —
 * when ``status='backfilling'`` and this count is increasing, the client
 * renders a soft progress chip.
 */
export const zFingerprintStatusSurface = z.object({
    activeModel: z.string().max(128),
    activeVersion: z.int().gte(0),
    degradeReasons: z.array(z.string()).optional(),
    documentCount: z.int().gte(0).optional().default(0),
    embeddedDocCount: z.int().gte(0).optional().default(0),
    embeddingEligibleDocCount: z.int().gte(0).optional().default(0),
    freshnessAt: z.iso.datetime(),
    kind: z.literal('fingerprint_status').optional().default('fingerprint_status'),
    lastError: z.string().max(200).nullish(),
    stage: z.enum(['stage0', 'stage1']),
    status: z.enum([
        'pending',
        'backfilling',
        'ready',
        'failed'
    ]),
    targetModel: z.string().max(128).nullish(),
    targetVersion: z.int().gte(0).nullish()
});

/**
 * FunnelStageScreening
 *
 * Screening stage funnel counts.
 */
export const zFunnelStageScreening = z.object({
    conflictsOpen: z.int().optional().default(0),
    conflictsResolved: z.int().optional().default(0),
    excluded: z.int().optional().default(0),
    included: z.int().optional().default(0),
    remaining: z.int().optional().default(0),
    screened: z.int().optional().default(0),
    total: z.int().optional().default(0)
});

/**
 * FunnelStageSynthesis
 *
 * Synthesis stage funnel counts.
 */
export const zFunnelStageSynthesis = z.object({
    extractionComplete: z.int().optional().default(0),
    extractionInProgress: z.int().optional().default(0),
    robComplete: z.int().optional().default(0),
    robInProgress: z.int().optional().default(0),
    robNotStarted: z.int().optional().default(0),
    total: z.int().optional().default(0)
});

/**
 * FunnelSummary
 *
 * Study funnel summary for an SR project (Addendum §3.3).
 */
export const zFunnelSummary = z.object({
    duplicatesRemoved: z.int().optional().default(0),
    fullTextScreening: zFunnelStageScreening.optional(),
    imported: z.int().optional().default(0),
    synthesis: zFunnelStageSynthesis.optional(),
    titleAbstractScreening: zFunnelStageScreening.optional(),
    totalIdentified: z.int().optional().default(0),
    uniqueRecords: z.int().optional().default(0)
});

/**
 * GetOrCreateSharingRequest
 *
 * Request body for the get-or-create sharing endpoint.
 *
 * Returns the existing sharing config if one already exists for the
 * given resource, or creates a new one with sensible defaults.
 */
export const zGetOrCreateSharingRequest = z.object({
    resource_id: z.string().min(1).max(255),
    resource_title: z.string().max(500).optional().default(''),
    resource_type: z.string().min(1).max(20)
});

/**
 * GraphEdgeCreate
 *
 * Create or upsert a graph edge (RFC-003 §2.6).
 *
 * workspace_id is NOT in the body — resolved from auth context.
 */
export const zGraphEdgeCreate = z.object({
    createdBySource: z.string().regex(/^(user|ai|import)$/).optional().default('user'),
    edgeType: z.string().regex(/^(cites|mentions|extracted_from|embeds|references|derives_from|comments_on_range|comments_on_node)$/),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    sourceNodeId: z.uuid(),
    sourceType: z.string().regex(/^(paper|note|figure|table|dataset|external_resource|comment)$/),
    targetNodeId: z.uuid(),
    targetType: z.string().regex(/^(paper|note|figure|table|dataset|external_resource|comment)$/),
    weight: z.number().gte(0).lte(1).nullish()
});

/**
 * GraphEdgeResponse
 *
 * Full graph edge response — maps 1:1 to Nova's GraphEdgeDTO.
 */
export const zGraphEdgeResponse = z.object({
    confirmCount: z.int().optional().default(0),
    createdAt: z.iso.datetime(),
    createdBySource: z.string(),
    deletedAt: z.string().nullish(),
    dismissCount: z.int().optional().default(0),
    edgeType: z.string(),
    firstCreatedAt: z.string().nullish(),
    firstCreatedBy: z.uuid().nullish(),
    id: z.uuid(),
    lastConfirmedAt: z.string().nullish(),
    lastDismissedAt: z.string().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    sourceNodeId: z.uuid(),
    sourceType: z.string(),
    targetNodeId: z.uuid(),
    targetType: z.string(),
    updatedAt: z.iso.datetime(),
    weight: z.number(),
    workspaceId: z.uuid()
});

/**
 * GraphLink
 *
 * A link (edge) in the workspace knowledge graph.
 */
export const zGraphLink = z.object({
    source: z.uuid(),
    target: z.uuid(),
    type: z.string(),
    weight: z.number()
});

/**
 * GraphNeighbourNode
 *
 * A node adjacent to the queried node, with its connecting edges.
 */
export const zGraphNeighbourNode = z.object({
    edges: z.array(zGraphEdgeResponse).optional(),
    nodeId: z.uuid(),
    nodeType: z.string()
});

/**
 * GraphNode
 *
 * A node in the workspace knowledge graph.
 */
export const zGraphNode = z.object({
    id: z.uuid(),
    label: z.string(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    type: z.string()
});

/**
 * HighlightCreate
 *
 * Request body for POST /documents/{id}/highlights.
 */
export const zHighlightCreate = z.object({
    color: z.string().max(20).optional().default('yellow'),
    comment: z.string().max(10000).nullish(),
    highlight_type: z.string().regex(/^(highlight|underline|note)$/).optional().default('highlight'),
    page_number: z.int().gte(1),
    rects: z.array(zBoundingBoxSchema).min(1).max(200),
    selected_text: z.string().max(50000).optional().default('')
});

/**
 * HighlightRead
 *
 * Response shape for a single document highlight.
 */
export const zHighlightRead = z.object({
    color: z.string(),
    comment: z.string().nullish(),
    createdAt: z.iso.datetime(),
    highlightType: z.string(),
    id: z.uuid(),
    pageNumber: z.int(),
    rects: z.array(zBoundingBoxSchema),
    selectedText: z.string(),
    updatedAt: z.iso.datetime()
});

/**
 * HighlightUpdate
 *
 * Request body for PATCH /documents/{id}/highlights/{highlight_id}.
 */
export const zHighlightUpdate = z.object({
    color: z.string().max(20).nullish(),
    comment: z.string().max(10000).nullish()
});

/**
 * HomeWorkspaceDTO
 *
 * Workspace identity lifted to the envelope.
 *
 * ``fingerprintStage`` is a coarse summary of where the workspace is in the
 * research-intelligence-engine lifecycle:
 *
 * * ``cold``   — no documents yet; only structural primitives render.
 * * ``stage0`` — at least one document, but below the fingerprint
 * readiness threshold (< 5 imports).
 * * ``stage1`` — fingerprint considered ready for ranking surfaces.
 *
 * The detailed ``workspace_embedding_state`` is exposed via the
 * ``fingerprint_status`` surface (Slice 3) when it is not yet ready;
 * healthy intelligence does not advertise itself.
 */
export const zHomeWorkspaceDto = z.object({
    documentCount: z.int().gte(0).optional().default(0),
    fingerprintStage: z.enum([
        'cold',
        'stage0',
        'stage1'
    ]).optional().default('cold'),
    id: z.uuid(),
    name: z.string().max(200),
    plan: z.enum([
        'free',
        'starter',
        'pro',
        'team',
        'enterprise'
    ]).optional().default('free')
});

/**
 * HumanReadableEntitlements
 *
 * User-facing translations of raw limits into meaningful actions.
 *
 * All estimates assume the tier's default model and a typical
 * chat (2K input + 800 output tokens). Displayed in the UI instead
 * of opaque cost unit numbers.
 */
export const zHumanReadableEntitlements = z.object({
    default_model_display: z.string().nullish(),
    estimated_documents: z.int().nullish(),
    estimated_messages: z.int().nullish(),
    extraction_jobs: z.int().nullish(),
    storage_display: z.string().nullish()
});

/**
 * ImportConfirmRequest
 *
 * Request body for POST /documents/import/confirm.
 */
export const zImportConfirmRequest = z.object({
    identifier: z.string().min(3).max(500),
    mode: z.string().regex(/^(with_pdf|metadata_only)$/),
    processing_mode: z.string().regex(/^(full|fast)$/).optional().default('full')
});

/**
 * ImportConfirmResponse
 *
 * Response from POST /documents/import/confirm.
 */
export const zImportConfirmResponse = z.object({
    arxivId: z.string().nullish(),
    doi: z.string().nullish(),
    id: z.uuid(),
    message: z.string(),
    source: z.string(),
    status: z.string(),
    title: z.string()
});

/**
 * ImportItemResponseSchema
 *
 * Response from importing a single workspace item with the full pipeline.
 */
export const zImportItemResponseSchema = z.object({
    documentId: z.string().nullish(),
    itemId: z.string(),
    message: z.string(),
    pdfAvailable: z.boolean().optional().default(false),
    status: z.string()
});

/**
 * BatchImportWithPdfResponseSchema
 *
 * Response from batch import with PDF pipeline.
 */
export const zBatchImportWithPdfResponseSchema = z.object({
    failed: z.array(zBatchImportFailure),
    imported: z.array(zImportItemResponseSchema),
    skipped: z.array(z.string())
});

/**
 * ImportPreviewMetadata
 *
 * Resolved metadata returned by the preview endpoint.
 */
export const zImportPreviewMetadata = z.object({
    abstract: z.string().nullish(),
    arxivId: z.string().nullish(),
    authors: z.array(z.string()).optional(),
    doi: z.string().nullish(),
    journal: z.string().nullish(),
    source: z.string(),
    title: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * ImportPreviewRequest
 *
 * Request body for POST /documents/import/preview.
 */
export const zImportPreviewRequest = z.object({
    identifier: z.string().min(3).max(500)
});

/**
 * ImportPreviewResponse
 *
 * Response from POST /documents/import/preview.
 *
 * Contains resolved metadata and PDF availability so the user can
 * decide whether to import with PDF, metadata-only, or upload their own.
 */
export const zImportPreviewResponse = z.object({
    identifierType: z.string(),
    identifierValue: z.string(),
    metadata: zImportPreviewMetadata,
    pdfAvailable: z.boolean().optional().default(false),
    pdfSource: z.string().nullish()
});

/**
 * IngestionJobStepRead
 *
 * A single pipeline stage step within an ingestion job (public).
 */
export const zIngestionJobStepRead = z.object({
    completedAt: z.iso.datetime().nullish(),
    durationMs: z.int().nullish(),
    failureCode: z.string().nullish(),
    failureReason: z.string().nullish(),
    id: z.uuid(),
    retryCount: z.int().optional().default(0),
    stage: z.string(),
    startedAt: z.iso.datetime().nullish(),
    status: z.string()
});

/**
 * IngestionJobRead
 *
 * An ingestion job with all its stage steps (public).
 */
export const zIngestionJobRead = z.object({
    attempt: z.int(),
    completedAt: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    id: z.uuid(),
    startedAt: z.iso.datetime().nullish(),
    status: z.string(),
    steps: z.array(zIngestionJobStepRead).optional().default([]),
    traceId: z.string().nullish(),
    trigger: z.string(),
    workspaceDocumentId: z.uuid()
});

/**
 * IngestionStepDetail
 *
 * Per-stage detail within an ingestion job.
 */
export const zIngestionStepDetail = z.object({
    adapter_used: z.string().nullish(),
    duration_ms: z.int().nullish(),
    failure_code: z.string().nullish(),
    failure_reason: z.string().nullish(),
    stage: z.string(),
    status: z.string()
});

/**
 * IngestionJobDetail
 *
 * Full detail of a single ingestion job.
 */
export const zIngestionJobDetail = z.object({
    attempt: z.int(),
    completed_at: z.string().nullish(),
    created_at: z.string(),
    id: z.string(),
    started_at: z.string().nullish(),
    status: z.string(),
    steps: z.array(zIngestionStepDetail).optional(),
    trigger: z.string(),
    workspace_document_id: z.string(),
    workspace_id: z.string()
});

/**
 * IngestionSummary
 *
 * Aggregate ingestion pipeline statistics.
 */
export const zIngestionSummary = z.object({
    by_stage: z.record(z.string(), z.int()).optional(),
    by_status: z.record(z.string(), z.int()).optional(),
    total_active: z.int().gte(0).optional().default(0)
});

/**
 * InitialCollection
 *
 * A collection to scaffold when creating a workspace.
 */
export const zInitialCollection = z.object({
    color: z.string().optional().default('neutral'),
    name: z.string().min(1).max(255)
});

/**
 * InsightType
 *
 * Types of research insights.
 */
export const zInsightType = z.enum([
    'finding',
    'question',
    'hypothesis',
    'decision',
    'contradiction'
]);

/**
 * IntelligenceCriticalSurface
 *
 * Critical Insight Shelf (Slice 4, spec §5.10).
 *
 * Tier-pinned-to-top above operational blockers — the only spec-allowed
 * override of the "operational interrupts ambient" rule (§7.2). Surfaces
 * high-confidence contradictions, retractions, and single-source
 * dependencies that invalidate prior research and warrant immediate
 * attention.
 *
 * Trigger: at least one HIGH-severity contradiction with confidence ≥ 0.85.
 * Capped at 3 rows; server orders by confidence then recency.
 */
export const zIntelligenceCriticalSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zCriticalInsightItem).min(1),
    kind: z.literal('intelligence_critical').optional().default('intelligence_critical')
});

/**
 * IntelligenceFeedbackCreate
 *
 * POST body for document feedback (accept/decline/save).
 */
export const zIntelligenceFeedbackCreate = z.object({
    action: zFeedbackActionSchema,
    search_query: z.string().nullish(),
    source_surface: zFeedbackSurface.optional().default('search')
});

/**
 * IntelligenceFeedbackRead
 *
 * Response schema for recorded feedback.
 */
export const zIntelligenceFeedbackRead = z.object({
    action: z.string(),
    createdAt: z.iso.datetime(),
    documentId: z.uuid(),
    id: z.uuid(),
    searchQuery: z.string().nullable(),
    sourceSurface: z.string().nullable(),
    updatedAt: z.iso.datetime(),
    userId: z.uuid().nullable(),
    workspaceId: z.uuid()
});

/**
 * InternalDocumentStatusResponse
 */
export const zInternalDocumentStatusResponse = z.object({
    created_at: z.iso.datetime().nullable(),
    document_id: z.string(),
    status: z.string(),
    updated_at: z.iso.datetime().nullable(),
    workspace_id: z.string()
});

/**
 * InternalHealthResponse
 */
export const zInternalHealthResponse = z.object({
    database: z.string(),
    status: z.string()
});

/**
 * InternalOutboxStatsResponse
 */
export const zInternalOutboxStatsResponse = z.object({
    pending: z.int(),
    processed_1h: z.int()
});

/**
 * InternalPipelineOverviewResponse
 */
export const zInternalPipelineOverviewResponse = z.object({
    failed: z.int(),
    processing: z.int(),
    ready: z.int(),
    total_24h: z.int(),
    uploading: z.int()
});

/**
 * InvitationAccept
 *
 * Accept an invitation by token.
 */
export const zInvitationAccept = z.object({
    token: z.string().length(64).regex(/^[0-9a-f]{64}$/)
});

/**
 * InvitationDecline
 *
 * Decline an invitation by token.
 *
 * Functionally identical to InvitationAccept but uses a distinct schema
 * name so OpenAPI docs clearly distinguish accept vs. decline payloads.
 */
export const zInvitationDecline = z.object({
    token: z.string().length(64).regex(/^[0-9a-f]{64}$/)
});

/**
 * InvitationRespondAction
 *
 * Action to take on an invitation via the authenticated in-app flow.
 */
export const zInvitationRespondAction = z.enum(['accept', 'decline']);

/**
 * InvitationRespond
 *
 * Accept or decline an invitation by ID (for authenticated users).
 *
 * Unlike the token-based accept/decline, this does not require the
 * secret token from the email link. The server verifies the
 * authenticated user's email matches the invitation recipient.
 *
 * Used by in-app notification actions (Slack/GitHub-style inline
 * accept/decline).
 */
export const zInvitationRespond = z.object({
    action: zInvitationRespondAction,
    invitation_id: z.uuid()
});

/**
 * InvitationRole
 *
 * Role to assign upon acceptance.
 *
 * Matches WorkspaceRole from identity.py but excludes OWNER
 * (ownership is assigned at workspace creation, never via invites).
 */
export const zInvitationRole = z.enum([
    'admin',
    'editor',
    'researcher',
    'viewer'
]);

/**
 * InvitationCreate
 *
 * Invite a member to a workspace.
 *
 * Maps to Nova InviteMemberDto:
 * { email, role?: "admin" | "editor" | "researcher" | "viewer" }
 */
export const zInvitationCreate = z.object({
    email: z.email(),
    role: zInvitationRole.optional().default('editor')
});

/**
 * InvitationStatus
 *
 * Invitation lifecycle status.
 */
export const zInvitationStatus = z.enum([
    'pending',
    'accepted',
    'declined',
    'expired',
    'revoked'
]);

/**
 * InvitationRead
 *
 * Invitation response for API.
 *
 * Maps to Nova Invitation interface from api/endpoints/workspaces.ts:
 * { id, workspace_id, workspace_name, email, role, status,
 * invited_by, created_at, expires_at }
 */
export const zInvitationRead = z.object({
    createdAt: z.iso.datetime(),
    email: z.string(),
    expiresAt: z.iso.datetime().nullish(),
    id: z.uuid(),
    invitedBy: z.uuid().nullish(),
    role: z.string(),
    status: zInvitationStatus,
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid(),
    workspaceName: z.string().optional().default('')
});

/**
 * InvitationVerify
 *
 * Public invitation verification response (no auth required).
 *
 * Returns minimal safe information for the invite landing page.
 * Never exposes full email — only masked version for confirmation.
 */
export const zInvitationVerify = z.object({
    emailMasked: z.string().optional().default(''),
    expiresAt: z.iso.datetime().nullish(),
    inviterName: z.string().optional().default(''),
    role: z.string().optional().default(''),
    valid: z.boolean(),
    workspaceName: z.string().optional().default('')
});

/**
 * JobSummary
 *
 * Per-job aggregation for the daily digest.
 */
export const zJobSummary = z.object({
    avgDurationMs: z.number().optional().default(0),
    failures: z.int().optional().default(0),
    jobName: z.string(),
    lastRunAt: z.iso.datetime().nullish(),
    successes: z.int().optional().default(0),
    timedOut: z.int().optional().default(0),
    totalRowsAffected: z.int().optional().default(0),
    totalRuns: z.int().optional().default(0)
});

/**
 * LibraryItemDTO
 */
export const zLibraryItemDto = z.object({
    added_at: z.string(),
    annotations: z.int().nullish(),
    citations: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    collections: z.array(z.string()).optional().default([]),
    credibility_score: z.int().nullish(),
    has_file: z.boolean().optional().default(false),
    highlights: z.int().nullish(),
    id: z.string(),
    is_metadata_only: z.boolean().optional().default(false),
    is_paywalled: z.boolean().nullish(),
    is_starred: z.boolean().optional().default(false),
    last_opened_at: z.string().nullish(),
    meta: zDocumentMeta,
    processing_progress: z.int().nullish(),
    processing_status: z.string().optional().default('ready'),
    read_progress: z.int().nullish(),
    relationships: z.array(z.record(z.string(), z.unknown())).optional().default([]),
    source: z.string().nullish(),
    summary: zAiSummary.nullish(),
    tags: z.array(z.string()).optional().default([]),
    thumbnail_url: z.string().nullish(),
    topics: z.array(z.string()).optional().default([])
});

/**
 * LibraryItemResponse
 *
 * Single-item response envelope for `GET /library/{item_id}`.
 */
export const zLibraryItemResponse = z.object({
    data: zLibraryItemDto
});

/**
 * LimitsRead
 *
 * Plan usage limits — effective limits resolved from plan, overrides, or beta config.
 */
export const zLimitsRead = z.object({
    ai_cost_units: z.union([
        z.int(),
        z.number()
    ]).nullish(),
    extraction_jobs: z.int().nullish(),
    max_documents: z.int().nullish(),
    max_seats: z.int().nullish(),
    seats_included: z.int().optional().default(1),
    storage_bytes: z.int().nullish()
});

/**
 * BillingPlanRead
 *
 * Plan definition with features and base limits.
 */
export const zBillingPlanRead = z.object({
    billing_period: z.string(),
    has_admin_controls: z.boolean().optional().default(false),
    has_annual_pool: z.boolean().optional().default(false),
    has_api_access: z.boolean().optional().default(false),
    has_audit_export: z.boolean().optional().default(false),
    has_custom_branding: z.boolean().optional().default(false),
    has_priority_queue: z.boolean().optional().default(false),
    has_sso: z.boolean().optional().default(false),
    has_usage_attribution: z.boolean().optional().default(false),
    id: z.string(),
    is_public: z.boolean().optional().default(true),
    limits: zLimitsRead.nullish(),
    name: z.string(),
    seat_price: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish()
});

/**
 * MaintenanceDigestResponse
 *
 * 24-hour maintenance digest summary.
 */
export const zMaintenanceDigestResponse = z.object({
    failures: z.int().optional().default(0),
    jobSummaries: z.array(zJobSummary).optional(),
    missedJobs: z.array(z.string()).optional(),
    successes: z.int().optional().default(0),
    timedOut: z.int().optional().default(0),
    totalRuns: z.int().optional().default(0),
    windowEnd: z.iso.datetime(),
    windowStart: z.iso.datetime()
});

/**
 * MaintenanceJobRunResponse
 *
 * Single maintenance job execution record.
 */
export const zMaintenanceJobRunResponse = z.object({
    createdAt: z.iso.datetime(),
    durationMs: z.int().nullish(),
    error: z.string().nullish(),
    finishedAt: z.iso.datetime().nullish(),
    id: z.uuid(),
    jobName: z.string(),
    resultDetail: z.record(z.string(), z.unknown()).nullish(),
    rowsAffected: z.int().nullish(),
    startedAt: z.iso.datetime(),
    status: z.string(),
    workerName: z.string().nullish()
});

/**
 * MemberHistoryEntry
 *
 * Single role change audit entry.
 */
export const zMemberHistoryEntry = z.object({
    action: z.string(),
    actor_user_id: z.string().nullish(),
    created_at: z.iso.datetime(),
    id: z.string(),
    ip_address: z.string().nullish(),
    new_role: z.string().nullish(),
    old_role: z.string().nullish(),
    reason: z.string().nullish(),
    user_id: z.string(),
    workspace_id: z.string()
});

/**
 * MemberHistoryPagination
 *
 * Pagination metadata for member history.
 */
export const zMemberHistoryPagination = z.object({
    has_more: z.boolean(),
    next_cursor: z.string().nullish(),
    prev_cursor: z.string().nullish(),
    total: z.int().gte(0)
});

/**
 * MemberHistoryResponse
 *
 * Paginated member role change history.
 */
export const zMemberHistoryResponse = z.object({
    data: z.array(zMemberHistoryEntry),
    pagination: zMemberHistoryPagination
});

/**
 * MessageFeedbackRequest
 *
 * User feedback on an assistant message (thumbs up/down).
 */
export const zMessageFeedbackRequest = z.object({
    feedback_text: z.string().max(2000).nullish(),
    rating: z.int().gte(1).lte(5)
});

/**
 * MessageFeedbackResponse
 *
 * Response confirming feedback was recorded.
 */
export const zMessageFeedbackResponse = z.object({
    messageId: z.uuid(),
    rating: z.int(),
    recorded: z.boolean().optional().default(true)
});

/**
 * MessageRead
 *
 * A chat message returned from the API.
 */
export const zMessageRead = z.object({
    citationValidationStatus: z.string().nullish(),
    content: z.string(),
    conversationId: z.uuid(),
    createdAt: z.iso.datetime(),
    groundingValidationStatus: z.string().nullish(),
    id: z.uuid(),
    invalidCitationsCount: z.int().optional().default(0),
    posthocModified: z.boolean().optional().default(false),
    role: z.string(),
    unsupportedClaimsCount: z.int().optional().default(0)
});

/**
 * MessageListResponse
 *
 * Cursor-paginated list of messages.
 */
export const zMessageListResponse = z.object({
    items: z.array(zMessageRead),
    pagination: zCursorPage
});

/**
 * MethodologyScoreRead
 *
 * LLM-assessed methodology quality for a research document.
 */
export const zMethodologyScoreRead = z.object({
    biasRisk: z.string().nullish(),
    overallQuality: z.number().nullish(),
    reproducibility: z.number().nullish(),
    sampleSizeAdequacy: z.string().nullish(),
    statisticalRigor: z.number().nullish(),
    strengths: z.array(z.string()).optional(),
    studyDesign: z.string().nullish(),
    weaknesses: z.array(z.string()).optional()
});

/**
 * MfaDisableInitiateResponse
 *
 * Response for MFA disable initiation.
 */
export const zMfaDisableInitiateResponse = z.object({
    expires_at: z.string(),
    message: z.string(),
    otp: z.string(),
    user_id: z.string()
});

/**
 * MfaSyncResponse
 *
 * Response for MFA enrollment sync endpoint.
 */
export const zMfaSyncResponse = z.object({
    mfa_enrolled: z.boolean()
});

/**
 * ModelCapabilitiesRead
 *
 * Capability flags and strengths for a model.
 */
export const zModelCapabilitiesRead = z.object({
    contextWindow: z.int(),
    strengths: z.array(z.string()).optional(),
    supportsStreaming: z.boolean().optional().default(true),
    supportsVision: z.boolean().optional().default(false)
});

/**
 * ModelComparisonItem
 *
 * Per-model quality breakdown.
 */
export const zModelComparisonItem = z.object({
    avg_latency_ms: z.int().optional().default(0),
    citation_accuracy: z.number().optional().default(0),
    count: z.int().optional().default(0),
    grounding_score: z.number().optional().default(0),
    hallucination_score: z.number().optional().default(0),
    model: z.string(),
    total_cost_usd: z.number().optional().default(0)
});

/**
 * ModelMetadataRead
 *
 * Model metadata for the frontend model picker.
 */
export const zModelMetadataRead = z.object({
    budgetMultiplier: z.number(),
    capabilities: zModelCapabilitiesRead,
    contextWindow: z.int(),
    costTier: z.string(),
    displayName: z.string(),
    id: z.string(),
    provider: z.string()
});

/**
 * ModelPricingRead
 *
 * Single model pricing entry.
 */
export const zModelPricingRead = z.object({
    cost_weight: z.number(),
    effective_from: z.iso.datetime(),
    effective_until: z.iso.datetime().nullish(),
    model_name: z.string(),
    token_type: z.string()
});

/**
 * ModelPricingResponse
 *
 * PUT /v1/billing/admin/pricing response.
 */
export const zModelPricingResponse = z.object({
    message: z.string(),
    updated: zModelPricingRead
});

/**
 * ModelsListResponse
 *
 * Response for GET /v1/chat/models.
 */
export const zModelsListResponse = z.object({
    defaultModel: z.string(),
    models: z.array(zModelMetadataRead)
});

/**
 * NormBBox
 *
 * Normalised bounding box — all values in [0, 1] relative to page dimensions.
 *
 * Stored in DB as JSONB. Converted to screen coordinates at render time only.
 */
export const zNormBBox = z.object({
    h: z.number().gt(0).lte(1),
    w: z.number().gt(0).lte(1),
    x: z.number().gte(0).lte(1),
    y: z.number().gte(0).lte(1)
});

/**
 * ArtifactPatch
 *
 * Update caption or bounding box of an existing artifact.
 */
export const zArtifactPatch = z.object({
    bboxOverride: zNormBBox.nullish(),
    captionOverride: z.string().max(2000).nullish()
});

/**
 * CompareItemCreate
 *
 * Single item in a compare board.
 */
export const zCompareItemCreate = z.object({
    artifactId: z.uuid().nullish(),
    bboxNorm: zNormBBox,
    docId: z.uuid(),
    label: z.string().max(255).optional().default(''),
    page: z.int().gte(1)
});

/**
 * CompareBoardCreate
 *
 * Create a new compare board.
 */
export const zCompareBoardCreate = z.object({
    items: z.array(zCompareItemCreate).optional(),
    layout: z.enum([
        '2up',
        '3up',
        '4up',
        '6up'
    ]).optional().default('2up'),
    title: z.string().max(255).nullish()
});

/**
 * CompareBoardPatch
 *
 * Update compare board items, layout, or zoom lock.
 */
export const zCompareBoardPatch = z.object({
    items: z.array(zCompareItemCreate).nullish(),
    layout: z.enum([
        '2up',
        '3up',
        '4up',
        '6up'
    ]).nullish(),
    title: z.string().nullish(),
    zoomLocked: z.boolean().nullish()
});

/**
 * DocumentSearchResult
 *
 * A single search result within a document.
 */
export const zDocumentSearchResult = z.object({
    artifact_id: z.string().nullish(),
    artifact_type: z.string().nullish(),
    bbox_norm: zNormBBox.nullish(),
    confidence: z.number().nullish(),
    id: z.string(),
    page: z.int().nullish(),
    score: z.number(),
    section_tag: z.string().nullish(),
    snippet: z.string(),
    stable_id: z.string().nullish(),
    type: z.string()
});

/**
 * DocumentSearchResponse
 *
 * Response for within-document search.
 */
export const zDocumentSearchResponse = z.object({
    document_id: z.uuid(),
    mode: z.string(),
    query: z.string(),
    results: z.array(zDocumentSearchResult).optional(),
    took_ms: z.int(),
    total: z.int()
});

/**
 * EvidenceArtifactRead
 *
 * Single artifact in the Evidence Rail.
 */
export const zEvidenceArtifactRead = z.object({
    artifactId: z.uuid(),
    bboxNorm: zNormBBox.nullish(),
    caption: z.string().nullish(),
    captionOverride: z.string().nullish(),
    confidence: z.number().nullish(),
    confidenceVersion: z.string().nullish(),
    createdAt: z.iso.datetime(),
    docId: z.uuid(),
    ocrText: z.string().nullish(),
    page: z.int().nullable(),
    previewUrl: z.string().nullish(),
    source: z.string(),
    status: z.string(),
    type: z.string(),
    workspaceId: z.uuid()
});

/**
 * DeepLinkResolveResponse
 *
 * Response from GET /deep-link/resolve — validated against JWT workspace.
 */
export const zDeepLinkResolveResponse = z.object({
    artifact: zEvidenceArtifactRead.nullish(),
    bboxNorm: zNormBBox.nullish(),
    docId: z.uuid(),
    page: z.int(),
    workspaceId: z.uuid()
});

/**
 * ManualArtifactCreate
 *
 * Create an artifact from user-drawn bounding box (region capture).
 */
export const zManualArtifactCreate = z.object({
    bboxNorm: zNormBBox,
    caption: z.string().max(2000).nullish(),
    page: z.int().gte(1),
    type: z.enum([
        'table',
        'figure',
        'equation',
        'section'
    ])
});

/**
 * NoteAIAnalysisRequest
 *
 * Request body for POST /v1/notes/{note_id}/ai-analyze.
 */
export const zNoteAiAnalysisRequest = z.object({
    analysis_type: z.enum([
        'summarize',
        'critique',
        'suggest_citations',
        'find_gaps',
        'expand'
    ]),
    context: z.string().min(1).max(30000),
    selection: z.string().max(10000).nullish()
});

/**
 * NoteAIAnalysisResponse
 *
 * Response for POST /v1/notes/{note_id}/ai-analyze.
 */
export const zNoteAiAnalysisResponse = z.object({
    content: z.string(),
    insightType: z.string(),
    model: z.string().nullish()
});

/**
 * NoteAssetConfirmResponse
 *
 * Phase 3 response: validated, presigned download URL.
 */
export const zNoteAssetConfirmResponse = z.object({
    assetId: z.string(),
    storageKey: z.string(),
    url: z.string()
});

/**
 * NoteAssetDiffRequest
 *
 * Body of ``POST /notes/{note_id}/assets/diff``.
 *
 * The frontend sends this on every editor save (debounced) with the
 * list of asset IDs the user removed from the note's content since
 * the last save.  The backend soft-deletes those rows and enqueues
 * R2 cleanup via ``purge_outbox``.
 */
export const zNoteAssetDiffRequest = z.object({
    removedAssetIds: z.array(z.uuid()).max(1000)
});

/**
 * NoteAssetDiffResponse
 *
 * Response for ``POST /notes/{note_id}/assets/diff``.
 *
 * ``purged_count`` counts rows that ACTUALLY transitioned ACTIVE →
 * DELETED in this call.  Rows that were already in DELETED/PURGED
 * state (idempotent retry) are skipped and not counted — the route
 * is safe to call multiple times with the same ID set.
 */
export const zNoteAssetDiffResponse = z.object({
    purgedCount: z.int().gte(0)
});

/**
 * NoteAssetResolveResponse
 *
 * Response for resolving a durable note asset key to a fresh display URL.
 */
export const zNoteAssetResolveResponse = z.object({
    storageKey: z.string(),
    url: z.string()
});

/**
 * NoteAssetUploadRequest
 *
 * Phase 1: Request a presigned upload URL for a note inline image.
 */
export const zNoteAssetUploadRequest = z.object({
    fileName: z.string().max(255),
    fileSize: z.int().gt(0).lte(5242880),
    mimeType: z.string(),
    noteId: z.uuid()
});

/**
 * NoteAssetUploadResponse
 *
 * Phase 1 response: presigned URL + headers for client PUT.
 */
export const zNoteAssetUploadResponse = z.object({
    assetId: z.string(),
    storageKey: z.string(),
    uploadHeaders: z.record(z.string(), z.string()),
    uploadUrl: z.string()
});

/**
 * NoteCommentCreate
 *
 * Create a comment on a note. Anchor fields are optional — omit for document-level comments.
 */
export const zNoteCommentCreate = z.object({
    anchorBlockId: z.string().max(64).nullish(),
    anchorFrom: z.int().gte(0).nullish(),
    anchorText: z.string().max(5000).nullish(),
    anchorTo: z.int().gte(0).nullish(),
    content: z.string().min(1).max(10000),
    parentCommentId: z.uuid().nullish()
});

/**
 * NoteCommentResponse
 *
 * Unified comment response used by all endpoints.
 */
export const zNoteCommentResponse = z.object({
    anchorBlockId: z.string().nullish(),
    anchorFrom: z.int().nullish(),
    anchorText: z.string().nullish(),
    anchorTo: z.int().nullish(),
    authorEmail: z.string().nullish(),
    authorUserId: z.uuid(),
    content: z.string(),
    createdAt: z.iso.datetime(),
    editedAt: z.iso.datetime().nullish(),
    id: z.uuid(),
    noteId: z.uuid(),
    parentCommentId: z.uuid().nullish(),
    resolved: z.boolean().optional().default(false),
    resolvedAt: z.iso.datetime().nullish(),
    resolvedByUserId: z.uuid().nullish(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * NoteCommentUpdate
 *
 * Update a comment — content only. Sets edited_at timestamp.
 */
export const zNoteCommentUpdate = z.object({
    content: z.string().min(1).max(10000)
});

/**
 * NoteCoverConfirmResponse
 *
 * Phase 3 response for ``POST /notes/{id}/cover/confirm``.
 *
 * Returned after the cover bytes have been validated, EXIF-stripped,
 * and re-uploaded. We don't return a presigned download URL — covers
 * are served via the same lifecycle the rest of the note media uses.
 *
 * Distinct from :class:`NoteCoverUploadResponse` so the *confirm*
 * response no longer has to fake sentinel ``upload_url=""`` /
 * ``expires_in_seconds=0`` / empty headers.
 */
export const zNoteCoverConfirmResponse = z.object({
    status: z.string().optional().default('confirmed'),
    storageKey: z.string()
});

/**
 * NoteCoverUploadResponse
 *
 * Phase 1 response: presigned PUT URL to upload a note cover image.
 */
export const zNoteCoverUploadResponse = z.object({
    expiresInSeconds: z.int(),
    storageKey: z.string(),
    uploadHeaders: z.record(z.string(), z.string()),
    uploadUrl: z.string()
});

/**
 * NoteCreate
 *
 * Create a note.
 *
 * workspace_id is NOT in the body — resolved from X-Workspace-ID header.
 */
export const zNoteCreate = z.object({
    anchorPayload: z.record(z.string(), z.unknown()).nullish(),
    anchorType: z.enum([
        'text',
        'bbox',
        'artifact'
    ]).nullish(),
    collectionId: z.uuid().nullish(),
    content: z.unknown().nullish(),
    coverImageKey: z.string().max(1024).nullish(),
    coverImagePosition: z.number().gte(0).lte(100).nullish(),
    documentId: z.uuid().nullish(),
    icon: z.string().max(32).nullish(),
    linkedDocumentIds: z.array(z.uuid()).optional(),
    noteCitations: z.array(z.record(z.string(), z.unknown())).optional(),
    researchObjective: z.string().max(1000).nullish(),
    sessionId: z.uuid().nullish(),
    snapshotUrl: z.string().nullish(),
    tags: z.array(z.string()).max(20).optional(),
    title: z.string().max(500).optional().default(''),
    useInWriting: z.boolean().optional().default(false),
    visibility: z.enum(['private', 'workspace']).optional().default('private')
});

/**
 * NoteDeleteResponse
 *
 * Response for note delete/trash operations.
 */
export const zNoteDeleteResponse = z.object({
    deleted: z.boolean().optional().default(true),
    id: z.string(),
    permanent: z.boolean()
});

/**
 * NoteMoveRequest
 *
 * Move a note within the hierarchy tree.
 */
export const zNoteMoveRequest = z.object({
    parentNoteId: z.uuid().nullish(),
    sortOrder: z.int().gte(0).optional().default(0)
});

/**
 * NoteResponse
 *
 * Unified note response used by ALL endpoints.
 *
 * Maps to:
 * - Nova lib/notes-types.ts → Note
 * - Nova contracts/notes.ts → NoteDTO
 * - Nova api/endpoints/notes.ts → Note
 *
 * camelCase aliases produced by CamelModel:
 * collection_id          → collectionId
 * session_id         → sessionId
 * linked_document_ids   → linkedPaperIds
 * research_objective → researchObjective
 * word_count         → wordCount
 * workspace_id       → workspaceId
 * created_by         → createdBy
 */
export const zNoteResponse = z.object({
    anchorPayload: z.record(z.string(), z.unknown()).nullish(),
    anchorType: z.string().nullish(),
    collectionId: z.uuid().nullish(),
    content: z.unknown().nullish(),
    coverImageKey: z.string().nullish(),
    coverImagePosition: z.number().nullish(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid().nullish(),
    depth: z.int().optional().default(0),
    excerpt: z.string().nullish(),
    icon: z.string().nullish(),
    id: z.uuid(),
    linkedDocumentIds: z.array(z.uuid()).optional(),
    noteCitations: z.array(z.record(z.string(), z.unknown())).optional(),
    parentNoteId: z.uuid().nullish(),
    researchObjective: z.string().nullish(),
    sessionId: z.uuid().nullish(),
    snapshotUrl: z.string().nullish(),
    sortOrder: z.int().optional().default(0),
    starred: z.boolean().optional().default(false),
    tags: z.array(z.string()).optional(),
    title: z.string(),
    updatedAt: z.iso.datetime(),
    useInWriting: z.boolean().optional().default(false),
    version: z.int().optional().default(1),
    visibility: z.string().optional().default('private'),
    warnings: z.array(z.string()).optional(),
    wordCount: z.int().optional().default(0),
    workspaceId: z.uuid()
});

/**
 * NoteSnapshotDetailResponse
 *
 * Full snapshot with body — used for preview/diff.
 */
export const zNoteSnapshotDetailResponse = z.object({
    body: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
    createdById: z.uuid().nullish(),
    id: z.uuid(),
    noteId: z.uuid(),
    title: z.string(),
    trigger: z.string(),
    version: z.int(),
    wordCount: z.int().optional().default(0)
});

/**
 * NoteSnapshotResponse
 *
 * Snapshot list item — lightweight, no body (for list views).
 */
export const zNoteSnapshotResponse = z.object({
    createdAt: z.string(),
    createdById: z.uuid().nullish(),
    id: z.uuid(),
    noteId: z.uuid(),
    title: z.string(),
    trigger: z.string(),
    version: z.int(),
    wordCount: z.int().optional().default(0)
});

/**
 * NoteSnapshotRestoreResponse
 *
 * Response after restoring a note from a snapshot.
 *
 * Industry-standard pattern: mutation responses include the full updated
 * resource so the client can update local state without a race-prone refetch.
 */
export const zNoteSnapshotRestoreResponse = z.object({
    content: z.record(z.string(), z.unknown()).nullish(),
    message: z.string().optional().default('Snapshot restored successfully'),
    newVersion: z.int(),
    noteId: z.uuid(),
    restoredFromSnapshotId: z.uuid(),
    title: z.string(),
    wordCount: z.int().optional().default(0)
});

/**
 * NoteSyncStatusResponse
 *
 * Response for note write-buffer sync status check.
 */
export const zNoteSyncStatusResponse = z.object({
    persisted: z.boolean()
});

/**
 * NoteUpdate
 *
 * Update a note — all fields optional.
 *
 * Metadata fields (title, tags, starred, collection_id, ...) are written
 * directly to the DB.  Content updates route through the Redis write-buffer
 * when the route detects a content-only autosave.
 */
export const zNoteUpdate = z.object({
    anchorPayload: z.record(z.string(), z.unknown()).nullish(),
    anchorType: z.enum([
        'text',
        'bbox',
        'artifact'
    ]).nullish(),
    collectionId: z.uuid().nullish(),
    content: z.unknown().nullish(),
    coverImageKey: z.string().nullish(),
    coverImagePosition: z.number().gte(0).lte(100).nullish(),
    icon: z.string().nullish(),
    linkedDocumentIds: z.array(z.uuid()).nullish(),
    noteCitations: z.array(z.record(z.string(), z.unknown())).nullish(),
    researchObjective: z.string().max(1000).nullish(),
    snapshotUrl: z.string().nullish(),
    starred: z.boolean().nullish(),
    tags: z.array(z.string()).max(20).nullish(),
    title: z.string().min(0).max(500).nullish(),
    useInWriting: z.boolean().nullish(),
    version: z.int().gte(1).nullish(),
    visibility: z.enum(['private', 'workspace']).nullish(),
    wordCount: z.int().gte(0).nullish(),
    writeId: z.string().max(36).nullish()
});

/**
 * NotesActivityItem
 *
 * One row in the Notes Activity surface (Slice 6, spec §5.12).
 *
 * The richer ``session_diff`` fields (words added, edges created,
 * duration) defined in the spec live on a ``note_sessions`` ledger
 * that has not been built yet. This V1 returns recency only;
 * additive fields can land later without a contract break.
 */
export const zNotesActivityItem = z.object({
    excerpt: z.string().max(200).nullish(),
    lastActivityAt: z.iso.datetime(),
    noteId: z.uuid(),
    title: z.string().max(255),
    wordCount: z.int().gte(0).optional().default(0)
});

/**
 * NotesActivitySurface
 *
 * Last 5 notes touched in the last 7 days. Trigger: count >= 1.
 */
export const zNotesActivitySurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zNotesActivityItem).min(1),
    kind: z.literal('notes_activity').optional().default('notes_activity')
});

/**
 * NotesConnectedArtifactsSurface
 *
 * Documents most cited/referenced/embedded from notes in this workspace
 * this week. Trigger: >= 3 distinct artifacts with edges in last 7d.
 * Ranking: total_weight desc, last_edge_at desc. Cap 5.
 *
 * Slice 6 implements this via a direct GROUP BY on graph_edges rather
 * than a materialized view (spec §5.14 calls for an MV with composite
 * weight; the MV is reserved for a future slice once cardinality
 * warrants it).
 */
export const zNotesConnectedArtifactsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zConnectedArtifactItem).min(3),
    kind: z.literal('notes_connected_artifacts').optional().default('notes_connected_artifacts')
});

/**
 * NotificationActorRead
 *
 * Minimal actor info embedded in notification responses.
 */
export const zNotificationActorRead = z.object({
    avatarUrl: z.string().nullish(),
    firstName: z.string().nullish(),
    id: z.uuid(),
    lastName: z.string().nullish()
});

/**
 * NotificationCategory
 *
 * Top-level notification categories for frontend filtering and grouping.
 */
export const zNotificationCategory = z.enum([
    'research',
    'collaboration',
    'system',
    'ai'
]);

/**
 * NotificationChannel
 *
 * Delivery channels for notifications.
 */
export const zNotificationChannel = z.enum(['in_app', 'email']);

/**
 * NotificationChannelSchema
 *
 * A single notification channel (in-app + email toggles).
 */
export const zNotificationChannelSchema = z.object({
    email: z.boolean().optional().default(false),
    in_app: z.boolean().optional().default(true)
});

/**
 * NotificationDismiss
 *
 * Dismiss one or more notifications.
 */
export const zNotificationDismiss = z.object({
    notificationIds: z.array(z.uuid()).min(1).max(100)
});

/**
 * NotificationMarkRead
 *
 * Mark one or more notifications as read.
 */
export const zNotificationMarkRead = z.object({
    notificationIds: z.array(z.uuid()).min(1).max(100)
});

/**
 * NotificationPriority
 *
 * Notification priority levels.
 */
export const zNotificationPriority = z.enum([
    'low',
    'normal',
    'high',
    'urgent'
]);

/**
 * NotificationSettingsRead
 *
 * Notification settings as returned by the API.
 */
export const zNotificationSettingsRead = z.object({
    ai_insights: zNotificationChannelSchema.optional(),
    collaboration: zNotificationChannelSchema.optional(),
    document_updates: zNotificationChannelSchema.optional(),
    mentions: zNotificationChannelSchema.optional(),
    research_activity: zNotificationChannelSchema.optional(),
    weekly_digest: z.boolean().optional().default(true)
});

/**
 * NotificationSettingsUpdate
 *
 * Partial update for notification settings. All fields optional.
 */
export const zNotificationSettingsUpdate = z.object({
    ai_insights: zNotificationChannelSchema.nullish(),
    collaboration: zNotificationChannelSchema.nullish(),
    document_updates: zNotificationChannelSchema.nullish(),
    mentions: zNotificationChannelSchema.nullish(),
    research_activity: zNotificationChannelSchema.nullish(),
    weekly_digest: z.boolean().nullish()
});

/**
 * NotificationType
 *
 * Types of notifications with dot-separated namespacing.
 *
 * Each member carries its own category and default priority, eliminating
 * the need for separate mapping dicts and runtime validation.
 */
export const zNotificationType = z.enum([
    'paper.suggested',
    'paper.ingested',
    'paper.ingestion_failed',
    'extraction.complete',
    'extraction.failed',
    'citation.alert',
    'workspace.invitation',
    'workspace.member_joined',
    'workspace.member_left',
    'workspace.role_changed',
    'comment.mention',
    'document.shared',
    'collection.shared',
    'billing.payment_failed',
    'billing.trial_ending',
    'billing.plan_change',
    'billing.usage_warning',
    'billing.usage_critical',
    'billing.usage_limit_reached',
    'security.new_login',
    'security.suspicious',
    'system.maintenance',
    'data_export.ready',
    'ai.insight',
    'ai.suggestion'
]);

/**
 * NotificationPreferenceRead
 *
 * Single notification preference.
 */
export const zNotificationPreferenceRead = z.object({
    channel: zNotificationChannel,
    isEnabled: z.boolean(),
    notificationType: zNotificationType
});

/**
 * NotificationPreferenceUpdate
 *
 * Update a single notification preference.
 */
export const zNotificationPreferenceUpdate = z.object({
    channel: zNotificationChannel,
    isEnabled: z.boolean(),
    notificationType: zNotificationType
});

/**
 * NotificationPreferencesBulkUpdate
 *
 * Bulk update notification preferences.
 */
export const zNotificationPreferencesBulkUpdate = z.object({
    preferences: z.array(zNotificationPreferenceUpdate).min(1).max(100)
});

/**
 * NotificationPreferencesRead
 *
 * All notification preferences for a user.
 */
export const zNotificationPreferencesRead = z.object({
    preferences: z.array(zNotificationPreferenceRead)
});

/**
 * NotificationRead
 *
 * Single notification response.
 */
export const zNotificationRead = z.object({
    actionUrl: z.string().nullish(),
    actor: zNotificationActorRead.nullish(),
    actorId: z.uuid().nullish(),
    body: z.string().nullish(),
    category: zNotificationCategory,
    createdAt: z.iso.datetime(),
    data: z.record(z.string(), z.unknown()).nullish(),
    dismissedAt: z.iso.datetime().nullish(),
    expiresAt: z.iso.datetime().nullish(),
    groupKey: z.string().nullish(),
    id: z.uuid(),
    isDismissed: z.boolean().optional().default(false),
    isRead: z.boolean().optional().default(false),
    notificationType: zNotificationType,
    priority: zNotificationPriority,
    readAt: z.iso.datetime().nullish(),
    title: z.string(),
    updatedAt: z.iso.datetime(),
    userId: z.uuid(),
    workspaceId: z.uuid().nullish(),
    workspaceName: z.string().nullish()
});

/**
 * ObjectiveDTO
 *
 * Workspace research objective. ``None`` when no objective is set.
 *
 * Historical note: this DTO briefly carried ``documentsAlignedCount``
 * and ``totalDocuments`` placeholder fields that were always returned
 * as ``None`` for a roadmap feature that never landed. They were
 * intentionally removed in the home-board final bundle (commit
 * ``13355630``) as part of the "clean out anything legacy" pass.
 * ``oasdiff`` flagged the removal as breaking; the break is
 * intentional because:
 *
 * * Nova (the only API consumer of this DTO) lives in the same
 * monorepo and was regenerated against the new spec atomically.
 * * The fields were never populated in production — no consumer
 * could be relying on their values.
 * * Keeping deprecated-always-null fields around just to
 * re-delete them later is API theater, not graceful deprecation.
 *
 * See ``docs/runbooks/workspace-embedding-state.md`` § "API contract
 * notes" for the audit trail of the removal.
 */
export const zObjectiveDto = z.object({
    text: z.string(),
    updatedAt: z.iso.datetime()
});

/**
 * OnboardingCompleteResponse
 *
 * Response after completing onboarding.
 */
export const zOnboardingCompleteResponse = z.object({
    activeWorkspaceId: z.uuid().nullable(),
    onboardingCompleted: z.boolean(),
    redirectTo: z.string()
});

/**
 * OnboardingDiscoverRequest
 *
 * Request body for the onboarding discovery search.
 */
export const zOnboardingDiscoverRequest = z.object({
    topic: z.string().min(3).max(500)
});

/**
 * OnboardingDiscoverResponse
 *
 * Response from the onboarding discovery search.
 */
export const zOnboardingDiscoverResponse = z.object({
    cacheHit: z.boolean().optional().default(false),
    query: z.string(),
    results: z.array(zDiscoveryResultItem),
    totalFound: z.int().optional().default(0)
});

/**
 * OnboardingProfileUpdate
 *
 * Partial update for the onboarding profile.
 *
 * All fields are optional — the client sends only the fields that changed.
 * The service auto-advances ``current_step`` based on which data groups
 * have been populated.
 */
export const zOnboardingProfileUpdate = z.object({
    ai_tone: z.string().max(32).nullish(),
    citation_style: z.string().max(32).nullish(),
    current_topic: z.string().max(500).nullish(),
    custom_domain: z.string().max(200).nullish(),
    domain: z.string().max(64).nullish(),
    first_value_topic: z.string().max(500).nullish(),
    goals: z.array(z.string().max(200)).max(20).nullish(),
    methodology: z.string().max(32).nullish(),
    panel_layout: z.string().max(32).nullish(),
    research_stage: z.string().max(64).nullish(),
    role: z.string().max(64).nullish(),
    source_types: z.array(z.string().max(200)).max(10).nullish(),
    suggested_collections: z.array(z.string().max(200)).max(20).nullish(),
    workspace_color: z.string().max(32).nullish(),
    workspace_description: z.string().max(500).nullish(),
    workspace_icon: z.string().max(32).nullish(),
    workspace_name: z.string().min(1).max(200).nullish()
});

/**
 * OnboardingSkipResponse
 *
 * Response after skipping onboarding.
 */
export const zOnboardingSkipResponse = z.object({
    activeWorkspaceId: z.uuid().nullable(),
    onboardingCompleted: z.boolean(),
    redirectTo: z.string(),
    skipped: z.boolean().optional().default(true),
    skippedAtStep: z.string()
});

/**
 * OnboardingStep
 *
 * Tracks the highest onboarding milestone the user has reached.
 *
 * Backend tracks data milestones, not frontend UI steps. Presentational
 * steps (welcome, education, habit) are frontend-only and not stored here.
 */
export const zOnboardingStep = z.enum([
    'intent',
    'personalization',
    'workspace',
    'first_value',
    'completed'
]);

/**
 * OnboardingProfileResponse
 *
 * Full onboarding profile — server-authoritative state.
 *
 * Returned by both GET /status and PATCH /profile.
 */
export const zOnboardingProfileResponse = z.object({
    activeWorkspaceId: z.uuid().nullish(),
    aiTone: z.string().nullish(),
    citationStyle: z.string().nullish(),
    completedAt: z.iso.datetime().nullish(),
    currentStep: zOnboardingStep,
    currentTopic: z.string().nullish(),
    customDomain: z.string().nullish(),
    domain: z.string().nullish(),
    firstValueTopic: z.string().nullish(),
    goals: z.array(z.string()).nullish(),
    methodology: z.string().nullish(),
    onboardingCompleted: z.boolean().optional().default(false),
    panelLayout: z.string().nullish(),
    researchStage: z.string().nullish(),
    role: z.string().nullish(),
    skipped: z.boolean().optional().default(false),
    skippedAtStep: z.string().nullish(),
    sourceTypes: z.array(z.string()).nullish(),
    suggestedCollections: z.array(z.string()).nullish(),
    workspaceColor: z.string().nullish(),
    workspaceDescription: z.string().nullish(),
    workspaceIcon: z.string().nullish(),
    workspaceName: z.string().nullish()
});

/**
 * OrgRole
 */
export const zOrgRole = z.enum([
    'org_owner',
    'org_admin',
    'member'
]);

/**
 * OrgSummary
 *
 * Minimal org info sent in the sync response for the client sidebar.
 */
export const zOrgSummary = z.object({
    id: z.uuid(),
    name: z.string(),
    role: zOrgRole,
    slug: z.string()
});

/**
 * OutboxStats
 */
export const zOutboxStats = z.object({
    failed: z.int().optional().default(0),
    oldest_pending_seconds: z.number().optional().default(0),
    pending: z.int().optional().default(0),
    processed_1h: z.int().optional().default(0)
});

/**
 * OwnershipTransferRequest
 *
 * Request to transfer workspace ownership to another member.
 */
export const zOwnershipTransferRequest = z.object({
    target_user_id: z.uuid()
});

/**
 * OwnershipTransferResponse
 *
 * Response after successful ownership transfer.
 */
export const zOwnershipTransferResponse = z.object({
    new_owner_id: z.string(),
    previous_owner_id: z.string(),
    transferred: z.boolean().optional().default(true)
});

/**
 * PaginatedDeadLetters
 *
 * Paginated list of dead letter jobs.
 */
export const zPaginatedDeadLetters = z.object({
    items: z.array(zDeadLetterDetail),
    limit: z.int(),
    offset: z.int(),
    total: z.int()
});

/**
 * PaginatedIngestionJobs
 *
 * Paginated list of ingestion jobs.
 */
export const zPaginatedIngestionJobs = z.object({
    items: z.array(zIngestionJobDetail),
    limit: z.int(),
    offset: z.int(),
    total: z.int()
});

/**
 * PaginationMeta
 *
 * Pagination metadata — cursor-first with optional offset support.
 *
 * Cursor-based endpoints omit page/total_pages (meaningless with keyset).
 * Offset-based endpoints populate all fields for backward compat.
 */
export const zPaginationMeta = z.object({
    hasMore: z.boolean(),
    nextCursor: z.string().nullish(),
    page: z.int().gte(1).nullish(),
    pageSize: z.int().gte(1).lte(200),
    prevCursor: z.string().nullish(),
    totalItems: z.int().gte(0).nullish(),
    totalPages: z.int().gte(0).nullish()
});

/**
 * InvitationListResponse
 *
 * Paginated list of invitations with total count.
 */
export const zInvitationListResponse = z.object({
    items: z.array(zInvitationRead),
    pagination: zPaginationMeta
});

/**
 * NotificationListResponse
 *
 * Paginated notification list with unread count.
 */
export const zNotificationListResponse = z.object({
    items: z.array(zNotificationRead),
    pagination: zPaginationMeta,
    unreadCount: z.int().gte(0).optional().default(0)
});

/**
 * PaginatedResponse
 *
 * Paginated response — cursor-first design.
 *
 * Cursor mode (default):
 * { "items": [...], "pagination": { "pageSize": 20, "hasMore": true, "nextCursor": "..." } }
 *
 * Offset mode:
 * { "items": [...], "pagination": { "page": 1, "pageSize": 20, "totalItems": 100, "hasMore": true } }
 */
export const zPaginatedResponse = z.object({
    items: z.array(z.unknown()),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[AISuggestionRead]
 */
export const zPaginatedResponseAiSuggestionRead = z.object({
    items: z.array(zAiSuggestionRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[CitationRead]
 */
export const zPaginatedResponseCitationRead = z.object({
    items: z.array(zCitationRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[ClaimRowRead]
 */
export const zPaginatedResponseClaimRowRead = z.object({
    items: z.array(zClaimRowRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[CollectionRead]
 */
export const zPaginatedResponseCollectionRead = z.object({
    items: z.array(zCollectionRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[ColumnTemplateGroupRead]
 */
export const zPaginatedResponseColumnTemplateGroupRead = z.object({
    items: z.array(zColumnTemplateGroupRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[CompareBoardRead]
 */
export const zPaginatedResponseCompareBoardRead = z.object({
    items: z.array(zCompareBoardRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[DeadLetterListItem]
 */
export const zPaginatedResponseDeadLetterListItem = z.object({
    items: z.array(zDeadLetterListItem),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[DocumentPinRead]
 */
export const zPaginatedResponseDocumentPinRead = z.object({
    items: z.array(zDocumentPinRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[DocumentVersionRead]
 */
export const zPaginatedResponseDocumentVersionRead = z.object({
    items: z.array(zDocumentVersionRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[DuplicateAlertRead]
 */
export const zPaginatedResponseDuplicateAlertRead = z.object({
    items: z.array(zDuplicateAlertRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[EvidenceArtifactRead]
 */
export const zPaginatedResponseEvidenceArtifactRead = z.object({
    items: z.array(zEvidenceArtifactRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[ExtractionTableSummary]
 */
export const zPaginatedResponseExtractionTableSummary = z.object({
    items: z.array(zExtractionTableSummary),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[GraphEdgeResponse]
 */
export const zPaginatedResponseGraphEdgeResponse = z.object({
    items: z.array(zGraphEdgeResponse),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[GraphNeighbourNode]
 */
export const zPaginatedResponseGraphNeighbourNode = z.object({
    items: z.array(zGraphNeighbourNode),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[HighlightRead]
 */
export const zPaginatedResponseHighlightRead = z.object({
    items: z.array(zHighlightRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[IntelligenceFeedbackRead]
 */
export const zPaginatedResponseIntelligenceFeedbackRead = z.object({
    items: z.array(zIntelligenceFeedbackRead),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[LibraryItemDTO]
 */
export const zPaginatedResponseLibraryItemDto = z.object({
    items: z.array(zLibraryItemDto),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[MaintenanceJobRunResponse]
 */
export const zPaginatedResponseMaintenanceJobRunResponse = z.object({
    items: z.array(zMaintenanceJobRunResponse),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[NoteCommentResponse]
 */
export const zPaginatedResponseNoteCommentResponse = z.object({
    items: z.array(zNoteCommentResponse),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[NoteResponse]
 */
export const zPaginatedResponseNoteResponse = z.object({
    items: z.array(zNoteResponse),
    pagination: zPaginationMeta
});

/**
 * PaginatedResponse[NoteSnapshotResponse]
 */
export const zPaginatedResponseNoteSnapshotResponse = z.object({
    items: z.array(zNoteSnapshotResponse),
    pagination: zPaginationMeta
});

/**
 * PatternTier
 *
 * Calm-not-alarmed urgency tier for ambient patterns.
 */
export const zPatternTier = z.enum(['medium', 'low']);

/**
 * PatternType
 *
 * Pattern type for the Ambient Pattern Feed surface.
 *
 * Slice 4 scope drives patterns from existing tables:
 *
 * * ``research_gap`` — under-represented topic from ``research_gaps``
 * * ``contradiction`` — pairwise conflict from ``contradiction_pairs``
 * (medium/low severity only — HIGH lives in 5.10).
 * * ``trending_cluster`` — active K-means cluster from ``trending_clusters``.
 *
 * Future types (e.g. ``open_question_thread``) will plug in via
 * additional values once their detectors land.
 */
export const zPatternType = z.enum([
    'research_gap',
    'contradiction',
    'trending_cluster'
]);

/**
 * PatternItem
 *
 * One row in the Ambient Pattern Feed surface.
 */
export const zPatternItem = z.object({
    confidence: z.number().gte(0).lte(1),
    description: z.string().max(500),
    detectedAt: z.iso.datetime(),
    patternId: z.uuid(),
    sourceDocumentIds: z.array(z.uuid()).max(8).optional(),
    tier: zPatternTier,
    title: z.string().max(200),
    type: zPatternType
});

/**
 * IntelligencePatternsSurface
 *
 * Ambient Pattern Feed (Slice 4, spec §5.9).
 *
 * Cross-document patterns: research gaps, pairwise contradictions
 * (non-critical severity), and trending topic clusters. Per spec the
 * surface fires only when the workspace has ≥ 5 ready documents — below
 * that threshold the Fingerprint Status surface (5.6) carries the
 * cold-start message instead. Capped at 4 rows; server orders by tier
 * then confidence then recency.
 */
export const zIntelligencePatternsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zPatternItem).min(1),
    kind: z.literal('intelligence_patterns').optional().default('intelligence_patterns')
});

/**
 * PerArtifactStatusRead
 *
 * Per-artifact processing status for a document (included in DocumentDetailRead).
 *
 * When an artifact is in 'failed' state, the corresponding failure detail
 * field provides the reason (surfaced in the ready_with_warnings checklist).
 *
 * Capability fields (``*_cap``) are populated by ``build_artifact_capability_status``
 * in the document service layer when the workspace context is available.  They give
 * the frontend authoritative plan-tier information so it never needs a local tier map.
 * The ``*_cap`` fields are None when the caller did not request capability enrichment
 * (e.g. lightweight list endpoints that don't have workspace context).
 */
export const zPerArtifactStatusRead = z.object({
    citations: zArtifactStatusEnum.nullish(),
    citationsFailure: zArtifactFailureDetail.nullish(),
    claims: zArtifactStatusEnum.nullish(),
    claimsCap: z.record(z.string(), z.unknown()).nullish(),
    claimsFailure: zArtifactFailureDetail.nullish(),
    embeddings: zArtifactStatusEnum.nullish(),
    embeddingsFailure: zArtifactFailureDetail.nullish(),
    figures: zArtifactStatusEnum.nullish(),
    figuresCap: z.record(z.string(), z.unknown()).nullish(),
    figuresFailure: zArtifactFailureDetail.nullish(),
    sections: zArtifactStatusEnum.nullish(),
    sectionsFailure: zArtifactFailureDetail.nullish(),
    tables: zArtifactStatusEnum.nullish(),
    tablesCap: z.record(z.string(), z.unknown()).nullish(),
    tablesFailure: zArtifactFailureDetail.nullish(),
    text: zArtifactStatusEnum.nullish(),
    textFailure: zArtifactFailureDetail.nullish()
});

/**
 * PermissionCheckResponse
 *
 * Result of a workspace permission check.
 */
export const zPermissionCheckResponse = z.object({
    has_permission: z.boolean(),
    required_role: z.string(),
    user_id: z.string(),
    user_role: z.string().nullish(),
    workspace_id: z.string()
});

/**
 * PermissionSnapshotResponse
 *
 * Server-signed permission snapshot for a workspace membership.
 */
export const zPermissionSnapshotResponse = z.object({
    confirmedAt: z.int(),
    features: z.record(z.string(), z.boolean()),
    permissions: z.record(z.string(), z.boolean()),
    plan: z.string(),
    role: z.string(),
    signature: z.string(),
    userId: z.uuid(),
    workspaceId: z.uuid()
});

/**
 * PgCronJobStatusResponse
 *
 * Status of a single pg_cron job.
 */
export const zPgCronJobStatusResponse = z.object({
    expectedSchedule: z.string().nullish(),
    jobname: z.string(),
    lastRunAt: z.iso.datetime().nullish(),
    lastStatus: z.string().nullish(),
    recentFailures: z.int().optional().default(0),
    schedule: z.string(),
    scheduleDrift: z.boolean().optional().default(false)
});

/**
 * PgCronHealthResponse
 *
 * pg_cron health report — drift detection + failure analysis.
 */
export const zPgCronHealthResponse = z.object({
    driftedSchedules: z.array(z.string()).optional(),
    error: z.string().nullish(),
    healthy: z.boolean().optional().default(true),
    missingJobs: z.array(z.string()).optional(),
    pgcronAvailable: z.boolean().optional().default(true),
    recentFailures: z.int().optional().default(0),
    registeredJobs: z.array(zPgCronJobStatusResponse).optional(),
    timestamp: z.iso.datetime().nullish(),
    unexpectedJobs: z.array(z.string()).optional()
});

/**
 * PinnedCollectionItem
 *
 * One row in the Pinned Collections surface (Slice 7, spec §5.18).
 */
export const zPinnedCollectionItem = z.object({
    collectionId: z.uuid(),
    color: z.string().max(20),
    itemCount: z.int().gte(0).optional().default(0),
    lastUpdatedAt: z.iso.datetime(),
    name: z.string().max(255)
});

/**
 * PinnedCollectionsSurface
 *
 * Pinned-collection shortcuts. Trigger: count(collections WHERE
 * is_pinned=true AND deleted_at IS NULL) >= 1. Ranking: updated_at desc.
 * Cap 6.
 */
export const zPinnedCollectionsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zPinnedCollectionItem).min(1),
    kind: z.literal('pinned_collections').optional().default('pinned_collections')
});

/**
 * PinnedEvidenceRequest
 *
 * A user-pinned evidence item attached to a send request.
 */
export const zPinnedEvidenceRequest = z.object({
    artifact_id: z.string().nullish(),
    bbox_norm: z.record(z.string(), z.number()).nullish(),
    doc_id: z.uuid(),
    item_id: z.string(),
    page: z.int().optional().default(0),
    pin_level: z.int().gte(0).lte(2).optional().default(0)
});

/**
 * PipelineOverview
 */
export const zPipelineOverview = z.object({
    avg_processing_seconds: z.number().optional().default(0),
    failed: z.int().optional().default(0),
    processing: z.int().optional().default(0),
    ready: z.int().optional().default(0),
    total_24h: z.int().optional().default(0),
    uploading: z.int().optional().default(0)
});

/**
 * PipelinePulseSurface
 *
 * Ambient pipeline-state chips. Renders only when in-flight or recent.
 */
export const zPipelinePulseSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    documentsProcessing: z.int().gte(0),
    freshnessAt: z.iso.datetime(),
    jobsQueued: z.int().gte(0),
    kind: z.literal('pipeline_pulse'),
    lastCompletedAt: z.iso.datetime().nullish()
});

/**
 * PipelineStage
 *
 * Processing pipeline stages — mirrors PipelineStageEnum from ingestion model.
 */
export const zPipelineStage = z.enum([
    'preflight',
    'dedup',
    'parse',
    'metadata',
    'segment',
    'detect',
    'extract',
    'structure',
    'citations',
    'claims',
    'embed',
    'enrich',
    'summarize',
    'qa_precompute',
    'thumbnail',
    'ready'
]);

/**
 * PipelineStats
 *
 * Document processing pipeline statistics.
 */
export const zPipelineStats = z.object({
    avg_processing_time_minutes: z.number().gte(0).optional().default(0),
    documents_pending: z.int().gte(0),
    documents_processed: z.int().gte(0),
    stale_extractions: z.int().gte(0),
    total_documents: z.int().gte(0)
});

/**
 * PlanCatalogFeatures
 *
 * Boolean feature flags for a plan tier.
 */
export const zPlanCatalogFeatures = z.object({
    admin_controls: z.boolean().optional().default(false),
    annual_pool: z.boolean().optional().default(false),
    api_access: z.boolean().optional().default(false),
    audit_export: z.boolean().optional().default(false),
    custom_branding: z.boolean().optional().default(false),
    priority_queue: z.boolean().optional().default(false),
    sso: z.boolean().optional().default(false),
    usage_attribution: z.boolean().optional().default(false)
});

/**
 * PlanCatalogLimits
 *
 * Display-ready limits for a plan tier.
 */
export const zPlanCatalogLimits = z.object({
    ai_capacity: z.string(),
    default_model: z.string().nullish(),
    documents: z.string().nullish(),
    extraction_jobs: z.string().nullish(),
    seats: z.string().nullish(),
    storage: z.string().nullish()
});

/**
 * PlanCatalogEntry
 *
 * Full display metadata for a single plan tier.
 */
export const zPlanCatalogEntry = z.object({
    annual_price: z.int().nullish(),
    badge: z.string().nullish(),
    cta_action: z.string().optional().default('checkout'),
    cta_label: z.string().nullish(),
    description: z.string(),
    features: zPlanCatalogFeatures,
    highlights: z.array(z.string()).optional(),
    id: z.string(),
    is_public: z.boolean().optional().default(true),
    limits: zPlanCatalogLimits,
    monthly_price: z.int().nullish(),
    name: z.string(),
    savings_percent: z.int().nullish(),
    sort_order: z.int().optional().default(0),
    trial_days: z.int().nullish()
});

/**
 * PlanCatalogResponse
 *
 * GET /billing/plans response — full plan catalog for comparison/picker UI.
 */
export const zPlanCatalogResponse = z.object({
    feature_matrix: z.array(zFeatureMatrixCategory).optional(),
    metadata: zCatalogMetadata,
    plans: z.array(zPlanCatalogEntry)
});

/**
 * PlanDistributionItem
 */
export const zPlanDistributionItem = z.object({
    count: z.int(),
    plan: z.string()
});

/**
 * PlanDistributionResponse
 */
export const zPlanDistributionResponse = z.object({
    items: z.array(zPlanDistributionItem).optional()
});

/**
 * PlanTier
 *
 * Canonical subscription plan tiers for billing and feature gating.
 *
 * This is the single source of truth for plan tiers across the monorepo.
 * All services (core, seeker, nova) must use these values.
 *
 * Plan Hierarchy (ascending):
 * - FREE: Personal use, limited features
 * - PRO: Individual power users, advanced features
 * - TEAM: Collaboration, shared workspaces, priority support
 * - ENTERPRISE: Custom features, unlimited usage, SLA
 */
export const zPlanTier = z.enum([
    'free',
    'starter',
    'pro',
    'team',
    'enterprise'
]);

/**
 * PlatformLimits
 *
 * Platform limit configuration.
 */
export const zPlatformLimits = z.object({
    max_documents_per_workspace: z.int().gte(0).optional().default(10000),
    max_team_members_per_workspace: z.int().gte(0).optional().default(100),
    max_upload_file_size_mb: z.int().gte(0).optional().default(500),
    max_workspaces_per_user: z.int().gte(0).optional().default(50)
});

/**
 * ConfigurationResponse
 *
 * Response for configuration endpoint.
 */
export const zConfigurationResponse = z.object({
    api_version: z.string().optional().default('v1'),
    cache_enabled: z.boolean().optional().default(false),
    cors_enabled: z.boolean().optional().default(true),
    environment: z.string(),
    features: zFeatureConfig,
    limits: zPlatformLimits,
    rate_limiting_enabled: z.boolean().optional().default(true),
    timestamp: z.string()
});

/**
 * PlatformSummary
 */
export const zPlatformSummary = z.object({
    computed_at: z.string().optional().default(''),
    dau: z.int().optional().default(0),
    mau: z.int().optional().default(0),
    mtd_ai_spend_usd: z.number().optional().default(0),
    total_documents: z.int().optional().default(0),
    total_users: z.int().optional().default(0),
    total_workspaces: z.int().optional().default(0),
    wau: z.int().optional().default(0)
});

/**
 * PresignedUploadResponse
 *
 * Response with presigned upload URL.
 *
 * Client uses this to upload file directly to storage.
 * When ``is_duplicate`` is True the file already exists; no upload is needed.
 */
export const zPresignedUploadResponse = z.object({
    duplicate_asset_id: z.string().nullish(),
    expires_in: z.int(),
    is_duplicate: z.boolean().optional().default(false),
    storage_key: z.string(),
    upload_headers: z.record(z.string(), z.string()).optional(),
    upload_url: z.string().nullish(),
    validation_rules: z.record(z.string(), z.unknown()).optional()
});

/**
 * PricingAuditFinding
 *
 * Single issue found during a pricing audit.
 */
export const zPricingAuditFinding = z.object({
    message: z.string(),
    model_id: z.string(),
    severity: z.string()
});

/**
 * PricingAuditResponse
 *
 * GET /v1/billing/admin/pricing-audit response.
 */
export const zPricingAuditResponse = z.object({
    errors: z.int(),
    findings: z.array(zPricingAuditFinding),
    total_findings: z.int(),
    warnings: z.int()
});

/**
 * PricingVerificationResponse
 *
 * GET /v1/billing/admin/pricing-verification response.
 */
export const zPricingVerificationResponse = z.object({
    active: z.int(),
    deprecated: z.int(),
    models: z.array(z.record(z.string(), z.unknown())),
    providers: z.record(z.string(), z.int()),
    stale_verifications: z.int(),
    total_models: z.int()
});

/**
 * PrivacySettingsRead
 *
 * Privacy & consent settings as returned by the API.
 */
export const zPrivacySettingsRead = z.object({
    analytics_consent: z.boolean().nullish(),
    analytics_consent_declined_at: z.iso.datetime().nullish(),
    feedback_consent: z.boolean().optional().default(true)
});

/**
 * PrivacySettingsUpdate
 *
 * Partial update for privacy settings. All fields optional.
 */
export const zPrivacySettingsUpdate = z.object({
    analytics_consent: z.boolean().nullish(),
    analytics_consent_declined_at: z.iso.datetime().nullish(),
    feedback_consent: z.boolean().nullish()
});

/**
 * ProbeStatusResponse
 *
 * Common health probe response for Redis, DB, and storage endpoints.
 */
export const zProbeStatusResponse = z.object({
    cached: z.boolean().nullish(),
    latency_ms: z.number().nullish(),
    status: z.string(),
    timestamp: z.string()
});

/**
 * ProblemDetail
 *
 * RFC 9457 Problem Details for HTTP APIs.
 *
 * This provides a standardized format for returning machine-readable
 * error details from HTTP APIs.
 *
 * Reference: https://www.rfc-editor.org/rfc/rfc9457.html
 */
export const zProblemDetail = z.object({
    detail: z.string().nullish(),
    errors: z.array(z.record(z.string(), z.unknown())).nullish(),
    instance: z.string().nullish(),
    request_id: z.string().nullish(),
    status: z.int(),
    title: z.string(),
    type: z.string().optional().default('about:blank')
});

/**
 * ProcessInfo
 *
 * Process resource information.
 */
export const zProcessInfo = z.object({
    connections: z.int().gte(0),
    memory_rss_mb: z.number().gte(0),
    memory_vms_mb: z.number().gte(0),
    open_files: z.int().gte(0),
    pid: z.int(),
    threads: z.int().gte(0)
});

/**
 * ProcessShareDocumentRequest
 *
 * Request to enqueue pipeline processing for an already-saved shared document.
 */
export const zProcessShareDocumentRequest = z.object({
    document_id: z.uuid()
});

/**
 * ProcessShareDocumentResponse
 *
 * Result of triggering full pipeline processing on a saved shared document.
 */
export const zProcessShareDocumentResponse = z.object({
    documentId: z.string(),
    status: z.string().optional().default('processing')
});

/**
 * ProcessingProvenanceRead
 *
 * User-facing provenance metadata for a processed document.
 *
 * Returned on GET /documents/{id}. This schema is TRIMMED to trust signals
 * that belong in the end-user UI (Copilot review #11 + CLAUDE.md: "pipeline
 * versions, embedding model names are backend-only"). The fields kept here
 * are exactly what the UX audit calls out as user-safe: parser identity,
 * OCR detection, and fallback/degraded-parse warnings.
 *
 * Backend-only fields — ``pipeline_version``, ``embedding_model``,
 * ``extractor_version``, ``model_versions``, ``processed_plan_tier`` —
 * were removed from this public schema. If an admin/staff surface needs
 * them, add a separate ``ProcessingProvenanceAdminRead`` gated behind a
 * staff check rather than exposing them on the user endpoint.
 */
export const zProcessingProvenanceRead = z.object({
    ocrDetected: z.boolean().optional().default(false),
    parseDegraded: z.boolean().optional().default(false),
    parserVendor: z.string(),
    parserVersion: z.string(),
    processedAt: z.iso.datetime().nullish(),
    processingWarnings: z.array(z.string()).optional()
});

/**
 * PipelineStatusRead
 *
 * Response from GET /documents/{id}/pipeline (public).
 */
export const zPipelineStatusRead = z.object({
    artifactStatuses: zPerArtifactStatusRead.nullish(),
    currentJob: zIngestionJobRead.nullish(),
    documentId: z.uuid(),
    documentStatus: z.string(),
    jobHistory: z.array(zIngestionJobRead).optional().default([]),
    provenance: zProcessingProvenanceRead.nullish()
});

/**
 * ProfilePictureResponse
 *
 * Response for GET /users/{id}/profile-picture.
 */
export const zProfilePictureResponse = z.object({
    url: z.string().nullish()
});

/**
 * ProfileSettingsRead
 *
 * Profile settings as returned by the API (read-only fields included).
 */
export const zProfileSettingsRead = z.object({
    avatar_url: z.string().nullish(),
    bio: z.string().optional().default(''),
    department: z.string().optional().default(''),
    first_name: z.string().optional().default(''),
    google_scholar_id: z.string().optional().default(''),
    institution: z.string().optional().default(''),
    last_name: z.string().optional().default(''),
    orcid: z.string().optional().default(''),
    public_profile: z.boolean().optional().default(false),
    research_fields: z.array(z.string()).optional(),
    role: z.string().optional().default('')
});

/**
 * ProfileSettingsUpdate
 *
 * Partial update for profile settings. All fields optional.
 */
export const zProfileSettingsUpdate = z.object({
    bio: z.string().max(2000).nullish(),
    department: z.string().max(200).nullish(),
    first_name: z.string().max(100).nullish(),
    google_scholar_id: z.string().max(20).nullish(),
    institution: z.string().max(300).nullish(),
    last_name: z.string().max(100).nullish(),
    orcid: z.string().max(19).nullish(),
    public_profile: z.boolean().nullish(),
    research_fields: z.array(z.string()).nullish(),
    role: z.string().max(200).nullish()
});

/**
 * ProviderMetaSchema
 */
export const zProviderMetaSchema = z.object({
    error: z.string().nullish(),
    latencyMs: z.number(),
    name: z.string(),
    resultCount: z.int()
});

/**
 * DiscoverySearchResponseSchema
 */
export const zDiscoverySearchResponseSchema = z.object({
    cacheHit: z.boolean().optional().default(false),
    latencyMs: z.number(),
    providers: z.array(zProviderMetaSchema),
    query: z.string(),
    results: z.array(zDiscoveryResultSchema),
    totalAfterDedup: z.int(),
    totalBeforeDedup: z.int(),
    totalReturned: z.int()
});

/**
 * PublicHealthCheckResponse
 *
 * Public health check response model.
 */
export const zPublicHealthCheckResponse = z.object({
    database: z.boolean(),
    service: z.string(),
    status: z.string(),
    storage: z.boolean(),
    timestamp: z.iso.datetime(),
    version: z.string()
});

/**
 * PublicSharingResponse
 *
 * Public response for token resolution (no auth required).
 *
 * Returns enough info for the share page to render the access gate
 * and route to the correct content viewer without leaking internal details.
 */
export const zPublicSharingResponse = z.object({
    allowLibrarySave: z.boolean().optional().default(false),
    id: z.string(),
    isExpired: z.boolean(),
    isPasswordProtected: z.boolean(),
    linkAccessLevel: z.string(),
    resourceId: z.string(),
    resourceTitle: z.string(),
    resourceType: z.string(),
    showAnnotations: z.boolean().optional().default(false)
});

/**
 * PythonInfo
 *
 * Python runtime information.
 */
export const zPythonInfo = z.object({
    compiler: z.string(),
    implementation: z.string(),
    version: z.string()
});

/**
 * QAPairRead
 *
 * Pre-computed Q&A pair for instant document answers.
 */
export const zQaPairRead = z.object({
    answer: z.string(),
    confidence: z.number().optional().default(0.8),
    question: z.string(),
    sourceSection: z.string().nullish()
});

/**
 * QualitySummaryResponse
 *
 * Aggregated quality metrics for a workspace.
 */
export const zQualitySummaryResponse = z.object({
    avg_citation_accuracy: z.number().optional().default(0),
    avg_coherence_score: z.number().optional().default(0),
    avg_grounding_score: z.number().optional().default(0),
    avg_hallucination_score: z.number().optional().default(0),
    avg_latency_ms: z.int().optional().default(0),
    avg_user_rating: z.number().optional().default(0),
    days: z.int().optional().default(30),
    feedback_count: z.int().optional().default(0),
    total_cost_usd: z.number().optional().default(0),
    total_responses: z.int().optional().default(0)
});

/**
 * QualityTimeseriesPoint
 *
 * Single day in the quality time-series.
 */
export const zQualityTimeseriesPoint = z.object({
    avg_latency_ms: z.int().optional().default(0),
    citation_accuracy: z.number().optional().default(0),
    count: z.int().optional().default(0),
    date: z.string().nullish(),
    grounding_score: z.number().optional().default(0),
    hallucination_score: z.number().optional().default(0)
});

/**
 * QualityDashboardResponse
 *
 * Full quality dashboard data.
 */
export const zQualityDashboardResponse = z.object({
    models: z.array(zModelComparisonItem),
    summary: zQualitySummaryResponse,
    timeseries: z.array(zQualityTimeseriesPoint)
});

/**
 * QueueDetail
 *
 * Statistics for a single job queue.
 */
export const zQueueDetail = z.object({
    completed_24h: z.int().gte(0).optional().default(0),
    failed_24h: z.int().gte(0).optional().default(0),
    in_progress: z.int().gte(0).optional().default(0),
    name: z.string(),
    pending: z.int().gte(0).optional().default(0)
});

/**
 * QueueStats
 *
 * Statistics for a job queue.
 */
export const zQueueStats = z.object({
    completed_jobs_24h: z.int().gte(0).optional().default(0),
    failed_jobs_24h: z.int().gte(0).optional().default(0),
    in_progress_jobs: z.int().gte(0),
    pending_jobs: z.int().gte(0),
    queue_name: z.string()
});

/**
 * ReadinessCheckResponse
 *
 * Response for readiness check endpoint.
 */
export const zReadinessCheckResponse = z.object({
    checks: z.record(z.string(), z.union([
        zDependencyCheckStatus,
        z.record(z.string(), z.unknown())
    ])),
    status: z.string(),
    timestamp: z.string()
});

/**
 * ReadingProgressRead
 *
 * Response for GET/PUT /documents/{id}/reading-progress.
 */
export const zReadingProgressRead = z.object({
    documentId: z.uuid(),
    lastPage: z.int(),
    maxPageReached: z.int(),
    progressPercent: z.int(),
    scrollTop: z.number().nullish(),
    totalPages: z.int(),
    updatedAt: z.iso.datetime()
});

/**
 * ReadingProgressUpdate
 *
 * Request body for PUT /documents/{id}/reading-progress.
 */
export const zReadingProgressUpdate = z.object({
    lastPage: z.int().gte(1).lte(10000),
    maxPageReached: z.int().gte(1).lte(10000),
    scrollTop: z.number().gte(0).nullish(),
    totalPages: z.int().gte(1).lte(10000)
});

/**
 * ReaperStatus
 *
 * Status of the Worker Reaper process.
 */
export const zReaperStatus = z.object({
    cycle_duration_ms: z.int().nullish(),
    is_healthy: z.boolean(),
    last_run_at: z.string().nullish(),
    stuck_jobs_last_run: z.int().gte(0).optional().default(0),
    total_runs: z.int().gte(0).optional().default(0),
    workers_alive: z.int().gte(0).optional().default(0),
    workers_dead: z.int().gte(0).optional().default(0)
});

/**
 * RecentConversationItem
 *
 * One row in the Recent Conversations surface.
 *
 * ``scopeMode`` lets the client render a small chip distinguishing
 * workspace-wide chats from doc/collection-scoped ones. Title is
 * nullable on the model — the server passes through as-is.
 *
 * ``lastMessagePreview`` is the most recent **user-role** message for
 * the conversation (role-filtered on the server). Note that this
 * differs from the chat list endpoint (``/chat/conversations``), which
 * computes previews without a role filter. Used as the home dashboard
 * fall-back when ``title`` has not yet been set or auto-generated;
 * the client can render
 * ``title || lastMessagePreview.slice(0, 50) || 'New conversation'``
 * to keep the surface consistent with the chat sidebar.
 */
export const zRecentConversationItem = z.object({
    conversationId: z.uuid(),
    lastActivityAt: z.iso.datetime(),
    lastMessagePreview: z.string().max(200).nullish(),
    scopeMode: z.enum([
        'workspace',
        'collection',
        'document',
        'custom'
    ]).optional().default('workspace'),
    title: z.string().max(512).nullish()
});

/**
 * RecentConversationsSurface
 *
 * Resume in-flight inquiry — last 5 conversations the user touched
 * in the last 14 days (Slice 5, spec §5.15). Ranking is recency-only
 * in this slice; the open-question boost lands when the memory ledger
 * field ships.
 */
export const zRecentConversationsSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zRecentConversationItem).min(1),
    kind: z.literal('recent_conversations').optional().default('recent_conversations')
});

/**
 * RecentReadyItem
 *
 * One row in the Recent Ready surface.
 */
export const zRecentReadyItem = z.object({
    documentId: z.uuid(),
    readyAt: z.iso.datetime(),
    title: z.string()
});

/**
 * RecentReadySurface
 *
 * Documents that just crossed to READY in the last 24h.
 *
 * Ranking input in Slice 2 is recency-only. The spec calls for
 * ``objective_relevance × 0.6 + recency × 0.4`` once the workspace
 * fingerprint ships (Slice 3); until then the surface returns
 * ``degrade_reasons: ["fingerprint_pending"]`` so the client can render
 * a small badge indicating relevance is unavailable.
 */
export const zRecentReadySurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zRecentReadyItem).min(1),
    kind: z.literal('recent_ready').optional().default('recent_ready')
});

/**
 * RecommendationRead
 *
 * A single recommended document from centroid search.
 */
export const zRecommendationRead = z.object({
    authors: z.array(z.string()).optional(),
    documentId: z.uuid(),
    doi: z.string().nullish(),
    journal: z.string().nullish(),
    nearestSeedId: z.uuid().nullish(),
    relevanceScore: z.number().gte(0).lte(1),
    title: z.string(),
    year: z.int().nullish()
});

/**
 * RecommendationListRead
 *
 * Response for "More Like These" recommendations.
 */
export const zRecommendationListRead = z.object({
    recommendations: z.array(zRecommendationRead).optional(),
    seedDocumentIds: z.array(z.uuid()),
    total: z.int().gte(0).optional().default(0)
});

/**
 * RecommendationRequest
 *
 * Request body for centroid-based recommendations.
 */
export const zRecommendationRequest = z.object({
    documentIds: z.array(z.uuid()).min(2).max(5)
});

/**
 * RedisHealthDetailResponse
 *
 * Response for Redis health detail endpoint.
 */
export const zRedisHealthDetailResponse = z.object({
    connected_clients: z.int().gte(0).optional().default(0),
    latency_ms: z.number().gte(0),
    redis_version: z.string().optional().default('unknown'),
    status: z.string(),
    timestamp: z.string(),
    uptime_in_seconds: z.int().gte(0).optional().default(0),
    used_memory_human: z.string().optional().default('unknown')
});

/**
 * RegisterRequest
 *
 * Request body for user registration via POST /auth/register.
 *
 * Called by the frontend after Supabase signup + OTP verification.
 * This is the primary endpoint for new user creation.
 */
export const zRegisterRequest = z.object({
    first_name: z.string().max(100).nullish(),
    last_name: z.string().max(100).nullish()
});

/**
 * RelevanceExplanation
 *
 * One signal's contribution to the relevance score.
 */
export const zRelevanceExplanation = z.object({
    icon: z.string().nullish(),
    label: z.string(),
    score: z.number(),
    signal: z.string(),
    weight: z.number()
});

/**
 * RelevanceItemSchema
 *
 * A single paper to score for relevance against workspace context.
 */
export const zRelevanceItemSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).optional(),
    citationCount: z.int().optional().default(0),
    externalId: z.string().min(1).max(200),
    source: z.string().optional().default('semantic_scholar'),
    title: z.string().min(1).max(2000),
    venue: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * RelevanceScoreRequestSchema
 *
 * Batch relevance scoring request against workspace research context.
 */
export const zRelevanceScoreRequestSchema = z.object({
    items: z.array(zRelevanceItemSchema).min(1).max(20),
    workspaceId: z.uuid()
});

/**
 * RelevanceScoreResultSchema
 *
 * Score for a single paper.
 */
export const zRelevanceScoreResultSchema = z.object({
    externalId: z.string(),
    label: z.enum([
        'high',
        'medium',
        'low',
        'unrelated'
    ]),
    reason: z.string().max(200),
    score: z.number().gte(0).lte(1)
});

/**
 * RelevanceScoreResponseSchema
 *
 * Batch relevance scoring response.
 */
export const zRelevanceScoreResponseSchema = z.object({
    latencyMs: z.number(),
    model: z.string().nullish(),
    scores: z.array(zRelevanceScoreResultSchema)
});

/**
 * ReorderPositionResponse
 *
 * Response for POST /collections/{id}/items:reorder-between.
 */
export const zReorderPositionResponse = z.object({
    position: z.number()
});

/**
 * RequeueDeadLettersRequest
 */
export const zRequeueDeadLettersRequest = z.object({
    entity_type: z.string().max(64).nullish(),
    limit: z.int().gte(1).lte(5000).optional().default(500)
});

/**
 * RequeueDeadLettersResponse
 */
export const zRequeueDeadLettersResponse = z.object({
    entity_type: z.string(),
    requeued: z.int()
});

/**
 * ResearchGapRead
 *
 * An under-represented topic area in the workspace library.
 */
export const zResearchGapRead = z.object({
    clusterId: z.uuid().nullish(),
    description: z.string(),
    documentCount: z.int().gte(0).optional().default(0),
    id: z.uuid(),
    relevanceToObjective: z.number().gte(0).lte(1),
    severity: z.string(),
    signalSource: z.string(),
    topicLabel: z.string()
});

/**
 * ResearchGapListRead
 *
 * Research gaps for a workspace, ordered by severity.
 */
export const zResearchGapListRead = z.object({
    criticalCount: z.int().gte(0).optional().default(0),
    gaps: z.array(zResearchGapRead).optional(),
    minorCount: z.int().gte(0).optional().default(0),
    notableCount: z.int().gte(0).optional().default(0),
    total: z.int().gte(0).optional().default(0)
});

/**
 * ResearchGapSummary
 *
 * Research gap counts by severity for the dashboard.
 */
export const zResearchGapSummary = z.object({
    criticalCount: z.int().optional().default(0),
    minorCount: z.int().optional().default(0),
    notableCount: z.int().optional().default(0),
    total: z.int().optional().default(0)
});

/**
 * ResearchMode
 */
export const zResearchMode = z.enum([
    'exploratory',
    'balanced',
    'conservative'
]);

/**
 * ResolvedReference
 *
 * Result of resolving a VReference against the current document state.
 */
export const zResolvedReference = z.object({
    artifact_id: z.uuid().nullish(),
    bbox_norm: zNormBBox.nullish(),
    confidence: z.number().nullish(),
    doc_id: z.uuid(),
    message: z.string().nullish(),
    page: z.int().nullish(),
    resolution: z.enum([
        'exact',
        'stable_id',
        'text_match',
        'page_fallback'
    ]),
    snippet: z.string().nullish(),
    stable_id: z.uuid().nullish(),
    stale: z.boolean().optional().default(false),
    workspace_id: z.uuid()
});

/**
 * ResourceDeleteResponse
 *
 * Typed response for DELETE endpoints that return confirmation.
 *
 * Replaces raw ``{"id": "...", "deleted": True}`` dicts to maintain
 * the OpenAPI contract (Nova auto-generates types from the spec).
 */
export const zResourceDeleteResponse = z.object({
    deleted: z.boolean().optional().default(true),
    id: z.uuid()
});

/**
 * RevokeStaffRoleRequest
 *
 * Revoke a user's Commander staff role.
 */
export const zRevokeStaffRoleRequest = z.object({
    reason: z.string().min(10).max(500)
});

/**
 * RotateSecretResponse
 *
 * Response from secret rotation.
 *
 * **SECURITY:** Store the new secret securely. The old secret
 * will be invalidated immediately.
 */
export const zRotateSecretResponse = z.object({
    new_secret: z.string()
});

/**
 * RunTimings
 *
 * Timing breakdown for a chat run.
 */
export const zRunTimings = z.object({
    contextBuildMs: z.int().nullish(),
    integrityMs: z.int().nullish(),
    modelFirstTokenMs: z.int().nullish(),
    retrievalMs: z.int().nullish(),
    totalMs: z.int().nullish()
});

/**
 * RunDebugRead
 *
 * Full run audit details (flag-gated debug endpoint).
 */
export const zRunDebugRead = z.object({
    chosenModel: z.string(),
    citationValidationStatus: z.string().nullish(),
    citations: z.array(zChatCitationRead).optional(),
    completedAt: z.iso.datetime().nullish(),
    conversationId: z.uuid(),
    costEstimateUsd: z.number().nullish(),
    createdAt: z.iso.datetime(),
    degradeReasons: z.array(z.string()).optional(),
    evidenceItems: z.array(zEvidenceItemRead).optional(),
    fallbackModelUsed: z.string().nullish(),
    groundingValidationStatus: z.string().nullish(),
    id: z.uuid(),
    inputTokens: z.int().nullish(),
    invalidCitationsCount: z.int().optional().default(0),
    outputTokens: z.int().nullish(),
    plannerCompactionTriggered: z.boolean().optional().default(false),
    plannerCompactionType: z.string().nullish(),
    posthocModified: z.boolean().optional().default(false),
    resolvedAiProfile: z.record(z.string(), z.unknown()).nullish(),
    runKind: z.string(),
    status: z.string(),
    timings: zRunTimings,
    tokensByChannel: z.record(z.string(), z.int()).nullish(),
    unsupportedClaimsCount: z.int().optional().default(0)
});

/**
 * SLODetail
 *
 * Individual SLO with current status and error budget.
 */
export const zSloDetail = z.object({
    budget_minutes_per_month: z.number(),
    burn_rates: z.record(z.string(), z.number()).optional(),
    consumed_percent: z.number(),
    current_value: z.number(),
    description: z.string(),
    error_budget_percent: z.string(),
    is_compliant: z.boolean(),
    labels: z.record(z.string(), z.string()).optional(),
    name: z.string(),
    remaining_percent: z.number(),
    slo_type: z.string(),
    target: z.number(),
    target_percent: z.string(),
    threshold_ms: z.int().nullish(),
    time_to_exhaustion_hours: z.number().nullish(),
    window_days: z.number()
});

/**
 * SLODashboard
 *
 * Aggregated SLO dashboard payload.
 */
export const zSloDashboard = z.object({
    overall_compliance: z.number(),
    slo_count: z.int(),
    slos: z.array(zSloDetail),
    timestamp: z.string()
});

/**
 * SRAmendmentCreate
 *
 * Create a protocol amendment (required after finalization).
 */
export const zSrAmendmentCreate = z.object({
    amendment_type: z.string().min(1).max(50),
    justification: z.string().min(50),
    new_value: z.record(z.string(), z.unknown()).nullish(),
    previous_value: z.record(z.string(), z.unknown()).nullish()
});

/**
 * SRAmendmentRead
 *
 * A protocol amendment record.
 */
export const zSrAmendmentRead = z.object({
    amendedBy: z.uuid(),
    amendmentType: z.string(),
    createdAt: z.iso.datetime(),
    id: z.uuid(),
    justification: z.string(),
    newValue: z.record(z.string(), z.unknown()).nullish(),
    previousValue: z.record(z.string(), z.unknown()).nullish(),
    reviewId: z.uuid(),
    updatedAt: z.iso.datetime()
});

/**
 * SRAuditEntry
 *
 * A single audit event in the SR audit trail.
 */
export const zSrAuditEntry = z.object({
    action: z.string(),
    actorId: z.uuid().nullable(),
    auditStatus: z.string().nullish(),
    changes: z.record(z.string(), z.unknown()).nullish(),
    description: z.string().nullable(),
    entityId: z.string().nullish(),
    entityType: z.string().nullish(),
    id: z.uuid(),
    resourceId: z.uuid().nullable(),
    resourceType: z.string().nullable(),
    timestamp: z.iso.datetime()
});

/**
 * SRAuditTrailPage
 *
 * Paginated audit trail response.
 */
export const zSrAuditTrailPage = z.object({
    items: z.array(zSrAuditEntry),
    page: z.int(),
    pageSize: z.int(),
    total: z.int()
});

/**
 * SRConflictStatus
 *
 * Screening conflict queue status (§10).
 */
export const zSrConflictStatus = z.enum(['open', 'resolved']);

/**
 * SRCriterionType
 *
 * Eligibility criterion direction (§8.1).
 */
export const zSrCriterionType = z.enum(['inclusion', 'exclusion']);

/**
 * SRCriterionCreate
 *
 * Add an eligibility criterion.
 */
export const zSrCriterionCreate = z.object({
    category: z.string().min(1).max(100),
    criterion_type: zSrCriterionType,
    description: z.string().max(5000).nullish(),
    label: z.string().min(1).max(500),
    sort_order: z.int().nullish()
});

/**
 * SRCriterionRead
 *
 * An eligibility criterion.
 */
export const zSrCriterionRead = z.object({
    category: z.string(),
    createdAt: z.iso.datetime(),
    criterionType: zSrCriterionType,
    description: z.string().nullish(),
    id: z.uuid(),
    isActive: z.boolean(),
    label: z.string(),
    protocolVersion: z.int(),
    reviewId: z.uuid(),
    sortOrder: z.int().nullish(),
    updatedAt: z.iso.datetime()
});

/**
 * SRCriterionUpdate
 *
 * Update an eligibility criterion.
 */
export const zSrCriterionUpdate = z.object({
    description: z.string().max(5000).nullish(),
    is_active: z.boolean().nullish(),
    label: z.string().min(1).max(500).nullish(),
    sort_order: z.int().nullish()
});

/**
 * SRDecision
 *
 * Possible screening decisions (§9.2).
 */
export const zSrDecision = z.enum([
    'include',
    'exclude',
    'uncertain'
]);

/**
 * SRDedupClusterResolve
 *
 * Resolve a dedup cluster (confirm or reject duplicates).
 */
export const zSrDedupClusterResolve = z.object({
    decision: z.string().regex(/^(confirm_duplicate|keep_separate)$/),
    master_record_id: z.uuid().nullish()
});

/**
 * SRDedupResult
 *
 * Summary of a dedup run.
 */
export const zSrDedupResult = z.object({
    autoMergedCount: z.int(),
    clustersCreated: z.int(),
    totalRecordsProcessed: z.int()
});

/**
 * SREvidenceRow
 *
 * One row in the evidence table (one included study).
 */
export const zSrEvidenceRow = z.object({
    authors: z.array(z.string()).nullish(),
    doi: z.string().nullish(),
    extractionValues: z.record(z.string(), z.union([
        z.string(),
        z.number()
    ]).nullable()).optional(),
    recordId: z.uuid(),
    studyLabel: z.string().nullish(),
    title: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * SREvidenceTable
 *
 * Full evidence table for included studies.
 */
export const zSrEvidenceTable = z.object({
    columns: z.array(z.string()),
    fieldIds: z.array(z.string()).optional(),
    limit: z.int(),
    offset: z.int(),
    reviewId: z.uuid(),
    rows: z.array(zSrEvidenceRow),
    total: z.int()
});

/**
 * SRExclusionBreakdown
 *
 * Per-reason exclusion count for PRISMA full-text box.
 */
export const zSrExclusionBreakdown = z.object({
    count: z.int(),
    label: z.string(),
    reasonId: z.uuid().nullable()
});

/**
 * SRExclusionReasonCount
 */
export const zSrExclusionReasonCount = z.object({
    count: z.int(),
    label: z.string(),
    reasonId: z.uuid()
});

/**
 * SRExclusionReasonCreate
 */
export const zSrExclusionReasonCreate = z.object({
    category: z.string().max(100).nullish(),
    label: z.string().max(500),
    sort_order: z.int().nullish()
});

/**
 * SRExclusionReasonRead
 */
export const zSrExclusionReasonRead = z.object({
    category: z.string().nullable(),
    id: z.uuid(),
    isSystem: z.boolean(),
    label: z.string(),
    reviewId: z.uuid(),
    sortOrder: z.int().nullable()
});

/**
 * SRExtractionFieldType
 *
 * Data types for configurable extraction fields (§13.1).
 */
export const zSrExtractionFieldType = z.enum([
    'text',
    'number',
    'date',
    'select',
    'multiselect',
    'boolean'
]);

/**
 * SRExtractionFieldCreate
 *
 * Create an extraction field on the review protocol.
 */
export const zSrExtractionFieldCreate = z.object({
    category: z.string().max(100).nullish(),
    field_type: zSrExtractionFieldType,
    is_required: z.boolean().optional().default(false),
    label: z.string().max(500),
    options: z.record(z.string(), z.unknown()).nullish(),
    sort_order: z.int().nullish()
});

/**
 * SRExtractionFieldRead
 *
 * Response schema for an extraction field.
 */
export const zSrExtractionFieldRead = z.object({
    category: z.string().nullable(),
    fieldType: zSrExtractionFieldType,
    id: z.uuid(),
    isRequired: z.boolean(),
    label: z.string(),
    options: z.record(z.string(), z.unknown()).nullable(),
    protocolVersion: z.int(),
    reviewId: z.uuid(),
    sortOrder: z.int().nullable()
});

/**
 * SRExtractionFieldUpdate
 *
 * Partial update of an extraction field.
 */
export const zSrExtractionFieldUpdate = z.object({
    category: z.string().max(100).nullish(),
    field_type: zSrExtractionFieldType.nullish(),
    is_required: z.boolean().nullish(),
    label: z.string().max(500).nullish(),
    options: z.record(z.string(), z.unknown()).nullish(),
    sort_order: z.int().nullish()
});

/**
 * SRExtractionRecordSummary
 *
 * Summary of extraction progress for a single record.
 */
export const zSrExtractionRecordSummary = z.object({
    completedFields: z.int(),
    hasUnconfirmedAi: z.boolean(),
    isComplete: z.boolean(),
    recordId: z.uuid(),
    totalFields: z.int()
});

/**
 * SRExtractionTemplate
 *
 * Descriptor for an available extraction template.
 */
export const zSrExtractionTemplate = z.object({
    description: z.string(),
    key: z.string()
});

/**
 * SRExtractionValueCreate
 *
 * Submit or update an extraction value for a field on a record.
 */
export const zSrExtractionValueCreate = z.object({
    is_confirmed: z.boolean().optional().default(false),
    source_artifact_id: z.uuid().nullish(),
    value_date: z.iso.date().nullish(),
    value_json: z.record(z.string(), z.unknown()).nullish(),
    value_number: z.number().nullish(),
    value_text: z.string().nullish()
});

/**
 * SRExtractionValueRead
 *
 * Response schema for an extraction value.
 */
export const zSrExtractionValueRead = z.object({
    aiSuggested: z.boolean(),
    extractorId: z.uuid(),
    fieldId: z.uuid(),
    id: z.uuid(),
    isConfirmed: z.boolean(),
    recordId: z.uuid(),
    reviewId: z.uuid(),
    sourceArtifactId: z.uuid().nullable(),
    valueDate: z.iso.date().nullable(),
    valueJson: z.record(z.string(), z.unknown()).nullable(),
    valueNumber: z.number().nullable(),
    valueText: z.string().nullable()
});

/**
 * SRFinalDecision
 *
 * Resolved final decisions for stage outcomes (§8.3).
 */
export const zSrFinalDecision = z.enum(['include', 'exclude']);

/**
 * SRConflictResolveConsensus
 *
 * Resolve a conflict via consensus — both reviewers agree on a decision.
 */
export const zSrConflictResolveConsensus = z.object({
    final_decision: zSrFinalDecision,
    primary_exclusion_reason_id: z.uuid().nullish(),
    resolution_notes: z.string().nullish(),
    version: z.int()
});

/**
 * SRConflictResolveLeadOverride
 *
 * Lead reviewer overrides the conflict with mandatory justification.
 */
export const zSrConflictResolveLeadOverride = z.object({
    final_decision: zSrFinalDecision,
    justification: z.string().min(100),
    primary_exclusion_reason_id: z.uuid().nullish(),
    version: z.int()
});

/**
 * SRConflictResolveThirdReviewer
 *
 * Submit a third-reviewer decision to resolve a conflict.
 */
export const zSrConflictResolveThirdReviewer = z.object({
    final_decision: zSrFinalDecision,
    notes: z.string().nullish(),
    primary_exclusion_reason_id: z.uuid().nullish(),
    version: z.int()
});

/**
 * SRImportResult
 *
 * Result of a record import operation.
 */
export const zSrImportResult = z.object({
    recordsCreated: z.int(),
    searchQueryId: z.uuid().nullish()
});

/**
 * SRManualMerge
 *
 * Manually merge records not flagged by dedup.
 */
export const zSrManualMerge = z.object({
    master_record_id: z.uuid(),
    record_ids: z.array(z.uuid()).min(2).max(100)
});

/**
 * SRManualRecordCreate
 *
 * Create a single record manually.
 */
export const zSrManualRecordCreate = z.object({
    abstract: z.string().max(50000).nullish(),
    authors: z.array(z.string()).nullish(),
    doi: z.string().max(255).nullish(),
    pmid: z.string().max(50).nullish(),
    raw_metadata: z.record(z.string(), z.unknown()).nullish(),
    source_name: z.string().max(255).nullish(),
    title: z.string().min(1).max(2000),
    year: z.int().gte(1800).lte(2100).nullish()
});

/**
 * SRManuscriptGenerateResponse
 *
 * Response after triggering manuscript generation.
 */
export const zSrManuscriptGenerateResponse = z.object({
    noteId: z.uuid(),
    sectionsCreated: z.int()
});

/**
 * SRManuscriptSectionType
 *
 * Manuscript section identifiers for structured generation (§9.3).
 */
export const zSrManuscriptSectionType = z.enum([
    'title_page',
    'abstract',
    'introduction',
    'methods_protocol',
    'methods_search',
    'methods_selection',
    'methods_extraction',
    'methods_rob',
    'results_prisma',
    'results_study_chars',
    'results_rob_summary',
    'results_individual',
    'discussion',
    'references',
    'appendices'
]);

/**
 * SRManuscriptSectionRead
 *
 * Response schema for a manuscript section's metadata.
 */
export const zSrManuscriptSectionRead = z.object({
    generatedAt: z.iso.datetime(),
    id: z.uuid(),
    isLocked: z.boolean(),
    isManuallyEdited: z.boolean(),
    noteBlockIds: z.record(z.string(), z.unknown()).nullable(),
    reviewId: z.uuid(),
    sectionType: zSrManuscriptSectionType,
    sourceHash: z.string()
});

/**
 * SRManuscriptRead
 *
 * Full manuscript overview including all sections.
 */
export const zSrManuscriptRead = z.object({
    generatedAt: z.iso.datetime().nullable(),
    noteId: z.uuid().nullable(),
    sections: z.array(zSrManuscriptSectionRead),
    staleCount: z.int()
});

/**
 * SRMemberRole
 *
 * Project-level roles layered on top of workspace RBAC (§5.3).
 */
export const zSrMemberRole = z.enum([
    'lead_reviewer',
    'reviewer',
    'observer',
    'data_extractor'
]);

/**
 * SRMemberAdd
 *
 * Add a member to the SR project team.
 */
export const zSrMemberAdd = z.object({
    role: zSrMemberRole,
    user_id: z.uuid()
});

/**
 * SRMemberRead
 *
 * A team member in an SR project.
 */
export const zSrMemberRead = z.object({
    assignedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    id: z.uuid(),
    reviewId: z.uuid(),
    role: zSrMemberRole,
    updatedAt: z.iso.datetime(),
    userId: z.uuid()
});

/**
 * SRPrismaCounts
 *
 * PRISMA 2020 flow counts, auto-derived from system state (§15.1).
 */
export const zSrPrismaCounts = z.object({
    assessedFullText: z.int().optional().default(0),
    duplicatesRemoved: z.int().optional().default(0),
    excludedFullText: z.int().optional().default(0),
    excludedTitleAbstract: z.int().optional().default(0),
    ftExclusionReasons: z.array(zSrExclusionBreakdown).optional(),
    notRetrieved: z.int().optional().default(0),
    recordsIdentifiedExternal: z.int().optional().default(0),
    recordsIdentifiedInternal: z.int().optional().default(0),
    recordsIdentifiedOther: z.int().optional().default(0),
    recordsScreened: z.int().optional().default(0),
    reviewId: z.uuid(),
    soughtFullText: z.int().optional().default(0),
    studiesIncluded: z.int().optional().default(0)
});

/**
 * SRQuestionFramework
 *
 * Structured question formulation framework (§6.1).
 */
export const zSrQuestionFramework = z.enum([
    'PICO',
    'SPIDER',
    'SPICE',
    'CUSTOM'
]);

/**
 * SRProjectCreate
 *
 * Create a new Systematic Review project.
 */
export const zSrProjectCreate = z.object({
    collection_id: z.uuid().nullish(),
    is_blinded_screening: z.boolean().optional().default(false),
    question_fields: z.record(z.string(), z.unknown()).nullish(),
    question_framework: zSrQuestionFramework.nullish(),
    registration_date: z.iso.date().nullish(),
    registration_number: z.string().max(255).nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    title: z.string().min(1).max(500)
});

/**
 * SRProjectUpdate
 *
 * Partial update of an SR project (pre-finalization or via amendment).
 */
export const zSrProjectUpdate = z.object({
    collection_id: z.uuid().nullish(),
    is_blinded_screening: z.boolean().nullish(),
    question_fields: z.record(z.string(), z.unknown()).nullish(),
    question_framework: zSrQuestionFramework.nullish(),
    registration_date: z.iso.date().nullish(),
    registration_number: z.string().max(255).nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    title: z.string().min(1).max(500).nullish()
});

/**
 * SRRecordSource
 *
 * Origin of a record imported into a review (§12).
 */
export const zSrRecordSource = z.enum([
    'internal_search',
    'pubmed_import',
    'ris_import',
    'bibtex_import',
    'csv_import',
    'manual'
]);

/**
 * SRDedupClusterRecord
 *
 * Record within a dedup cluster (subset of fields for comparison).
 */
export const zSrDedupClusterRecord = z.object({
    doi: z.string().nullish(),
    id: z.uuid(),
    normalizedAuthors: z.array(z.string()).nullish(),
    normalizedTitle: z.string().nullish(),
    pmid: z.string().nullish(),
    sourceName: z.string().nullish(),
    sourceType: zSrRecordSource,
    year: z.int().nullish()
});

/**
 * SRDedupClusterRead
 *
 * A dedup cluster with its member records.
 */
export const zSrDedupClusterRead = z.object({
    clusterId: z.uuid(),
    matchReason: z.string(),
    proposedMasterId: z.uuid().nullish(),
    records: z.array(zSrDedupClusterRecord)
});

/**
 * PaginatedResponse[SRDedupClusterRead]
 */
export const zPaginatedResponseSrDedupClusterRead = z.object({
    items: z.array(zSrDedupClusterRead),
    pagination: zPaginationMeta
});

/**
 * SRRecordStatus
 *
 * Record lifecycle within deduplication (§11).
 */
export const zSrRecordStatus = z.enum([
    'active',
    'merged',
    'excluded_dedup'
]);

/**
 * SRRecordListItem
 *
 * Summary item for record listing.
 */
export const zSrRecordListItem = z.object({
    abstract: z.string().nullish(),
    createdAt: z.iso.datetime(),
    doi: z.string().nullish(),
    id: z.uuid(),
    isDuplicate: z.boolean().nullish(),
    normalizedAuthors: z.array(z.string()).nullish(),
    normalizedTitle: z.string().nullish(),
    pmid: z.string().nullish(),
    reviewId: z.uuid(),
    sourceName: z.string().nullish(),
    sourceType: zSrRecordSource,
    status: zSrRecordStatus,
    updatedAt: z.iso.datetime(),
    year: z.int().nullish()
});

/**
 * PaginatedResponse[SRRecordListItem]
 */
export const zPaginatedResponseSrRecordListItem = z.object({
    items: z.array(zSrRecordListItem),
    pagination: zPaginationMeta
});

/**
 * SRRecordRead
 *
 * Full detail response for an SR record.
 */
export const zSrRecordRead = z.object({
    createdAt: z.iso.datetime(),
    dedupClusterId: z.uuid().nullish(),
    dedupMasterRecordId: z.uuid().nullish(),
    documentId: z.uuid().nullish(),
    doi: z.string().nullish(),
    id: z.uuid(),
    isDuplicate: z.boolean().nullish(),
    normalizedAuthors: z.array(z.string()).nullish(),
    normalizedTitle: z.string().nullish(),
    pmid: z.string().nullish(),
    rawMetadata: z.record(z.string(), z.unknown()),
    reviewId: z.uuid(),
    sourceName: z.string().nullish(),
    sourceQueryId: z.uuid().nullish(),
    sourceType: zSrRecordSource,
    status: zSrRecordStatus,
    studyId: z.uuid().nullish(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid(),
    year: z.int().nullish()
});

/**
 * SRRegenerateRequest
 *
 * Request to regenerate specific sections.
 */
export const zSrRegenerateRequest = z.object({
    sections: z.array(zSrManuscriptSectionType)
});

/**
 * SRRegenerateResponse
 *
 * Response after regenerating sections.
 */
export const zSrRegenerateResponse = z.object({
    sectionsRegenerated: z.array(zSrManuscriptSectionType),
    sectionsSkippedLocked: z.array(zSrManuscriptSectionType).optional(),
    sectionsSkippedNonRegenerable: z.array(zSrManuscriptSectionType).optional()
});

/**
 * SRResolutionType
 *
 * How a screening conflict was resolved (§10.3).
 */
export const zSrResolutionType = z.enum([
    'consensus',
    'agreement',
    'third_reviewer',
    'lead_override'
]);

/**
 * SRReviewerReasonUsage
 *
 * Per-reason count for a single reviewer.
 */
export const zSrReviewerReasonUsage = z.object({
    count: z.int(),
    reasonId: z.uuid(),
    reasonLabel: z.string()
});

/**
 * SRReviewerPattern
 *
 * Exclusion reason usage pattern for a single reviewer.
 */
export const zSrReviewerPattern = z.object({
    reasons: z.array(zSrReviewerReasonUsage),
    reviewerId: z.uuid(),
    totalExclusions: z.int()
});

/**
 * SRRoBAssessmentStatus
 *
 * RoB assessment completion status (§14).
 */
export const zSrRoBassessmentStatus = z.enum(['in_progress', 'complete']);

/**
 * SRRoBAssessmentUpdate
 *
 * Partial update to an in-progress RoB assessment.
 */
export const zSrRoBassessmentUpdate = z.object({
    confounding_factors_pre_specified: z.record(z.string(), z.unknown()).nullish(),
    domain_overrides: z.record(z.string(), z.unknown()).nullish(),
    domain_responses: z.record(z.string(), z.unknown()).nullish(),
    information_sources: z.array(z.string()).nullish(),
    overall_direction_of_bias: z.string().max(100).nullish(),
    overall_override: z.string().max(50).nullish(),
    overall_override_justification: z.string().nullish(),
    result_specification: z.record(z.string(), z.unknown()).nullish(),
    target_trial: z.record(z.string(), z.unknown()).nullish()
});

/**
 * SRRoBJudgment
 *
 * Risk-of-bias domain/overall judgments (§14.2).
 */
export const zSrRoBJudgment = z.enum([
    'low',
    'low_uncontrolled_confounding',
    'moderate',
    'serious',
    'critical'
]);

/**
 * SRRoBDomainJudgment
 *
 * Computed judgment for a single domain.
 */
export const zSrRoBDomainJudgment = z.object({
    domain: z.string(),
    isOverridden: z.boolean().optional().default(false),
    overrideJudgment: zSrRoBJudgment.nullish(),
    overrideJustification: z.string().nullish(),
    proposedJudgment: zSrRoBJudgment
});

/**
 * SRRoBJudgmentSummary
 *
 * Full computed judgment summary for an assessment.
 */
export const zSrRoBJudgmentSummary = z.object({
    assessmentId: z.uuid(),
    domainJudgments: z.array(zSrRoBDomainJudgment),
    isOverallOverridden: z.boolean().optional().default(false),
    overallFinal: zSrRoBJudgment.nullable(),
    overallProposed: zSrRoBJudgment.nullable()
});

/**
 * SRRoBTool
 *
 * Risk-of-bias assessment tools supported (§14.1).
 */
export const zSrRoBTool = z.enum([
    'ROBINS-I-V2',
    'RoB2',
    'CASP',
    'JBI',
    'custom'
]);

/**
 * SRRoBAssessmentCreate
 *
 * Create a new RoB assessment for a record (per-result).
 */
export const zSrRoBassessmentCreate = z.object({
    record_id: z.uuid(),
    result_specification: z.record(z.string(), z.unknown()).nullish(),
    tool: zSrRoBTool.optional().default('ROBINS-I-V2'),
    tool_version: z.string().max(50).optional().default('v2025-11')
});

/**
 * SRRoBAssessmentRead
 *
 * Response schema for a RoB assessment.
 */
export const zSrRoBassessmentRead = z.object({
    assessorId: z.uuid(),
    completedAt: z.iso.datetime().nullable(),
    confoundingFactorsPreSpecified: z.record(z.string(), z.unknown()).nullable(),
    domainJudgments: z.record(z.string(), z.unknown()).nullable(),
    domainOverrides: z.record(z.string(), z.unknown()).nullable(),
    domainResponses: z.record(z.string(), z.unknown()).nullable(),
    id: z.uuid(),
    informationSources: z.array(z.string()).nullable(),
    overallDirectionOfBias: z.string().nullable(),
    overallJudgment: zSrRoBJudgment.nullable(),
    overallOverride: z.string().nullable(),
    overallOverrideJustification: z.string().nullable(),
    recordId: z.uuid(),
    resultSpecification: z.record(z.string(), z.unknown()).nullable(),
    reviewId: z.uuid(),
    status: zSrRoBassessmentStatus,
    targetTrial: z.record(z.string(), z.unknown()).nullable(),
    tool: zSrRoBTool,
    toolVersion: z.string()
});

/**
 * SRRoBTrafficLightEntry
 *
 * Traffic light entry for a single record's RoB status.
 */
export const zSrRoBTrafficLightEntry = z.object({
    assessmentId: z.uuid().nullish(),
    domainJudgments: z.record(z.string(), z.unknown()).nullish(),
    overallJudgment: zSrRoBJudgment.nullish(),
    recordId: z.uuid(),
    status: zSrRoBassessmentStatus.nullish()
});

/**
 * SRScreeningStage
 *
 * Stages at which screening decisions are recorded (§9).
 */
export const zSrScreeningStage = z.enum(['title_abstract', 'full_text']);

/**
 * SRDecisionCreate
 *
 * Submit a screening decision for a record at a stage.
 */
export const zSrDecisionCreate = z.object({
    decision: zSrDecision,
    exclusion_reason_ids: z.array(z.uuid()).optional(),
    notes: z.string().nullish(),
    stage: zSrScreeningStage
});

/**
 * SRDecisionRead
 */
export const zSrDecisionRead = z.object({
    aiConfidence: z.number().nullable(),
    aiSuggestion: z.string().nullable(),
    decision: zSrDecision,
    exclusionReasonIds: z.array(z.uuid()).optional(),
    id: z.uuid(),
    notes: z.string().nullable(),
    recordId: z.uuid(),
    reviewerId: z.uuid(),
    stage: zSrScreeningStage,
    submittedAt: z.iso.datetime()
});

/**
 * SRConflictRead
 */
export const zSrConflictRead = z.object({
    decisions: z.array(zSrDecisionRead).optional(),
    id: z.uuid(),
    recordId: z.uuid(),
    resolutionNotes: z.string().nullable(),
    resolutionType: zSrResolutionType.nullable(),
    stage: zSrScreeningStage,
    status: zSrConflictStatus,
    thirdReviewerId: z.uuid().nullable(),
    version: z.int()
});

/**
 * SRExclusionAnalysis
 *
 * Exclusion reason distribution for a stage.
 */
export const zSrExclusionAnalysis = z.object({
    byReason: z.array(zSrExclusionReasonCount),
    stage: zSrScreeningStage,
    totalExcluded: z.int()
});

/**
 * SRFunnelActivityItem
 *
 * A single record movement in the funnel.
 */
export const zSrFunnelActivityItem = z.object({
    finalDecision: zSrFinalDecision,
    recordId: z.uuid(),
    recordTitle: z.string().nullish(),
    resolutionType: zSrResolutionType.nullish(),
    resolvedAt: z.iso.datetime(),
    resolvedBy: z.uuid().nullish(),
    stage: zSrScreeningStage
});

/**
 * SRFunnelActivityPage
 *
 * Paginated funnel activity feed.
 */
export const zSrFunnelActivityPage = z.object({
    items: z.array(zSrFunnelActivityItem),
    limit: z.int(),
    offset: z.int(),
    total: z.int()
});

/**
 * SRReviewerPatterns
 *
 * Per-reviewer exclusion reason usage — lead-only.
 */
export const zSrReviewerPatterns = z.object({
    reviewers: z.array(zSrReviewerPattern),
    stage: zSrScreeningStage
});

/**
 * SRScreeningStats
 *
 * Screening progress and quality metrics for a review stage.
 */
export const zSrScreeningStats = z.object({
    agreementRate: z.number().nullish(),
    cohensKappa: z.number().nullish(),
    conflictsOpen: z.int(),
    conflictsResolved: z.int(),
    excluded: z.int(),
    included: z.int(),
    pending: z.int(),
    screened: z.int(),
    stage: zSrScreeningStage,
    totalRecords: z.int()
});

/**
 * SRSectionLockUpdate
 *
 * Toggle lock state on a manuscript section.
 */
export const zSrSectionLockUpdate = z.object({
    is_locked: z.boolean()
});

/**
 * SRStageOutcomeRead
 */
export const zSrStageOutcomeRead = z.object({
    finalDecision: zSrFinalDecision,
    id: z.uuid(),
    primaryExclusionReasonId: z.uuid().nullable(),
    recordId: z.uuid(),
    resolutionType: zSrResolutionType.nullable(),
    resolvedAt: z.iso.datetime(),
    resolvedBy: z.uuid().nullable(),
    stage: zSrScreeningStage
});

/**
 * SRStalenessChange
 *
 * A single field-level change detected for staleness.
 */
export const zSrStalenessChange = z.object({
    field: z.string(),
    newHash: z.string(),
    oldHash: z.string()
});

/**
 * SRSectionStaleness
 *
 * Staleness report for a single section.
 */
export const zSrSectionStaleness = z.object({
    changes: z.array(zSrStalenessChange).optional(),
    isStale: z.boolean(),
    sectionType: zSrManuscriptSectionType
});

/**
 * SRStatus
 *
 * Lifecycle states for a Systematic Review project (§5.1).
 */
export const zSrStatus = z.enum([
    'DRAFT',
    'PROTOCOL_FINALIZED',
    'SEARCHING',
    'DEDUP_REVIEW',
    'TITLE_ABSTRACT_SCREENING',
    'FULL_TEXT_SCREENING',
    'DATA_EXTRACTION',
    'RISK_OF_BIAS',
    'SYNTHESIS',
    'COMPLETED',
    'ARCHIVED'
]);

/**
 * SRAdvanceStatus
 *
 * Request to advance the review to a specific status.
 */
export const zSrAdvanceStatus = z.object({
    target_status: zSrStatus
});

/**
 * SRProjectListItem
 *
 * Summary item for SR project listing.
 */
export const zSrProjectListItem = z.object({
    createdAt: z.iso.datetime(),
    createdBy: z.uuid(),
    id: z.uuid(),
    isBlindedScreening: z.boolean(),
    memberCount: z.int().optional().default(0),
    protocolVersion: z.int(),
    questionFramework: zSrQuestionFramework.nullish(),
    recordCount: z.int().optional().default(0),
    status: zSrStatus,
    title: z.string(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * PaginatedResponse[SRProjectListItem]
 */
export const zPaginatedResponseSrProjectListItem = z.object({
    items: z.array(zSrProjectListItem),
    pagination: zPaginationMeta
});

/**
 * SRProjectRead
 *
 * Full detail response for an SR project.
 */
export const zSrProjectRead = z.object({
    collectionId: z.uuid().nullish(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid(),
    id: z.uuid(),
    isBlindedScreening: z.boolean(),
    memberCount: z.int().optional().default(0),
    protocolFinalizedAt: z.iso.datetime().nullish(),
    protocolVersion: z.int(),
    questionFields: z.record(z.string(), z.unknown()).nullish(),
    questionFramework: zSrQuestionFramework.nullish(),
    recordCount: z.int().optional().default(0),
    registrationDate: z.iso.date().nullish(),
    registrationNumber: z.string().nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    status: zSrStatus,
    title: z.string(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * SaveAndImportRequestSchema
 *
 * Save a discovery result AND import it through the full pipeline in one call.
 */
export const zSaveAndImportRequestSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    citationCount: z.int().optional().default(0),
    collectionId: z.uuid().nullish(),
    doi: z.string().nullish(),
    externalId: z.string().nullish(),
    feedItemId: z.uuid().nullish(),
    itemType: z.string().optional().default('paper'),
    processingMode: z.string().regex(/^(full|fast|metadata_only)$/).optional().default('full'),
    source: z.string().optional().default('semantic_scholar'),
    title: z.string().min(1).max(2000),
    url: z.url().min(1).max(2083).nullish(),
    venue: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * SaveFromShareRequest
 *
 * Request to save a shared document into the caller's workspace library.
 *
 * The PDF is always copied. processing_mode controls pipeline depth:
 * - "metadata": fast pipeline (text extraction, metadata, thumbnail, search index)
 * - "full": complete intelligence pipeline (all stages including AI extraction)
 */
export const zSaveFromShareRequest = z.object({
    processing_mode: z.string().regex(/^(metadata|full)$/).optional().default('metadata'),
    target_workspace_id: z.uuid().nullish()
});

/**
 * SaveFromShareResponse
 *
 * Result of saving a shared document to the caller's library.
 */
export const zSaveFromShareResponse = z.object({
    documentId: z.string().nullish(),
    hasPdf: z.boolean().optional().default(false),
    processingMode: z.string(),
    title: z.string().nullish()
});

/**
 * SaveItemRequestSchema
 */
export const zSaveItemRequestSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    citationCount: z.int().optional().default(0),
    doi: z.string().nullish(),
    externalId: z.string().nullish(),
    itemType: z.string().optional().default('paper'),
    source: z.string().optional().default('semantic_scholar'),
    title: z.string().min(1).max(2000),
    url: z.url().min(1).max(2083).nullish(),
    venue: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * SaveItemResponseSchema
 */
export const zSaveItemResponseSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    citationCount: z.int().nullish(),
    createdAt: z.string().nullish(),
    doi: z.string().nullish(),
    externalId: z.string().nullish(),
    id: z.string().nullish(),
    reason: z.string().nullish(),
    saved: z.boolean(),
    source: z.string().nullish(),
    status: z.string().nullish(),
    title: z.string().nullish(),
    type: z.string().nullish(),
    url: z.string().nullish(),
    venue: z.string().nullish(),
    workspaceId: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * SavedIdsResponseSchema
 */
export const zSavedIdsResponseSchema = z.object({
    savedIds: z.array(z.string())
});

/**
 * SavedInboxItemStatus
 *
 * Lifecycle status of an item in the saved inbox.
 *
 * Only items in ``SAVED`` or ``READING`` are surfaced — once an item has
 * been ``REVIEWED`` / ``IMPORTED`` / ``ARCHIVED`` it is no longer in the
 * triage queue. ``SUPERSEDED`` items are filtered out at the query layer.
 */
export const zSavedInboxItemStatus = z.enum(['saved', 'reading']);

/**
 * SavedInboxSource
 *
 * Provider that originally surfaced the saved item.
 *
 * Mirrors :class:`app.models.discovery.DiscoverySource` — the wire enum
 * is held separate from the DB enum so additions on the model side don't
 * silently leak through without a schema update.
 */
export const zSavedInboxSource = z.enum([
    'semantic_scholar',
    'openalex',
    'exa',
    'crossref',
    'share'
]);

/**
 * SavedInboxItem
 *
 * One row in the Discovery Saved Inbox surface.
 */
export const zSavedInboxItem = z.object({
    itemId: z.uuid(),
    savedAt: z.iso.datetime(),
    source: zSavedInboxSource,
    status: zSavedInboxItemStatus,
    supersededByItemId: z.uuid().nullish(),
    title: z.string()
});

/**
 * DiscoverySavedInboxSurface
 *
 * Discovery Saved Inbox (Slice 3, spec §5.8).
 *
 * Saved/Reading items the user has flagged for triage but has not yet
 * imported. Trigger: at least one active row with
 * ``status IN ('saved', 'reading')``. Ranking is ``created_at DESC``
 * (most recently saved first), capped at 5 rows.
 */
export const zDiscoverySavedInboxSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zSavedInboxItem).min(1),
    kind: z.literal('discovery_saved_inbox').optional().default('discovery_saved_inbox')
});

/**
 * ScopeMode
 *
 * Chat scope modes.
 */
export const zScopeMode = z.enum([
    'workspace',
    'collection',
    'document',
    'custom'
]);

/**
 * ChatScopeRequest
 *
 * Scope for a chat message.
 */
export const zChatScopeRequest = z.object({
    mode: zScopeMode.optional().default('workspace'),
    ref_ids: z.record(z.string(), z.unknown()).nullish()
});

/**
 * ChatSendRequest
 *
 * Request body for POST /v1/chat/send.
 */
export const zChatSendRequest = z.object({
    ai_profile_override: zAiProfileOverrideRequest.nullish(),
    conversation_id: z.uuid().nullish(),
    enable_deep_research: z.boolean().optional().default(false),
    message: z.string().min(1).max(32000),
    pinned_evidence: z.array(zPinnedEvidenceRequest).nullish(),
    preferred_model: z.string().nullish(),
    scope: zChatScopeRequest.nullish()
});

/**
 * ScoringWeightRead
 *
 * Response schema for scoring weight configuration.
 */
export const zScoringWeightRead = z.object({
    createdAt: z.iso.datetime(),
    description: z.string().nullable(),
    effectiveAt: z.iso.datetime().nullable(),
    experimentId: z.string().nullable(),
    id: z.uuid(),
    name: z.string(),
    status: z.string(),
    updatedAt: z.iso.datetime(),
    wBm25Score: z.number(),
    wCitationOverlap: z.number(),
    wJournalTier: z.number(),
    wMethodologyMatch: z.number(),
    wRecency: z.number(),
    wTrending: z.number(),
    wVectorSimilarity: z.number(),
    workspaceNegPenalty: z.number(),
    workspacePosBoost: z.number()
});

/**
 * PaginatedResponse[ScoringWeightRead]
 */
export const zPaginatedResponseScoringWeightRead = z.object({
    items: z.array(zScoringWeightRead),
    pagination: zPaginationMeta
});

/**
 * SearchAnswerFilters
 *
 * Optional filters for answer-first search.
 */
export const zSearchAnswerFilters = z.object({
    collection_ids: z.array(z.uuid()).max(20).nullish(),
    year_max: z.int().gte(1900).lte(2100).nullish(),
    year_min: z.int().gte(1900).lte(2100).nullish()
});

/**
 * SearchAnswerScope
 *
 * Scope for answer-first search.
 */
export const zSearchAnswerScope = z.enum(['library', 'smart']);

/**
 * SearchAnswerRequest
 *
 * Request body for POST /search/answer.
 *
 * Query length is 5–500 characters. Scope controls whether the search
 * targets the user's library or the wider research corpus.
 */
export const zSearchAnswerRequest = z.object({
    filters: zSearchAnswerFilters.nullish(),
    max_sources: z.int().gte(5).lte(30).optional().default(20),
    query: z.string().min(5).max(500),
    scope: zSearchAnswerScope.optional().default('smart')
});

/**
 * SearchFiltersSchema
 *
 * Query-level search filters.
 */
export const zSearchFiltersSchema = z.object({
    journal_tier: z.array(z.string()).nullish(),
    methodology: z.array(z.string()).nullish(),
    study_type: z.array(z.string()).nullish(),
    year_max: z.int().gte(1900).lte(2100).nullish(),
    year_min: z.int().gte(1900).lte(2100).nullish()
});

/**
 * SearchMode
 */
export const zSearchMode = z.enum([
    'auto',
    'keyword',
    'question'
]);

/**
 * SearchRequestSchema
 *
 * POST body for /search.
 */
export const zSearchRequestSchema = z.object({
    filters: zSearchFiltersSchema.optional(),
    mode: zSearchMode.optional().default('auto'),
    page: z.int().gte(1).lte(1000).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(20),
    query: z.string().min(1).max(1000)
});

/**
 * SearchResultRead
 *
 * A single search result with explainability annotation.
 */
export const zSearchResultRead = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    documentId: z.uuid(),
    doi: z.string().nullish(),
    explanations: z.array(zRelevanceExplanation).optional(),
    journal: z.string().nullish(),
    publicationYear: z.int().nullish(),
    relevanceScore: z.number(),
    title: z.string(),
    workspaceBoost: z.number().optional().default(0),
    workspacePenalty: z.number().optional().default(0)
});

/**
 * SearchResponseSchema
 *
 * Response envelope for search results.
 */
export const zSearchResponseSchema = z.object({
    classifiedIntent: z.string(),
    filtersApplied: z.record(z.string(), z.unknown()).optional(),
    latencyMs: z.int(),
    query: z.string(),
    results: z.array(zSearchResultRead),
    totalCount: z.int()
});

/**
 * SeatUpdateRequest
 *
 * POST /v1/billing/seats/update request.
 */
export const zSeatUpdateRequest = z.object({
    action: z.enum(['add', 'remove']),
    user_id: z.uuid()
});

/**
 * SeatUpdateResponse
 *
 * POST /v1/billing/seats/update response.
 */
export const zSeatUpdateResponse = z.object({
    estimated_additional_cost: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish(),
    included_seats: z.int(),
    message: z.string(),
    over_limit: z.boolean(),
    seat_count: z.int()
});

/**
 * SeatsUsage
 *
 * Workspace seat allocation and status.
 */
export const zSeatsUsage = z.object({
    additional_seats_billed: z.int().optional().default(0),
    included_seats: z.int(),
    max_seats_allowed: z.int().nullish(),
    over_limit: z.boolean().optional().default(false),
    seat_count: z.int()
});

/**
 * SendRecipient
 *
 * A recipient for the send-to-people flow.
 */
export const zSendRecipient = z.object({
    email: z.string().max(320),
    name: z.string().max(255).nullish()
});

/**
 * SendRecipientResult
 *
 * Result for a single recipient in the send flow.
 */
export const zSendRecipientResult = z.object({
    createdAt: z.string(),
    email: z.string(),
    emailSentAt: z.string().nullish(),
    id: z.string(),
    isVirzaUser: z.boolean().optional().default(false),
    notificationSentAt: z.string().nullish(),
    recipientName: z.string().nullish()
});

/**
 * SendShareRequest
 *
 * Send a share to specific people (adds them as collaborators + notifies).
 */
export const zSendShareRequest = z.object({
    message: z.string().max(2000).nullish(),
    recipients: z.array(zSendRecipient).min(1).max(50),
    share_url: z.string().max(2048)
});

/**
 * SendShareResponse
 *
 * Response from the send-to-people endpoint.
 */
export const zSendShareResponse = z.object({
    recipients: z.array(zSendRecipientResult),
    sentCount: z.int()
});

/**
 * ServiceInfo
 *
 * Service metadata.
 */
export const zServiceInfo = z.object({
    environment: z.string(),
    name: z.string(),
    timestamp: z.string(),
    version: z.string()
});

/**
 * ServiceStatus
 */
export const zServiceStatus = z.enum([
    'healthy',
    'degraded',
    'critical',
    'unknown'
]);

/**
 * DependencyCheck
 *
 * Individual dependency check result.
 */
export const zDependencyCheck = z.object({
    latency_ms: z.number().nullish(),
    name: z.string(),
    status: zServiceStatus
});

/**
 * ServiceHealth
 *
 * Health status of a single service.
 */
export const zServiceHealth = z.object({
    checks: z.array(zDependencyCheck).optional(),
    environment: z.string().nullish(),
    error_rate_percent: z.number().nullish(),
    latency_ms: z.number().nullish(),
    name: z.string(),
    status: zServiceStatus,
    version: z.string().nullish()
});

/**
 * ServicesDashboard
 *
 * Cross-service health dashboard payload.
 */
export const zServicesDashboard = z.object({
    services: z.array(zServiceHealth),
    timestamp: z.string()
});

/**
 * SessionCategory
 *
 * Session research category.
 */
export const zSessionCategory = z.enum([
    'literature-review',
    'market-research',
    'technical-analysis',
    'policy-review',
    'competitive-analysis',
    'data-exploration',
    'creative-writing',
    'thesis',
    'general'
]);

/**
 * SessionCollaborator
 *
 * A collaborator in a session.
 *
 * avatar_url is resolved at response time from the user's avatar_asset_id.
 */
export const zSessionCollaborator = z.object({
    avatarUrl: z.string().nullish(),
    email: z.string(),
    isOnline: z.boolean().optional().default(false),
    joinedAt: z.iso.datetime(),
    lastActiveAt: z.iso.datetime(),
    name: z.string(),
    role: zCollaboratorRole,
    userId: z.uuid()
});

/**
 * SessionGoal
 *
 * A goal within a research session.
 */
export const zSessionGoal = z.object({
    completedAt: z.iso.datetime().nullish(),
    id: z.uuid(),
    isCompleted: z.boolean().optional().default(false),
    order: z.int().optional().default(0),
    text: z.string()
});

/**
 * SessionGoalCreate
 *
 * Add a goal to a session.
 */
export const zSessionGoalCreate = z.object({
    order: z.int().optional().default(0),
    text: z.string().min(1).max(1000)
});

/**
 * SessionGoalUpdate
 *
 * Update a session goal.
 */
export const zSessionGoalUpdate = z.object({
    is_completed: z.boolean().nullish(),
    order: z.int().nullish(),
    text: z.string().nullish()
});

/**
 * SessionInsight
 *
 * A research insight captured during a session.
 */
export const zSessionInsight = z.object({
    confidence: zConfidenceLevel,
    content: z.string(),
    conversationId: z.string().nullish(),
    createdAt: z.iso.datetime(),
    id: z.uuid(),
    isPinned: z.boolean().optional().default(false),
    sourceIds: z.array(z.string()).optional(),
    type: zInsightType
});

/**
 * SessionInsightCreate
 *
 * Record an insight during a session.
 */
export const zSessionInsightCreate = z.object({
    confidence: zConfidenceLevel.optional().default('medium'),
    content: z.string().min(1).max(5000),
    conversation_id: z.string().nullish(),
    source_ids: z.array(z.string()).optional(),
    type: zInsightType.optional().default('finding')
});

/**
 * SessionPatternAnalysis
 *
 * Analysis of user session patterns
 */
export const zSessionPatternAnalysis = z.object({
    average_session_duration_minutes: z.number().nullable(),
    first_login: z.iso.datetime().nullable(),
    last_login: z.iso.datetime().nullable(),
    most_common_location: z.string().nullable(),
    most_used_device: z.string().nullable(),
    suspicious_logins: z.int(),
    total_logins: z.int(),
    unique_devices: z.int(),
    unique_ips: z.int(),
    user_id: z.uuid()
});

/**
 * SessionPhase
 *
 * Research methodology phase.
 */
export const zSessionPhase = z.enum([
    'framing',
    'gathering',
    'exploring',
    'synthesizing',
    'presenting'
]);

/**
 * SessionPriority
 *
 * Session priority level.
 */
export const zSessionPriority = z.enum([
    'critical',
    'high',
    'medium',
    'low'
]);

/**
 * SessionCreate
 *
 * Create a research session.
 *
 * Maps to Nova CreateSessionRequest from contracts/sessions.ts:
 * { title, description, objective, category, priority?,
 * tags?, color?, templateId? }
 *
 * workspace_id is NOT in the body — it is resolved from the
 * X-Workspace-ID request header by the route dependency.
 */
export const zSessionCreate = z.object({
    category: zSessionCategory.optional().default('general'),
    color: z.string().max(20).nullish(),
    description: z.string().max(2000),
    objective: z.string().max(2000),
    priority: zSessionPriority.optional().default('medium'),
    tags: z.array(z.string()).optional(),
    template_id: z.uuid().nullish(),
    title: z.string().min(1).max(500)
});

/**
 * SessionRevokeAllResponse
 *
 * Response for revoking all sessions
 */
export const zSessionRevokeAllResponse = z.object({
    message: z.string(),
    revoked_count: z.int()
});

/**
 * SessionRevokeRequest
 *
 * Request to revoke a session
 */
export const zSessionRevokeRequest = z.object({
    reason: z.string().max(500).nullish(),
    session_id: z.uuid()
});

/**
 * SessionRevokeResponse
 *
 * Response for session revocation
 */
export const zSessionRevokeResponse = z.object({
    message: z.string(),
    session_id: z.string()
});

/**
 * SessionRevokeResult
 */
export const zSessionRevokeResult = z.object({
    revoked_count: z.int().optional().default(1),
    session_id: z.string(),
    status: z.string()
});

/**
 * SessionStatus
 *
 * Session lifecycle status.
 */
export const zSessionStatus = z.enum([
    'draft',
    'active',
    'paused',
    'reviewing',
    'completed',
    'archived'
]);

/**
 * SessionRead
 *
 * Full session detail.
 *
 * Extends SessionSummaryRead with nested objects.
 * Maps to Nova SessionDTO from contracts/sessions.ts.
 */
export const zSessionRead = z.object({
    actualHoursLogged: z.number().optional().default(0),
    category: zSessionCategory,
    citationIds: z.array(z.uuid()).optional(),
    citationsCount: z.int().optional().default(0),
    collaborators: z.array(zSessionCollaborator).optional(),
    collaboratorsCount: z.int().optional().default(0),
    color: z.string().nullish(),
    completedAt: z.iso.datetime().nullish(),
    conversationIds: z.array(z.string()).optional(),
    conversationsCount: z.int().optional().default(0),
    createdAt: z.iso.datetime(),
    deadline: z.iso.datetime().nullish(),
    description: z.string(),
    documentIds: z.array(z.uuid()).optional(),
    documentsCount: z.int().optional().default(0),
    estimatedHours: z.number().nullish(),
    goals: z.array(zSessionGoal).optional(),
    goalsCompleted: z.int().optional().default(0),
    goalsTotal: z.int().optional().default(0),
    id: z.uuid(),
    insights: z.array(zSessionInsight).optional(),
    insightsCount: z.int().optional().default(0),
    isShared: z.boolean().optional().default(false),
    lastActiveAt: z.iso.datetime().nullish(),
    noteIds: z.array(z.uuid()).optional(),
    notesCount: z.int().optional().default(0),
    objective: z.string(),
    ownerId: z.uuid(),
    parentSessionId: z.uuid().nullish(),
    phase: zSessionPhase,
    priority: zSessionPriority,
    startedAt: z.iso.datetime().nullish(),
    status: zSessionStatus,
    tags: z.array(z.string()).optional(),
    templateId: z.uuid().nullish(),
    title: z.string(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * SessionSummaryRead
 *
 * Session summary for list views.
 *
 * Maps to Nova SessionSummaryDTO from contracts/sessions.ts:
 * { id, title, description, objective, status, phase, priority,
 * category, color?, tags, documentsCount, notesCount, ...counts,
 * goalsTotal, goalsCompleted, isShared, lastActiveAt, deadline? }
 */
export const zSessionSummaryRead = z.object({
    category: zSessionCategory,
    citationsCount: z.int().optional().default(0),
    collaboratorsCount: z.int().optional().default(0),
    color: z.string().nullish(),
    conversationsCount: z.int().optional().default(0),
    createdAt: z.iso.datetime(),
    deadline: z.iso.datetime().nullish(),
    description: z.string(),
    documentsCount: z.int().optional().default(0),
    goalsCompleted: z.int().optional().default(0),
    goalsTotal: z.int().optional().default(0),
    id: z.uuid(),
    insightsCount: z.int().optional().default(0),
    isShared: z.boolean().optional().default(false),
    lastActiveAt: z.iso.datetime().nullish(),
    notesCount: z.int().optional().default(0),
    objective: z.string(),
    phase: zSessionPhase,
    priority: zSessionPriority,
    status: zSessionStatus,
    tags: z.array(z.string()).optional(),
    title: z.string(),
    updatedAt: z.iso.datetime()
});

/**
 * PaginatedResponse[SessionSummaryRead]
 */
export const zPaginatedResponseSessionSummaryRead = z.object({
    items: z.array(zSessionSummaryRead),
    pagination: zPaginationMeta
});

/**
 * SessionUpdate
 *
 * Update a research session.
 *
 * Maps to Nova UpdateSessionRequest:
 * { title?, description?, objective?, status?, phase?,
 * priority?, category?, tags?, color? }
 */
export const zSessionUpdate = z.object({
    category: zSessionCategory.nullish(),
    color: z.string().nullish(),
    description: z.string().max(2000).nullish(),
    objective: z.string().max(2000).nullish(),
    phase: zSessionPhase.nullish(),
    priority: zSessionPriority.nullish(),
    status: zSessionStatus.nullish(),
    tags: z.array(z.string()).nullish(),
    title: z.string().min(1).max(500).nullish()
});

/**
 * ShareCommentResponse
 *
 * A single comment on a shared resource.
 */
export const zShareCommentResponse = z.object({
    authorId: z.string(),
    authorName: z.string(),
    content: z.string(),
    createdAt: z.iso.datetime(),
    id: z.string(),
    pageNumber: z.int().nullish(),
    parentId: z.string().nullish(),
    replyCount: z.int().optional().default(0),
    sharingId: z.string(),
    updatedAt: z.iso.datetime()
});

/**
 * ShareCommentsResponse
 *
 * Paginated list of comments on a shared resource.
 */
export const zShareCommentsResponse = z.object({
    data: z.array(zShareCommentResponse),
    hasMore: z.boolean().optional().default(false),
    total: z.int()
});

/**
 * SharedContentData
 *
 * Actual resource content delivered to authorized share viewers.
 *
 * Contains the rendered content for the share page viewer:
 * - PDF documents: presigned URL + page count
 * - Notes: TipTap JSON content
 * - Sessions: message transcript
 * - Citations: bibliographic metadata
 */
export const zSharedContentData = z.object({
    citationAbstract: z.string().nullish(),
    citationAuthors: z.array(z.string()).nullish(),
    citationDoi: z.string().nullish(),
    citationJournal: z.string().nullish(),
    citationTitle: z.string().nullish(),
    citationYear: z.int().nullish(),
    documentFormat: z.string().nullish(),
    documentPageCount: z.int().nullish(),
    documentUrl: z.string().nullish(),
    highlights: z.array(z.record(z.string(), z.unknown())).nullish(),
    noteContent: z.record(z.string(), z.unknown()).nullish(),
    resourceId: z.string(),
    resourceTitle: z.string(),
    resourceType: z.string(),
    sessionMessages: z.array(z.record(z.string(), z.unknown())).nullish(),
    sessionTitle: z.string().nullish()
});

/**
 * SharedContentResponse
 *
 * Wrapper for shared content delivery.
 */
export const zSharedContentResponse = z.object({
    data: zSharedContentData
});

/**
 * SharedWithMeItem
 *
 * A resource shared with the current user (for "Shared with me" listing).
 */
export const zSharedWithMeItem = z.object({
    invitedAt: z.iso.datetime(),
    invitedByName: z.string().nullish(),
    resourceId: z.string(),
    resourceTitle: z.string(),
    resourceType: z.string(),
    role: z.string(),
    shareUrl: z.string().nullish(),
    sharingId: z.string(),
    workspaceId: z.string()
});

/**
 * SharedWithMeResponse
 *
 * Paginated list of resources shared with the current user.
 */
export const zSharedWithMeResponse = z.object({
    data: z.array(zSharedWithMeItem),
    hasMore: z.boolean().optional().default(false),
    total: z.int()
});

/**
 * SharingConfigResponse
 *
 * Full sharing configuration for a resource.
 *
 * This is the canonical response for GET /sharing/{resource_type}/{resource_id}.
 * Includes both the link settings and the list of named collaborators.
 */
export const zSharingConfigResponse = z.object({
    accessCount: z.int().optional().default(0),
    allowLibrarySave: z.boolean().optional().default(false),
    collaborators: z.array(zCollaboratorResponse).optional(),
    createdAt: z.iso.datetime(),
    createdById: z.string().nullish(),
    expiresAt: z.iso.datetime().nullish(),
    id: z.string(),
    isPasswordProtected: z.boolean().optional().default(false),
    linkAccessLevel: z.string().optional().default('disabled'),
    maxAccessCount: z.int().nullish(),
    resourceId: z.string(),
    resourceTitle: z.string().optional().default(''),
    resourceType: z.string(),
    shareUrl: z.string().nullish(),
    showAnnotations: z.boolean().optional().default(false),
    updatedAt: z.iso.datetime(),
    workspaceId: z.string().optional().default('')
});

/**
 * ListSharesResponse
 *
 * Cursor-paginated list of sharing configs in a workspace.
 */
export const zListSharesResponse = z.object({
    data: z.array(zSharingConfigResponse),
    hasMore: z.boolean(),
    nextCursor: z.string().nullish(),
    prevCursor: z.string().nullish(),
    total: z.int()
});

/**
 * SmartRuleField
 *
 * Allowed filter fields for smart collection rules.
 *
 * Each field maps to a fixed parameterized query fragment — never interpolated
 * into SQL to prevent injection.
 */
export const zSmartRuleField = z.enum([
    'tag',
    'author',
    'year',
    'source',
    'status',
    'format',
    'is_key_source'
]);

/**
 * SmartRuleOp
 *
 * Comparison operators for smart collection rules.
 */
export const zSmartRuleOp = z.enum([
    '=',
    '!=',
    '>',
    '>=',
    '<',
    '<=',
    'IN',
    'NOT_IN',
    'CONTAINS'
]);

/**
 * SmartRule
 *
 * A single filter rule in a smart collection.
 */
export const zSmartRule = z.object({
    field: zSmartRuleField,
    op: zSmartRuleOp,
    value: z.union([
        z.string(),
        z.int(),
        z.boolean(),
        z.array(z.string())
    ])
});

/**
 * SmartRuleOperator
 *
 * Logical operators for combining rules.
 */
export const zSmartRuleOperator = z.enum(['AND', 'OR']);

/**
 * SmartRules
 *
 * Complete smart collection filter definition.
 */
export const zSmartRules = z.object({
    operator: zSmartRuleOperator.optional().default('AND'),
    rules: z.array(zSmartRule).min(1).max(20),
    version: z.int().gte(1).lte(1).optional().default(1)
});

/**
 * CollectionTree
 *
 * Recursive collection tree node for sidebar navigation.
 */
export const zCollectionTree = z.object({
    children: z.array(z.lazy((): any => zCollectionTree)).optional(),
    collectionType: zCollectionType.optional().default('personal'),
    color: z.string().optional().default('neutral'),
    description: z.string().nullish(),
    documentCount: z.int().optional().default(0),
    id: z.uuid(),
    isArchived: z.boolean().optional().default(false),
    isPinned: z.boolean().optional().default(false),
    name: z.string(),
    noteCount: z.int().optional().default(0),
    ownerId: z.uuid().nullish(),
    parentId: z.uuid().nullish(),
    smartRules: zSmartRules.nullish(),
    sortMode: z.string().optional().default('manual')
});

/**
 * PaginatedResponse[CollectionTree]
 */
export const zPaginatedResponseCollectionTree = z.object({
    items: z.array(zCollectionTree),
    pagination: zPaginationMeta
});

/**
 * SmartCollectionCreate
 *
 * Create a smart (auto) collection with filter rules.
 */
export const zSmartCollectionCreate = z.object({
    color: z.string().optional().default('neutral'),
    description: z.string().max(2000).nullish(),
    is_pinned: z.boolean().optional().default(false),
    name: z.string().min(1).max(255),
    rules: zSmartRules
});

/**
 * SourceCredibility
 */
export const zSourceCredibility = z.enum([
    'any',
    'moderate',
    'high',
    'peer-reviewed-only'
]);

/**
 * StaffRole
 *
 * Commander staff console role for granular RBAC.
 *
 * Role Hierarchy (ascending):
 * - VIEWER: Read-only access to non-PII dashboards (analytics, overview)
 * - OPERATOR: Viewer + PII access, investigate, retry documents, triage feedback
 * - ADMIN: Operator + approve Sentinel mutations, impersonate, bulk-retry, billing access
 *
 * Requires is_superuser=True to access Commander at all.
 * StaffRole controls what a superuser can do within Commander.
 */
export const zStaffRole = z.enum([
    'viewer',
    'operator',
    'admin'
]);

/**
 * GrantStaffRoleRequest
 *
 * Grant or change a user's Commander staff role.
 */
export const zGrantStaffRoleRequest = z.object({
    reason: z.string().min(10).max(500),
    role: zStaffRole
});

/**
 * StaffRoleResponse
 *
 * Staff user with role information.
 */
export const zStaffRoleResponse = z.object({
    email: z.string(),
    is_superuser: z.boolean(),
    message: z.string().optional().default(''),
    staff_role: z.string().nullable(),
    user_id: z.string()
});

/**
 * StageStatus
 *
 * Status of a pipeline stage — mirrors JobStepStatus + pending.
 */
export const zStageStatus = z.enum([
    'pending',
    'running',
    'success',
    'skipped',
    'failed'
]);

/**
 * PipelineStageState
 *
 * Status of a single pipeline stage.
 */
export const zPipelineStageState = z.object({
    completedAt: z.iso.datetime().nullish(),
    error: z.string().nullish(),
    progress: z.number().gte(0).lte(100).nullish(),
    stage: zPipelineStage,
    startedAt: z.iso.datetime().nullish(),
    status: zStageStatus
});

/**
 * StripeWebhookAck
 *
 * Acknowledgement returned to Stripe after receiving a webhook event.
 *
 * ``processed`` reflects whether the event was actually enqueued for handling
 * (True) or skipped — duplicates and disabled-billing replies set it to
 * False with the ``reason`` field populated for observability.
 */
export const zStripeWebhookAck = z.object({
    eventType: z.string().nullish(),
    processed: z.boolean().optional().default(false),
    reason: z.string().nullish(),
    received: z.boolean().optional().default(true)
});

/**
 * StructuredSummary
 *
 * Structured breakdown of a document's research content.
 */
export const zStructuredSummary = z.object({
    implications: z.string().nullish(),
    keyFindings: z.array(z.string()).optional(),
    limitations: z.array(z.string()).optional(),
    methodology: z.string().nullish(),
    researchQuestion: z.string().nullish()
});

/**
 * DocumentSummaryRead
 *
 * Multi-level AI summary for a document.
 */
export const zDocumentSummaryRead = z.object({
    executive: z.string().nullish(),
    generatedAt: z.iso.datetime().nullish(),
    model: z.string().nullish(),
    structured: zStructuredSummary.nullish(),
    summaryVersion: z.string().nullish(),
    tldr: z.string().nullish()
});

/**
 * DocumentAISummaryResponse
 *
 * Response for GET /documents/{id}/ai-summary.
 */
export const zDocumentAiSummaryResponse = z.object({
    methodologyScore: zMethodologyScoreRead.nullish(),
    qaPairs: z.array(zQaPairRead).optional(),
    summary: zDocumentSummaryRead.nullish()
});

/**
 * DocumentDetailRead
 *
 * Rich document response for the contract/adapter layer.
 *
 * Maps to Nova DocumentDTO from contracts/documents.ts.
 */
export const zDocumentDetailRead = z.object({
    artifactCounts: zArtifactCounts.nullish(),
    artifactStatuses: zPerArtifactStatusRead.nullish(),
    attemptNumber: z.int().optional().default(1),
    authors: z.array(z.string()).optional(),
    commentCount: z.int().optional().default(0),
    createdAt: z.iso.datetime(),
    currentRunId: z.uuid().nullish(),
    enrichment: zDocumentEnrichment.nullish(),
    errorMessage: z.string().nullish(),
    fileSize: z.int().optional().default(0),
    format: zDocumentFormat.optional().default('pdf'),
    id: z.uuid(),
    isKeySource: z.boolean().optional().default(false),
    matchedDocumentId: z.uuid().nullish(),
    metadata: zDocumentMetadata.nullish(),
    notes: z.string().nullish(),
    owner: zDocumentOwner,
    pageCount: z.int().nullish(),
    pdfFailureCode: z.string().nullish(),
    pdfFailureReason: z.string().nullish(),
    pdfResolutionStatus: z.string().nullish(),
    pdfRetryable: z.boolean().nullish(),
    pdfSource: z.string().nullish(),
    pipeline: z.array(zPipelineStageState).optional(),
    priorFailureCount: z.int().optional().default(0),
    processedAt: z.iso.datetime().nullish(),
    provenance: zProcessingProvenanceRead.nullish(),
    source: z.string().optional().default(''),
    status: zDocumentStatus.optional().default('uploading'),
    summary: zDocumentSummaryRead.nullish(),
    tags: z.array(z.string()).optional(),
    thumbnailUrl: z.string().nullish(),
    title: z.string(),
    updatedAt: z.iso.datetime()
});

/**
 * PaginatedResponse[DocumentDetailRead]
 */
export const zPaginatedResponseDocumentDetailRead = z.object({
    items: z.array(zDocumentDetailRead),
    pagination: zPaginationMeta
});

/**
 * StuckDocument
 */
export const zStuckDocument = z.object({
    document_id: z.string(),
    last_error: z.string().nullish(),
    retry_count: z.int().optional().default(0),
    status: z.string(),
    stuck_minutes: z.int().optional().default(0),
    workspace_id: z.string()
});

/**
 * PaginatedStuckDocuments
 */
export const zPaginatedStuckDocuments = z.object({
    items: z.array(zStuckDocument).optional(),
    total: z.int().optional().default(0)
});

/**
 * SubscriptionStatus
 *
 * Maps to Stripe subscription statuses.
 */
export const zSubscriptionStatus = z.enum([
    'trialing',
    'active',
    'past_due',
    'canceled',
    'paused'
]);

/**
 * BillingSubscriptionRead
 *
 * Active subscription state for a workspace.
 */
export const zBillingSubscriptionRead = z.object({
    annual_commitment: z.boolean().optional().default(false),
    auto_expand_seats: z.boolean().optional().default(false),
    created_at: z.iso.datetime().nullish(),
    current_period_end: z.iso.datetime(),
    current_period_start: z.iso.datetime(),
    id: z.uuid(),
    included_seats: z.int(),
    max_seats_allowed: z.int().nullish(),
    overage_allowed: z.boolean().optional().default(false),
    overage_cap_pct: z.number().optional().default(0),
    plan_id: z.string(),
    provider_customer_id: z.string().nullish(),
    provider_subscription_id: z.string().nullish(),
    seat_count: z.int(),
    status: zSubscriptionStatus,
    updated_at: z.iso.datetime().nullish(),
    workspace_id: z.uuid()
});

/**
 * BillingPlanResponse
 *
 * GET /v1/billing/plan response.
 * Returns active plan, subscription state, effective limits, and mode flags.
 */
export const zBillingPlanResponse = z.object({
    active_addons: z.array(zAddOnRead).optional(),
    addon_effects: z.record(z.string(), z.int()).optional(),
    billing_mode: zBillingMode,
    days_until_reset: z.int(),
    effective_limits: zLimitsRead,
    enforcement_mode: zEnforcementMode,
    entitlements: zHumanReadableEntitlements.nullish(),
    is_beta: z.boolean(),
    is_over_seat_limit: z.boolean(),
    period_key: z.string(),
    plan: zBillingPlanRead,
    subscription: zBillingSubscriptionRead.nullish()
});

/**
 * SuccessResponse
 *
 * Standard success response with optional data payload.
 *
 * All successful API responses follow this structure for consistency.
 */
export const zSuccessResponse = z.object({
    data: z.record(z.string(), z.unknown()).nullish(),
    message: z.string(),
    request_id: z.string().nullish(),
    success: z.boolean().optional().default(true)
});

/**
 * SuggestionItem
 *
 * A single workspace-aware chat suggestion.
 */
export const zSuggestionItem = z.object({
    icon: z.string().optional().default('sparkles'),
    label: z.string()
});

/**
 * ChatSuggestionsResponse
 *
 * Response for GET /v1/chat/suggestions.
 */
export const zChatSuggestionsResponse = z.object({
    suggestions: z.array(zSuggestionItem).optional()
});

/**
 * SuggestionReasonRead
 *
 * One reason why a document was suggested.
 */
export const zSuggestionReasonRead = z.object({
    label: z.string(),
    score: z.number(),
    signal: z.string()
});

/**
 * SuggestionRead
 *
 * A single pre-computed suggestion.
 */
export const zSuggestionRead = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    documentId: z.uuid(),
    doi: z.string().nullish(),
    expiresAt: z.iso.datetime(),
    id: z.uuid(),
    journal: z.string().nullish(),
    publicationYear: z.int().nullish(),
    rankPosition: z.int(),
    reasons: z.array(zSuggestionReasonRead).optional(),
    relevanceScore: z.number(),
    source: z.string(),
    title: z.string().nullish()
});

/**
 * SuggestionListResponse
 *
 * Response for GET /suggestions.
 */
export const zSuggestionListResponse = z.object({
    generatedAt: z.iso.datetime().nullish(),
    nextRefreshAt: z.iso.datetime().nullish(),
    suggestions: z.array(zSuggestionRead),
    total: z.int(),
    workspaceId: z.uuid()
});

/**
 * SummaryDepth
 */
export const zSummaryDepth = z.enum([
    'brief',
    'standard',
    'comprehensive'
]);

/**
 * SuperuserGrant
 *
 * Grant superuser status to a user.
 *
 * **Security:** Admin-only operation, requires audit trail
 */
export const zSuperuserGrant = z.object({
    reason: z.string().min(10).max(500),
    user_id: z.uuid()
});

/**
 * SuspendUser
 *
 * Suspend a user account.
 *
 * **Security:** Blocks access, requires admin approval
 */
export const zSuspendUser = z.object({
    reason: z.string().min(10).max(500),
    user_id: z.uuid()
});

/**
 * SyncRequest
 *
 * Optional body for auth sync — carries Supabase user_metadata.
 */
export const zSyncRequest = z.object({
    first_name: z.string().nullish(),
    last_name: z.string().nullish()
});

/**
 * SynthesisItemSchema
 *
 * A paper included in the synthesis batch.
 */
export const zSynthesisItemSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).optional(),
    citationCount: z.int().optional().default(0),
    doi: z.string().nullish(),
    source: z.string().optional().default('semantic_scholar'),
    title: z.string().min(1).max(2000),
    venue: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * SynthesisRequestSchema
 *
 * Request for batch synthesis across selected papers.
 */
export const zSynthesisRequestSchema = z.object({
    items: z.array(zSynthesisItemSchema).min(2).max(50),
    prompt: z.string().max(500).nullish(),
    workspaceId: z.uuid()
});

/**
 * SystemHardwareInfo
 *
 * System hardware information.
 */
export const zSystemHardwareInfo = z.object({
    architecture: z.string(),
    cpu_count: z.int().gte(0).nullish(),
    cpu_percent: z.number().gte(0).optional().default(0),
    platform: z.string(),
    processor: z.string().optional().default('')
});

/**
 * SystemInfoResponse
 *
 * Response for system info endpoint.
 */
export const zSystemInfoResponse = z.object({
    error: z.string().nullish(),
    fastapi: zFastApiInfo,
    process: zProcessInfo,
    python: zPythonInfo,
    system: zSystemHardwareInfo,
    timestamp: z.string()
});

/**
 * SystemStatusMetrics
 *
 * Metrics returned by the system status endpoint.
 */
export const zSystemStatusMetrics = z.object({
    recent_heartbeats_5min: z.int().gte(0).optional().default(0),
    recent_plays_5min: z.int().gte(0).optional().default(0),
    total_users: z.int().gte(0).optional().default(0),
    total_workspaces: z.int().gte(0).optional().default(0)
});

/**
 * AdminSystemStatusResponse
 *
 * Response for admin system status endpoint.
 */
export const zAdminSystemStatusResponse = z.object({
    database: z.record(z.string(), z.unknown()).optional(),
    error: z.string().nullish(),
    health: z.string(),
    metrics: z.union([
        zSystemStatusMetrics,
        z.record(z.string(), z.unknown())
    ]).optional(),
    service: zServiceInfo
});

/**
 * TimelineEvent
 *
 * Single event in the monitoring timeline.
 */
export const zTimelineEvent = z.object({
    details: z.record(z.string(), z.unknown()).nullish(),
    event_type: z.string(),
    severity: z.string().optional().default('info'),
    summary: z.string(),
    timestamp: z.string()
});

/**
 * PaginatedResponse[TimelineEvent]
 */
export const zPaginatedResponseTimelineEvent = z.object({
    items: z.array(zTimelineEvent),
    pagination: zPaginationMeta
});

/**
 * ToggleStarData
 */
export const zToggleStarData = z.object({
    id: z.string(),
    isStarred: z.boolean()
});

/**
 * ToggleStarResponse
 *
 * Result of `POST /library/{item_id}/star`.
 */
export const zToggleStarResponse = z.object({
    data: zToggleStarData
});

/**
 * TokenClaimsResponse
 *
 * JWT token claims response.
 */
export const zTokenClaimsResponse = z.object({
    email: z.string(),
    is_anonymous: z.boolean().optional().default(false),
    session_id: z.string().nullish(),
    supabase_uid: z.string()
});

/**
 * TopClaimRead
 *
 * A high-confidence claim surfaced in the synthesis meter.
 */
export const zTopClaimRead = z.object({
    artifactId: z.uuid(),
    claimText: z.string().max(500),
    confidence: z.number().nullish(),
    documentId: z.uuid(),
    documentTitle: z.string(),
    effectDirection: z.string().nullish(),
    evidenceType: z.string()
});

/**
 * EvidenceSynthesisRead
 *
 * Workspace-level evidence synthesis meter.
 *
 * Aggregates extracted claims across all workspace documents to surface
 * evidence composition, directional balance, and high-confidence claims.
 * Designed for the evidence meter UI component.
 */
export const zEvidenceSynthesisRead = z.object({
    avgConfidence: z.number().nullish(),
    byDirection: z.array(zDirectionBucket).optional(),
    byEvidenceType: z.array(zEvidenceTypeBucket).optional(),
    documentsWithClaims: z.int().gte(0),
    topClaims: z.array(zTopClaimRead).optional(),
    totalClaims: z.int().gte(0)
});

/**
 * TopCoCitationListRead
 *
 * Top co-citation pairs across an entire workspace (no DOI filter).
 *
 * Used by the Library knowledge surface to show workspace-level connections
 * without requiring the user to select a specific document.
 */
export const zTopCoCitationListRead = z.object({
    pairs: z.array(zCoCitationPairRead).optional(),
    total: z.int().gte(0).optional().default(0)
});

/**
 * TopWorkspaceItem
 */
export const zTopWorkspaceItem = z.object({
    document_count: z.int().optional().default(0),
    mtd_cost_usd: z.number().optional().default(0),
    name: z.string(),
    plan: z.string(),
    workspace_id: z.string()
});

/**
 * TopWorkspacesResponse
 */
export const zTopWorkspacesResponse = z.object({
    items: z.array(zTopWorkspaceItem).optional()
});

/**
 * TransparencyLevel
 */
export const zTransparencyLevel = z.enum([
    'minimal',
    'standard',
    'verbose'
]);

/**
 * TrashEmptyResponse
 *
 * Result of emptying the workspace trash.
 */
export const zTrashEmptyResponse = z.object({
    deleted: z.int(),
    workspace_id: z.string()
});

/**
 * TrashItemDocument
 *
 * Trashed document summary for the workspace trash view.
 */
export const zTrashItemDocument = z.object({
    deletedAt: z.iso.datetime().nullish(),
    deletedBy: z.string().nullish(),
    fileName: z.string().nullish(),
    id: z.uuid(),
    title: z.string().nullish(),
    trashExpiresAt: z.iso.datetime().nullish()
});

/**
 * TrashItemNote
 *
 * Trashed note summary for the workspace trash view.
 */
export const zTrashItemNote = z.object({
    deletedAt: z.iso.datetime().nullish(),
    deletedBy: z.string().nullish(),
    id: z.uuid(),
    title: z.string().nullish(),
    trashExpiresAt: z.iso.datetime().nullish()
});

/**
 * TrashListResponse
 *
 * Paginated unified trash view (documents + notes).
 */
export const zTrashListResponse = z.object({
    documents: z.array(zTrashItemDocument),
    hasMore: z.boolean().optional().default(false),
    nextCursor: z.string().nullish(),
    notes: z.array(zTrashItemNote),
    pageSize: z.int(),
    prevCursor: z.string().nullish()
});

/**
 * TrendingClusterRead
 *
 * Response schema for a trending research cluster.
 */
export const zTrendingClusterRead = z.object({
    citationVelocity: z.number(),
    clusterLabel: z.string().nullable(),
    computedAt: z.iso.datetime().nullable(),
    documentCount: z.int(),
    id: z.uuid(),
    status: z.string(),
    topDocuments: z.array(z.record(z.string(), z.unknown())).optional(),
    velocityChangePct: z.number()
});

/**
 * TrendingListResponse
 *
 * Response for GET /trending.
 */
export const zTrendingListResponse = z.object({
    clusters: z.array(zTrendingClusterRead),
    computedAt: z.iso.datetime().nullish(),
    total: z.int()
});

/**
 * TriggerExtractionResponse
 *
 * Response after triggering extraction.
 */
export const zTriggerExtractionResponse = z.object({
    pendingCells: z.int(),
    status: zExtractionTableStatus,
    tableId: z.uuid()
});

/**
 * UnconfirmedEdgeItem
 *
 * One row in the Unconfirmed AI Edges surface (Slice 6, spec §5.13).
 *
 * Source is constrained to AI/import — user-created edges never need
 * confirmation. ``weight`` is the upstream edge weight (0–1); the
 * renderer can use it to size or sort visually.
 */
export const zUnconfirmedEdgeItem = z.object({
    edgeId: z.uuid(),
    edgeType: z.enum([
        'cites',
        'mentions',
        'extracted_from',
        'embeds',
        'references',
        'derives_from',
        'comments_on_range',
        'comments_on_node',
        'related'
    ]),
    firstCreatedAt: z.iso.datetime(),
    source: z.enum(['ai', 'import']),
    weight: z.number().gte(0).lte(1)
});

/**
 * NotesUnconfirmedEdgesSurface
 *
 * AI/import-inferred connections awaiting human confirmation.
 *
 * Trigger: count >= 1 where confirm_count = 0 AND created_by_source IN
 * ('ai', 'import') AND deleted_at IS NULL. Ranking: weight desc,
 * first_created_at desc. Cap 10.
 */
export const zNotesUnconfirmedEdgesSurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    items: z.array(zUnconfirmedEdgeItem).min(1),
    kind: z.literal('notes_unconfirmed_edges').optional().default('notes_unconfirmed_edges')
});

/**
 * UnlockUserAccount
 *
 * Unlock a user account (from failed login lockout).
 *
 * **Security:** Admin-only, resets failed attempts counter
 */
export const zUnlockUserAccount = z.object({
    reason: z.string().min(5).max(500),
    user_id: z.uuid()
});

/**
 * UnreadCountResponse
 *
 * Lightweight unread count response for badge polling.
 */
export const zUnreadCountResponse = z.object({
    byCategory: z.record(z.string(), z.int()).optional(),
    unreadCount: z.int().gte(0)
});

/**
 * UnsaveResponseSchema
 */
export const zUnsaveResponseSchema = z.object({
    message: z.string(),
    success: z.boolean()
});

/**
 * UpdateCollaboratorRequest
 *
 * Update a collaborator's role.
 */
export const zUpdateCollaboratorRequest = z.object({
    role: z.string()
});

/**
 * UpdateLinkAccessRequest
 *
 * Update the link sharing level for a resource.
 *
 * Setting access_level to "disabled" turns off link sharing (invite-only).
 * Setting it to "viewer"/"commenter"/"editor" enables link sharing at that level.
 *
 * Only explicitly-included fields are updated; omitted fields are untouched.
 * Pass null for password / expires_in_hours / max_access_count to clear them.
 */
export const zUpdateLinkAccessRequest = z.object({
    access_level: z.string(),
    allow_library_save: z.boolean().nullish(),
    expires_in_hours: z.int().gte(1).lte(2160).nullish(),
    max_access_count: z.int().gte(1).nullish(),
    password: z.string().min(8).max(128).nullish(),
    show_annotations: z.boolean().nullish()
});

/**
 * UpdateModelPricingRequest
 *
 * PUT /v1/billing/admin/pricing request.
 */
export const zUpdateModelPricingRequest = z.object({
    cost_weight: z.number().gt(0),
    model_name: z.string().max(100),
    token_type: z.string().max(20)
});

/**
 * UpdateShareCommentRequest
 *
 * Edit own comment content.
 */
export const zUpdateShareCommentRequest = z.object({
    content: z.string().min(1).max(2000)
});

/**
 * UsageBreakdown
 */
export const zUsageBreakdown = z.object({
    ai_cost_units: z.number().optional().default(0),
    document_count: z.int().optional().default(0),
    extraction_jobs: z.int().optional().default(0),
    period: z.string().optional().default(''),
    storage_bytes: z.int().optional().default(0),
    total_cost_usd: z.number().optional().default(0)
});

/**
 * UsageBreakdownItem
 *
 * A single row in the usage breakdown — one action_type + model combination.
 */
export const zUsageBreakdownItem = z.object({
    action_type: z.string(),
    cost_units: z.number(),
    model: z.string().nullish(),
    percent_of_total: z.number(),
    provider: z.string().nullish(),
    request_count: z.int()
});

/**
 * UsageBreakdownResponse
 *
 * GET /v1/billing/usage/breakdown response.
 *
 * Returns AI cost units grouped by action_type + model for the current
 * (or specified) period, sorted descending by cost_units.
 */
export const zUsageBreakdownResponse = z.object({
    items: z.array(zUsageBreakdownItem),
    period_key: z.string(),
    total_cost_units: z.number()
});

/**
 * UsagePeriodSummary
 *
 * Single period summary for history endpoint.
 */
export const zUsagePeriodSummary = z.object({
    ai_cost_units: z.number(),
    ai_limit: z.union([
        z.int(),
        z.number()
    ]).nullish(),
    extraction_jobs: z.int(),
    extraction_limit: z.int().nullish(),
    period_end: z.iso.datetime(),
    period_key: z.string(),
    period_start: z.iso.datetime(),
    storage_bytes: z.int(),
    storage_limit: z.int().nullish()
});

/**
 * UsageHistoryResponse
 *
 * GET /v1/billing/usage/history response.
 */
export const zUsageHistoryResponse = z.object({
    billing_mode: zBillingMode,
    periods: z.array(zUsagePeriodSummary)
});

/**
 * UserHealthMetrics
 *
 * User health metrics.
 */
export const zUserHealthMetrics = z.object({
    active_users_7d: z.int().gte(0),
    total_users: z.int().gte(0)
});

/**
 * UserHealthResponse
 *
 * Response for user health endpoint.
 */
export const zUserHealthResponse = z.object({
    metrics: zUserHealthMetrics,
    service: z.string().optional().default('users'),
    status: z.string(),
    timestamp: z.string()
});

/**
 * UserSessionRead
 *
 * Full user session details
 */
export const zUserSessionRead = z.object({
    browser: z.string().nullish(),
    browser_version: z.string().nullish(),
    created_at: z.iso.datetime(),
    device_info: z.string().optional().default('Unknown device'),
    device_name: z.string().nullish(),
    device_type: z.string().nullish(),
    expires_at: z.iso.datetime().nullable(),
    id: z.uuid(),
    ip_address: z.string().nullish(),
    ip_city: z.string().nullish(),
    ip_country: z.string().nullish(),
    is_suspicious: z.boolean(),
    is_trusted_device: z.boolean(),
    last_activity_at: z.iso.datetime(),
    location_info: z.string().optional().default('Unknown location'),
    login_at: z.iso.datetime(),
    login_method: z.string().nullish(),
    logout_at: z.iso.datetime().nullable(),
    notes: z.string().nullable(),
    os: z.string().nullish(),
    os_version: z.string().nullish(),
    session_id: z.string().nullable(),
    status: zAuthSessionStatus,
    suspicious_reason: z.string().nullable(),
    updated_at: z.iso.datetime(),
    user_agent: z.string().nullish(),
    user_id: z.uuid()
});

/**
 * PaginatedResponse[UserSessionRead]
 */
export const zPaginatedResponseUserSessionRead = z.object({
    items: z.array(zUserSessionRead),
    pagination: zPaginationMeta
});

/**
 * UserSessionSummary
 *
 * Condensed view for active sessions list
 */
export const zUserSessionSummary = z.object({
    device_info: z.string(),
    id: z.uuid(),
    ip_address: z.string().nullable(),
    ip_city: z.string().nullish(),
    ip_country: z.string().nullish(),
    is_current: z.boolean().optional().default(false),
    is_suspicious: z.boolean(),
    is_trusted_device: z.boolean(),
    last_activity_at: z.iso.datetime(),
    location_info: z.string(),
    login_at: z.iso.datetime(),
    session_id: z.string().nullable(),
    status: zAuthSessionStatus
});

/**
 * ActiveSessionsList
 *
 * List of active sessions with metadata
 */
export const zActiveSessionsList = z.object({
    active_count: z.int(),
    current_session_id: z.string().nullish(),
    sessions: z.array(zUserSessionSummary),
    total_count: z.int()
});

/**
 * UserStatus
 *
 * User account status for access control and lifecycle management.
 *
 * States:
 * - ACTIVE: User can access the platform normally
 * - SUSPENDED: Temporarily blocked (security violation, payment issues)
 * - DEACTIVATED: User-requested account deactivation (soft delete alternative)
 * - PENDING_VERIFICATION: New account awaiting email/identity verification
 *
 * Status-based access control enforced at API layer.
 */
export const zUserStatus = z.enum([
    'active',
    'suspended',
    'deactivated',
    'pending_verification'
]);

/**
 * UserRead
 *
 * User response with timestamps (safe for client).
 *
 * Nova frontend expects: { id, email, first_name, last_name, avatar_url, status, ... }
 *
 * avatar_url is NOT stored on the User model. It is resolved at serialization
 * time from avatar_asset_id via the Asset table (signed URL or CDN proxy).
 * The API route handler must call resolve_avatar_url() before returning.
 */
export const zUserRead = z.object({
    activeWorkspaceId: z.uuid().nullish(),
    avatarAssetId: z.uuid().nullish(),
    avatarUrl: z.string().nullish(),
    createdAt: z.iso.datetime(),
    defaultOrgId: z.uuid().nullish(),
    defaultWorkspaceId: z.uuid().nullish(),
    email: z.email(),
    emailVerified: z.boolean(),
    firstName: z.string().nullish(),
    id: z.uuid(),
    lastLoginAt: z.iso.datetime().nullish(),
    lastName: z.string().nullish(),
    onboardingCompleted: z.boolean().optional().default(false),
    onboardingStep: zOnboardingStep.nullish(),
    status: zUserStatus,
    updatedAt: z.iso.datetime()
});

/**
 * PaginatedResponse[UserRead]
 */
export const zPaginatedResponseUserRead = z.object({
    items: z.array(zUserRead),
    pagination: zPaginationMeta
});

/**
 * UserReadFull
 *
 * Full user info including account security details.
 *
 * **SECURITY:** Only return to superusers or the user themselves
 */
export const zUserReadFull = z.object({
    activeWorkspaceId: z.uuid().nullish(),
    avatarAssetId: z.uuid().nullish(),
    avatarUrl: z.string().nullish(),
    createdAt: z.iso.datetime(),
    defaultOrgId: z.uuid().nullish(),
    defaultWorkspaceId: z.uuid().nullish(),
    email: z.email(),
    emailVerified: z.boolean(),
    failedLoginAttempts: z.int(),
    firstName: z.string().nullish(),
    id: z.uuid(),
    isAccountLocked: z.boolean(),
    isSuperuser: z.boolean().optional().default(false),
    isSuperuserSetAt: z.iso.datetime().nullish(),
    lastLoginAt: z.iso.datetime().nullish(),
    lastName: z.string().nullish(),
    onboardingCompleted: z.boolean().optional().default(false),
    onboardingStep: zOnboardingStep.nullish(),
    status: zUserStatus,
    updatedAt: z.iso.datetime()
});

/**
 * UserStatusChangeResponse
 */
export const zUserStatusChangeResponse = z.object({
    status: z.string(),
    user_id: z.string()
});

/**
 * UserUpdate
 *
 * Update user profile (non-sensitive fields only).
 *
 * Avatar is managed via asset reference, not raw URL.
 * Upload the avatar through POST /asset/presigned-upload-url first,
 * then set avatar_asset_id to the resulting asset ID.
 */
export const zUserUpdate = z.object({
    avatar_asset_id: z.uuid().nullish(),
    first_name: z.string().max(100).nullish(),
    last_name: z.string().max(100).nullish()
});

/**
 * VReference
 *
 * Universal reference to a location within a document.
 */
export const zVReference = z.object({
    anchor: z.enum([
        'artifact',
        'text_span',
        'page',
        'highlight'
    ]).optional().default('page'),
    artifact_id: z.uuid().nullish(),
    bbox: zNormBBox.nullish(),
    confidence: z.number().gte(0).lte(1).nullish(),
    doc_id: z.uuid(),
    gv_id: z.uuid().nullish(),
    page: z.int().gte(1).nullish(),
    snippet: z.string().max(200).nullish(),
    stable_id: z.uuid().nullish(),
    text_hash: z.string().max(64).nullish(),
    v: z.int().optional().default(2)
});

/**
 * VerifyEndpointResponse
 *
 * Response from endpoint verification.
 */
export const zVerifyEndpointResponse = z.object({
    error: z.string().nullish(),
    status: z.string(),
    success: z.boolean()
});

/**
 * VerifySharePasswordData
 *
 * Result of a share password verification attempt.
 */
export const zVerifySharePasswordData = z.object({
    accessToken: z.string().nullish(),
    valid: z.boolean()
});

/**
 * VerifySharePasswordResponse
 *
 * Wrapper for share password verification results.
 */
export const zVerifySharePasswordResponse = z.object({
    data: zVerifySharePasswordData
});

/**
 * VerifySharingPasswordRequest
 *
 * Verify the password for a password-protected share link.
 */
export const zVerifySharingPasswordRequest = z.object({
    password: z.string()
});

/**
 * VersionInfoResponse
 *
 * Response for version info endpoint.
 */
export const zVersionInfoResponse = z.object({
    api_version: z.string(),
    build: z.string(),
    build_date: z.string(),
    documentation: z.string(),
    environment: z.string(),
    openapi_schema: z.string(),
    version: z.string()
});

/**
 * WarningLevel
 *
 * Server-computed warning level for a usage bucket.
 */
export const zWarningLevel = z.enum([
    'ok',
    'warn',
    'critical',
    'blocked'
]);

/**
 * AiBucketUsage
 *
 * AI usage bucket with cost-unit tracking and burn-rate projection.
 */
export const zAiBucketUsage = z.object({
    cost_units_used: z.number().optional().default(0),
    daily_burn_rate: z.number().nullish(),
    estimated_messages_remaining: z.int().nullish(),
    estimated_messages_total: z.int().nullish(),
    limit: z.number().nullish(),
    percent_used: z.number(),
    projected_end_of_period_pct: z.number().nullish(),
    projected_end_of_period_used: z.number().nullish(),
    projected_exhaustion_date: z.iso.date().nullish(),
    remaining: z.number().nullish(),
    requests: z.int().optional().default(0),
    used: z.number(),
    warning_level: zWarningLevel.optional().default('ok')
});

/**
 * ExtractionBucketUsage
 *
 * Extraction job usage bucket.
 */
export const zExtractionBucketUsage = z.object({
    jobs_used: z.int().optional().default(0),
    limit: z.int().nullish(),
    percent_used: z.number(),
    remaining: z.int().nullish(),
    used: z.int(),
    warning_level: zWarningLevel.optional().default('ok')
});

/**
 * StorageBucketUsage
 *
 * Storage usage bucket with byte-level tracking.
 */
export const zStorageBucketUsage = z.object({
    bytes_used: z.int().optional().default(0),
    gb_used: z.number().optional().default(0),
    limit: z.int().nullish(),
    percent_used: z.number(),
    remaining: z.int().nullish(),
    used: z.int(),
    warning_level: zWarningLevel.optional().default('ok')
});

/**
 * BucketsUsage
 */
export const zBucketsUsage = z.object({
    ai: zAiBucketUsage,
    extraction: zExtractionBucketUsage,
    storage: zStorageBucketUsage
});

/**
 * UsageResponse
 *
 * GET /v1/billing/usage response.
 * Returns current period usage for all buckets with server-computed warning levels.
 */
export const zUsageResponse = z.object({
    billing_mode: zBillingMode,
    buckets: zBucketsUsage,
    days_until_reset: z.int(),
    enforcement_mode: zEnforcementMode,
    period_end: z.iso.datetime(),
    period_key: z.string(),
    period_start: z.iso.datetime(),
    seats: zSeatsUsage
});

/**
 * WebhookDeliveryResponse
 *
 * Webhook delivery attempt details.
 */
export const zWebhookDeliveryResponse = z.object({
    attempt_count: z.int().optional().default(0),
    completed_at: z.iso.datetime().nullish(),
    created_at: z.iso.datetime(),
    endpoint_id: z.uuid(),
    error_message: z.string().nullish(),
    error_type: z.string().nullish(),
    event_id: z.uuid(),
    event_timestamp: z.iso.datetime(),
    event_type: z.string(),
    id: z.uuid(),
    max_attempts: z.int().optional().default(5),
    response_status_code: z.int().nullish(),
    response_time_ms: z.int().nullish(),
    scheduled_at: z.iso.datetime(),
    started_at: z.iso.datetime().nullish(),
    status: z.string()
});

/**
 * WebhookDeliveryList
 *
 * Paginated list of deliveries.
 */
export const zWebhookDeliveryList = z.object({
    deliveries: z.array(zWebhookDeliveryResponse).optional(),
    page: z.int().gte(1),
    page_size: z.int().gte(1).lte(200),
    total: z.int().gte(0)
});

/**
 * WebhookDeliveryStatus
 *
 * Delivery attempt status.
 */
export const zWebhookDeliveryStatus = z.enum([
    'pending',
    'success',
    'failed',
    'retrying'
]);

/**
 * WebhookEndpointResponse
 *
 * Webhook endpoint details.
 */
export const zWebhookEndpointResponse = z.object({
    batch_interval_seconds: z.int(),
    batch_playback_events: z.boolean().optional().default(false),
    consecutive_failures: z.int().optional().default(0),
    created_at: z.iso.datetime(),
    description: z.string().nullish(),
    id: z.uuid(),
    include_ots_metrics: z.boolean().optional().default(false),
    is_enabled: z.boolean(),
    last_failure_at: z.iso.datetime().nullish(),
    last_failure_reason: z.string().nullish(),
    last_success_at: z.iso.datetime().nullish(),
    name: z.string(),
    status: z.string(),
    subscribed_events: z.array(z.string()).optional(),
    updated_at: z.iso.datetime().nullish(),
    url: z.string(),
    verified_at: z.iso.datetime().nullish()
});

/**
 * PaginatedResponse[WebhookEndpointResponse]
 */
export const zPaginatedResponseWebhookEndpointResponse = z.object({
    items: z.array(zWebhookEndpointResponse),
    pagination: zPaginationMeta
});

/**
 * WebhookEndpointWithSecret
 *
 * Endpoint response with signing secret (only on create).
 *
 * **SECURITY:** The signing secret is only returned once on creation.
 * Store it securely for verifying webhook payloads.
 */
export const zWebhookEndpointWithSecret = z.object({
    batch_interval_seconds: z.int(),
    batch_playback_events: z.boolean().optional().default(false),
    consecutive_failures: z.int().optional().default(0),
    created_at: z.iso.datetime(),
    description: z.string().nullish(),
    id: z.uuid(),
    include_ots_metrics: z.boolean().optional().default(false),
    is_enabled: z.boolean(),
    last_failure_at: z.iso.datetime().nullish(),
    last_failure_reason: z.string().nullish(),
    last_success_at: z.iso.datetime().nullish(),
    name: z.string(),
    signing_secret: z.string(),
    status: z.string(),
    subscribed_events: z.array(z.string()).optional(),
    updated_at: z.iso.datetime().nullish(),
    url: z.string(),
    verified_at: z.iso.datetime().nullish()
});

/**
 * WebhookEventType
 *
 * Event types that can trigger webhook notifications.
 *
 * Research platform event patterns for document, session, and citation lifecycle.
 */
export const zWebhookEventType = z.enum([
    'document.uploaded',
    'document.processing',
    'document.ready',
    'document.failed',
    'document.deleted',
    'extraction.started',
    'extraction.completed',
    'extraction.failed',
    'session.created',
    'session.updated',
    'session.completed',
    'session.archived',
    'citation.created',
    'citation.verified',
    'citation.flagged',
    'workspace.member_invited',
    'workspace.member_joined',
    'workspace.member_removed',
    'invoice.generated',
    'payment.received',
    'payment.failed',
    'export.ready',
    'export.failed'
]);

/**
 * WebhookEndpointCreate
 *
 * Request to create a webhook endpoint.
 */
export const zWebhookEndpointCreate = z.object({
    batch_interval_seconds: z.int().gte(10).lte(3600).optional().default(60),
    batch_playback_events: z.boolean().optional().default(false),
    description: z.string().max(500).nullish(),
    include_ots_metrics: z.boolean().optional().default(false),
    name: z.string().min(1).max(100),
    subscribed_events: z.array(zWebhookEventType).min(1),
    url: z.url().min(1).max(2083)
});

/**
 * WebhookEndpointUpdate
 *
 * Request to update a webhook endpoint.
 */
export const zWebhookEndpointUpdate = z.object({
    batch_interval_seconds: z.int().gte(10).lte(3600).nullish(),
    batch_playback_events: z.boolean().nullish(),
    description: z.string().max(500).nullish(),
    include_ots_metrics: z.boolean().nullish(),
    is_enabled: z.boolean().nullish(),
    name: z.string().min(1).max(100).nullish(),
    subscribed_events: z.array(zWebhookEventType).nullish(),
    url: z.url().min(1).max(2083).nullish()
});

/**
 * WebhookStats
 *
 * Webhook endpoint statistics.
 */
export const zWebhookStats = z.object({
    avg_response_time_ms: z.number().nullish(),
    endpoint_id: z.uuid(),
    failed_deliveries: z.int().gte(0),
    last_24h_deliveries: z.int().gte(0),
    pending_deliveries: z.int().gte(0),
    success_rate: z.number().gte(0).lte(100),
    successful_deliveries: z.int().gte(0),
    total_deliveries: z.int().gte(0)
});

/**
 * WorkerDetail
 *
 * Detailed status of a single worker.
 */
export const zWorkerDetail = z.object({
    heartbeat_age_seconds: z.int().nullish(),
    name: z.string(),
    queue: z.string().optional().default('default'),
    queue_depth: z.int().gte(0).optional().default(0),
    status: z.string(),
    stuck_job_count: z.int().gte(0).optional().default(0)
});

/**
 * CommandCenterOverview
 *
 * Unified dashboard payload for the Command Center UI.
 */
export const zCommandCenterOverview = z.object({
    alerts: z.array(z.record(z.string(), z.unknown())).optional(),
    dead_letter_summary: zDeadLetterSummary.optional(),
    ingestion_summary: zIngestionSummary.optional(),
    queues: z.array(zQueueDetail).optional(),
    reaper: zReaperStatus,
    status: z.string(),
    timestamp: z.string(),
    workers: z.array(zWorkerDetail).optional()
});

/**
 * WorkerHeartbeatDetail
 *
 * Individual worker heartbeat status.
 */
export const zWorkerHeartbeatDetail = z.object({
    age_seconds: z.int().gte(0).nullish(),
    error: z.string().nullish(),
    is_alive: z.boolean(),
    name: z.string(),
    stale_threshold: z.int().gte(0).nullish(),
    ttl_remaining: z.int().nullish()
});

/**
 * WorkerHeartbeatHealthResponse
 *
 * Response for worker heartbeat health detail endpoint.
 */
export const zWorkerHeartbeatHealthResponse = z.object({
    alive: z.int().gte(0),
    dead: z.int().gte(0),
    status: z.string(),
    timestamp: z.string(),
    total: z.int().gte(0),
    workers: z.array(zWorkerHeartbeatDetail)
});

/**
 * WorkerMetricsResponse
 *
 * Response for detailed worker metrics endpoint.
 */
export const zWorkerMetricsResponse = z.object({
    summary: z.record(z.string(), z.unknown()),
    timestamp: z.string(),
    workers: z.record(z.string(), z.unknown())
});

/**
 * WorkerProbeResponse
 *
 * Worker heartbeat probe response.
 */
export const zWorkerProbeResponse = z.object({
    alive: z.int(),
    dead: z.int(),
    status: z.string(),
    timestamp: z.string(),
    total: z.int()
});

/**
 * WorkerStatus
 *
 * Status of an ARQ background worker.
 */
export const zWorkerStatus = z.object({
    is_running: z.boolean(),
    jobs_in_progress: z.int().gte(0).optional().default(0),
    jobs_pending: z.int().gte(0).optional().default(0),
    last_health_check: z.string().nullish(),
    name: z.string(),
    queue: z.string()
});

/**
 * SystemHealthResponse
 *
 * Overall system health status.
 *
 * Aggregates health status from workers, queues, and document pipeline.
 * Status values: healthy, degraded, critical
 */
export const zSystemHealthResponse = z.object({
    alerts: z.array(z.record(z.string(), z.unknown())).optional(),
    pipeline_stats: zPipelineStats.nullish(),
    queues: z.array(zQueueStats).optional(),
    status: z.string(),
    timestamp: z.string(),
    workers: z.array(zWorkerStatus).optional()
});

/**
 * WorkspaceColor
 *
 * Gradient color pair for workspace branding.
 */
export const zWorkspaceColor = z.object({
    colorFrom: z.string().max(20),
    colorTo: z.string().max(20)
});

/**
 * WorkspaceContextCreate
 *
 * POST body for initial workspace context setup (onboarding wizard).
 */
export const zWorkspaceContextCreate = z.object({
    geography_tags: z.array(z.string()).max(30).optional(),
    journal_preferences: z.array(z.string()).max(50).optional(),
    methodology_tags: z.array(z.string()).max(30).optional(),
    objective: z.string().min(10).max(5000),
    research_topics: z.array(z.string()).max(50).optional()
});

/**
 * WorkspaceContextRead
 *
 * Response schema for workspace context.
 */
export const zWorkspaceContextRead = z.object({
    coldStartCompleted: z.boolean().optional().default(false),
    coldStartCompletedAt: z.iso.datetime().nullish(),
    compositionWeights: z.record(z.string(), z.number()).nullish(),
    createdAt: z.iso.datetime(),
    embeddingModelVersion: z.string().nullish(),
    embeddingUpdatedAt: z.iso.datetime().nullish(),
    geographyTags: z.array(z.string()).nullish(),
    id: z.uuid(),
    journalPreferences: z.array(z.string()).nullish(),
    methodologyTags: z.array(z.string()).nullish(),
    objective: z.string(),
    researchTopics: z.array(z.string()).nullish(),
    trendingClusterIds: z.array(z.uuid()).nullish(),
    updatedAt: z.iso.datetime(),
    workspaceId: z.uuid()
});

/**
 * IntelligenceDashboard
 *
 * Aggregate intelligence state for the workspace sidebar and library surface.
 */
export const zIntelligenceDashboard = z.object({
    coCitationPairCount: z.int().optional().default(0),
    context: zWorkspaceContextRead.nullish(),
    contradictions: zContradictionSummary.optional(),
    feedbackStats: z.record(z.string(), z.int()).optional(),
    pendingSuggestionCount: z.int().optional().default(0),
    recentSearches: z.int().optional().default(0),
    researchGaps: zResearchGapSummary.optional(),
    suggestionCount: z.int().optional().default(0),
    totalCitations: z.int().optional().default(0),
    totalConnections: z.int().optional().default(0),
    trendingClusterCount: z.int().optional().default(0)
});

/**
 * WorkspaceContextUpdate
 *
 * PATCH body for updating research context.
 */
export const zWorkspaceContextUpdate = z.object({
    geography_tags: z.array(z.string()).nullish(),
    journal_preferences: z.array(z.string()).nullish(),
    methodology_tags: z.array(z.string()).nullish(),
    objective: z.string().min(10).max(5000).nullish(),
    research_topics: z.array(z.string()).nullish()
});

/**
 * WorkspaceGraphResponse
 *
 * Full workspace graph for force-directed visualization.
 */
export const zWorkspaceGraphResponse = z.object({
    links: z.array(zGraphLink).optional(),
    nodes: z.array(zGraphNode).optional()
});

/**
 * WorkspaceHealthMetrics
 *
 * Workspace health metrics.
 */
export const zWorkspaceHealthMetrics = z.object({
    total_workspaces: z.int().gte(0)
});

/**
 * WorkspaceHealthResponse
 *
 * Response for workspace health endpoint.
 */
export const zWorkspaceHealthResponse = z.object({
    metrics: zWorkspaceHealthMetrics,
    service: z.string().optional().default('workspaces'),
    status: z.string(),
    timestamp: z.string()
});

/**
 * WorkspaceIdentitySurface
 *
 * The greeting + objective anchor. Always present in the response.
 */
export const zWorkspaceIdentitySurface = z.object({
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    kind: z.literal('workspace_identity'),
    objective: zObjectiveDto.nullish()
});

/**
 * HomeBoardResponse
 *
 * Composed payload for the workspace home board.
 *
 * The envelope carries workspace identity (always) plus an ordered list of
 * surface variants. The server ordering follows the orchestration priority
 * in the spec (§7.2); the client renders surfaces in the order received
 * and does not re-sort.
 *
 * The render contract is strict: a surface appears in ``surfaces[]`` if
 * and only if its server-side trigger fires. No empty DTOs, no placeholder
 * stubs — the legacy flat schema is fully retired.
 */
export const zHomeBoardResponse = z.object({
    cacheTtlSeconds: z.int().gte(0).lte(600).optional().default(30),
    degradeReasons: z.array(z.string()).optional(),
    freshnessAt: z.iso.datetime(),
    surfaces: z.array(z.union([
        z.object({
            kind: z.literal('workspace_identity')
        }).and(zWorkspaceIdentitySurface),
        z.object({
            kind: z.literal('pipeline_pulse')
        }).and(zPipelinePulseSurface),
        z.object({
            kind: z.literal('critical_operational_blockers')
        }).and(zCriticalBlockersSurface),
        z.object({
            kind: z.literal('standard_attention_items')
        }).and(zAttentionItemsSurface),
        z.object({
            kind: z.literal('recent_ready')
        }).and(zRecentReadySurface),
        z.object({
            kind: z.literal('active_work')
        }).and(zActiveWorkSurface),
        z.object({
            kind: z.literal('fingerprint_status')
        }).and(zFingerprintStatusSurface),
        z.object({
            kind: z.literal('discovery_suggestions')
        }).and(zDiscoverySuggestionsSurface),
        z.object({
            kind: z.literal('discovery_saved_inbox')
        }).and(zDiscoverySavedInboxSurface),
        z.object({
            kind: z.literal('evidence_highlights')
        }).and(zEvidenceHighlightsSurface),
        z.object({
            kind: z.literal('intelligence_critical')
        }).and(zIntelligenceCriticalSurface),
        z.object({
            kind: z.literal('intelligence_patterns')
        }).and(zIntelligencePatternsSurface),
        z.object({
            kind: z.literal('recent_conversations')
        }).and(zRecentConversationsSurface),
        z.object({
            kind: z.literal('notes_activity')
        }).and(zNotesActivitySurface),
        z.object({
            kind: z.literal('notes_unconfirmed_edges')
        }).and(zNotesUnconfirmedEdgesSurface),
        z.object({
            kind: z.literal('notes_connected_artifacts')
        }).and(zNotesConnectedArtifactsSurface),
        z.object({
            kind: z.literal('pinned_collections')
        }).and(zPinnedCollectionsSurface)
    ])),
    workspace: zHomeWorkspaceDto
});

/**
 * WorkspaceItemSchema
 */
export const zWorkspaceItemSchema = z.object({
    abstract: z.string().nullish(),
    authors: z.array(z.string()).nullish(),
    citationCount: z.int().optional().default(0),
    createdAt: z.string().nullish(),
    documentId: z.string().nullish(),
    documentIdentityId: z.string().nullish(),
    doi: z.string().nullish(),
    externalId: z.string().nullish(),
    id: z.string(),
    importedAt: z.string().nullish(),
    source: z.string(),
    status: z.string(),
    title: z.string(),
    type: z.string(),
    url: z.string().nullish(),
    venue: z.string().nullish(),
    workspaceId: z.string(),
    year: z.int().nullish()
});

/**
 * WorkspaceItemsListSchema
 */
export const zWorkspaceItemsListSchema = z.object({
    hasMore: z.boolean().optional().default(false),
    items: z.array(zWorkspaceItemSchema),
    nextCursor: z.string().nullish(),
    prevCursor: z.string().nullish(),
    total: z.int()
});

/**
 * WorkspaceMetadata
 *
 * Workspace metadata for onboarding, analytics, and workspace scaffolding.
 *
 * This data is used for:
 * - User segmentation and analytics
 * - Personalized onboarding flows
 * - Feature recommendations
 * - Sales and support context
 * - Workspace scaffolding (category, research context, templates)
 */
export const zWorkspaceMetadata = z.object({
    category: z.string().max(50).nullish(),
    color: zWorkspaceColor.nullish(),
    companySize: z.string().max(50).nullish(),
    industry: z.string().max(100).nullish(),
    referralSource: z.string().max(100).nullish(),
    researchFields: z.array(z.string()).max(20).nullish(),
    researchGoals: z.array(z.string()).max(10).nullish(),
    researchTopic: z.string().max(300).nullish(),
    sourceTypes: z.array(z.string()).max(20).nullish(),
    templateNames: z.array(z.string()).max(20).nullish(),
    useCase: z.string().max(200).nullish()
});

/**
 * WorkspaceCreate
 *
 * Create a new workspace.
 *
 * Nova sends: { name, description?, metadata?, initialCollections? }
 */
export const zWorkspaceCreate = z.object({
    description: z.string().max(500).nullish(),
    initialCollections: z.array(zInitialCollection).max(20).nullish(),
    metadata: zWorkspaceMetadata.nullish(),
    name: z.string().min(2).max(200),
    plan: zPlanTier.optional().default('free'),
    timezone: z.string().optional().default('UTC')
});

/**
 * WorkspaceRead
 *
 * Workspace response.
 *
 * Nova expects: { id, name, description?, type: 'personal'|'team', owner_id, member_count?, created_at, updated_at }
 */
export const zWorkspaceRead = z.object({
    createdAt: z.iso.datetime(),
    description: z.string().nullish(),
    id: z.uuid(),
    memberCount: z.int().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    name: z.string(),
    ownerId: z.uuid().nullish(),
    plan: zPlanTier.optional().default('free'),
    timezone: z.string().optional().default('UTC'),
    type: z.string(),
    updatedAt: z.iso.datetime()
});

/**
 * WorkspaceRole
 *
 * Workspace role hierarchy for RBAC.
 *
 * Permission Levels (descending):
 * - OWNER: Full control including billing, workspace deletion, ownership transfer
 * - ADMIN: Manage users, approve content, configure settings
 * - EDITOR: Create and manage content but no user or billing permissions
 * - RESEARCHER: Read-only access for analytics and reporting (no content creation)
 * - VIEWER: Can view content but cannot create or edit (for external stakeholders)
 *
 * Note: Permission checks are enforced at the API layer using
 * `require_role(WorkspaceRole.ADMIN)` decorators.
 */
export const zWorkspaceRole = z.enum([
    'owner',
    'admin',
    'editor',
    'researcher',
    'viewer'
]);

/**
 * UserWorkspaceRoleCreate
 *
 * Assign a user to a workspace with a role.
 */
export const zUserWorkspaceRoleCreate = z.object({
    role: zWorkspaceRole,
    user_id: z.uuid(),
    workspace_id: z.uuid()
});

/**
 * UserWorkspaceRoleUpdate
 *
 * Update user's role in workspace.
 */
export const zUserWorkspaceRoleUpdate = z.object({
    role: zWorkspaceRole
});

/**
 * WorkspaceCreateResponse
 *
 * Response for workspace creation.
 *
 * Includes:
 * - workspace: Full workspace details
 * - role: Creator's role (always 'owner')
 * - membership_id: UserWorkspaceRole record ID
 */
export const zWorkspaceCreateResponse = z.object({
    membership_id: z.uuid(),
    role: zWorkspaceRole,
    workspace: zWorkspaceRead
});

/**
 * WorkspaceMemberRead
 *
 * Workspace member as expected by Nova frontend.
 *
 * Nova expects: { id, user_id, workspace_id, role, email, first_name?, last_name?, joined_at }
 */
export const zWorkspaceMemberRead = z.object({
    createdAt: z.iso.datetime(),
    email: z.email().nullish(),
    firstName: z.string().nullish(),
    id: z.uuid(),
    joinedAt: z.iso.datetime().nullish(),
    lastName: z.string().nullish(),
    role: zWorkspaceRole,
    updatedAt: z.iso.datetime(),
    userId: z.uuid(),
    workspaceId: z.uuid()
});

/**
 * PaginatedResponse[WorkspaceMemberRead]
 */
export const zPaginatedResponseWorkspaceMemberRead = z.object({
    items: z.array(zWorkspaceMemberRead),
    pagination: zPaginationMeta
});

/**
 * WorkspaceStatsResponse
 *
 * Aggregate workspace statistics for the dashboard.
 */
export const zWorkspaceStatsResponse = z.object({
    collectionCount: z.int().optional().default(0),
    conversationCount: z.int().optional().default(0),
    documentCount: z.int().optional().default(0),
    memberCount: z.int().optional().default(0),
    noteCount: z.int().optional().default(0),
    sessionCount: z.int().optional().default(0),
    workspaceId: z.uuid()
});

/**
 * WorkspaceSummary
 *
 * Minimal workspace info sent in the sync response.
 */
export const zWorkspaceSummary = z.object({
    id: z.uuid(),
    name: z.string(),
    org_id: z.uuid().nullish(),
    slug: z.string()
});

/**
 * SyncUserResponse
 *
 * Full response from POST /auth/sync and POST /auth/register.
 *
 * Contains everything the client needs to bootstrap the app session:
 * - user profile (same shape as UserRead)
 * - default org/workspace IDs for routing
 * - org + workspace summaries for immediate UI rendering
 */
export const zSyncUserResponse = z.object({
    active_workspace_id: z.uuid().nullish(),
    avatar_asset_id: z.uuid().nullish(),
    avatar_url: z.string().nullish(),
    created_at: z.iso.datetime().nullish(),
    default_org_id: z.uuid().nullish(),
    default_workspace: zWorkspaceSummary.nullish(),
    default_workspace_id: z.uuid().nullish(),
    email: z.email(),
    email_verified: z.boolean(),
    first_name: z.string().nullish(),
    id: z.uuid(),
    last_login_at: z.iso.datetime().nullish(),
    last_name: z.string().nullish(),
    onboarding_completed: z.boolean().optional().default(false),
    onboarding_step: zOnboardingStep.nullish(),
    organizations: z.array(zOrgSummary).optional(),
    pending_invitation_count: z.int().optional().default(0),
    status: zUserStatus,
    updated_at: z.iso.datetime().nullish()
});

/**
 * WorkspaceUpdate
 *
 * Update workspace settings.
 *
 * Nova sends: { name?, description? }
 */
export const zWorkspaceUpdate = z.object({
    description: z.string().max(500).nullish(),
    metadata: zWorkspaceMetadata.nullish(),
    name: z.string().min(2).max(200).nullish(),
    plan: zPlanTier.nullish(),
    timezone: z.string().nullish()
});

/**
 * WorkspaceWithRoleResponse
 *
 * Workspace with the current user's role — returned by list-my-workspaces.
 */
export const zWorkspaceWithRoleResponse = z.object({
    role: zWorkspaceRole,
    workspace: zWorkspaceRead
});

/**
 * PaginatedResponse[WorkspaceWithRoleResponse]
 */
export const zPaginatedResponseWorkspaceWithRoleResponse = z.object({
    items: z.array(zWorkspaceWithRoleResponse),
    pagination: zPaginationMeta
});

/**
 * WritingTone
 */
export const zWritingTone = z.enum([
    'formal',
    'neutral',
    'conversational'
]);

/**
 * AISettingsRead
 *
 * AI settings as returned by the API.
 */
export const zAiSettingsRead = z.object({
    citation_style: zCitationStyle.optional().default('apa7'),
    default_chat_model: z.string().max(100).nullish(),
    preferred_sources: z.array(z.string()).optional(),
    research_mode: zResearchMode.optional().default('balanced'),
    source_credibility: zSourceCredibility.optional().default('moderate'),
    summary_depth: zSummaryDepth.optional().default('standard'),
    transparency_level: zTransparencyLevel.optional().default('standard'),
    writing_tone: zWritingTone.optional().default('neutral')
});

/**
 * AISettingsUpdate
 *
 * Partial update for AI settings. All fields optional. Enum-validated.
 */
export const zAiSettingsUpdate = z.object({
    citation_style: zCitationStyle.nullish(),
    default_chat_model: z.string().max(100).nullish(),
    preferred_sources: z.array(z.string()).nullish(),
    research_mode: zResearchMode.nullish(),
    source_credibility: zSourceCredibility.nullish(),
    summary_depth: zSummaryDepth.nullish(),
    transparency_level: zTransparencyLevel.nullish(),
    writing_tone: zWritingTone.nullish()
});

/**
 * UserSettingsRead
 *
 * Full user settings response — matches Nova's UserSettingsDTO.
 *
 * This is what GET /users/me/settings returns.
 */
export const zUserSettingsRead = z.object({
    ai: zAiSettingsRead,
    notifications: zNotificationSettingsRead,
    privacy: zPrivacySettingsRead,
    profile: zProfileSettingsRead,
    updated_at: z.iso.datetime(),
    version: z.int()
});

/**
 * Successful Response
 */
export const zGetAdminAnalyticsBillingOverviewResponse = zBillingOverview;

export const zGetAdminAnalyticsBillingUsageQuery = z.object({
    period: z.string().regex(/^\d{4}-\d{2}$/).nullish()
});

/**
 * Successful Response
 */
export const zGetAdminAnalyticsBillingUsageResponse = zUsageBreakdown;

export const zGetAdminAnalyticsDauTrendQuery = z.object({
    days: z.int().gte(7).lte(365).optional().default(90)
});

/**
 * Successful Response
 */
export const zGetAdminAnalyticsDauTrendResponse = zDauTrendResponse;

/**
 * Successful Response
 */
export const zGetAdminAnalyticsPlanDistributionResponse = zPlanDistributionResponse;

/**
 * Successful Response
 */
export const zGetAdminAnalyticsSummaryResponse = zPlatformSummary;

export const zGetAdminAnalyticsTopWorkspacesQuery = z.object({
    sort_by: z.enum(['documents', 'cost']).optional().default('documents'),
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zGetAdminAnalyticsTopWorkspacesResponse = zTopWorkspacesResponse;

export const zPostAdminAuthUsersUserIdDisableMfaBody = zDisableMfa;

export const zPostAdminAuthUsersUserIdDisableMfaPath = z.object({
    user_id: z.uuid()
});

/**
 * MFA disable result
 */
export const zPostAdminAuthUsersUserIdDisableMfaResponse = zDisableMfaResponse;

export const zPostAdminAuthUsersUserIdGrantSuperuserBody = zSuperuserGrant;

export const zPostAdminAuthUsersUserIdGrantSuperuserPath = z.object({
    user_id: z.uuid()
});

/**
 * Updated user
 */
export const zPostAdminAuthUsersUserIdGrantSuperuserResponse = zUserReadFull;

export const zPostAdminAuthUsersUserIdMfaDisableInitiateBody = zDisableMfaInitiate;

export const zPostAdminAuthUsersUserIdMfaDisableInitiatePath = z.object({
    user_id: z.uuid()
});

/**
 * OTP generation confirmation
 */
export const zPostAdminAuthUsersUserIdMfaDisableInitiateResponse = zMfaDisableInitiateResponse;

export const zPostAdminAuthUsersUserIdReactivatePath = z.object({
    user_id: z.uuid()
});

/**
 * Reactivated user
 */
export const zPostAdminAuthUsersUserIdReactivateResponse = zUserReadFull;

export const zPostAdminAuthUsersUserIdSuspendBody = zSuspendUser;

export const zPostAdminAuthUsersUserIdSuspendPath = z.object({
    user_id: z.uuid()
});

/**
 * Suspended user
 */
export const zPostAdminAuthUsersUserIdSuspendResponse = zUserReadFull;

export const zPostAdminAuthUsersUserIdUnlockBody = zUnlockUserAccount;

export const zPostAdminAuthUsersUserIdUnlockPath = z.object({
    user_id: z.uuid()
});

/**
 * Unlocked user
 */
export const zPostAdminAuthUsersUserIdUnlockResponse = zUserReadFull;

export const zGetAdminCommandCenterDeadLettersQuery = z.object({
    job_function: z.string().nullish(),
    unreplayed_only: z.boolean().optional().default(true),
    limit: z.int().gte(1).lte(200).optional().default(20),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetAdminCommandCenterDeadLettersResponse = zPaginatedDeadLetters;

export const zPostAdminCommandCenterDeadLettersReplayAllQuery = z.object({
    job_function: z.string().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zPostAdminCommandCenterDeadLettersReplayAllResponse = zDeadLetterBulkReplayResponse;

export const zPostAdminCommandCenterDeadLettersDeadLetterIdReplayPath = z.object({
    dead_letter_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminCommandCenterDeadLettersDeadLetterIdReplayResponse = zDeadLetterReplayResponse;

export const zGetAdminCommandCenterIngestionJobsQuery = z.object({
    status: z.string().nullish(),
    workspace_id: z.uuid().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(20),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetAdminCommandCenterIngestionJobsResponse = zPaginatedIngestionJobs;

/**
 * Successful Response
 */
export const zGetAdminCommandCenterOverviewResponse = zCommandCenterOverview;

/**
 * Successful Response
 */
export const zGetAdminCommandCenterReaperStatusResponse = zReaperStatus;

export const zGetAdminCommandCenterTimelineQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetAdminCommandCenterTimelineResponse = zPaginatedResponseTimelineEvent;

/**
 * Configuration object
 */
export const zGetAdminConfigResponse = zConfigurationResponse;

export const zGetAdminDeadLetterQuery = z.object({
    unreplayed_only: z.boolean().optional().default(true),
    job_function: z.string().nullish(),
    workspace_id: z.uuid().nullish(),
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetAdminDeadLetterResponse = zPaginatedResponseDeadLetterListItem;

export const zPostAdminDeadLetterReplayBulkBody = zDeadLetterBulkReplayRequest;

/**
 * Successful Response
 */
export const zPostAdminDeadLetterReplayBulkResponse = zDeadLetterBulkReplayResult;

export const zGetAdminDeadLetterDeadLetterIdPath = z.object({
    dead_letter_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetAdminDeadLetterDeadLetterIdResponse = zDeadLetterDetailView;

export const zPostAdminDeadLetterDeadLetterIdMarkReplayedBody = zDeadLetterReplayRequest;

export const zPostAdminDeadLetterDeadLetterIdMarkReplayedPath = z.object({
    dead_letter_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminDeadLetterDeadLetterIdMarkReplayedResponse = zDeadLetterReplayResult;

export const zPostAdminDeadLetterDeadLetterIdReplayBody = zDeadLetterReplayRequest;

export const zPostAdminDeadLetterDeadLetterIdReplayPath = z.object({
    dead_letter_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminDeadLetterDeadLetterIdReplayResponse = zDeadLetterReplayResult;

/**
 * Grouped endpoint list
 */
export const zGetAdminEndpointsResponse = zEndpointListResponse;

export const zGetAdminFeedbackQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    category: z.string().nullish(),
    status: z.string().nullish(),
    priority: z.string().nullish(),
    source: z.string().nullish(),
    fromTs: z.iso.datetime().nullish(),
    toTs: z.iso.datetime().nullish(),
    search: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zGetAdminFeedbackResponse = zFeedbackListResponse;

/**
 * Successful Response
 */
export const zGetAdminFeedbackStatsResponse = zFeedbackStats;

export const zGetAdminFeedbackFeedbackIdPath = z.object({
    feedback_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetAdminFeedbackFeedbackIdResponse = zFeedbackRead;

export const zPatchAdminFeedbackFeedbackIdBody = zFeedbackUpdate;

export const zPatchAdminFeedbackFeedbackIdPath = z.object({
    feedback_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchAdminFeedbackFeedbackIdResponse = zFeedbackRead;

/**
 * Successful Response
 */
export const zGetAdminHealthDbDetailResponse = zDatabaseHealthDetailResponse;

/**
 * Readiness status
 */
export const zGetAdminHealthReadyResponse = zReadinessCheckResponse;

/**
 * Successful Response
 */
export const zGetAdminHealthRedisDetailResponse = zRedisHealthDetailResponse;

/**
 * Successful Response
 */
export const zGetAdminHealthWorkersDetailResponse = zWorkerHeartbeatHealthResponse;

/**
 * System runtime info
 */
export const zGetAdminInfoResponse = zSystemInfoResponse;

/**
 * Successful Response
 */
export const zPostAdminMaintenanceBackfillSearchEligibilityResponse = zBackfillTriggerResponse;

export const zGetAdminMaintenanceDigestQuery = z.object({
    hours: z.int().optional().default(24)
});

/**
 * Successful Response
 */
export const zGetAdminMaintenanceDigestResponse = zMaintenanceDigestResponse;

/**
 * Successful Response
 */
export const zGetAdminMaintenancePgCronResponse = zPgCronHealthResponse;

/**
 * Successful Response
 */
export const zPostAdminMaintenanceRollbackSearchEligibilityBackfillResponse = zBackfillTriggerResponse;

export const zGetAdminMaintenanceRunsQuery = z.object({
    job_name: z.string().nullish(),
    status: z.string().nullish(),
    date_from: z.iso.datetime().nullish(),
    date_to: z.iso.datetime().nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(50)
});

/**
 * Successful Response
 */
export const zGetAdminMaintenanceRunsResponse = zPaginatedResponseMaintenanceJobRunResponse;

/**
 * Successful Response
 */
export const zGetAdminObservabilityApdexResponse = zApdexDashboard;

/**
 * Successful Response
 */
export const zGetAdminObservabilityCircuitBreakersResponse = zCircuitBreakerDashboard;

/**
 * Successful Response
 */
export const zGetAdminObservabilityDoraResponse = zDoraDashboard;

/**
 * Successful Response
 */
export const zGetAdminObservabilityErrorBudgetsResponse = zErrorBudgetDashboard;

/**
 * Successful Response
 */
export const zGetAdminObservabilityServicesResponse = zServicesDashboard;

/**
 * Successful Response
 */
export const zGetAdminObservabilitySlosResponse = zSloDashboard;

export const zGetAdminPipelineDocumentDocumentIdPath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetAdminPipelineDocumentDocumentIdResponse = zDocumentPipelineDetail;

export const zPostAdminPipelineDocumentDocumentIdRetryPath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminPipelineDocumentDocumentIdRetryResponse = zDocumentRetryResponse;

export const zPostAdminPipelineOutboxRequeueDeadLettersBody = zRequeueDeadLettersRequest;

/**
 * Successful Response
 */
export const zPostAdminPipelineOutboxRequeueDeadLettersResponse = zRequeueDeadLettersResponse;

/**
 * Successful Response
 */
export const zGetAdminPipelineOutboxStatsResponse = zOutboxStats;

/**
 * Successful Response
 */
export const zGetAdminPipelineOverviewResponse = zPipelineOverview;

export const zGetAdminPipelineStuckQuery = z.object({
    minutes_threshold: z.int().gte(5).lte(1440).optional().default(10),
    limit: z.int().gte(1).lte(100).optional().default(20),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetAdminPipelineStuckResponse = zPaginatedStuckDocuments;

/**
 * Admin settings
 */
export const zGetAdminSettingsResponse = zAdminSettingsResponse;

/**
 * Feature flag states
 */
export const zGetAdminSettingsFeatureFlagsResponse = zFeatureFlagsResponse;

/**
 * Response Get Admin Staff Users
 *
 * Successful Response
 */
export const zGetAdminStaffUsersResponse = z.array(zStaffRoleResponse);

export const zPostAdminStaffUsersUserIdGrantRoleBody = zGrantStaffRoleRequest;

export const zPostAdminStaffUsersUserIdGrantRolePath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminStaffUsersUserIdGrantRoleResponse = zStaffRoleResponse;

export const zPostAdminStaffUsersUserIdRevokeRoleBody = zRevokeStaffRoleRequest;

export const zPostAdminStaffUsersUserIdRevokeRolePath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminStaffUsersUserIdRevokeRoleResponse = zStaffRoleResponse;

/**
 * Database statistics
 */
export const zGetAdminStatsDatabaseResponse = zDatabaseStatsResponse;

/**
 * System status object
 */
export const zGetAdminStatusResponse = zAdminSystemStatusResponse;

/**
 * User service status
 */
export const zGetAdminUserHealthResponse = zUserHealthResponse;

export const zGetAdminUsersQuery = z.object({
    search: z.string().nullish(),
    status: z.string().nullish(),
    limit: z.int().gte(1).lte(100).optional().default(20),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetAdminUsersResponse = zAdminPaginatedResponse;

export const zGetAdminUsersUserIdPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetAdminUsersUserIdResponse = zAdminUserDetail;

export const zGetAdminUsersUserIdApiKeysPath = z.object({
    user_id: z.uuid()
});

/**
 * Response Get Admin Users User Id Api-Keys
 *
 * Successful Response
 */
export const zGetAdminUsersUserIdApiKeysResponse = z.array(zAdminApiKeyRead);

export const zDeleteAdminUsersUserIdApiKeysKeyIdPath = z.object({
    user_id: z.uuid(),
    key_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteAdminUsersUserIdApiKeysKeyIdResponse = zApiKeyRevokeResult;

export const zPostAdminUsersUserIdDisablePath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminUsersUserIdDisableResponse = zUserStatusChangeResponse;

export const zPostAdminUsersUserIdEnablePath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminUsersUserIdEnableResponse = zUserStatusChangeResponse;

export const zGetAdminUsersUserIdSessionsPath = z.object({
    user_id: z.uuid()
});

/**
 * Response Get Admin Users User Id Sessions
 *
 * Successful Response
 */
export const zGetAdminUsersUserIdSessionsResponse = z.array(zAdminSessionRead);

export const zPostAdminUsersUserIdSessionsRevokeAllPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminUsersUserIdSessionsRevokeAllResponse = zSessionRevokeResult;

export const zPostAdminUsersUserIdSessionsSessionIdRevokePath = z.object({
    user_id: z.uuid(),
    session_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostAdminUsersUserIdSessionsSessionIdRevokeResponse = zSessionRevokeResult;

/**
 * Version and build details
 */
export const zGetAdminVersionResponse = zVersionInfoResponse;

/**
 * Successful Response
 */
export const zGetAdminWorkersBreakersResponse = zCircuitBreakerStatusResponse;

export const zPostAdminWorkersBreakersNameResetPath = z.object({
    name: z.string()
});

/**
 * Successful Response
 */
export const zPostAdminWorkersBreakersNameResetResponse = zCircuitBreakerResetResponse;

/**
 * Worker metrics
 */
export const zGetAdminWorkersMetricsResponse = zWorkerMetricsResponse;

/**
 * Worker and queue status
 */
export const zGetAdminWorkersStatusResponse = zSystemHealthResponse;

/**
 * Workspace service status
 */
export const zGetAdminWorkspacesResponse = zWorkspaceHealthResponse;

export const zGetAdminWorkspacesListQuery = z.object({
    search: z.string().nullish(),
    plan: z.string().nullish(),
    limit: z.int().gte(1).lte(100).optional().default(20),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetAdminWorkspacesListResponse = zAdminPaginatedResponse;

export const zGetAdminWorkspacesListWorkspaceIdPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetAdminWorkspacesListWorkspaceIdResponse = zAdminWorkspaceDetail;

export const zPostApiV1AiExtractHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1AiExtractQuery = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1AiExtractResponse = zAiExtractionResponse;

export const zPostApiV1AnalyticsEmbedTokenBody = zEmbedTokenRequest;

export const zPostApiV1AnalyticsEmbedTokenHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1AnalyticsEmbedTokenResponse = zEmbedTokenResponse;

export const zGetApiV1AnalyticsHealthHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1AnalyticsHealthResponse = zAnalyticsHealthResponse;

export const zGetApiV1AnalyticsQualityHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1AnalyticsQualityQuery = z.object({
    days: z.int().optional().default(30)
});

/**
 * Successful Response
 */
export const zGetApiV1AnalyticsQualityResponse = zQualityDashboardResponse;

export const zPostApiV1AssetsBody = zAssetCreateRequest;

export const zPostApiV1AssetsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Asset record
 */
export const zPostApiV1AssetsResponse = zAssetResponse;

export const zGetApiV1AssetsChecksumChecksumPath = z.object({
    checksum: z.string()
});

/**
 * Response Get Api V1 Assets Checksum Checksum
 *
 * Asset or null
 */
export const zGetApiV1AssetsChecksumChecksumResponse = zAssetResponse.nullable();

export const zGetApiV1AssetsDocumentDocumentIdPath = z.object({
    document_id: z.uuid()
});

/**
 * Document asset list
 */
export const zGetApiV1AssetsDocumentDocumentIdResponse = zAssetListResponse;

export const zGetApiV1AssetsExportUserIdPath = z.object({
    user_id: z.uuid()
});

/**
 * User asset export
 */
export const zGetApiV1AssetsExportUserIdResponse = zAssetExportResponse;

export const zPostApiV1AssetsPresignedUploadUrlBody = zAssetUploadRequest;

/**
 * Presigned upload URL
 */
export const zPostApiV1AssetsPresignedUploadUrlResponse = zPresignedUploadResponse;

/**
 * Asset statistics
 */
export const zGetApiV1AssetsStatsResponse = zAssetStatsResponse;

export const zGetApiV1AssetsUserUserIdAvatarPath = z.object({
    user_id: z.uuid()
});

/**
 * Response Get Api V1 Assets User User Id Avatar
 *
 * User avatar
 */
export const zGetApiV1AssetsUserUserIdAvatarResponse = zAssetResponse.nullable();

export const zGetApiV1AssetsWorkspaceWorkspaceIdPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1AssetsWorkspaceWorkspaceIdQuery = z.object({
    asset_type: z.string().nullish()
});

/**
 * Workspace asset list
 */
export const zGetApiV1AssetsWorkspaceWorkspaceIdResponse = zAssetListResponse;

/**
 * Payload
 */
export const zDeleteApiV1AssetsAssetIdBody = zAssetDeleteRequest.nullable();

export const zDeleteApiV1AssetsAssetIdPath = z.object({
    asset_id: z.uuid()
});

/**
 * Deleted asset
 */
export const zDeleteApiV1AssetsAssetIdResponse = zAssetResponse;

export const zGetApiV1AssetsAssetIdPath = z.object({
    asset_id: z.uuid()
});

/**
 * Asset record
 */
export const zGetApiV1AssetsAssetIdResponse = zAssetResponse;

export const zGetApiV1AssetsAssetIdHistoryPath = z.object({
    asset_id: z.uuid()
});

/**
 * Asset history
 */
export const zGetApiV1AssetsAssetIdHistoryResponse = zAssetHistoryResponse;

/**
 * Payload
 */
export const zPostApiV1AssetsAssetIdRestoreBody = zAssetRestoreRequest.nullable();

export const zPostApiV1AssetsAssetIdRestorePath = z.object({
    asset_id: z.uuid()
});

/**
 * Restored asset
 */
export const zPostApiV1AssetsAssetIdRestoreResponse = zAssetResponse;

/**
 * Logout successful
 */
export const zPostApiV1AuthLogoutResponse = z.void();

/**
 * Token claims retrieved successfully
 */
export const zGetApiV1AuthMeResponse = zTokenClaimsResponse;

/**
 * MFA enrollment synced
 */
export const zPostApiV1AuthMfaSyncResponse = zMfaSyncResponse;

export const zPostApiV1AuthRegisterBody = zRegisterRequest;

export const zPostApiV1AuthRegisterHeaders = z.object({
    'x-turnstile-token': z.string().nullish()
});

/**
 * User registered successfully
 */
export const zPostApiV1AuthRegisterResponse = zSyncUserResponse;

/**
 * Active sessions
 */
export const zGetApiV1AuthSessionsActiveResponse = zActiveSessionsList;

/**
 * Pattern analysis
 */
export const zGetApiV1AuthSessionsAnalysisResponse = zSessionPatternAnalysis;

export const zGetApiV1AuthSessionsHistoryQuery = z.object({
    status: zAuthSessionStatus.nullish(),
    suspicious_only: z.boolean().optional().default(false),
    trusted_only: z.boolean().optional().default(false),
    ip_address: z.string().nullish(),
    device_type: z.string().nullish(),
    page: z.int().gte(1).lte(500).optional().default(1),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    count_total: z.boolean().optional().default(false)
});

/**
 * Login history
 */
export const zGetApiV1AuthSessionsHistoryResponse = zPaginatedResponseUserSessionRead;

export const zPostApiV1AuthSessionsRevokeBody = zSessionRevokeRequest;

/**
 * Session revoked
 */
export const zPostApiV1AuthSessionsRevokeResponse = zSessionRevokeResponse;

/**
 * Sessions revoked
 */
export const zPostApiV1AuthSessionsRevokeAllResponse = zSessionRevokeAllResponse;

export const zPostApiV1AuthSessionsTrustDeviceBody = zDeviceTrustRequest;

/**
 * Trust updated
 */
export const zPostApiV1AuthSessionsTrustDeviceResponse = zUserSessionRead;

export const zGetApiV1AuthSessionsSessionIdPath = z.object({
    session_id: z.uuid()
});

/**
 * Session details
 */
export const zGetApiV1AuthSessionsSessionIdResponse = zUserSessionRead;

/**
 * Body
 */
export const zPostApiV1AuthSyncBody = zSyncRequest.nullable();

/**
 * User synced successfully
 */
export const zPostApiV1AuthSyncResponse = zSyncUserResponse;

export const zGetApiV1BillingAddonsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1BillingAddonsResponse = zActiveAddOnsResponse;

export const zGetApiV1BillingAddonsCatalogHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1BillingAddonsCatalogResponse = zAddOnCatalogResponse;

export const zPostApiV1BillingAddonsCheckoutBody = zCreateAddOnCheckoutRequest;

export const zPostApiV1BillingAddonsCheckoutHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1BillingAddonsCheckoutResponse = zAddOnCheckoutResponse;

export const zGetApiV1BillingAddonsRecommendationHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Response Get Api V1 Billing Addons Recommendation
 *
 * Successful Response
 */
export const zGetApiV1BillingAddonsRecommendationResponse = zAddOnRecommendation.nullable();

export const zDeleteApiV1BillingAddonsAddonIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1BillingAddonsAddonIdPath = z.object({
    addon_id: z.string()
});

/**
 * Successful Response
 */
export const zDeleteApiV1BillingAddonsAddonIdResponse = zAddOnRead;

export const zPutApiV1BillingAdminPricingBody = zUpdateModelPricingRequest;

export const zPutApiV1BillingAdminPricingHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPutApiV1BillingAdminPricingResponse = zModelPricingResponse;

export const zGetApiV1BillingAdminPricingAuditHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1BillingAdminPricingAuditQuery = z.object({
    stale_days: z.int().gte(1).lte(365).optional().default(90)
});

/**
 * Successful Response
 */
export const zGetApiV1BillingAdminPricingAuditResponse = zPricingAuditResponse;

export const zGetApiV1BillingAdminPricingVerificationHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1BillingAdminPricingVerificationResponse = zPricingVerificationResponse;

export const zGetApiV1BillingAuditExportHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1BillingAuditExportQuery = z.object({
    period: z.string().optional().default('current'),
    format: z.string().optional().default('json'),
    page_size: z.int().gte(1).lte(10000).optional().default(1000),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1BillingAuditExportResponse = zAuditExportResponse;

export const zPostApiV1BillingCheckoutSessionBody = zCreateCheckoutSessionRequest;

export const zPostApiV1BillingCheckoutSessionHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1BillingCheckoutSessionResponse = zCheckoutSessionResponse;

export const zGetApiV1BillingPlanHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1BillingPlanResponse = zBillingPlanResponse;

/**
 * Successful Response
 */
export const zGetApiV1BillingPlansResponse = zPlanCatalogResponse;

export const zPostApiV1BillingPortalHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1BillingPortalResponse = zBillingPortalResponse;

export const zPostApiV1BillingSeatsUpdateBody = zSeatUpdateRequest;

export const zPostApiV1BillingSeatsUpdateHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1BillingSeatsUpdateResponse = zSeatUpdateResponse;

export const zGetApiV1BillingUsageHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1BillingUsageQuery = z.object({
    period: z.string().optional().default('current')
});

/**
 * Successful Response
 */
export const zGetApiV1BillingUsageResponse = zUsageResponse;

export const zGetApiV1BillingUsageBreakdownHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1BillingUsageBreakdownQuery = z.object({
    period: z.string().optional().default('current')
});

/**
 * Successful Response
 */
export const zGetApiV1BillingUsageBreakdownResponse = zUsageBreakdownResponse;

export const zGetApiV1BillingUsageHistoryHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1BillingUsageHistoryQuery = z.object({
    periods: z.int().gte(1).lte(24).optional().default(6)
});

/**
 * Successful Response
 */
export const zGetApiV1BillingUsageHistoryResponse = zUsageHistoryResponse;

export const zGetApiV1ChatConversationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ChatConversationsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1ChatConversationsResponse = zConversationListResponse;

export const zDeleteApiV1ChatConversationsConversationIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1ChatConversationsConversationIdPath = z.object({
    conversation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1ChatConversationsConversationIdResponse = z.void();

export const zPatchApiV1ChatConversationsConversationIdBody = zConversationUpdateRequest;

export const zPatchApiV1ChatConversationsConversationIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1ChatConversationsConversationIdPath = z.object({
    conversation_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1ChatConversationsConversationIdResponse = zConversationListItem;

export const zGetApiV1ChatConversationsConversationIdMessagesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ChatConversationsConversationIdMessagesPath = z.object({
    conversation_id: z.uuid()
});

export const zGetApiV1ChatConversationsConversationIdMessagesQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1ChatConversationsConversationIdMessagesResponse = zMessageListResponse;

export const zGetApiV1ChatConversationsConversationIdMessagesMessageIdBibtexHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ChatConversationsConversationIdMessagesMessageIdBibtexPath = z.object({
    conversation_id: z.uuid(),
    message_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1ChatConversationsConversationIdMessagesMessageIdBibtexResponse = z.string();

export const zPostApiV1ChatMessagesMessageIdFeedbackBody = zMessageFeedbackRequest;

export const zPostApiV1ChatMessagesMessageIdFeedbackHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1ChatMessagesMessageIdFeedbackPath = z.object({
    message_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1ChatMessagesMessageIdFeedbackResponse = zMessageFeedbackResponse;

export const zGetApiV1ChatModelsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1ChatModelsResponse = zModelsListResponse;

export const zGetApiV1ChatRunsRunIdDebugHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ChatRunsRunIdDebugPath = z.object({
    run_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1ChatRunsRunIdDebugResponse = zRunDebugRead;

export const zPostApiV1ChatSendBody = zChatSendRequest;

export const zPostApiV1ChatSendHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ChatSuggestionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1ChatSuggestionsResponse = zChatSuggestionsResponse;

export const zGetApiV1CitationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CitationsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish(),
    search: z.string().nullish(),
    sort_by: z.string().optional().default('created_at'),
    sort_order: z.string().optional().default('desc')
});

/**
 * Paginated list of citations.
 */
export const zGetApiV1CitationsResponse = zPaginatedResponseCitationRead;

export const zPostApiV1CitationsBody = zCitationCreate;

export const zPostApiV1CitationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * The newly created citation.
 */
export const zPostApiV1CitationsResponse = zCitationRead;

export const zGetApiV1CitationsDuplicatesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * List of duplicate citation alerts.
 */
export const zGetApiV1CitationsDuplicatesResponse = zPaginatedResponseDuplicateAlertRead;

export const zGetApiV1CitationsExportHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CitationsExportQuery = z.object({
    format: zCitationExportFormat,
    ids: z.array(z.uuid()).nullish()
});

export const zGetApiV1CitationsMetricsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Citation metrics for the workspace.
 */
export const zGetApiV1CitationsMetricsResponse = zCitationMetricsRead;

export const zGetApiV1CitationsSuggestionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * List of AI-generated citation suggestions.
 */
export const zGetApiV1CitationsSuggestionsResponse = zPaginatedResponseAiSuggestionRead;

export const zDeleteApiV1CitationsCitationIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1CitationsCitationIdPath = z.object({
    citation_id: z.uuid()
});

/**
 * Confirmation with the deleted citation ID.
 */
export const zDeleteApiV1CitationsCitationIdResponse = zResourceDeleteResponse;

export const zGetApiV1CitationsCitationIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CitationsCitationIdPath = z.object({
    citation_id: z.uuid()
});

/**
 * The requested citation.
 */
export const zGetApiV1CitationsCitationIdResponse = zCitationRead;

export const zPatchApiV1CitationsCitationIdBody = zCitationUpdate;

export const zPatchApiV1CitationsCitationIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1CitationsCitationIdPath = z.object({
    citation_id: z.uuid()
});

/**
 * The updated citation.
 */
export const zPatchApiV1CitationsCitationIdResponse = zCitationRead;

export const zGetApiV1CollectionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    collection_type: z.string().nullish(),
    include_archived: z.boolean().optional().default(false),
    is_pinned: z.boolean().nullish(),
    search: z.string().max(200).nullish(),
    sort_by: z.string().optional().default('name'),
    sort_order: z.string().optional().default('asc')
});

/**
 * Paginated list of collections
 */
export const zGetApiV1CollectionsResponse = zPaginatedResponseCollectionRead;

export const zPostApiV1CollectionsBody = zCollectionCreate;

export const zPostApiV1CollectionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Collection created successfully
 */
export const zPostApiV1CollectionsResponse = zCollectionRead;

export const zGetApiV1CollectionsRootHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsRootQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(100),
    cursor: z.string().nullish()
});

/**
 * Paginated list of root-level collections
 */
export const zGetApiV1CollectionsRootResponse = zPaginatedResponseCollectionRead;

export const zPostApiV1CollectionsSmartBody = zSmartCollectionCreate;

export const zPostApiV1CollectionsSmartHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * The newly created smart collection.
 */
export const zPostApiV1CollectionsSmartResponse = zCollectionRead;

export const zGetApiV1CollectionsTreeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsTreeQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(200)
});

/**
 * Hierarchical collection tree
 */
export const zGetApiV1CollectionsTreeResponse = zPaginatedResponseCollectionTree;

export const zDeleteApiV1CollectionsCollectionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1CollectionsCollectionIdPath = z.object({
    collection_id: z.uuid()
});

/**
 * Collection deleted
 */
export const zDeleteApiV1CollectionsCollectionIdResponse = z.void();

export const zGetApiV1CollectionsCollectionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsCollectionIdPath = z.object({
    collection_id: z.uuid()
});

/**
 * Collection details
 */
export const zGetApiV1CollectionsCollectionIdResponse = zCollectionRead;

export const zPatchApiV1CollectionsCollectionIdBody = zCollectionUpdate;

export const zPatchApiV1CollectionsCollectionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1CollectionsCollectionIdPath = z.object({
    collection_id: z.uuid()
});

/**
 * Updated collection
 */
export const zPatchApiV1CollectionsCollectionIdResponse = zCollectionRead;

export const zGetApiV1CollectionsCollectionIdItemsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsCollectionIdItemsPath = z.object({
    collection_id: z.uuid()
});

export const zGetApiV1CollectionsCollectionIdItemsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(100),
    cursor: z.string().nullish(),
    sort: z.string().optional().default('manual')
});

/**
 * Paginated list of collection items
 */
export const zGetApiV1CollectionsCollectionIdItemsResponse = zCollectionItemsListResponse;

export const zPostApiV1CollectionsCollectionIdItemsAddBody = zCollectionItemsAdd;

export const zPostApiV1CollectionsCollectionIdItemsAddHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1CollectionsCollectionIdItemsAddPath = z.object({
    collection_id: z.uuid()
});

/**
 * Count of newly added documents
 */
export const zPostApiV1CollectionsCollectionIdItemsAddResponse = zCollectionModifyResponse;

export const zPostApiV1CollectionsCollectionIdItemsRemoveBody = zCollectionItemsRemove;

export const zPostApiV1CollectionsCollectionIdItemsRemoveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1CollectionsCollectionIdItemsRemovePath = z.object({
    collection_id: z.uuid()
});

/**
 * Count of removed documents
 */
export const zPostApiV1CollectionsCollectionIdItemsRemoveResponse = zCollectionModifyResponse;

export const zPostApiV1CollectionsCollectionIdItemsReorderBody = zCollectionItemReorder;

export const zPostApiV1CollectionsCollectionIdItemsReorderHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1CollectionsCollectionIdItemsReorderPath = z.object({
    collection_id: z.uuid()
});

/**
 * Documents reordered successfully
 */
export const zPostApiV1CollectionsCollectionIdItemsReorderResponse = z.void();

export const zPostApiV1CollectionsCollectionIdItemsReorderBetweenBody = zCollectionItemReorderBetween;

export const zPostApiV1CollectionsCollectionIdItemsReorderBetweenHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1CollectionsCollectionIdItemsReorderBetweenPath = z.object({
    collection_id: z.uuid()
});

/**
 * New position of the moved document
 */
export const zPostApiV1CollectionsCollectionIdItemsReorderBetweenResponse = zReorderPositionResponse;

export const zGetApiV1CollectionsCollectionIdNotesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsCollectionIdNotesPath = z.object({
    collection_id: z.uuid()
});

export const zGetApiV1CollectionsCollectionIdNotesQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    search: z.string().max(200).nullish(),
    sort_by: z.string().optional().default('updated_at'),
    sort_order: z.string().optional().default('desc')
});

/**
 * Paginated list of notes in the collection
 */
export const zGetApiV1CollectionsCollectionIdNotesResponse = zCollectionItemsListResponse;

export const zPostApiV1CollectionsCollectionIdNotesAddBody = zCollectionNotesAdd;

export const zPostApiV1CollectionsCollectionIdNotesAddHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1CollectionsCollectionIdNotesAddPath = z.object({
    collection_id: z.uuid()
});

/**
 * Count of notes added
 */
export const zPostApiV1CollectionsCollectionIdNotesAddResponse = zCollectionModifyResponse;

export const zPostApiV1CollectionsCollectionIdNotesRemoveBody = zCollectionNotesRemove;

export const zPostApiV1CollectionsCollectionIdNotesRemoveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1CollectionsCollectionIdNotesRemovePath = z.object({
    collection_id: z.uuid()
});

/**
 * Count of notes removed
 */
export const zPostApiV1CollectionsCollectionIdNotesRemoveResponse = zCollectionModifyResponse;

export const zGetApiV1CollectionsCollectionIdSmartItemsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1CollectionsCollectionIdSmartItemsPath = z.object({
    collection_id: z.uuid()
});

export const zGetApiV1CollectionsCollectionIdSmartItemsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Paginated list of matching documents
 */
export const zGetApiV1CollectionsCollectionIdSmartItemsResponse = zPaginatedResponse;

export const zListBoardsApiV1CompareBoardsGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zListBoardsApiV1CompareBoardsGetQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListBoardsApiV1CompareBoardsGetResponse = zPaginatedResponseCompareBoardRead;

export const zCreateBoardApiV1CompareBoardsPostBody = zCompareBoardCreate;

export const zCreateBoardApiV1CompareBoardsPostHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zCreateBoardApiV1CompareBoardsPostResponse = zCompareBoardRead;

export const zDeleteBoardApiV1CompareBoardsBoardIdDeleteHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteBoardApiV1CompareBoardsBoardIdDeletePath = z.object({
    board_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteBoardApiV1CompareBoardsBoardIdDeleteResponse = z.void();

export const zGetBoardApiV1CompareBoardsBoardIdGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetBoardApiV1CompareBoardsBoardIdGetPath = z.object({
    board_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetBoardApiV1CompareBoardsBoardIdGetResponse = zCompareBoardRead;

export const zPatchBoardApiV1CompareBoardsBoardIdPatchBody = zCompareBoardPatch;

export const zPatchBoardApiV1CompareBoardsBoardIdPatchHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchBoardApiV1CompareBoardsBoardIdPatchPath = z.object({
    board_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchBoardApiV1CompareBoardsBoardIdPatchResponse = zCompareBoardRead;

export const zPostApiV1DeepLinkEncodeBody = zVReference;

export const zPostApiV1DeepLinkEncodeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DeepLinkEncodeResponse = zEncodeResponse;

export const zGetApiV1DeepLinkResolveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DeepLinkResolveQuery = z.object({
    token: z.string().min(1)
});

/**
 * Successful Response
 */
export const zGetApiV1DeepLinkResolveResponse = zDeepLinkResolveResponse;

export const zGetApiV1DeepLinkResolveV2Headers = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DeepLinkResolveV2Query = z.object({
    token: z.string().min(1)
});

/**
 * Successful Response
 */
export const zGetApiV1DeepLinkResolveV2Response = zResolvedReference;

export const zPostApiV1DiscoveryRelevanceBody = zRelevanceScoreRequestSchema;

export const zPostApiV1DiscoveryRelevanceHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DiscoveryRelevanceResponse = zRelevanceScoreResponseSchema;

export const zPostApiV1DiscoverySaveBody = zSaveItemRequestSchema;

export const zPostApiV1DiscoverySaveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DiscoverySaveQuery = z.object({
    workspace_id: z.uuid()
});

/**
 * Saved item record.
 */
export const zPostApiV1DiscoverySaveResponse = zSaveItemResponseSchema;

export const zGetApiV1DiscoverySavedIdsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DiscoverySavedIdsQuery = z.object({
    workspace_id: z.uuid(),
    dois: z.string().max(5000).nullish(),
    urls: z.string().max(5000).nullish()
});

/**
 * Saved identifiers.
 */
export const zGetApiV1DiscoverySavedIdsResponse = zSavedIdsResponseSchema;

export const zPostApiV1DiscoverySearchBody = zDiscoverySearchRequestSchema;

export const zPostApiV1DiscoverySearchHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Search results.
 */
export const zPostApiV1DiscoverySearchResponse = zDiscoverySearchResponseSchema;

export const zPostApiV1DiscoverySynthesizeBody = zSynthesisRequestSchema;

export const zPostApiV1DiscoverySynthesizeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish(),
    collection_id: z.uuid().nullish(),
    search: z.string().nullish(),
    sort_by: z.string().optional().default('created_at'),
    sort_order: z.string().optional().default('desc'),
    trashed: z.boolean().optional().default(false),
    include_count: z.boolean().optional().default(false)
});

/**
 * Paginated list of documents
 */
export const zGetApiV1DocumentsResponse = zPaginatedResponseDocumentDetailRead;

export const zPostApiV1DocumentsBody = zDocumentCreate;

export const zPostApiV1DocumentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * The newly created document detail
 */
export const zPostApiV1DocumentsResponse = zDocumentDetailRead;

export const zPostApiV1DocumentsBatchReprocessBody = zBatchReprocessRequest;

export const zPostApiV1DocumentsBatchReprocessHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Reprocessing jobs enqueued
 */
export const zPostApiV1DocumentsBatchReprocessResponse = zBatchReprocessResponse;

export const zPostApiV1DocumentsBulkDeleteBody = zBulkDocumentIds;

export const zPostApiV1DocumentsBulkDeleteHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsBulkDeleteResponse = zBulkOperationResponse;

export const zPostApiV1DocumentsBulkMoveBody = zBulkMoveRequest;

export const zPostApiV1DocumentsBulkMoveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsBulkMoveResponse = zBulkOperationResponse;

export const zPostApiV1DocumentsBulkTagBody = zBulkTagRequest;

export const zPostApiV1DocumentsBulkTagHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsBulkTagResponse = zBulkOperationResponse;

export const zGetApiV1DocumentsDownloadDocumentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDownloadDocumentIdPath = z.object({
    document_id: z.uuid()
});

/**
 * Presigned download URL
 */
export const zGetApiV1DocumentsDownloadDocumentIdResponse = zDownloadUrlResponse;

export const zPostApiV1DocumentsImportExternalBody = zExternalImportRequest;

export const zPostApiV1DocumentsImportExternalHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsImportExternalResponse = zExternalImportResponse;

export const zPostApiV1DocumentsImportConfirmBody = zImportConfirmRequest;

export const zPostApiV1DocumentsImportConfirmHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsImportConfirmResponse = zImportConfirmResponse;

export const zPostApiV1DocumentsImportPreviewBody = zImportPreviewRequest;

export const zPostApiV1DocumentsImportPreviewHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsImportPreviewResponse = zImportPreviewResponse;

export const zPostApiV1DocumentsUploadUrlBody = zDocumentUploadUrlRequest;

export const zPostApiV1DocumentsUploadUrlHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Presigned upload URL
 */
export const zPostApiV1DocumentsUploadUrlResponse = zDocumentUploadUrlResponse;

export const zGetCitationGraphApiV1DocumentsDocIdCitationGraphGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetCitationGraphApiV1DocumentsDocIdCitationGraphGetPath = z.object({
    doc_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetCitationGraphApiV1DocumentsDocIdCitationGraphGetResponse = zCitationGraphRead;

export const zListEvidenceApiV1DocumentsDocIdEvidenceGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zListEvidenceApiV1DocumentsDocIdEvidenceGetPath = z.object({
    doc_id: z.uuid()
});

export const zListEvidenceApiV1DocumentsDocIdEvidenceGetQuery = z.object({
    type: z.string().nullish(),
    status: z.string().nullish(),
    page_num: z.int().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListEvidenceApiV1DocumentsDocIdEvidenceGetResponse = zPaginatedResponseEvidenceArtifactRead;

export const zCreateManualArtifactApiV1DocumentsDocIdEvidenceManualPostBody = zManualArtifactCreate;

export const zCreateManualArtifactApiV1DocumentsDocIdEvidenceManualPostHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zCreateManualArtifactApiV1DocumentsDocIdEvidenceManualPostPath = z.object({
    doc_id: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateManualArtifactApiV1DocumentsDocIdEvidenceManualPostResponse = zEvidenceArtifactRead;

export const zGetExtractionStatusApiV1DocumentsDocIdExtractionStatusGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetExtractionStatusApiV1DocumentsDocIdExtractionStatusGetPath = z.object({
    doc_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetExtractionStatusApiV1DocumentsDocIdExtractionStatusGetResponse = zExtractionStatusRead;

export const zDeleteApiV1DocumentsDocumentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1DocumentsDocumentIdPath = z.object({
    document_id: z.uuid()
});

export const zDeleteApiV1DocumentsDocumentIdQuery = z.object({
    permanent: z.boolean().optional().default(false)
});

/**
 * Confirmation of deletion with permanent flag
 */
export const zDeleteApiV1DocumentsDocumentIdResponse = zDocumentDeleteResponse;

export const zGetApiV1DocumentsDocumentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdPath = z.object({
    document_id: z.uuid()
});

/**
 * Document detail
 */
export const zGetApiV1DocumentsDocumentIdResponse = zDocumentDetailRead;

export const zPatchApiV1DocumentsDocumentIdBody = zDocumentUpdate;

export const zPatchApiV1DocumentsDocumentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1DocumentsDocumentIdPath = z.object({
    document_id: z.uuid()
});

/**
 * The updated document detail
 */
export const zPatchApiV1DocumentsDocumentIdResponse = zDocumentDetailRead;

export const zGetApiV1DocumentsDocumentIdAiSummaryHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdAiSummaryPath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1DocumentsDocumentIdAiSummaryResponse = zDocumentAiSummaryResponse;

export const zGetApiV1DocumentsDocumentIdAnnotationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdAnnotationsPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1DocumentsDocumentIdAnnotationsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Highlight list
 */
export const zGetApiV1DocumentsDocumentIdAnnotationsResponse = zPaginatedResponseHighlightRead;

export const zPostApiV1DocumentsDocumentIdAnnotationsBody = zHighlightCreate;

export const zPostApiV1DocumentsDocumentIdAnnotationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdAnnotationsPath = z.object({
    document_id: z.uuid()
});

/**
 * Highlight created
 */
export const zPostApiV1DocumentsDocumentIdAnnotationsResponse = zHighlightRead;

export const zDeleteApiV1DocumentsDocumentIdAnnotationsHighlightIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1DocumentsDocumentIdAnnotationsHighlightIdPath = z.object({
    document_id: z.uuid(),
    highlight_id: z.uuid()
});

/**
 * Highlight deleted
 */
export const zDeleteApiV1DocumentsDocumentIdAnnotationsHighlightIdResponse = zResourceDeleteResponse;

export const zPatchApiV1DocumentsDocumentIdAnnotationsHighlightIdBody = zHighlightUpdate;

export const zPatchApiV1DocumentsDocumentIdAnnotationsHighlightIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1DocumentsDocumentIdAnnotationsHighlightIdPath = z.object({
    document_id: z.uuid(),
    highlight_id: z.uuid()
});

/**
 * Highlight updated
 */
export const zPatchApiV1DocumentsDocumentIdAnnotationsHighlightIdResponse = zHighlightRead;

export const zGetApiV1DocumentsDocumentIdArtifactsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdArtifactsPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1DocumentsDocumentIdArtifactsQuery = z.object({
    artifact_type: z.string().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(100),
    cursor: z.string().nullish()
});

/**
 * List of extracted artifacts with metadata
 */
export const zGetApiV1DocumentsDocumentIdArtifactsResponse = zArtifactListResponse;

export const zPostApiV1DocumentsDocumentIdArtifactsArtifactIdAiBody = zArtifactAiRequest;

export const zPostApiV1DocumentsDocumentIdArtifactsArtifactIdAiHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdArtifactsArtifactIdAiPath = z.object({
    document_id: z.uuid(),
    artifact_id: z.uuid()
});

export const zPostApiV1DocumentsDocumentIdAttachPdfBody = zAttachPdfRequest;

export const zPostApiV1DocumentsDocumentIdAttachPdfHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdAttachPdfPath = z.object({
    document_id: z.uuid()
});

/**
 * Presigned upload URL for the PDF attachment
 */
export const zPostApiV1DocumentsDocumentIdAttachPdfResponse = zDocumentUploadUrlResponse;

export const zGetApiV1DocumentsDocumentIdCollectionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdCollectionsPath = z.object({
    document_id: z.uuid()
});

/**
 * Collection IDs containing the document
 */
export const zGetApiV1DocumentsDocumentIdCollectionsResponse = zDocumentCollectionsResponse;

export const zPostApiV1DocumentsDocumentIdCollectionsAddBody = zDocumentCollectionsAdd;

export const zPostApiV1DocumentsDocumentIdCollectionsAddHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdCollectionsAddPath = z.object({
    document_id: z.uuid()
});

/**
 * Count of collections added
 */
export const zPostApiV1DocumentsDocumentIdCollectionsAddResponse = zCollectionModifyResponse;

export const zGetApiV1DocumentsDocumentIdCommentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdCommentsPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1DocumentsDocumentIdCommentsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(100),
    cursor: z.string().nullish()
});

/**
 * Comment list
 */
export const zGetApiV1DocumentsDocumentIdCommentsResponse = zDocumentCommentListResponse;

export const zPostApiV1DocumentsDocumentIdCommentsBody = zDocumentCommentCreate;

export const zPostApiV1DocumentsDocumentIdCommentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdCommentsPath = z.object({
    document_id: z.uuid()
});

/**
 * Comment created
 */
export const zPostApiV1DocumentsDocumentIdCommentsResponse = zDocumentCommentRead;

export const zDeleteApiV1DocumentsDocumentIdCommentsCommentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1DocumentsDocumentIdCommentsCommentIdPath = z.object({
    document_id: z.uuid(),
    comment_id: z.uuid()
});

/**
 * Comment deleted
 */
export const zDeleteApiV1DocumentsDocumentIdCommentsCommentIdResponse = zResourceDeleteResponse;

export const zPatchApiV1DocumentsDocumentIdCommentsCommentIdBody = zDocumentCommentUpdate;

export const zPatchApiV1DocumentsDocumentIdCommentsCommentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1DocumentsDocumentIdCommentsCommentIdPath = z.object({
    document_id: z.uuid(),
    comment_id: z.uuid()
});

/**
 * Comment updated
 */
export const zPatchApiV1DocumentsDocumentIdCommentsCommentIdResponse = zDocumentCommentRead;

export const zPostApiV1DocumentsDocumentIdConfirmUploadBody = zDocumentConfirmUploadRequest;

export const zPostApiV1DocumentsDocumentIdConfirmUploadHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdConfirmUploadPath = z.object({
    document_id: z.uuid()
});

/**
 * Confirmed document detail
 */
export const zPostApiV1DocumentsDocumentIdConfirmUploadResponse = zDocumentDetailRead;

export const zPostApiV1DocumentsDocumentIdExtractHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdExtractPath = z.object({
    document_id: z.uuid()
});

/**
 * Confirmation that extraction job was queued
 */
export const zPostApiV1DocumentsDocumentIdExtractResponse = zExtractionQueuedResponse;

export const zPostApiV1DocumentsDocumentIdMoveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdMovePath = z.object({
    document_id: z.uuid()
});

export const zPostApiV1DocumentsDocumentIdMoveQuery = z.object({
    collection_id: z.uuid().nullish()
});

/**
 * No content on success
 */
export const zPostApiV1DocumentsDocumentIdMoveResponse = z.void();

export const zGetApiV1DocumentsDocumentIdPinsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdPinsPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1DocumentsDocumentIdPinsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Paginated list of pinned extraction artifacts
 */
export const zGetApiV1DocumentsDocumentIdPinsResponse = zPaginatedResponseDocumentPinRead;

export const zPostApiV1DocumentsDocumentIdPinsBody = zDocumentPinCreate;

export const zPostApiV1DocumentsDocumentIdPinsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdPinsPath = z.object({
    document_id: z.uuid()
});

/**
 * The created pin
 */
export const zPostApiV1DocumentsDocumentIdPinsResponse = zDocumentPinRead;

export const zDeleteApiV1DocumentsDocumentIdPinsPinIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1DocumentsDocumentIdPinsPinIdPath = z.object({
    document_id: z.uuid(),
    pin_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1DocumentsDocumentIdPinsPinIdResponse = z.void();

export const zGetApiV1DocumentsDocumentIdPipelineHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdPipelinePath = z.object({
    document_id: z.uuid()
});

/**
 * Pipeline status with current job, steps, and artifact statuses
 */
export const zGetApiV1DocumentsDocumentIdPipelineResponse = zPipelineStatusRead;

export const zGetApiV1DocumentsDocumentIdProgressHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdProgressPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1DocumentsDocumentIdReadingProgressHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdReadingProgressPath = z.object({
    document_id: z.uuid()
});

/**
 * Reading progress for the document
 */
export const zGetApiV1DocumentsDocumentIdReadingProgressResponse = zReadingProgressRead;

export const zPutApiV1DocumentsDocumentIdReadingProgressBody = zReadingProgressUpdate;

export const zPutApiV1DocumentsDocumentIdReadingProgressHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPutApiV1DocumentsDocumentIdReadingProgressPath = z.object({
    document_id: z.uuid()
});

/**
 * Updated reading progress
 */
export const zPutApiV1DocumentsDocumentIdReadingProgressResponse = zReadingProgressRead;

export const zPostApiV1DocumentsDocumentIdReprocessHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdReprocessPath = z.object({
    document_id: z.uuid()
});

/**
 * Document reset and requeued — returns updated document
 */
export const zPostApiV1DocumentsDocumentIdReprocessResponse = zDocumentDetailRead;

export const zPostApiV1DocumentsDocumentIdRestoreHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdRestorePath = z.object({
    document_id: z.uuid()
});

/**
 * The restored document detail
 */
export const zPostApiV1DocumentsDocumentIdRestoreResponse = zDocumentDetailRead;

export const zPostApiV1DocumentsDocumentIdSearchBody = zDocumentSearchRequest;

export const zPostApiV1DocumentsDocumentIdSearchHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdSearchPath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1DocumentsDocumentIdSearchResponse = zDocumentSearchResponse;

export const zGetApiV1DocumentsDocumentIdVersionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1DocumentsDocumentIdVersionsPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1DocumentsDocumentIdVersionsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Version list
 */
export const zGetApiV1DocumentsDocumentIdVersionsResponse = zPaginatedResponseDocumentVersionRead;

export const zPostApiV1DocumentsDocumentIdVersionsBody = zDocumentVersionCreate;

export const zPostApiV1DocumentsDocumentIdVersionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdVersionsPath = z.object({
    document_id: z.uuid()
});

/**
 * Version created
 */
export const zPostApiV1DocumentsDocumentIdVersionsResponse = zDocumentVersionRead;

export const zPostApiV1DocumentsDocumentIdVersionsUploadUrlBody = zDocumentVersionUploadUrlRequest;

export const zPostApiV1DocumentsDocumentIdVersionsUploadUrlHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1DocumentsDocumentIdVersionsUploadUrlPath = z.object({
    document_id: z.uuid()
});

/**
 * Upload URL issued
 */
export const zPostApiV1DocumentsDocumentIdVersionsUploadUrlResponse = zDocumentVersionUploadUrlResponse;

export const zDeleteArtifactApiV1EvidenceArtifactIdDeleteHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteArtifactApiV1EvidenceArtifactIdDeletePath = z.object({
    artifact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteArtifactApiV1EvidenceArtifactIdDeleteResponse = z.void();

export const zPatchArtifactApiV1EvidenceArtifactIdPatchBody = zArtifactPatch;

export const zPatchArtifactApiV1EvidenceArtifactIdPatchHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchArtifactApiV1EvidenceArtifactIdPatchPath = z.object({
    artifact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchArtifactApiV1EvidenceArtifactIdPatchResponse = zEvidenceArtifactRead;

export const zGetApiV1ExtractionTablesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ExtractionTablesQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1ExtractionTablesResponse = zPaginatedResponseExtractionTableSummary;

export const zPostApiV1ExtractionTablesBody = zExtractionTableCreate;

export const zPostApiV1ExtractionTablesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1ExtractionTablesResponse = zExtractionTableDetailRead;

export const zGetApiV1ExtractionTablesColumnTemplatesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1ExtractionTablesColumnTemplatesResponse = zPaginatedResponseColumnTemplateGroupRead;

export const zDeleteApiV1ExtractionTablesTableIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1ExtractionTablesTableIdPath = z.object({
    table_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1ExtractionTablesTableIdResponse = z.void();

export const zGetApiV1ExtractionTablesTableIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ExtractionTablesTableIdPath = z.object({
    table_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1ExtractionTablesTableIdResponse = zExtractionTableDetailRead;

export const zPatchApiV1ExtractionTablesTableIdBody = zExtractionTableUpdate;

export const zPatchApiV1ExtractionTablesTableIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1ExtractionTablesTableIdPath = z.object({
    table_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1ExtractionTablesTableIdResponse = zExtractionTableDetailRead;

export const zPostApiV1ExtractionTablesTableIdColumnsBody = zExtractionColumnCreate;

export const zPostApiV1ExtractionTablesTableIdColumnsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1ExtractionTablesTableIdColumnsPath = z.object({
    table_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1ExtractionTablesTableIdColumnsResponse = zExtractionColumnRead;

export const zDeleteApiV1ExtractionTablesTableIdColumnsColumnIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1ExtractionTablesTableIdColumnsColumnIdPath = z.object({
    table_id: z.uuid(),
    column_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1ExtractionTablesTableIdColumnsColumnIdResponse = z.void();

export const zPostApiV1ExtractionTablesTableIdDocumentsBody = zAddDocumentsRequest;

export const zPostApiV1ExtractionTablesTableIdDocumentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1ExtractionTablesTableIdDocumentsPath = z.object({
    table_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1ExtractionTablesTableIdDocumentsResponse = zTriggerExtractionResponse;

export const zGetApiV1ExtractionTablesTableIdExportCsvHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1ExtractionTablesTableIdExportCsvPath = z.object({
    table_id: z.uuid()
});

export const zPostApiV1ExtractionTablesTableIdExtractHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1ExtractionTablesTableIdExtractPath = z.object({
    table_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1ExtractionTablesTableIdExtractResponse = zTriggerExtractionResponse;

export const zGetApiV1FeedHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1FeedQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1FeedResponse = zFeedListResponse;

export const zGetApiV1FeedPreferencesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1FeedPreferencesResponse = zFeedPreferencesRead;

export const zPutApiV1FeedPreferencesBody = zFeedPreferencesUpdate;

export const zPutApiV1FeedPreferencesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPutApiV1FeedPreferencesResponse = zFeedPreferencesRead;

export const zPostApiV1FeedItemIdDismissHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1FeedItemIdDismissPath = z.object({
    item_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1FeedItemIdDismissResponse = zFeedActionResponse;

export const zPostApiV1GraphEdgesBody = zGraphEdgeCreate;

export const zPostApiV1GraphEdgesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1GraphEdgesResponse = zGraphEdgeResponse;

export const zDeleteApiV1GraphEdgesEdgeIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1GraphEdgesEdgeIdPath = z.object({
    edge_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1GraphEdgesEdgeIdResponse = zGraphEdgeResponse;

export const zGetApiV1GraphNodesNodeIdEdgesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1GraphNodesNodeIdEdgesPath = z.object({
    node_id: z.uuid()
});

export const zGetApiV1GraphNodesNodeIdEdgesQuery = z.object({
    edge_type: z.string().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1GraphNodesNodeIdEdgesResponse = zPaginatedResponseGraphEdgeResponse;

export const zGetApiV1GraphNodesNodeIdNeighboursHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1GraphNodesNodeIdNeighboursPath = z.object({
    node_id: z.uuid()
});

export const zGetApiV1GraphNodesNodeIdNeighboursQuery = z.object({
    edge_type: z.string().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1GraphNodesNodeIdNeighboursResponse = zPaginatedResponseGraphNeighbourNode;

export const zGetApiV1GraphWorkspaceHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1GraphWorkspaceQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(200)
});

/**
 * Successful Response
 */
export const zGetApiV1GraphWorkspaceResponse = zWorkspaceGraphResponse;

export const zGetApiV1IntelligenceScoringWeightsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1IntelligenceScoringWeightsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Paginated weight configuration
 */
export const zGetApiV1IntelligenceScoringWeightsResponse = zPaginatedResponseScoringWeightRead;

export const zGetApiV1IntelligenceTrendingHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1IntelligenceTrendingQuery = z.object({
    limit: z.int().gte(1).lte(50).optional().default(10)
});

/**
 * Trending clusters
 */
export const zGetApiV1IntelligenceTrendingResponse = zTrendingListResponse;

export const zPostApiV1InvitationsAcceptBody = zInvitationAccept;

/**
 * The accepted invitation record.
 */
export const zPostApiV1InvitationsAcceptResponse = zInvitationRead;

export const zPostApiV1InvitationsDeclineBody = zInvitationDecline;

/**
 * The declined invitation record.
 */
export const zPostApiV1InvitationsDeclineResponse = zInvitationRead;

export const zGetApiV1InvitationsMeQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Paginated list of user's pending invitations.
 */
export const zGetApiV1InvitationsMeResponse = zInvitationListResponse;

export const zPostApiV1InvitationsRespondBody = zInvitationRespond;

/**
 * The updated invitation record.
 */
export const zPostApiV1InvitationsRespondResponse = zInvitationRead;

export const zGetApiV1InvitationsVerifyQuery = z.object({
    token: z.string().length(64).regex(/^[0-9a-f]{64}$/)
});

/**
 * Invitation verification result with masked details.
 */
export const zGetApiV1InvitationsVerifyResponse = zInvitationVerify;

export const zGetApiV1LibraryHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1LibraryQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish(),
    search: z.string().nullish(),
    sort_by: z.string().optional().default('recent'),
    sort_direction: z.string().optional().default('desc'),
    has_annotations: z.boolean().nullish(),
    read_status: z.string().nullish(),
    date_from: z.string().nullish(),
    date_to: z.string().nullish()
});

/**
 * Paginated list of library items
 */
export const zGetApiV1LibraryResponse = zPaginatedResponseLibraryItemDto;

export const zDeleteApiV1LibraryDocumentIdCollectionsCollectionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1LibraryDocumentIdCollectionsCollectionIdPath = z.object({
    document_id: z.uuid(),
    collection_id: z.string()
});

/**
 * No content on success
 */
export const zDeleteApiV1LibraryDocumentIdCollectionsCollectionIdResponse = z.void();

export const zPostApiV1LibraryDocumentIdCollectionsCollectionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1LibraryDocumentIdCollectionsCollectionIdPath = z.object({
    document_id: z.uuid(),
    collection_id: z.string()
});

/**
 * No content on success
 */
export const zPostApiV1LibraryDocumentIdCollectionsCollectionIdResponse = z.void();

export const zGetApiV1LibraryItemIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1LibraryItemIdPath = z.object({
    item_id: z.uuid()
});

/**
 * Library item detail
 */
export const zGetApiV1LibraryItemIdResponse = zLibraryItemResponse;

export const zPostApiV1LibraryItemIdStarHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1LibraryItemIdStarPath = z.object({
    item_id: z.uuid()
});

/**
 * Updated star status for the item
 */
export const zPostApiV1LibraryItemIdStarResponse = zToggleStarResponse;

export const zGetApiV1MeFeaturesHeaders = z.object({
    'X-Workspace-ID': z.string().nullish()
});

export const zGetApiV1NotesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish(),
    search: z.string().nullish(),
    sort_by: z.string().optional().default('updated_at'),
    sort_order: z.string().regex(/^(asc|desc)$/).optional().default('desc'),
    starred: z.boolean().nullish(),
    tag: z.string().max(50).nullish(),
    collection_id: z.uuid().nullish(),
    document_id: z.uuid().nullish(),
    trashed: z.boolean().optional().default(false),
    include_count: z.boolean().optional().default(false)
});

/**
 * Paginated list of notes.
 */
export const zGetApiV1NotesResponse = zPaginatedResponseNoteResponse;

export const zPostApiV1NotesBody = zNoteCreate;

export const zPostApiV1NotesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * The newly created note.
 */
export const zPostApiV1NotesResponse = zNoteResponse;

export const zGetApiV1NotesAssetsResolveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesAssetsResolveQuery = z.object({
    storage_key: z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1NotesAssetsResolveResponse = zNoteAssetResolveResponse;

export const zPostApiV1NotesAssetsUploadUrlBody = zNoteAssetUploadRequest;

export const zPostApiV1NotesAssetsUploadUrlHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesAssetsUploadUrlResponse = zNoteAssetUploadResponse;

export const zPostApiV1NotesAssetsAssetIdConfirmHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesAssetsAssetIdConfirmPath = z.object({
    asset_id: z.string()
});

export const zPostApiV1NotesAssetsAssetIdConfirmQuery = z.object({
    storage_key: z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesAssetsAssetIdConfirmResponse = zNoteAssetConfirmResponse;

export const zGetApiV1NotesDocumentDocumentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesDocumentDocumentIdPath = z.object({
    document_id: z.uuid()
});

export const zGetApiV1NotesDocumentDocumentIdQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    include_count: z.boolean().optional().default(false)
});

/**
 * Paginated list of notes linked to the document.
 */
export const zGetApiV1NotesDocumentDocumentIdResponse = zPaginatedResponseNoteResponse;

export const zGetApiV1NotesTreeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesTreeQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(200),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1NotesTreeResponse = zPaginatedResponseNoteResponse;

export const zDeleteApiV1NotesNoteIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1NotesNoteIdPath = z.object({
    note_id: z.uuid()
});

export const zDeleteApiV1NotesNoteIdQuery = z.object({
    permanent: z.boolean().optional().default(false)
});

/**
 * Deletion confirmation.
 */
export const zDeleteApiV1NotesNoteIdResponse = zNoteDeleteResponse;

export const zGetApiV1NotesNoteIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesNoteIdPath = z.object({
    note_id: z.uuid()
});

/**
 * The requested note.
 */
export const zGetApiV1NotesNoteIdResponse = zNoteResponse;

export const zPatchApiV1NotesNoteIdBody = zNoteUpdate;

export const zPatchApiV1NotesNoteIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1NotesNoteIdPath = z.object({
    note_id: z.uuid()
});

/**
 * The updated note.
 */
export const zPatchApiV1NotesNoteIdResponse = zNoteResponse;

export const zPostApiV1NotesNoteIdAiAnalyzeBody = zNoteAiAnalysisRequest;

export const zPostApiV1NotesNoteIdAiAnalyzeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdAiAnalyzePath = z.object({
    note_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdAiAnalyzeResponse = zNoteAiAnalysisResponse;

export const zPostApiV1NotesNoteIdAssetsDiffBody = zNoteAssetDiffRequest;

export const zPostApiV1NotesNoteIdAssetsDiffHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdAssetsDiffPath = z.object({
    note_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdAssetsDiffResponse = zNoteAssetDiffResponse;

export const zGetApiV1NotesNoteIdCommentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesNoteIdCommentsPath = z.object({
    note_id: z.uuid()
});

export const zGetApiV1NotesNoteIdCommentsQuery = z.object({
    resolved: z.boolean().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(100),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1NotesNoteIdCommentsResponse = zPaginatedResponseNoteCommentResponse;

export const zPostApiV1NotesNoteIdCommentsBody = zNoteCommentCreate;

export const zPostApiV1NotesNoteIdCommentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdCommentsPath = z.object({
    note_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdCommentsResponse = zNoteCommentResponse;

export const zDeleteApiV1NotesNoteIdCommentsCommentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1NotesNoteIdCommentsCommentIdPath = z.object({
    note_id: z.uuid(),
    comment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1NotesNoteIdCommentsCommentIdResponse = z.void();

export const zPatchApiV1NotesNoteIdCommentsCommentIdBody = zNoteCommentUpdate;

export const zPatchApiV1NotesNoteIdCommentsCommentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1NotesNoteIdCommentsCommentIdPath = z.object({
    note_id: z.uuid(),
    comment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1NotesNoteIdCommentsCommentIdResponse = zNoteCommentResponse;

export const zPostApiV1NotesNoteIdCommentsCommentIdResolveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdCommentsCommentIdResolvePath = z.object({
    note_id: z.uuid(),
    comment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdCommentsCommentIdResolveResponse = zNoteCommentResponse;

export const zPostApiV1NotesNoteIdCoverConfirmHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdCoverConfirmPath = z.object({
    note_id: z.uuid()
});

export const zPostApiV1NotesNoteIdCoverConfirmQuery = z.object({
    storage_key: z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdCoverConfirmResponse = zNoteCoverConfirmResponse;

export const zPostApiV1NotesNoteIdCoverUploadUrlHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdCoverUploadUrlPath = z.object({
    note_id: z.uuid()
});

export const zPostApiV1NotesNoteIdCoverUploadUrlQuery = z.object({
    content_type: z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdCoverUploadUrlResponse = zNoteCoverUploadResponse;

export const zPostApiV1NotesNoteIdMoveBody = zNoteMoveRequest;

export const zPostApiV1NotesNoteIdMoveHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdMovePath = z.object({
    note_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdMoveResponse = zNoteResponse;

export const zPostApiV1NotesNoteIdRestoreHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdRestorePath = z.object({
    note_id: z.uuid()
});

/**
 * The restored note.
 */
export const zPostApiV1NotesNoteIdRestoreResponse = zNoteResponse;

export const zGetApiV1NotesNoteIdSnapshotsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesNoteIdSnapshotsPath = z.object({
    note_id: z.uuid()
});

export const zGetApiV1NotesNoteIdSnapshotsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1NotesNoteIdSnapshotsResponse = zPaginatedResponseNoteSnapshotResponse;

export const zGetApiV1NotesNoteIdSnapshotsSnapshotIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesNoteIdSnapshotsSnapshotIdPath = z.object({
    note_id: z.uuid(),
    snapshot_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1NotesNoteIdSnapshotsSnapshotIdResponse = zNoteSnapshotDetailResponse;

export const zPostApiV1NotesNoteIdSnapshotsSnapshotIdRestoreHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1NotesNoteIdSnapshotsSnapshotIdRestorePath = z.object({
    note_id: z.uuid(),
    snapshot_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1NotesNoteIdSnapshotsSnapshotIdRestoreResponse = zNoteSnapshotRestoreResponse;

export const zPatchApiV1NotesNoteIdStarHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1NotesNoteIdStarPath = z.object({
    note_id: z.uuid()
});

/**
 * The note with updated starred state.
 */
export const zPatchApiV1NotesNoteIdStarResponse = zNoteResponse;

export const zGetApiV1NotesNoteIdSyncStatusHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1NotesNoteIdSyncStatusPath = z.object({
    note_id: z.uuid()
});

export const zGetApiV1NotesNoteIdSyncStatusQuery = z.object({
    write_id: z.string().max(36)
});

/**
 * Successful Response
 */
export const zGetApiV1NotesNoteIdSyncStatusResponse = zNoteSyncStatusResponse;

export const zPatchApiV1NotesNoteIdVisibilityHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1NotesNoteIdVisibilityPath = z.object({
    note_id: z.uuid()
});

/**
 * The note with updated visibility.
 */
export const zPatchApiV1NotesNoteIdVisibilityResponse = zNoteResponse;

export const zGetApiV1NotificationsQuery = z.object({
    workspace_id: z.uuid().nullish(),
    category: zNotificationCategory.nullish(),
    unread_only: z.boolean().optional().default(false),
    page: z.int().gte(1).lte(500).optional().default(1),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    count_total: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zGetApiV1NotificationsResponse = zNotificationListResponse;

export const zPostApiV1NotificationsDismissBody = zNotificationDismiss;

/**
 * Successful Response
 */
export const zPostApiV1NotificationsDismissResponse = zSuccessResponse;

export const zPostApiV1NotificationsDismissAllQuery = z.object({
    workspace_id: z.uuid().nullish(),
    category: zNotificationCategory.nullish()
});

/**
 * Successful Response
 */
export const zPostApiV1NotificationsDismissAllResponse = zSuccessResponse;

export const zPostApiV1NotificationsMarkAllReadQuery = z.object({
    workspace_id: z.uuid().nullish(),
    category: zNotificationCategory.nullish()
});

/**
 * Successful Response
 */
export const zPostApiV1NotificationsMarkAllReadResponse = zSuccessResponse;

export const zPostApiV1NotificationsMarkReadBody = zNotificationMarkRead;

/**
 * Successful Response
 */
export const zPostApiV1NotificationsMarkReadResponse = zSuccessResponse;

/**
 * Successful Response
 */
export const zGetApiV1NotificationsPreferencesResponse = zNotificationPreferencesRead;

export const zPutApiV1NotificationsPreferencesBody = zNotificationPreferencesBulkUpdate;

/**
 * Successful Response
 */
export const zPutApiV1NotificationsPreferencesResponse = zSuccessResponse;

export const zGetApiV1NotificationsUnreadCountQuery = z.object({
    workspace_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1NotificationsUnreadCountResponse = zUnreadCountResponse;

export const zGetApiV1NotificationsNotificationIdPath = z.object({
    notification_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1NotificationsNotificationIdResponse = zNotificationRead;

/**
 * Onboarding completed
 */
export const zPostApiV1OnboardingCompleteResponse = zOnboardingCompleteResponse;

export const zPostApiV1OnboardingDiscoverBody = zOnboardingDiscoverRequest;

/**
 * Search results returned
 */
export const zPostApiV1OnboardingDiscoverResponse = zOnboardingDiscoverResponse;

export const zPatchApiV1OnboardingProfileBody = zOnboardingProfileUpdate;

/**
 * Profile updated
 */
export const zPatchApiV1OnboardingProfileResponse = zOnboardingProfileResponse;

/**
 * Onboarding skipped successfully
 */
export const zPostApiV1OnboardingSkipResponse = zOnboardingSkipResponse;

/**
 * Onboarding profile retrieved
 */
export const zGetApiV1OnboardingStatusResponse = zOnboardingProfileResponse;

export const zPostApiV1SearchAnswerBody = zSearchAnswerRequest;

export const zPostApiV1SearchAnswerHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SessionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SessionsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish(),
    status: z.string().nullish(),
    category: z.string().nullish(),
    search: z.string().nullish(),
    sort_by: z.string().optional().default('updated_at'),
    sort_order: z.string().optional().default('desc')
});

/**
 * Paginated list of research session summaries.
 */
export const zGetApiV1SessionsResponse = zPaginatedResponseSessionSummaryRead;

export const zPostApiV1SessionsBody = zSessionCreate;

export const zPostApiV1SessionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * The newly created research session summary.
 */
export const zPostApiV1SessionsResponse = zSessionSummaryRead;

export const zDeleteApiV1SessionsSessionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1SessionsSessionIdPath = z.object({
    session_id: z.uuid()
});

/**
 * Confirmation with the deleted session ID.
 */
export const zDeleteApiV1SessionsSessionIdResponse = zResourceDeleteResponse;

export const zGetApiV1SessionsSessionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SessionsSessionIdPath = z.object({
    session_id: z.uuid()
});

/**
 * The full research session details.
 */
export const zGetApiV1SessionsSessionIdResponse = zSessionRead;

export const zPatchApiV1SessionsSessionIdBody = zSessionUpdate;

export const zPatchApiV1SessionsSessionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SessionsSessionIdPath = z.object({
    session_id: z.uuid()
});

/**
 * The updated research session summary.
 */
export const zPatchApiV1SessionsSessionIdResponse = zSessionSummaryRead;

export const zPostApiV1SessionsSessionIdGoalsBody = zSessionGoalCreate;

export const zPostApiV1SessionsSessionIdGoalsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SessionsSessionIdGoalsPath = z.object({
    session_id: z.uuid()
});

/**
 * Session with added goal.
 */
export const zPostApiV1SessionsSessionIdGoalsResponse = zSessionRead;

export const zPatchApiV1SessionsSessionIdGoalsGoalIdBody = zSessionGoalUpdate;

export const zPatchApiV1SessionsSessionIdGoalsGoalIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SessionsSessionIdGoalsGoalIdPath = z.object({
    session_id: z.uuid(),
    goal_id: z.string()
});

/**
 * Session with updated goal.
 */
export const zPatchApiV1SessionsSessionIdGoalsGoalIdResponse = zSessionRead;

export const zPostApiV1SessionsSessionIdInsightsBody = zSessionInsightCreate;

export const zPostApiV1SessionsSessionIdInsightsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SessionsSessionIdInsightsPath = z.object({
    session_id: z.uuid()
});

/**
 * Session with added insight.
 */
export const zPostApiV1SessionsSessionIdInsightsResponse = zSessionRead;

export const zGetApiV1SharesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SharesQuery = z.object({
    resource_type: z.string().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1SharesResponse = zListSharesResponse;

export const zPostApiV1SharesBody = zGetOrCreateSharingRequest;

export const zPostApiV1SharesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesResponse = zSharingConfigResponse;

export const zGetApiV1SharesResolveTokenPath = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1SharesResolveTokenResponse = zPublicSharingResponse;

export const zGetApiV1SharesResolveTokenCommentsPath = z.object({
    token: z.string()
});

export const zGetApiV1SharesResolveTokenCommentsQuery = z.object({
    page_size: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SharesResolveTokenCommentsResponse = zShareCommentsResponse;

export const zPostApiV1SharesResolveTokenCommentsBody = zCreateShareCommentRequest;

export const zPostApiV1SharesResolveTokenCommentsPath = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesResolveTokenCommentsResponse = zShareCommentResponse;

export const zDeleteApiV1SharesResolveTokenCommentsCommentIdPath = z.object({
    token: z.string(),
    comment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1SharesResolveTokenCommentsCommentIdResponse = z.void();

export const zPatchApiV1SharesResolveTokenCommentsCommentIdBody = zUpdateShareCommentRequest;

export const zPatchApiV1SharesResolveTokenCommentsCommentIdPath = z.object({
    token: z.string(),
    comment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SharesResolveTokenCommentsCommentIdResponse = zShareCommentResponse;

export const zGetApiV1SharesResolveTokenCommentsCommentIdRepliesPath = z.object({
    token: z.string(),
    comment_id: z.uuid()
});

export const zGetApiV1SharesResolveTokenCommentsCommentIdRepliesQuery = z.object({
    page_size: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SharesResolveTokenCommentsCommentIdRepliesResponse = zShareCommentsResponse;

export const zGetApiV1SharesResolveTokenContentPath = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zGetApiV1SharesResolveTokenContentResponse = zSharedContentResponse;

export const zPostApiV1SharesResolveTokenProcessBody = zProcessShareDocumentRequest;

export const zPostApiV1SharesResolveTokenProcessHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SharesResolveTokenProcessPath = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesResolveTokenProcessResponse = zProcessShareDocumentResponse;

export const zPostApiV1SharesResolveTokenSaveToLibraryBody = zSaveFromShareRequest;

export const zPostApiV1SharesResolveTokenSaveToLibraryHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SharesResolveTokenSaveToLibraryPath = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesResolveTokenSaveToLibraryResponse = zSaveFromShareResponse;

export const zGetApiV1SharesSharedWithMeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SharesSharedWithMeQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SharesSharedWithMeResponse = zSharedWithMeResponse;

export const zGetApiV1SharesSharingIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SharesSharingIdPath = z.object({
    sharing_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SharesSharingIdResponse = zSharingConfigResponse;

export const zPostApiV1SharesSharingIdAccessPath = z.object({
    sharing_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesSharingIdAccessResponse = z.void();

export const zPostApiV1SharesSharingIdCollaboratorsBody = zAddCollaboratorsRequest;

export const zPostApiV1SharesSharingIdCollaboratorsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SharesSharingIdCollaboratorsPath = z.object({
    sharing_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesSharingIdCollaboratorsResponse = zSharingConfigResponse;

export const zDeleteApiV1SharesSharingIdCollaboratorsCollaboratorIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1SharesSharingIdCollaboratorsCollaboratorIdPath = z.object({
    sharing_id: z.uuid(),
    collaborator_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1SharesSharingIdCollaboratorsCollaboratorIdResponse = z.void();

export const zPatchApiV1SharesSharingIdCollaboratorsCollaboratorIdBody = zUpdateCollaboratorRequest;

export const zPatchApiV1SharesSharingIdCollaboratorsCollaboratorIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SharesSharingIdCollaboratorsCollaboratorIdPath = z.object({
    sharing_id: z.uuid(),
    collaborator_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SharesSharingIdCollaboratorsCollaboratorIdResponse = zCollaboratorResponse;

export const zPutApiV1SharesSharingIdLinkBody = zUpdateLinkAccessRequest;

export const zPutApiV1SharesSharingIdLinkHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPutApiV1SharesSharingIdLinkPath = z.object({
    sharing_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPutApiV1SharesSharingIdLinkResponse = zSharingConfigResponse;

export const zPostApiV1SharesSharingIdSendBody = zSendShareRequest;

export const zPostApiV1SharesSharingIdSendHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SharesSharingIdSendPath = z.object({
    sharing_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesSharingIdSendResponse = zSendShareResponse;

export const zPostApiV1SharesSharingIdVerifyPasswordBody = zVerifySharingPasswordRequest;

export const zPostApiV1SharesSharingIdVerifyPasswordPath = z.object({
    sharing_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SharesSharingIdVerifyPasswordResponse = zVerifySharePasswordResponse;

export const zGetApiV1SystematicReviewsReviewIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdResponse = zSrProjectRead;

export const zPatchApiV1SystematicReviewsReviewIdBody = zSrProjectUpdate;

export const zPatchApiV1SystematicReviewsReviewIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SystematicReviewsReviewIdPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SystematicReviewsReviewIdResponse = zSrProjectRead;

export const zPostApiV1SystematicReviewsReviewIdAdvanceStatusBody = zSrAdvanceStatus;

export const zPostApiV1SystematicReviewsReviewIdAdvanceStatusHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdAdvanceStatusPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdAdvanceStatusResponse = zSrProjectRead;

export const zPostApiV1SystematicReviewsReviewIdAmendmentsBody = zSrAmendmentCreate;

export const zPostApiV1SystematicReviewsReviewIdAmendmentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdAmendmentsPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdAmendmentsResponse = zSrAmendmentRead;

export const zGetApiV1SystematicReviewsReviewIdAuditTrailHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdAuditTrailPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdAuditTrailQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdAuditTrailResponse = zSrAuditTrailPage;

export const zGetApiV1SystematicReviewsReviewIdConflictsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdConflictsPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdConflictsQuery = z.object({
    stage: zSrScreeningStage.nullish(),
    status: zSrConflictStatus.nullish(),
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Conflicts
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdConflictsResponse = z.array(zSrConflictRead);

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveConsensusBody = zSrConflictResolveConsensus;

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveConsensusHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveConsensusPath = z.object({
    review_id: z.uuid(),
    conflict_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveConsensusResponse = zSrStageOutcomeRead;

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveLeadOverrideBody = zSrConflictResolveLeadOverride;

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveLeadOverrideHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveLeadOverridePath = z.object({
    review_id: z.uuid(),
    conflict_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveLeadOverrideResponse = zSrStageOutcomeRead;

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveThirdReviewerBody = zSrConflictResolveThirdReviewer;

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveThirdReviewerHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveThirdReviewerPath = z.object({
    review_id: z.uuid(),
    conflict_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdConflictsConflictIdResolveThirdReviewerResponse = zSrStageOutcomeRead;

export const zGetApiV1SystematicReviewsReviewIdCriteriaHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdCriteriaPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdCriteriaQuery = z.object({
    active_only: z.boolean().optional().default(true)
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Criteria
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdCriteriaResponse = z.array(zSrCriterionRead);

export const zPostApiV1SystematicReviewsReviewIdCriteriaBody = zSrCriterionCreate;

export const zPostApiV1SystematicReviewsReviewIdCriteriaHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdCriteriaPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdCriteriaResponse = zSrCriterionRead;

export const zPatchApiV1SystematicReviewsReviewIdCriteriaCriterionIdBody = zSrCriterionUpdate;

export const zPatchApiV1SystematicReviewsReviewIdCriteriaCriterionIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SystematicReviewsReviewIdCriteriaCriterionIdPath = z.object({
    review_id: z.uuid(),
    criterion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SystematicReviewsReviewIdCriteriaCriterionIdResponse = zSrCriterionRead;

export const zGetApiV1SystematicReviewsReviewIdDedupClustersHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdDedupClustersPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdDedupClustersQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdDedupClustersResponse = zPaginatedResponseSrDedupClusterRead;

export const zPatchApiV1SystematicReviewsReviewIdDedupClustersClusterIdBody = zSrDedupClusterResolve;

export const zPatchApiV1SystematicReviewsReviewIdDedupClustersClusterIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SystematicReviewsReviewIdDedupClustersClusterIdPath = z.object({
    review_id: z.uuid(),
    cluster_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SystematicReviewsReviewIdDedupClustersClusterIdResponse = z.void();

export const zPostApiV1SystematicReviewsReviewIdDedupManualMergeBody = zSrManualMerge;

export const zPostApiV1SystematicReviewsReviewIdDedupManualMergeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdDedupManualMergePath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdDedupManualMergeResponse = z.void();

export const zPostApiV1SystematicReviewsReviewIdDedupRunHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdDedupRunPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdDedupRunResponse = zSrDedupResult;

export const zGetApiV1SystematicReviewsReviewIdEvidenceTableHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdEvidenceTablePath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdEvidenceTableQuery = z.object({
    limit: z.int().gte(1).lte(1000).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdEvidenceTableResponse = zSrEvidenceTable;

export const zGetApiV1SystematicReviewsReviewIdExclusionReasonsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExclusionReasonsPath = z.object({
    review_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Exclusion-Reasons
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdExclusionReasonsResponse = z.array(zSrExclusionReasonRead);

export const zPostApiV1SystematicReviewsReviewIdExclusionReasonsBody = zSrExclusionReasonCreate;

export const zPostApiV1SystematicReviewsReviewIdExclusionReasonsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdExclusionReasonsPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdExclusionReasonsResponse = zSrExclusionReasonRead;

export const zGetApiV1SystematicReviewsReviewIdExportsEvidenceTableCsvHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExportsEvidenceTableCsvPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdExportsEvidenceTableDocxHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExportsEvidenceTableDocxPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdExportsProtocolPdfHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExportsProtocolPdfPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdExportsProtocolPdfResponse = z.string();

export const zGetApiV1SystematicReviewsReviewIdExportsRecordsRisHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExportsRecordsRisPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdExportsRecordsRisQuery = z.object({
    included_only: z.boolean().optional().default(false)
});

export const zGetApiV1SystematicReviewsReviewIdExportsScreeningDecisionsCsvHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExportsScreeningDecisionsCsvPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdExtractionFieldsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExtractionFieldsPath = z.object({
    review_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Extraction-Fields
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdExtractionFieldsResponse = z.array(zSrExtractionFieldRead);

export const zPostApiV1SystematicReviewsReviewIdExtractionFieldsBody = zSrExtractionFieldCreate;

export const zPostApiV1SystematicReviewsReviewIdExtractionFieldsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdExtractionFieldsPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdExtractionFieldsResponse = zSrExtractionFieldRead;

export const zDeleteApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdPath = z.object({
    review_id: z.uuid(),
    field_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdResponse = z.void();

export const zPatchApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdBody = zSrExtractionFieldUpdate;

export const zPatchApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdPath = z.object({
    review_id: z.uuid(),
    field_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SystematicReviewsReviewIdExtractionFieldsFieldIdResponse = zSrExtractionFieldRead;

export const zGetApiV1SystematicReviewsReviewIdExtractionTemplatesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdExtractionTemplatesPath = z.object({
    review_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Extraction-Templates
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdExtractionTemplatesResponse = z.array(zSrExtractionTemplate);

export const zPostApiV1SystematicReviewsReviewIdExtractionTemplatesTemplateKeyHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdExtractionTemplatesTemplateKeyPath = z.object({
    review_id: z.uuid(),
    template_key: z.string()
});

/**
 * Response Post Api V1 Systematic-Reviews Review Id Extraction-Templates Template Key
 *
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdExtractionTemplatesTemplateKeyResponse = z.array(zSrExtractionFieldRead);

export const zPostApiV1SystematicReviewsReviewIdFinalizeProtocolHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdFinalizeProtocolPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdFinalizeProtocolResponse = zSrProjectRead;

export const zGetApiV1SystematicReviewsReviewIdFunnelHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdFunnelPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdFunnelResponse = zFunnelSummary;

export const zGetApiV1SystematicReviewsReviewIdFunnelActivityHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdFunnelActivityPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdFunnelActivityQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdFunnelActivityResponse = zSrFunnelActivityPage;

export const zPostApiV1SystematicReviewsReviewIdImportBody = zBodyPostApiV1SystematicReviewsReviewIdImport;

export const zPostApiV1SystematicReviewsReviewIdImportHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdImportPath = z.object({
    review_id: z.uuid()
});

export const zPostApiV1SystematicReviewsReviewIdImportQuery = z.object({
    source_type: zSrRecordSource,
    source_name: z.string().max(255).nullish(),
    search_query_string: z.string().max(10000).nullish()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdImportResponse = zSrImportResult;

export const zGetApiV1SystematicReviewsReviewIdManuscriptHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdManuscriptPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdManuscriptResponse = zSrManuscriptRead;

export const zPostApiV1SystematicReviewsReviewIdManuscriptGenerateHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdManuscriptGeneratePath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdManuscriptGenerateResponse = zSrManuscriptGenerateResponse;

export const zPostApiV1SystematicReviewsReviewIdManuscriptRegenerateBody = zSrRegenerateRequest;

export const zPostApiV1SystematicReviewsReviewIdManuscriptRegenerateHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdManuscriptRegeneratePath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdManuscriptRegenerateResponse = zSrRegenerateResponse;

export const zPatchApiV1SystematicReviewsReviewIdManuscriptSectionsSectionTypeBody = zSrSectionLockUpdate;

export const zPatchApiV1SystematicReviewsReviewIdManuscriptSectionsSectionTypeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SystematicReviewsReviewIdManuscriptSectionsSectionTypePath = z.object({
    review_id: z.uuid(),
    section_type: zSrManuscriptSectionType
});

/**
 * Successful Response
 */
export const zPatchApiV1SystematicReviewsReviewIdManuscriptSectionsSectionTypeResponse = zSrManuscriptSectionRead;

export const zGetApiV1SystematicReviewsReviewIdManuscriptStalenessHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdManuscriptStalenessPath = z.object({
    review_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Manuscript Staleness
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdManuscriptStalenessResponse = z.array(zSrSectionStaleness);

export const zGetApiV1SystematicReviewsReviewIdMembersHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdMembersPath = z.object({
    review_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Members
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdMembersResponse = z.array(zSrMemberRead);

export const zPostApiV1SystematicReviewsReviewIdMembersBody = zSrMemberAdd;

export const zPostApiV1SystematicReviewsReviewIdMembersHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdMembersPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdMembersResponse = zSrMemberRead;

export const zDeleteApiV1SystematicReviewsReviewIdMembersUserIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1SystematicReviewsReviewIdMembersUserIdPath = z.object({
    review_id: z.uuid(),
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1SystematicReviewsReviewIdMembersUserIdResponse = z.void();

export const zGetApiV1SystematicReviewsReviewIdPrismaHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdPrismaPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdPrismaResponse = zSrPrismaCounts;

export const zGetApiV1SystematicReviewsReviewIdPrismaDiagramHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdPrismaDiagramPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdPrismaDiagramResponse = z.string();

export const zGetApiV1SystematicReviewsReviewIdRecordsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRecordsPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdRecordsQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    status: zSrRecordStatus.nullish(),
    source_type: zSrRecordSource.nullish(),
    dedup_cluster_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRecordsResponse = zPaginatedResponseSrRecordListItem;

export const zPostApiV1SystematicReviewsReviewIdRecordsBody = zSrManualRecordCreate;

export const zPostApiV1SystematicReviewsReviewIdRecordsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdRecordsPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdRecordsResponse = zSrRecordRead;

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdPath = z.object({
    review_id: z.uuid(),
    record_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdResponse = zSrRecordRead;

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsPath = z.object({
    review_id: z.uuid(),
    record_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsQuery = z.object({
    stage: zSrScreeningStage.nullish()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Records Record Id Decisions
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsResponse = z.array(zSrDecisionRead);

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsBody = zSrDecisionCreate;

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsPath = z.object({
    review_id: z.uuid(),
    record_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdDecisionsResponse = zSrDecisionRead;

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionPath = z.object({
    review_id: z.uuid(),
    record_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Records Record Id Extraction
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionResponse = z.array(zSrExtractionValueRead);

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionCompleteHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionCompletePath = z.object({
    review_id: z.uuid(),
    record_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionCompleteResponse = zSrExtractionRecordSummary;

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionFieldIdBody = zSrExtractionValueCreate;

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionFieldIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionFieldIdPath = z.object({
    review_id: z.uuid(),
    record_id: z.uuid(),
    field_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdRecordsRecordIdExtractionFieldIdResponse = zSrExtractionValueRead;

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsQuery = z.object({
    record_id: z.uuid().nullish()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Rob-Assessments
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsResponse = z.array(zSrRoBassessmentRead);

export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsBody = zSrRoBassessmentCreate;

export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsPath = z.object({
    review_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsResponse = zSrRoBassessmentRead;

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdPath = z.object({
    review_id: z.uuid(),
    assessment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdResponse = zSrRoBassessmentRead;

export const zPatchApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdBody = zSrRoBassessmentUpdate;

export const zPatchApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdPath = z.object({
    review_id: z.uuid(),
    assessment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdResponse = zSrRoBassessmentRead;

export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdCompleteHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdCompletePath = z.object({
    review_id: z.uuid(),
    assessment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdCompleteResponse = zSrRoBassessmentRead;

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdJudgmentHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdJudgmentPath = z.object({
    review_id: z.uuid(),
    assessment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRobAssessmentsAssessmentIdJudgmentResponse = zSrRoBJudgmentSummary;

export const zGetApiV1SystematicReviewsReviewIdRobTrafficLightHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdRobTrafficLightPath = z.object({
    review_id: z.uuid()
});

/**
 * Response Get Api V1 Systematic-Reviews Review Id Rob-Traffic-Light
 *
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdRobTrafficLightResponse = z.array(zSrRoBTrafficLightEntry);

export const zGetApiV1SystematicReviewsReviewIdScreeningExclusionAnalysisHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdScreeningExclusionAnalysisPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdScreeningExclusionAnalysisQuery = z.object({
    stage: zSrScreeningStage
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdScreeningExclusionAnalysisResponse = zSrExclusionAnalysis;

export const zGetApiV1SystematicReviewsReviewIdScreeningReviewerPatternsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdScreeningReviewerPatternsPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdScreeningReviewerPatternsQuery = z.object({
    stage: zSrScreeningStage
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdScreeningReviewerPatternsResponse = zSrReviewerPatterns;

export const zGetApiV1SystematicReviewsReviewIdScreeningStatsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1SystematicReviewsReviewIdScreeningStatsPath = z.object({
    review_id: z.uuid()
});

export const zGetApiV1SystematicReviewsReviewIdScreeningStatsQuery = z.object({
    stage: zSrScreeningStage
});

/**
 * Successful Response
 */
export const zGetApiV1SystematicReviewsReviewIdScreeningStatsResponse = zSrScreeningStats;

export const zGetApiV1UsersHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1UsersQuery = z.object({
    email: z.string().min(3).max(254),
    limit: z.int().gte(1).lte(50).optional().default(20)
});

/**
 * User list
 */
export const zGetApiV1UsersResponse = zPaginatedResponseUserRead;

/**
 * Deletion confirmation with schedule
 */
export const zDeleteApiV1UsersMeResponse = zAccountDeletionResponse;

/**
 * User profile
 */
export const zGetApiV1UsersMeResponse = zUserRead;

export const zPutApiV1UsersMeBody = zUserUpdate;

/**
 * Updated profile
 */
export const zPutApiV1UsersMeResponse = zUserRead;

/**
 * Response Get Api V1 Users Me Deletion-Status
 *
 * Deletion status details, or null if no pending deletion
 */
export const zGetApiV1UsersMeDeletionStatusResponse = zAccountDeletionStatusResponse.nullable();

/**
 * Payload
 */
export const zPostApiV1UsersMeExportBody = zDataExportRequest.nullable();

/**
 * Successful Response
 */
export const zPostApiV1UsersMeExportResponse = zDataExportRead;

export const zGetApiV1UsersMeExportsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(10),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1UsersMeExportsResponse = zDataExportList;

export const zGetApiV1UsersMeExportsExportIdPath = z.object({
    export_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1UsersMeExportsExportIdResponse = zDataExportRead;

/**
 * Reactivated user
 */
export const zPostApiV1UsersMeReactivateResponse = zUserRead;

export const zGetApiV1UsersMeSettingsHeaders = z.object({
    'If-None-Match': z.string().nullish()
});

/**
 * Full settings object
 */
export const zGetApiV1UsersMeSettingsResponse = zUserSettingsRead;

export const zPatchApiV1UsersMeSettingsAiBody = zAiSettingsUpdate;

export const zPatchApiV1UsersMeSettingsAiHeaders = z.object({
    'If-Match': z.string().nullish()
});

/**
 * Updated settings
 */
export const zPatchApiV1UsersMeSettingsAiResponse = zUserSettingsRead;

export const zPatchApiV1UsersMeSettingsNotificationsBody = zNotificationSettingsUpdate;

export const zPatchApiV1UsersMeSettingsNotificationsHeaders = z.object({
    'If-Match': z.string().nullish()
});

/**
 * Updated settings
 */
export const zPatchApiV1UsersMeSettingsNotificationsResponse = zUserSettingsRead;

export const zPatchApiV1UsersMeSettingsPrivacyBody = zPrivacySettingsUpdate;

export const zPatchApiV1UsersMeSettingsPrivacyHeaders = z.object({
    'If-Match': z.string().nullish()
});

/**
 * Updated settings
 */
export const zPatchApiV1UsersMeSettingsPrivacyResponse = zUserSettingsRead;

export const zPatchApiV1UsersMeSettingsProfileBody = zProfileSettingsUpdate;

export const zPatchApiV1UsersMeSettingsProfileHeaders = z.object({
    'If-Match': z.string().nullish()
});

/**
 * Updated settings
 */
export const zPatchApiV1UsersMeSettingsProfileResponse = zUserSettingsRead;

export const zGetApiV1UsersUserIdPath = z.object({
    user_id: z.uuid()
});

/**
 * User record
 */
export const zGetApiV1UsersUserIdResponse = zUserRead;

export const zGetApiV1UsersUserIdProfilePicturePath = z.object({
    user_id: z.uuid()
});

export const zGetApiV1UsersUserIdProfilePictureQuery = z.object({
    size: z.string().regex(/^(small|medium|large)$/).optional().default('medium')
});

/**
 * Profile picture URL
 */
export const zGetApiV1UsersUserIdProfilePictureResponse = zProfilePictureResponse;

export const zGetApiV1WebhooksHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WebhooksQuery = z.object({
    include_disabled: z.boolean().optional().default(false),
    limit: z.int().gte(1).lte(200).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * List of webhook endpoints
 */
export const zGetApiV1WebhooksResponse = zPaginatedResponseWebhookEndpointResponse;

export const zPostApiV1WebhooksBody = zWebhookEndpointCreate;

export const zPostApiV1WebhooksHeaders = z.object({
    'X-Workspace-ID': z.string()
});

/**
 * Webhook endpoint with secret
 */
export const zPostApiV1WebhooksResponse = zWebhookEndpointWithSecret;

/**
 * Response Get Api V1 Webhooks Events
 *
 * Available event types
 */
export const zGetApiV1WebhooksEventsResponse = z.array(z.record(z.string(), z.unknown()));

export const zPostApiV1WebhooksStripeHeaders = z.object({
    'Stripe-Signature': z.string()
});

/**
 * Successful Response
 */
export const zPostApiV1WebhooksStripeResponse = zStripeWebhookAck;

export const zDeleteApiV1WebhooksEndpointIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WebhooksEndpointIdPath = z.object({
    endpoint_id: z.uuid()
});

/**
 * Endpoint deleted
 */
export const zDeleteApiV1WebhooksEndpointIdResponse = z.void();

export const zGetApiV1WebhooksEndpointIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WebhooksEndpointIdPath = z.object({
    endpoint_id: z.uuid()
});

/**
 * Webhook endpoint details
 */
export const zGetApiV1WebhooksEndpointIdResponse = zWebhookEndpointResponse;

export const zPatchApiV1WebhooksEndpointIdBody = zWebhookEndpointUpdate;

export const zPatchApiV1WebhooksEndpointIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1WebhooksEndpointIdPath = z.object({
    endpoint_id: z.uuid()
});

/**
 * Updated webhook endpoint
 */
export const zPatchApiV1WebhooksEndpointIdResponse = zWebhookEndpointResponse;

export const zGetApiV1WebhooksEndpointIdDeliveriesHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WebhooksEndpointIdDeliveriesPath = z.object({
    endpoint_id: z.uuid()
});

export const zGetApiV1WebhooksEndpointIdDeliveriesQuery = z.object({
    status: zWebhookDeliveryStatus.nullish(),
    event_type: z.string().nullish(),
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(10).lte(200).optional().default(50)
});

/**
 * Delivery history
 */
export const zGetApiV1WebhooksEndpointIdDeliveriesResponse = zWebhookDeliveryList;

export const zPostApiV1WebhooksEndpointIdRotateSecretHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WebhooksEndpointIdRotateSecretPath = z.object({
    endpoint_id: z.uuid()
});

/**
 * New signing secret
 */
export const zPostApiV1WebhooksEndpointIdRotateSecretResponse = zRotateSecretResponse;

export const zGetApiV1WebhooksEndpointIdStatsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WebhooksEndpointIdStatsPath = z.object({
    endpoint_id: z.uuid()
});

/**
 * Delivery statistics
 */
export const zGetApiV1WebhooksEndpointIdStatsResponse = zWebhookStats;

export const zPostApiV1WebhooksEndpointIdVerifyHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WebhooksEndpointIdVerifyPath = z.object({
    endpoint_id: z.uuid()
});

/**
 * Verification result
 */
export const zPostApiV1WebhooksEndpointIdVerifyResponse = zVerifyEndpointResponse;

export const zGetApiV1WorkspacesQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Workspace list
 */
export const zGetApiV1WorkspacesResponse = zPaginatedResponseWorkspaceWithRoleResponse;

export const zPostApiV1WorkspacesBody = zWorkspaceCreate;

/**
 * Workspace created successfully
 */
export const zPostApiV1WorkspacesResponse = zWorkspaceCreateResponse;

export const zPostApiV1WorkspacesPersonalResponse = z.union([
    zWorkspaceCreateResponse,
    z.unknown()
]);

export const zDeleteApiV1WorkspacesWorkspaceIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WorkspacesWorkspaceIdPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Deletion confirmation
 */
export const zDeleteApiV1WorkspacesWorkspaceIdResponse = z.void();

export const zGetApiV1WorkspacesWorkspaceIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Workspace details
 */
export const zGetApiV1WorkspacesWorkspaceIdResponse = zWorkspaceRead;

export const zPatchApiV1WorkspacesWorkspaceIdBody = zWorkspaceUpdate;

export const zPatchApiV1WorkspacesWorkspaceIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1WorkspacesWorkspaceIdPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Updated workspace
 */
export const zPatchApiV1WorkspacesWorkspaceIdResponse = zWorkspaceRead;

export const zGetApiV1WorkspacesWorkspaceIdApiKeysHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdApiKeysPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdApiKeysQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdApiKeysResponse = zApiKeyList;

export const zPostApiV1WorkspacesWorkspaceIdApiKeysBody = zApiKeyCreate;

export const zPostApiV1WorkspacesWorkspaceIdApiKeysHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdApiKeysPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1WorkspacesWorkspaceIdApiKeysResponse = zApiKeyCreated;

export const zDeleteApiV1WorkspacesWorkspaceIdApiKeysKeyIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WorkspacesWorkspaceIdApiKeysKeyIdPath = z.object({
    workspace_id: z.uuid(),
    key_id: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteApiV1WorkspacesWorkspaceIdApiKeysKeyIdResponse = z.void();

export const zGetApiV1WorkspacesWorkspaceIdAuditHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdAuditPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdAuditQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    actorUserId: z.uuid().nullish(),
    entityType: z.string().nullish(),
    entityId: z.string().nullish(),
    action: zAuditAction.nullish(),
    actionPrefix: z.string().max(50).nullish(),
    status: zAuditStatus.nullish(),
    fromTs: z.iso.datetime().nullish(),
    toTs: z.iso.datetime().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdAuditResponse = zAuditListResponse;

export const zGetApiV1WorkspacesWorkspaceIdAuditEventIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdAuditEventIdPath = z.object({
    workspace_id: z.uuid(),
    event_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdAuditEventIdResponse = zAuditEventRead;

export const zListWorkspaceClaimsApiV1WorkspacesWorkspaceIdClaimsGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zListWorkspaceClaimsApiV1WorkspacesWorkspaceIdClaimsGetPath = z.object({
    workspace_id: z.uuid()
});

export const zListWorkspaceClaimsApiV1WorkspacesWorkspaceIdClaimsGetQuery = z.object({
    evidence_type: z.string().nullish(),
    effect_direction: z.string().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Successful Response
 */
export const zListWorkspaceClaimsApiV1WorkspacesWorkspaceIdClaimsGetResponse = zPaginatedResponseClaimRowRead;

export const zGetCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsGetPath = z.object({
    workspace_id: z.uuid()
});

export const zGetCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsGetQuery = z.object({
    doi: z.string(),
    limit: z.int().gte(1).lte(200).optional().default(20)
});

/**
 * Successful Response
 */
export const zGetCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsGetResponse = zCoCitationListRead;

export const zGetTopCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsTopGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetTopCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsTopGetPath = z.object({
    workspace_id: z.uuid()
});

export const zGetTopCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsTopGetQuery = z.object({
    limit: z.int().gte(1).lte(50).optional().default(10)
});

/**
 * Successful Response
 */
export const zGetTopCoCitationsApiV1WorkspacesWorkspaceIdCoCitationsTopGetResponse = zTopCoCitationListRead;

export const zGetContradictionsApiV1WorkspacesWorkspaceIdContradictionsGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetContradictionsApiV1WorkspacesWorkspaceIdContradictionsGetPath = z.object({
    workspace_id: z.uuid()
});

export const zGetContradictionsApiV1WorkspacesWorkspaceIdContradictionsGetQuery = z.object({
    severity: z.string().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zGetContradictionsApiV1WorkspacesWorkspaceIdContradictionsGetResponse = zContradictionListRead;

export const zGetEvidenceSummaryApiV1WorkspacesWorkspaceIdEvidenceSummaryGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetEvidenceSummaryApiV1WorkspacesWorkspaceIdEvidenceSummaryGetPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetEvidenceSummaryApiV1WorkspacesWorkspaceIdEvidenceSummaryGetResponse = zEvidenceSynthesisRead;

export const zGetApiV1WorkspacesWorkspaceIdFeedbackHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdFeedbackPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdFeedbackQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish(),
    category: z.string().nullish(),
    status: z.string().nullish(),
    priority: z.string().nullish(),
    source: z.string().nullish(),
    fromTs: z.iso.datetime().nullish(),
    toTs: z.iso.datetime().nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdFeedbackResponse = zFeedbackListResponse;

export const zPostApiV1WorkspacesWorkspaceIdFeedbackBody = zFeedbackCreate;

export const zPostApiV1WorkspacesWorkspaceIdFeedbackHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdFeedbackPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1WorkspacesWorkspaceIdFeedbackResponse = zFeedbackRead;

export const zGetApiV1WorkspacesWorkspaceIdFeedbackStatsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdFeedbackStatsPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdFeedbackStatsResponse = zFeedbackStats;

export const zGetApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdPath = z.object({
    workspace_id: z.uuid(),
    feedback_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdResponse = zFeedbackRead;

export const zPatchApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdBody = zFeedbackUpdate;

export const zPatchApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdPath = z.object({
    workspace_id: z.uuid(),
    feedback_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPatchApiV1WorkspacesWorkspaceIdFeedbackFeedbackIdResponse = zFeedbackRead;

export const zGetApiV1WorkspacesWorkspaceIdHomeHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdHomePath = z.object({
    workspace_id: z.uuid()
});

/**
 * The composed home board payload.
 */
export const zGetApiV1WorkspacesWorkspaceIdHomeResponse = zHomeBoardResponse;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceCoCitationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceCoCitationsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceCoCitationsQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Co-citation pairs ordered by frequency
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceCoCitationsResponse = zTopCoCitationListRead;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContextHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContextPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Workspace context
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContextResponse = zWorkspaceContextRead;

export const zPatchApiV1WorkspacesWorkspaceIdIntelligenceContextBody = zWorkspaceContextUpdate;

export const zPatchApiV1WorkspacesWorkspaceIdIntelligenceContextHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1WorkspacesWorkspaceIdIntelligenceContextPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Context updated
 */
export const zPatchApiV1WorkspacesWorkspaceIdIntelligenceContextResponse = zWorkspaceContextRead;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceContextBody = zWorkspaceContextCreate;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceContextHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceContextPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Workspace context created
 */
export const zPostApiV1WorkspacesWorkspaceIdIntelligenceContextResponse = zWorkspaceContextRead;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContradictionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContradictionsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContradictionsQuery = z.object({
    severity: z.string().regex(/^(high|medium|low)$/).nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Contradiction pairs ordered by severity
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceContradictionsResponse = zContradictionListRead;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceDashboardHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceDashboardPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Dashboard data
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceDashboardResponse = zIntelligenceDashboard;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceDocumentsDocumentIdFeedbackBody = zIntelligenceFeedbackCreate;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceDocumentsDocumentIdFeedbackHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceDocumentsDocumentIdFeedbackPath = z.object({
    workspace_id: z.uuid(),
    document_id: z.uuid()
});

/**
 * Feedback recorded
 */
export const zPostApiV1WorkspacesWorkspaceIdIntelligenceDocumentsDocumentIdFeedbackResponse = zFeedbackAcknowledge;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceFeedbackHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceFeedbackPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceFeedbackQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Feedback list
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceFeedbackResponse = zPaginatedResponseIntelligenceFeedbackRead;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceResearchGapsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceResearchGapsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceResearchGapsQuery = z.object({
    severity: z.string().regex(/^(critical|notable|minor)$/).nullish(),
    limit: z.int().gte(1).lte(200).optional().default(20)
});

/**
 * Research gaps ordered by severity
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceResearchGapsResponse = zResearchGapListRead;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSearchBody = zSearchRequestSchema;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSearchHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSearchPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Search results
 */
export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSearchResponse = zSearchResponseSchema;

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(20)
});

/**
 * Suggestion list
 */
export const zGetApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsResponse = zSuggestionListResponse;

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsRefreshHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsRefreshPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Refreshed suggestions
 */
export const zPostApiV1WorkspacesWorkspaceIdIntelligenceSuggestionsRefreshResponse = zSuggestionListResponse;

export const zGetApiV1WorkspacesWorkspaceIdInvitationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdInvitationsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdInvitationsQuery = z.object({
    status: z.string().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Paginated list of invitations for the workspace.
 */
export const zGetApiV1WorkspacesWorkspaceIdInvitationsResponse = zInvitationListResponse;

export const zPostApiV1WorkspacesWorkspaceIdInvitationsBody = zInvitationCreate;

export const zPostApiV1WorkspacesWorkspaceIdInvitationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdInvitationsPath = z.object({
    workspace_id: z.uuid()
});

/**
 * The created invitation record.
 */
export const zPostApiV1WorkspacesWorkspaceIdInvitationsResponse = zInvitationRead;

export const zDeleteApiV1WorkspacesWorkspaceIdInvitationsInvitationIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WorkspacesWorkspaceIdInvitationsInvitationIdPath = z.object({
    workspace_id: z.uuid(),
    invitation_id: z.uuid()
});

/**
 * No content — invitation successfully revoked.
 */
export const zDeleteApiV1WorkspacesWorkspaceIdInvitationsInvitationIdResponse = z.void();

export const zPostApiV1WorkspacesWorkspaceIdInvitationsInvitationIdResendHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdInvitationsInvitationIdResendPath = z.object({
    workspace_id: z.uuid(),
    invitation_id: z.uuid()
});

/**
 * The updated invitation record with refreshed expiry.
 */
export const zPostApiV1WorkspacesWorkspaceIdInvitationsInvitationIdResendResponse = zInvitationRead;

export const zGetApiV1WorkspacesWorkspaceIdItemsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdItemsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdItemsQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Saved workspace items.
 */
export const zGetApiV1WorkspacesWorkspaceIdItemsResponse = zWorkspaceItemsListSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsBody = zSaveItemRequestSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Saved item record.
 */
export const zPostApiV1WorkspacesWorkspaceIdItemsResponse = zSaveItemResponseSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsExportCitationsBody = zCitationExportRequestSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsExportCitationsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsExportCitationsPath = z.object({
    workspace_id: z.uuid()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsImportBody = zBatchImportRequestSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsImportHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsImportPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Import results.
 */
export const zPostApiV1WorkspacesWorkspaceIdItemsImportResponse = zBatchImportResponseSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsImportWithPdfBody = zBatchImportWithPdfRequestSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsImportWithPdfHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsImportWithPdfPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Batch import results.
 */
export const zPostApiV1WorkspacesWorkspaceIdItemsImportWithPdfResponse = zBatchImportWithPdfResponseSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsSaveAndImportBody = zSaveAndImportRequestSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsSaveAndImportHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsSaveAndImportPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Import result with document ID.
 */
export const zPostApiV1WorkspacesWorkspaceIdItemsSaveAndImportResponse = zImportItemResponseSchema;

export const zDeleteApiV1WorkspacesWorkspaceIdItemsItemIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WorkspacesWorkspaceIdItemsItemIdPath = z.object({
    workspace_id: z.uuid(),
    item_id: z.uuid()
});

/**
 * Unsave confirmation.
 */
export const zDeleteApiV1WorkspacesWorkspaceIdItemsItemIdResponse = zUnsaveResponseSchema;

export const zGetApiV1WorkspacesWorkspaceIdItemsItemIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdItemsItemIdPath = z.object({
    workspace_id: z.uuid(),
    item_id: z.uuid()
});

/**
 * Workspace item detail.
 */
export const zGetApiV1WorkspacesWorkspaceIdItemsItemIdResponse = zWorkspaceItemSchema;

export const zPostApiV1WorkspacesWorkspaceIdItemsItemIdImportHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsItemIdImportPath = z.object({
    workspace_id: z.uuid(),
    item_id: z.uuid()
});

export const zPostApiV1WorkspacesWorkspaceIdItemsItemIdImportQuery = z.object({
    processing_mode: z.string().regex(/^(full|fast)$/).optional().default('full')
});

/**
 * Import result with document ID and pipeline status.
 */
export const zPostApiV1WorkspacesWorkspaceIdItemsItemIdImportResponse = zImportItemResponseSchema;

export const zPostApiV1WorkspacesWorkspaceIdLeavePath = z.object({
    workspace_id: z.uuid()
});

/**
 * Leave confirmation
 */
export const zPostApiV1WorkspacesWorkspaceIdLeaveResponse = z.void();

export const zGetApiV1WorkspacesWorkspaceIdMembersHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdMembersPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdMembersQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(100),
    cursor: z.string().nullish()
});

/**
 * Paginated member list
 */
export const zGetApiV1WorkspacesWorkspaceIdMembersResponse = zPaginatedResponseWorkspaceMemberRead;

export const zPostApiV1WorkspacesWorkspaceIdMembersBody = zUserWorkspaceRoleCreate;

export const zPostApiV1WorkspacesWorkspaceIdMembersHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdMembersPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Membership record
 */
export const zPostApiV1WorkspacesWorkspaceIdMembersResponse = zWorkspaceMemberRead;

export const zGetApiV1WorkspacesWorkspaceIdMembersHistoryHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdMembersHistoryPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdMembersHistoryQuery = z.object({
    user_id: z.uuid().nullish(),
    page_size: z.int().gte(1).lte(200).optional().default(50),
    cursor: z.string().nullish()
});

/**
 * Role change history
 */
export const zGetApiV1WorkspacesWorkspaceIdMembersHistoryResponse = zMemberHistoryResponse;

export const zDeleteApiV1WorkspacesWorkspaceIdMembersMembershipIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WorkspacesWorkspaceIdMembersMembershipIdPath = z.object({
    workspace_id: z.uuid(),
    membership_id: z.uuid()
});

/**
 * Deletion confirmation
 */
export const zDeleteApiV1WorkspacesWorkspaceIdMembersMembershipIdResponse = z.void();

export const zPatchApiV1WorkspacesWorkspaceIdMembersMembershipIdBody = zUserWorkspaceRoleUpdate;

export const zPatchApiV1WorkspacesWorkspaceIdMembersMembershipIdHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPatchApiV1WorkspacesWorkspaceIdMembersMembershipIdPath = z.object({
    workspace_id: z.uuid(),
    membership_id: z.uuid()
});

/**
 * Updated membership
 */
export const zPatchApiV1WorkspacesWorkspaceIdMembersMembershipIdResponse = zWorkspaceMemberRead;

export const zGetApiV1WorkspacesWorkspaceIdMembersUserIdPermissionsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdMembersUserIdPermissionsPath = z.object({
    workspace_id: z.uuid(),
    user_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdMembersUserIdPermissionsQuery = z.object({
    required_role: zWorkspaceRole
});

/**
 * Permission check result
 */
export const zGetApiV1WorkspacesWorkspaceIdMembersUserIdPermissionsResponse = zPermissionCheckResponse;

export const zGetApiV1WorkspacesWorkspaceIdPermissionSnapshotHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdPermissionSnapshotPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Server-signed permission snapshot
 */
export const zGetApiV1WorkspacesWorkspaceIdPermissionSnapshotResponse = zPermissionSnapshotResponse;

export const zGetRecommendationsApiV1WorkspacesWorkspaceIdRecommendationsPostBody = zRecommendationRequest;

export const zGetRecommendationsApiV1WorkspacesWorkspaceIdRecommendationsPostHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetRecommendationsApiV1WorkspacesWorkspaceIdRecommendationsPostPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zGetRecommendationsApiV1WorkspacesWorkspaceIdRecommendationsPostResponse = zRecommendationListRead;

export const zGetResearchGapsApiV1WorkspacesWorkspaceIdResearchGapsGetHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetResearchGapsApiV1WorkspacesWorkspaceIdResearchGapsGetPath = z.object({
    workspace_id: z.uuid()
});

export const zGetResearchGapsApiV1WorkspacesWorkspaceIdResearchGapsGetQuery = z.object({
    severity: z.string().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(20)
});

/**
 * Successful Response
 */
export const zGetResearchGapsApiV1WorkspacesWorkspaceIdResearchGapsGetResponse = zResearchGapListRead;

export const zGetApiV1WorkspacesWorkspaceIdStatsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdStatsPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Workspace statistics
 */
export const zGetApiV1WorkspacesWorkspaceIdStatsResponse = zWorkspaceStatsResponse;

export const zGetApiV1WorkspacesWorkspaceIdSystematicReviewsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdSystematicReviewsPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdSystematicReviewsQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(50),
    offset: z.int().gte(0).optional().default(0),
    status: zSrStatus.nullish()
});

/**
 * Successful Response
 */
export const zGetApiV1WorkspacesWorkspaceIdSystematicReviewsResponse = zPaginatedResponseSrProjectListItem;

export const zPostApiV1WorkspacesWorkspaceIdSystematicReviewsBody = zSrProjectCreate;

export const zPostApiV1WorkspacesWorkspaceIdSystematicReviewsHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdSystematicReviewsPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPostApiV1WorkspacesWorkspaceIdSystematicReviewsResponse = zSrProjectRead;

export const zPostApiV1WorkspacesWorkspaceIdTransferOwnershipBody = zOwnershipTransferRequest;

export const zPostApiV1WorkspacesWorkspaceIdTransferOwnershipHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zPostApiV1WorkspacesWorkspaceIdTransferOwnershipPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Ownership transfer result
 */
export const zPostApiV1WorkspacesWorkspaceIdTransferOwnershipResponse = zOwnershipTransferResponse;

export const zDeleteApiV1WorkspacesWorkspaceIdTrashHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zDeleteApiV1WorkspacesWorkspaceIdTrashPath = z.object({
    workspace_id: z.uuid()
});

/**
 * Deletion count
 */
export const zDeleteApiV1WorkspacesWorkspaceIdTrashResponse = zTrashEmptyResponse;

export const zGetApiV1WorkspacesWorkspaceIdTrashHeaders = z.object({
    'X-Workspace-ID': z.string()
});

export const zGetApiV1WorkspacesWorkspaceIdTrashPath = z.object({
    workspace_id: z.uuid()
});

export const zGetApiV1WorkspacesWorkspaceIdTrashQuery = z.object({
    page_size: z.int().gte(1).lte(200).optional().default(20),
    cursor: z.string().nullish()
});

/**
 * Trashed items list
 */
export const zGetApiV1WorkspacesWorkspaceIdTrashResponse = zTrashListResponse;

/**
 * Successful Response
 */
export const zListErrorTypesErrorsGetResponse = zErrorCatalog;

export const zGetErrorDocumentationErrorsErrorTypeGetPath = z.object({
    error_type: z.string()
});

/**
 * Successful Response
 */
export const zGetErrorDocumentationErrorsErrorTypeGetResponse = zErrorDocumentation;

/**
 * Successful Response
 */
export const zHealthCheckHealthGetResponse = zPublicHealthCheckResponse;

/**
 * Successful Response
 */
export const zCircuitBreakerHealthPublicHealthCircuitsGetResponse = zCircuitBreakerProbeResponse;

/**
 * Successful Response
 */
export const zDatabaseHealthPublicHealthDbGetResponse = zProbeStatusResponse;

/**
 * Successful Response
 */
export const zHealthCheckDetailedHealthDetailedGetResponse = zDetailedHealthCheckResponse;

/**
 * Successful Response
 */
export const zRedisHealthPublicHealthRedisGetResponse = zProbeStatusResponse;

/**
 * Successful Response
 */
export const zStorageHealthPublicHealthStorageGetResponse = zProbeStatusResponse;

/**
 * Successful Response
 */
export const zWorkerHealthPublicHealthWorkersGetResponse = zWorkerProbeResponse;

export const zInternalHealthInternalV1HealthGetHeaders = z.object({
    'X-Service-Key': z.string()
});

/**
 * Successful Response
 */
export const zInternalHealthInternalV1HealthGetResponse = zInternalHealthResponse;

export const zInternalDocumentStatusInternalV1PipelineDocumentDocumentIdGetHeaders = z.object({
    'X-Service-Key': z.string()
});

export const zInternalDocumentStatusInternalV1PipelineDocumentDocumentIdGetPath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zInternalDocumentStatusInternalV1PipelineDocumentDocumentIdGetResponse = zInternalDocumentStatusResponse;

export const zInternalOutboxStatsInternalV1PipelineOutboxStatsGetHeaders = z.object({
    'X-Service-Key': z.string()
});

/**
 * Successful Response
 */
export const zInternalOutboxStatsInternalV1PipelineOutboxStatsGetResponse = zInternalOutboxStatsResponse;

export const zInternalPipelineOverviewInternalV1PipelineOverviewGetHeaders = z.object({
    'X-Service-Key': z.string()
});

/**
 * Successful Response
 */
export const zInternalPipelineOverviewInternalV1PipelineOverviewGetResponse = zInternalPipelineOverviewResponse;
